/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import config from "../Config/config";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Loader } from "@googlemaps/js-api-loader";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import DailyRegistrationActivityTab, { DailyRegistrationActivityUsersDialog } from "./DailyRegistrationActivityTab";
import ProductSearchTab, { ProductSearchRefreshDialog } from "./ProductSearchTab";
import HibidAuctionsTab from "./HibidAuctionsTab";
import HibidUsersTab from "./HibidUsersTab";
import { AccountManagerAdminDialog, ConsignorAdminDialog } from "./HibidAdminDialogs";
import {
  AuctionUserDetailsDialog,
  RegistrationFulfillmentConfirmDialog,
  ShippingTextDialog,
} from "./HibidAuctionDialogs";
import UserRegistrationsLotsDialog from "./UserRegistrationsLotsDialog";
import useDailyRegistrationActivity from "./useDailyRegistrationActivity";
import useHibidUsers from "./useHibidUsers";
import useUserRegistrationsLotsModal from "./useUserRegistrationsLotsModal";
import useProductSearch from "./useProductSearch";
import useHibidAuctionsLogic from "./useHibidAuctionsLogic";
import useHibidEmailTools from "./useHibidEmailTools";

import EmailEditor from "react-email-editor";
const API = config.API_BASE_URL;
const GOOGLE_MAPS_API_KEY = String(
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyDjAbPhawP8SIJwGHX-W5XonP5U_vOUqAM"
).trim();

const ROUTES = {
  users: "/users",
  userByEmail: "/users/by-email",
  usersUpdate: "/users/update",
  usersDelete: "/users/delete",

  auctions: "/auctions",
  auctionsCreate: "/auctions/create",
  auctionsSummary: "/auctions/summary",
  auctionRegistrations: "/auction-registrations",
  auctionRegistrationsUpload: "/auction-registrations/upload",
  registrationActivityMonthly: "/registration-activity/monthly",
  registrationActivityUsers: "/registration-activity/users",
  userLots: "/user/lots",
  lotsSearch: "/lots/search",
  userAuctions: "/user/auctions",
  orgProfile: "/org/profile",
  accountManagers: "/account-managers",
  accountManagersCreate: "/account-managers/create",
  accountManagersAssignTiers: "/account-managers/assign-tiers",
  consignors: "/consignors",
  consignorsCreate: "/consignors/create",

  customersUpload: "/customers/upload",
  lotsUpload: "/auction-lots/upload",
  listernautUpload: "/auction-listernaut/upload",
  auctionsUpdateEnd: "/auctions/update-end",
  shippingUpload: "/auction-registrations/shipping/upload",
  shippingUpdate: "/auction-registrations/shipping/update",
  pickupUpdate: "/auction-registrations/pickup/update",
  pickupAppointmentUpdate: "/auction-registrations/pickup-appointment/update",
  registrationShelfUpdate: "/auction-registrations/shelf/update",
  shippedUpdate: "/auction-registrations/shipped/update",
  deliveredUpdate: "/auction-registrations/delivered/update",
  deliveryPhotosUpload: "/auction-registrations/delivery-photos/upload",
  lotPickupUpdate: "/auction-lots/pickup/update",
  lotShippedUpdate: "/auction-lots/shipped/update",
  lotDeliveredUpdate: "/auction-lots/delivered/update",
  registrationSignatureUpdate: "/auction-registrations/signature/update",

  marketingSendEmail: "/send-email",
  marketingTemplates: "/email-templates",

  snapshotsWriteDaily: "/snapshots/write-daily",
};
const FLOOR_BIDDER_EMAIL = "floor@gmail.com";
const HIBID_CACHE_VERSION = "v2";
const HIBID_USERS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const HIBID_PRODUCT_SEARCH_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const HIBID_PRODUCT_SEARCH_BATCH_SIZE = 2000;
const HIBID_PRODUCT_SEARCH_PARALLEL_BATCHES = 4;
const HIBID_AUCTION_LIST_LAYOUT_LS_KEY = "hibid:auction-list-layout:v1";
const HIBID_USERS_FETCH_SIG = "all_users";
const AUCTION_CONSIGNOR_FILTER_UNASSIGNED = "__UNASSIGNED__";
const USERS_PAGE_SIZE = 200;
const USER_TABLE_ROW_HEIGHT = 80;
const USER_TABLE_OVERSCAN_ROWS = 8;
const REGISTRATION_TREND_TIME_ZONE = "America/Indiana/Indianapolis";
const USER_LOTS_PAGE_SIZE = 200;
const AUCTIONFUEL_BOX_LOGO_URL = "https://storage.googleapis.com/listalotimages/auctionfuel.png";
const AUCTIONFUEL_REVIEW_QR_URL = "https://storage.googleapis.com/listalotimages/qrcode.png";
const AUCTIONFUEL_BOX_FOOTER_LOGO_URL = "https://storage.googleapis.com/listalotimages/emailtemplates/logo.png";
const XLSX_MANIFEST_HEADER_RGB = "333999";
const XLSX_MANIFEST_LIGHT_RGB = "EEF2FF";
const XLSX_MANIFEST_ALT_ROW_RGB = "F8FAFF";
const XLSX_MANIFEST_BORDER_RGB = "CBD5E1";
const XLSX_MANIFEST_TEXT_RGB = "111827";
const XLSX_MANIFEST_MUTED_RGB = "475569";
const waitForNextPaint = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
      setTimeout(resolve, 0);
      return;
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });
const buildXlsxCellStyle = ({
  fillRgb = "",
  textRgb = XLSX_MANIFEST_TEXT_RGB,
  bold = false,
  fontSize = 11,
  italic = false,
  underline = false,
  horizontal = "left",
  vertical = "center",
  wrapText = true,
} = {}) => ({
  font: {
    bold,
    italic,
    underline,
    sz: fontSize,
    color: { rgb: textRgb },
  },
  fill: fillRgb
    ? {
        patternType: "solid",
        fgColor: { rgb: fillRgb },
        bgColor: { rgb: fillRgb },
      }
    : undefined,
  alignment: {
    horizontal,
    vertical,
    wrapText,
  },
  border: {
    top: { style: "thin", color: { rgb: XLSX_MANIFEST_BORDER_RGB } },
    bottom: { style: "thin", color: { rgb: XLSX_MANIFEST_BORDER_RGB } },
    left: { style: "thin", color: { rgb: XLSX_MANIFEST_BORDER_RGB } },
    right: { style: "thin", color: { rgb: XLSX_MANIFEST_BORDER_RGB } },
  },
});
const setWorksheetCellStyle = (worksheet, cellRef, style) => {
  if (!worksheet?.[cellRef]) return;
  worksheet[cellRef].s = style;
};
const styleWorksheetRow = (XLSX, worksheet, rowNumber, columnCount, style) => {
  const safeColumnCount = Math.max(0, Number(columnCount) || 0);
  for (let col = 0; col < safeColumnCount; col += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: rowNumber - 1, c: col });
    setWorksheetCellStyle(worksheet, cellRef, style);
  }
};
const PRODUCT_SEARCH_COLUMNS = [
  "thumbnail",
  "auctionId",
  "auctionEndDate",
  "lot",
  "title",
  "brand",
  "condition",
  "consignor",
  "bidderName",
  "userType",
  "emailLower",
  "bids",
  "watches",
  "views",
  "description",
  "quantity",
  "startBid",
  "upcAuctionRuns",
  "bid",
  "retailPrice",
  "sellingToRetail",
  "upc",
  "lister",
  "location",
  "color",
  "weight",
  "dimensions",
  "manufacturer",
  "mediaUrls",
];
const DESCRIPTION_PREVIEW_LENGTH = 20;
const DEFAULT_SHIPPING_POLICY_TEXT = [
  "All winning bids are final and create a legal obligation to pay the auction invoice. After auction close, Buyer must select Pickup or Shipping.",
  "If Shipping is selected, Shipping Saint will issue a separate invoice for actual shipping and handling charges. Shipping charges are not included in the auction invoice.",
  "Failure to select Shipping or Pickup does not void the sale. Items not paid for or not removed within the designated window may be deemed abandoned and subject to restocking, storage, or resale.",
  "By bidding and confirming fulfillment, Buyer agrees to these terms.",
].join("\n");
const HIBID_TIER_DEFINITION_LINES = [
  { name: "Casual", definition: "Annual profit proxy is $0 to under $300." },
  { name: "Emerging", definition: "Annual profit proxy is $300 to under $1,000." },
  { name: "Power", definition: "Annual profit proxy is $1,000 to under $5,000." },
  { name: "Elite", definition: "Annual profit proxy is $5,000 to under $10,000,000." },
];
const ACCOUNT_MANAGER_TIER_OPTIONS = [
  { key: "new", label: "New" },
  { key: "elite", label: "Elite" },
  { key: "power", label: "Power" },
  { key: "emerging", label: "Emerging" },
  { key: "casual", label: "Casual" },
];
const HIBID_TIER_OPTIONS = ["new", "casual", "emerging", "power", "elite"];
const CUSTOMER_TYPE_ORDER = [
  "shippingReseller",
  "professionalReseller",
  "contractorHighValue",
  "bargainHunter",
  "casualHousehold",
];
const CUSTOMER_TYPE_LABEL_BY_KEY = {
  shippingReseller: "Shipping Reseller",
  professionalReseller: "Professional Reseller",
  contractorHighValue: "Contractor High Value",
  bargainHunter: "Bargain Hunter",
  casualHousehold: "Casual Household",
  unclassified: "Unclassified",
};
const LEGACY_CUSTOMER_TYPE_TO_KEY = {
  "shipping reseller": "shippingReseller",
  reseller: "professionalReseller",
  "contractor / high-value": "contractorHighValue",
  "contractor / high value": "contractorHighValue",
  "contractor high-value": "contractorHighValue",
  "contractor high value": "contractorHighValue",
  "bargain hunter": "bargainHunter",
  "casual / household": "casualHousehold",
  "casual household": "casualHousehold",
};
const HIBID_USER_TYPE_DEFINITION_LINES = [
  {
    key: "shippingReseller",
    name: CUSTOMER_TYPE_LABEL_BY_KEY.shippingReseller,
    definition:
      "At least 40 lots won in 30 days, average hammer at most $35, at least 2 auctions in 90 days, and shipping enabled.",
  },
  {
    key: "professionalReseller",
    name: CUSTOMER_TYPE_LABEL_BY_KEY.professionalReseller,
    definition:
      "At least 50 lots won in 60 days, average hammer between $15 and $175, and at least 6 auctions in 90 days.",
  },
  {
    key: "contractorHighValue",
    name: CUSTOMER_TYPE_LABEL_BY_KEY.contractorHighValue,
    definition: "At least 3 lots won in 120 days and average hammer at least $85.",
  },
  {
    key: "bargainHunter",
    name: CUSTOMER_TYPE_LABEL_BY_KEY.bargainHunter,
    definition:
      "At least 5 lots won in 90 days, average hammer at most $35, at most 6 auctions in 90 days, and shipping disabled.",
  },
  {
    key: "casualHousehold",
    name: CUSTOMER_TYPE_LABEL_BY_KEY.casualHousehold,
    definition: "At most 4 lots won in 90 days.",
  },
];
const CUSTOMER_TYPE_OPTIONS = [...CUSTOMER_TYPE_ORDER];
const HIBID_LCV_DEFINITION_LINES = [
  {
    name: "Step 1",
    definition: "Monthly value proxy = (90-day hammer total / 90) x 30.",
  },
  {
    name: "Step 2",
    definition: "Monthly profit proxy = monthly value proxy x gross margin (default 30%).",
  },
  {
    name: "Step 3",
    definition: "Annual profit proxy = monthly profit proxy x 12, then tier is selected from the tier ranges above.",
  },
  {
    name: "Step 4",
    definition: "Active months by tier are Casual=18, Emerging=3, Power=12, Elite=6.",
  },
  {
    name: "Step 5",
    definition: "Final LCV estimate = monthly profit proxy x active months for that tier.",
  },
];
const HIBID_REGISTRATION_ACTIVITY_DEFINITION_LINES = [
  {
    name: "Current Registrations",
    definition: "All new registration events for the selected day.",
  },
  {
    name: "Fallout Bidder",
    definition: "Bidder registered again after more than 30 days and less than 60 days since their prior registration.",
  },
  {
    name: "Revived Bidder",
    definition: "Bidder registered again after more than 60 days and less than 90 days since their prior registration.",
  },
  {
    name: "DOA Bidder",
    definition: "Bidder registered again after more than 90 days since their prior registration.",
  },
];
const AUCTION_TABLE_TREND_METRICS = [
  { key: "totalRegistrations", isMoney: false },
  { key: "totalLots", isMoney: false },
  { key: "totalViews", isMoney: false },
  { key: "totalUniqueBidders", isMoney: false },
  { key: "totalBids", isMoney: false },
  { key: "totalHighValue", isMoney: true },
  { key: "fulfillmentShippingCount", isMoney: false },
  { key: "fulfillmentShippingPct", isMoney: false },
  { key: "fulfillmentPickupCount", isMoney: false },
  { key: "fulfillmentPickupPct", isMoney: false },
  { key: "fulfillmentNewUsersCount", isMoney: false },
  { key: "fulfillmentNewUsersPct", isMoney: false },
  { key: "fulfillmentNewShippingCount", isMoney: false },
  { key: "fulfillmentNewShippingPctOfNew", isMoney: false },
  { key: "fulfillmentNewShareOfShippersPct", isMoney: false },
  { key: "fulfillmentNewShippingPctOfTotalRegs", isMoney: false },
];

const normalize = (s) => String(s || "").trim().toLowerCase();
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
const resolveEmailFromName = (value = "", fallback = "Listernaut") =>
  String(value || "").trim() || String(fallback || "").trim() || "Listernaut";
const normalizeTierKey = (rawTier) => {
  const t = String(rawTier || "").trim().toLowerCase();
  if (["new", "elite", "power", "emerging", "casual"].includes(t)) return t;
  return "";
};
const normalizeAccountManagerId = (v) =>
  String(v || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, "");
const normalizeConsignorProfileId = (v) =>
  String(v || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, "");
const accountManagerDisplayName = (manager = {}) => {
  const first = String(manager?.firstName || "").trim();
  const last = String(manager?.lastName || "").trim();
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full) return full;
  const fallback = String(manager?.fullName || manager?.email || manager?.id || "").trim();
  return fallback || "Unknown manager";
};
const sanitizeAccountManagers = (list) => {
  const rows = Array.isArray(list) ? list : [];
  const seen = new Set();
  return rows
    .map((row) => {
      const id = normalizeAccountManagerId(row?.id || row?.managerId || "");
      if (!id || seen.has(id)) return null;
      seen.add(id);
      const manager = {
        id,
        firstName: String(row?.firstName || "").trim(),
        lastName: String(row?.lastName || "").trim(),
        email: String(row?.email || "").trim().toLowerCase(),
        phone: String(row?.phone || "").trim(),
        role: String(row?.role || "").trim(),
      };
      return {
        ...manager,
        fullName: accountManagerDisplayName(manager),
      };
    })
    .filter(Boolean);
};
const sanitizeAccountManagerTierMap = (rawMap, managers = []) => {
  const map = rawMap && typeof rawMap === "object" ? rawMap : {};
  const validIds = new Set((Array.isArray(managers) ? managers : []).map((m) => normalizeAccountManagerId(m?.id || "")));
  const out = {};
  ACCOUNT_MANAGER_TIER_OPTIONS.forEach((opt) => {
    const managerId = normalizeAccountManagerId(map?.[opt.key] || "");
    out[opt.key] = managerId && validIds.has(managerId) ? managerId : "";
  });
  return out;
};
const sanitizeConsignors = (list) => {
  const rows = Array.isArray(list) ? list : [];
  const seen = new Set();
  return rows
    .map((row) => {
      const consignorId = normalizeConsignorProfileId(row?.consignorId || row?.id || "");
      if (!consignorId || seen.has(consignorId)) return null;
      seen.add(consignorId);
      return {
        id: consignorId,
        consignorId,
        consignorName: String(row?.consignorName || row?.name || "").trim(),
        pointOfContact: String(row?.pointOfContact || row?.contactName || row?.contact || "").trim(),
        email: String(row?.email || "").trim().toLowerCase(),
        phone: String(row?.phone || row?.phoneNumber || "").trim(),
      };
    })
    .filter(Boolean);
};
const createNamedUrlEntry = () => ({ name: "", url: "" });
const normalizeNamedUrlsInput = (value) => {
  const rows = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? (Object.prototype.hasOwnProperty.call(value, "name") || Object.prototype.hasOwnProperty.call(value, "url"))
        ? [value]
        : Object.entries(value).map(([name, url]) => ({ name, url }))
      : [];
  const out = [];
  const seen = new Set();
  rows.forEach((row) => {
    const name = String(row?.name || row?.label || row?.key || "").trim();
    const url = String(row?.url || row?.value || row?.href || "").trim();
    if (!name && !url) return;
    const next = {
      name: name.slice(0, 80),
      url: url.slice(0, 1000),
    };
    const dedupeKey = `${normalize(next.name)}|${String(next.url || "").trim().toLowerCase()}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    out.push(next);
  });
  return out;
};
const coerceNamedUrlDraftRows = (value) => {
  const rows = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? (Object.prototype.hasOwnProperty.call(value, "name") || Object.prototype.hasOwnProperty.call(value, "url"))
        ? [value]
        : Object.entries(value).map(([name, url]) => ({ name, url }))
      : [];
  return rows.map((row) => ({
    name: String(row?.name || row?.label || row?.key || "").slice(0, 80),
    url: String(row?.url || row?.value || row?.href || "").slice(0, 1000),
  }));
};
const toLaunchableUrl = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    return new URL(withScheme).toString();
  } catch {
    return "";
  }
};
const normalizeCustomerTypeKey = (rawType) => {
  const raw = String(rawType || "").trim();
  if (!raw) return "";
  if (CUSTOMER_TYPE_LABEL_BY_KEY[raw]) return raw;
  const normalized = normalize(raw);
  if (LEGACY_CUSTOMER_TYPE_TO_KEY[normalized]) return LEGACY_CUSTOMER_TYPE_TO_KEY[normalized];
  const direct = CUSTOMER_TYPE_OPTIONS.find((opt) => normalize(opt) === normalized);
  return direct || raw;
};
const customerTypeLabel = (rawType) => {
  const key = normalizeCustomerTypeKey(rawType);
  return CUSTOMER_TYPE_LABEL_BY_KEY[key] || key;
};
const isShippingRequested = (row) => {
  const shipping = row?.shipping;
  if (shipping === true) return true;
  if (typeof shipping === "number") return shipping === 1;
  if (typeof shipping === "string") {
    const normalized = normalize(shipping);
    return normalized === "yes" || normalized === "true" || normalized === "1" || normalized === "y";
  }
  if (shipping && typeof shipping === "object" && !Array.isArray(shipping)) {
    const rawOptIn = shipping?.optIn ?? shipping?.optedIn;
    if (rawOptIn === true || rawOptIn === 1) return true;
    if (typeof rawOptIn === "string") {
      const normalized = normalize(rawOptIn);
      return normalized === "yes" || normalized === "true" || normalized === "1" || normalized === "y";
    }
  }
  return false;
};
const getRegistrationShippingPolicyObject = (row = {}) => {
  const policy = row?.shippingPolicy;
  if (policy && typeof policy === "object" && !Array.isArray(policy)) return policy;
  const shippingObj = row?.shipping;
  if (shippingObj && typeof shippingObj === "object" && !Array.isArray(shippingObj)) return shippingObj;
  return {};
};
const getRegistrationPickupSignatureObject = (row = {}) => {
  const pickupSig = row?.pickupSignature;
  if (pickupSig && typeof pickupSig === "object" && !Array.isArray(pickupSig)) return pickupSig;
  return {};
};
const isRegistrationSignedOptIn = (row = {}) => {
  const policy = getRegistrationShippingPolicyObject(row);
  const rawOptIn = policy?.optIn ?? policy?.optedIn;
  const shippingSigned = (
    rawOptIn === true ||
    rawOptIn === 1 ||
    (typeof rawOptIn === "string" && ["true", "yes", "1", "y"].includes(normalize(rawOptIn)))
  );
  if (shippingSigned) return true;
  const pickupSig = getRegistrationPickupSignatureObject(row);
  return pickupSig?.signed === true || !!safeToMillis(pickupSig?.signedAtMs);
};
const isChargebackEmailSentValue = (row = {}) => {
  if (row?.chargebackEmailSent === true) return true;
  if (row?.chargebackEmailSent === 1) return true;
  if (typeof row?.chargebackEmailSent === "string") {
    const v = normalize(row?.chargebackEmailSent);
    if (v === "true" || v === "yes" || v === "1") return true;
  }
  const sentAtMs = safeToMillis(row?.chargebackEmailSentAtMs) || safeToMillis(row?.chargebackEmailSentAt);
  return !!(sentAtMs && sentAtMs > 0);
};
const formatEngagementSeriesName = (name) => {
  const n = String(name || "").trim().toLowerCase();
  if (n.includes("bought")) return "Bought";
  if (n.includes("floor")) return "Passed";
  return String(name || "Value");
};
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const NUMBER_INT_FORMAT = { maximumFractionDigits: 0 };
const NUMBER_TWO_DECIMAL_FORMAT = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
const formatInt = (v) => safeNum(v).toLocaleString(undefined, NUMBER_INT_FORMAT);
const formatNumber2 = (v) => {
  const n = safeNum(v);
  if (Math.abs(n - Math.round(n)) < 1e-9) return formatInt(n);
  return n.toLocaleString(undefined, NUMBER_TWO_DECIMAL_FORMAT);
};
const formatMoney2 = (v) => safeNum(v).toLocaleString(undefined, NUMBER_TWO_DECIMAL_FORMAT);
const formatPercent2 = (v) => `${safeNum(v).toFixed(2)}%`;
const sanitizeFileNamePart = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 42);
const toAbsInt = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return Math.trunc(Math.abs(v));
  const s = String(v).trim();
  if (!s) return 0;
  const m = s.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  const n = m ? Number(m[0]) : 0;
  return Number.isFinite(n) ? Math.trunc(Math.abs(n)) : 0;
};
const toAbsFloat = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return Math.abs(v);
  const s = String(v).trim();
  if (!s) return 0;
  const m = s.replace(/[$, ]/g, "").match(/-?\d+(\.\d+)?/);
  const n = m ? Number(m[0]) : 0;
  return Number.isFinite(n) ? Math.abs(n) : 0;
};
const moneyPlain = (n) => formatMoney2(n);
const getPhoneTelHref = (value) => {
  const raw = String(value || "").trim();
  if (!raw || raw === "-") return "";
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";
  return `tel:${raw.startsWith("+") ? `+${digits}` : digits}`;
};
const parseShelfBoxLabels = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return [];

  if (/^\d+$/.test(raw)) {
    const boxCount = Math.max(0, Math.trunc(Number(raw) || 0));
    if (!boxCount) return [];
    return Array.from({ length: boxCount }, (_, idx) => `${idx + 1}/${boxCount}`);
  }

  const seen = new Set();
  return raw
    .split(/[\n\r,;]+/)
    .map((part) => String(part || "").trim().replace(/\s+/g, " "))
    .filter(Boolean)
    .filter((part) => {
      const key = normalize(part);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};
const compareSortValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") {
    if (a === b) return 0;
    return a > b ? 1 : -1;
  }
  const as = String(a ?? "");
  const bs = String(b ?? "");
  if (as === bs) return 0;
  return as.localeCompare(bs);
};
const applySortToggle = (activeKey, setKey, setDir, nextKey, defaultDir = "asc") => {
  if (activeKey === nextKey) {
    setDir((d) => (d === "asc" ? "desc" : "asc"));
    return;
  }
  setKey(nextKey);
  setDir(defaultDir);
};
const getTrendDeltaDisplay = (current, baseline, { money = false, percent = false } = {}) => {
  if (baseline == null) {
    return {
      hasBaseline: false,
      direction: "flat",
      color: "#64748b",
      text: "—",
    };
  }
  const delta = safeNum(current) - safeNum(baseline);
  const abs = Math.abs(delta);
  const magnitude = money
    ? `$${moneyPlain(abs)}`
    : percent
      ? `${abs.toFixed(2)}%`
      : formatNumber2(toAbsInt(Math.round(abs)));
  const text = delta > 0 ? `+${magnitude}` : delta < 0 ? `-${magnitude}` : money ? "$0.00" : percent ? "0.00%" : "0";
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const color = direction === "up" ? "#15803d" : direction === "down" ? "#b91c1c" : "#64748b";
  return {
    hasBaseline: true,
    direction,
    color,
    text,
  };
};
const safeToMillis = (ts) => {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const seconds = ts._seconds ?? ts.seconds;
  const nanos = ts._nanoseconds ?? ts.nanoseconds ?? ts.nanos;
  if (typeof seconds === "number") return seconds * 1000 + Math.floor((nanos || 0) / 1e6);
  if (ts.toMillis && typeof ts.toMillis === "function") return ts.toMillis();
  return null;
};
const fmtDate = (ts) => {
  const ms = safeToMillis(ts);
  if (!ms) return "-";
  return new Date(ms).toLocaleString();
};
const getListernautUploadStatusMeta = ({
  listernautUploadAtMs = 0,
  listernautUploadId = "",
  listernautRows = 0,
  listernautMatchedLots = 0,
  listernautUpdatedLots = 0,
} = {}) => {
  const uploadAtMs = safeToMillis(listernautUploadAtMs) ?? 0;
  const listernautId = String(listernautUploadId || "").trim();
  const hasUploadEvidence =
    uploadAtMs > 0 ||
    !!listernautId ||
    toAbsInt(listernautRows) > 0 ||
    toAbsInt(listernautMatchedLots) > 0 ||
    toAbsInt(listernautUpdatedLots) > 0;

  if (!hasUploadEvidence) {
    return {
      hasUpload: false,
      needsRefresh: false,
      label: "Not Uploaded",
      color: "#991b1b",
      background: "#fef2f2",
      note: "",
    };
  }

  return {
    hasUpload: true,
    needsRefresh: false,
    label: "Uploaded",
    color: "#166534",
    background: "#ecfdf5",
    note: "",
  };
};
const monthLabelFromKeyUTC = (monthKey = "") => {
  const m = String(monthKey || "").trim();
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(m)) return m;
  const iso = `${m}-01T00:00:00.000Z`;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return m;
  return dt.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
};
const isDateKeyUTC = (v = "") => /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(String(v || "").trim());
const dateKeyToUtcMs = (dateKey = "") => {
  const dk = String(dateKey || "").trim();
  if (!isDateKeyUTC(dk)) return null;
  const ms = Date.parse(`${dk}T00:00:00.000Z`);
  return Number.isFinite(ms) ? ms : null;
};
const utcMsToDateKey = (ms) => {
  const n = Number(ms);
  if (!Number.isFinite(n)) return "";
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};
const addDaysToDateKeyUTC = (dateKey = "", days = 0) => {
  const startMs = dateKeyToUtcMs(dateKey);
  if (!Number.isFinite(startMs)) return "";
  return utcMsToDateKey(startMs + Number(days || 0) * 24 * 60 * 60 * 1000);
};
const addMonthsToDateKeyUTC = (dateKey = "", months = 0) => {
  const ms = dateKeyToUtcMs(dateKey);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  d.setUTCMonth(d.getUTCMonth() + Number(months || 0));
  return utcMsToDateKey(d.getTime());
};
const weekStartDateKeyUTC = (dateKey = "") => {
  const ms = dateKeyToUtcMs(dateKey);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const day = d.getUTCDay();
  const diff = (day + 6) % 7; // Monday start
  d.setUTCDate(d.getUTCDate() - diff);
  return utcMsToDateKey(d.getTime());
};
const formatDateKeyShortUTC = (dateKey = "") => {
  const ms = dateKeyToUtcMs(dateKey);
  if (!Number.isFinite(ms)) return String(dateKey || "");
  return new Date(ms).toLocaleDateString(undefined, { month: "numeric", day: "numeric", timeZone: "UTC" });
};
const formatDateKeyRangeShortUTC = (startDateKey = "", endDateKey = "") => {
  const start = String(startDateKey || "").trim();
  const end = String(endDateKey || "").trim();
  if (!isDateKeyUTC(start) || !isDateKeyUTC(end)) return "";
  if (start === end) return formatDateKeyShortUTC(start);
  return `${formatDateKeyShortUTC(start)} - ${formatDateKeyShortUTC(end)}`;
};
const clampDateKeyToRangeUTC = (dateKey = "", minDateKey = "", maxDateKey = "") => {
  const minKey = isDateKeyUTC(minDateKey) ? String(minDateKey).trim() : "";
  const maxKey = isDateKeyUTC(maxDateKey) ? String(maxDateKey).trim() : "";
  const value = String(dateKey || "").trim();
  if (!minKey || !maxKey || !isDateKeyUTC(value)) return "";
  if (value < minKey) return minKey;
  if (value > maxKey) return maxKey;
  return value;
};
const getDatePartsInTimeZone = (ms, timeZone = REGISTRATION_TREND_TIME_ZONE) => {
  const safeMs = Number(ms);
  if (!Number.isFinite(safeMs)) return null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date(safeMs));
  const getPart = (type) => {
    const value = Number(parts.find((part) => part.type === type)?.value || 0);
    return Number.isFinite(value) ? value : 0;
  };
  return {
    year: getPart("year"),
    month: getPart("month"),
    day: getPart("day"),
    hour: getPart("hour"),
    minute: getPart("minute"),
    second: getPart("second"),
  };
};
const dateKeyInTimeZoneFromMs = (ms, timeZone = REGISTRATION_TREND_TIME_ZONE) => {
  const parts = getDatePartsInTimeZone(ms, timeZone);
  if (!parts) return "";
  return `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
};
const formatHourLabelEastern = (hourValue = 0) => {
  const rawHour = Number(hourValue);
  if (!Number.isFinite(rawHour)) return "";
  const hour = Math.min(23, Math.max(0, Math.trunc(rawHour)));
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 || 12;
  return `${normalizedHour}:00 ${suffix}`;
};
const isXlsxFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  return name.endsWith(".xlsx") || name.endsWith(".xls");
};
const parseListernautMediaUrls = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item || "").trim()).filter(Boolean);
  const raw = String(value || "").trim();
  if (!raw) return [];
  return raw
    .split(/[\n\r|;,]+/)
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};
const getListernautMediaUrls = (lot = {}) => {
  const direct = parseListernautMediaUrls(lot?.listernautMediaUrls);
  if (direct.length) return direct;
  return parseListernautMediaUrls(lot?.listernautMediaUrl || lot?.listernautMediaUrlsRaw || "");
};
const getListernautThumbnailUrl = (lot = {}) => {
  const direct = String(lot?.listernautThumbnailUrl || "").trim();
  if (direct) return direct;
  return String(getListernautMediaUrls(lot)[0] || "").trim();
};
const getProductSearchThumbnailUrl = (row = {}) => {
  const mediaUrls = Array.isArray(row?.mediaUrls)
    ? row.mediaUrls.map((item) => String(item || "").trim()).filter(Boolean)
    : parseListernautMediaUrls(row?.mediaUrls || "");
  const firstMediaUrl = String(mediaUrls[0] || "").trim();
  if (firstMediaUrl) return firstMediaUrl;
  const directThumbnail = String(row?.thumbnail || "").trim();
  if (directThumbnail) return directThumbnail;
  return String(getListernautThumbnailUrl(row) || "").trim();
};
const getLotDescriptionText = (lot = {}) => String(lot?.listernautDescription || lot?.description || "").trim();
const loadPdfThumbnailDataUrl = async (url, { maxWidth = 56, maxHeight = 56 } = {}) => {
  const src = String(url || "").trim();
  if (!src || typeof window === "undefined" || typeof document === "undefined" || typeof URL === "undefined") return "";
  try {
    const response = await fetch(src, { mode: "cors" });
    if (!response.ok) throw new Error(`Image request failed (${response.status})`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const naturalWidth = Number(img?.naturalWidth || img?.width || maxWidth) || maxWidth;
          const naturalHeight = Number(img?.naturalHeight || img?.height || maxHeight) || maxHeight;
          const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
          const width = Math.max(1, Math.round(naturalWidth * scale));
          const height = Math.max(1, Math.round(naturalHeight * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve("");
            return;
          }
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          try {
            resolve(canvas.toDataURL("image/jpeg", 0.82));
          } catch (e) {
            resolve("");
          }
        };
        img.onerror = () => resolve("");
        img.src = objectUrl;
      });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (e) {
    return "";
  }
};
const truncateDescriptionText = (value = "", maxLength = DESCRIPTION_PREVIEW_LENGTH) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};
const getLotPickupCounts = (row = {}) => {
  const totalLotsCount = toAbsInt(row?.lotsCount || row?.totalLotsCount || 0);
  const pickedUpLotsCount = Math.min(totalLotsCount, toAbsInt(row?.pickedUpLotsCount || 0));
  return {
    totalLotsCount,
    pickedUpLotsCount,
    pickupStatusLabel: `${pickedUpLotsCount}/${totalLotsCount}`,
    pickedUp: !!row?.pickedUp || (totalLotsCount > 0 && pickedUpLotsCount >= totalLotsCount),
  };
};
const getLotShippedCounts = (row = {}) => {
  const totalLotsCount = toAbsInt(row?.lotsCount || row?.totalLotsCount || 0);
  const shippedLotsCount = Math.min(totalLotsCount, toAbsInt(row?.shippedLotsCount || 0));
  return {
    totalLotsCount,
    shippedLotsCount,
    shippedStatusLabel: `${shippedLotsCount}/${totalLotsCount}`,
    shipped: !!row?.shipped || (totalLotsCount > 0 && shippedLotsCount >= totalLotsCount),
  };
};
const getRegistrationConsignors = (row = {}) =>
  uniqSortedOptions([
    ...(Array.isArray(row?.consignorRows) ? row.consignorRows.map((entry) => entry?.consignorName || entry?.name) : []),
    ...(Array.isArray(row?.consignors) ? row.consignors : []),
    row?.consignor,
  ]);
const getRegistrationConsignorRows = (row = {}) => {
  const rawRows = Array.isArray(row?.consignorRows) ? row.consignorRows : [];
  const seen = new Set();
  const rows = [];
  rawRows.forEach((entry) => {
    const consignorId = normalizeConsignorProfileId(entry?.consignorId || entry?.id || "");
    const consignorName = String(entry?.consignorName || entry?.name || consignorId || "").trim();
    const key = consignorId || normalize(consignorName);
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push({
      consignorId,
      consignorName: consignorName || consignorId,
    });
  });
  const fallbackIds = Array.isArray(row?.consignorIds) ? row.consignorIds : [];
  const fallbackNames = getRegistrationConsignors(row);
  fallbackIds.forEach((consignorIdRaw, idx) => {
    const consignorId = normalizeConsignorProfileId(consignorIdRaw);
    const consignorName = String(fallbackNames[idx] || consignorId || "").trim();
    const key = consignorId || normalize(consignorName);
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push({
      consignorId,
      consignorName: consignorName || consignorId,
    });
  });
  return rows.sort((a, b) => {
    const nameDiff = String(a?.consignorName || "").localeCompare(String(b?.consignorName || ""));
    if (nameDiff !== 0) return nameDiff;
    return String(a?.consignorId || "").localeCompare(String(b?.consignorId || ""));
  });
};
const getRegistrationConsignorIds = (row = {}) =>
  uniqSortedOptions([
    ...(Array.isArray(row?.consignorIds) ? row.consignorIds : []),
    ...getRegistrationConsignorRows(row).map((entry) => entry?.consignorId),
  ].filter(Boolean));
const getRegistrationPickupAppointment = (row = {}) => {
  const direct = row?.pickupAppointment && typeof row.pickupAppointment === "object" ? row.pickupAppointment : {};
  const location = String(
    direct?.location || row?.pickupAppointmentLocation || row?.pickupLocation || ""
  ).trim();
  const slotId = String(direct?.slotId || row?.pickupAppointmentSlotId || "").trim();
  const startAtMs = safeToMillis(direct?.startAtMs ?? row?.pickupAppointmentStartAtMs ?? row?.pickupDateTimeStartAtMs);
  const endAtMs = safeToMillis(direct?.endAtMs ?? row?.pickupAppointmentEndAtMs ?? row?.pickupDateTimeEndAtMs);
  const appointmentAtMs = safeToMillis(direct?.appointmentAtMs ?? row?.pickupAppointmentAtMs ?? row?.pickupDateTimeAtMs);
  const appointmentDateKey = String(direct?.appointmentDateKey || row?.pickupAppointmentDateKey || "").trim();
  const label = String(direct?.label || row?.pickupAppointmentLabel || "").trim();
  const source = String(direct?.source || row?.pickupAppointmentSource || "").trim();
  const additionalInfo = String(
    direct?.additionalInfo || row?.pickupAppointmentAdditionalInfo || row?.pickupAdditionalInfo || ""
  ).trim();
  return {
    location,
    slotId,
    startAtMs: startAtMs || 0,
    endAtMs: endAtMs || 0,
    appointmentAtMs: appointmentAtMs || 0,
    appointmentDateKey,
    label,
    source,
    additionalInfo,
    hasSelection: !!(location && slotId && (appointmentAtMs || startAtMs)),
  };
};
const getRegistrationDelivery = (row = {}) => {
  const direct = row?.delivery && typeof row.delivery === "object" ? row.delivery : {};
  const location = String(
    direct?.location || row?.deliveryLocation || row?.deliveryPickupLocation || row?.pickupLocation || ""
  ).trim();
  const status = String(direct?.status || row?.deliveryStatus || "").trim().toLowerCase();
  const transactionId = String(
    direct?.transactionId || row?.deliveryTransactionId || row?.deliveryBraintreeTransactionId || ""
  ).trim();
  const deliveryDateKey = String(direct?.deliveryDateKey || row?.deliveryDateKey || row?.deliveryDate || "").trim();
  const deliveryDateLabel = String(direct?.deliveryDateLabel || row?.deliveryDateLabel || deliveryDateKey).trim();
  const windowStartAtMs = safeToMillis(direct?.windowStartAtMs ?? row?.deliveryWindowStartAtMs ?? 0) || 0;
  const windowEndAtMs = safeToMillis(direct?.windowEndAtMs ?? row?.deliveryWindowEndAtMs ?? 0) || 0;
  const address = String(direct?.address || row?.deliveryAddress || row?.address || "").trim();
  const phone = String(direct?.phone || row?.deliveryPhone || row?.phone || row?.phone2 || "").trim();
  const notes = String(direct?.notes || row?.deliveryNotes || "").trim();
  const source = String(direct?.source || row?.deliverySource || "").trim();
  const transactionStatus = String(direct?.transactionStatus || row?.deliveryTransactionStatus || "").trim();
  const scheduledAtMs = safeToMillis(direct?.scheduledAtMs ?? row?.deliveryScheduledAtMs ?? 0) || 0;
  const deliveredRaw = direct?.delivered ?? row?.delivered ?? row?.deliveryDelivered;
  const delivered =
    deliveredRaw === true ||
    deliveredRaw === 1 ||
    String(deliveredRaw || "").trim().toLowerCase() === "true";
  const deliveredLotsCount = toAbsInt(direct?.deliveredLotsCount ?? row?.deliveredLotsCount ?? 0);
  const deliveredStatusLabel = String(direct?.deliveredStatusLabel || row?.deliveredStatusLabel || "").trim();
  const deliveredAtMs = safeToMillis(direct?.deliveredAtMs ?? row?.deliveryDeliveredAtMs ?? row?.deliveredAtMs ?? 0) || 0;
  const failedRaw = direct?.failed ?? row?.deliveryFailed;
  const failed =
    failedRaw === true ||
    failedRaw === 1 ||
    String(failedRaw || "").trim().toLowerCase() === "true";
  const failedReason = String(direct?.failedReason || row?.deliveryFailedReason || "").trim();
  const failedAtMs = safeToMillis(direct?.failedAtMs ?? row?.deliveryFailedAtMs ?? 0) || 0;
  const proofPhotos = (Array.isArray(direct?.proofPhotos)
    ? direct.proofPhotos
    : Array.isArray(row?.deliveryProofPhotos)
      ? row.deliveryProofPhotos
      : [])
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const url = String(entry?.url || "").trim();
      if (!url) return null;
      return {
        url,
        fileName: String(entry?.fileName || entry?.name || "").trim(),
        contentType: String(entry?.contentType || entry?.mimeType || "").trim(),
        size: toAbsInt(entry?.size || 0),
        uploadedAtMs: safeToMillis(entry?.uploadedAtMs ?? entry?.createdAtMs ?? 0) || 0,
        uploadedByEmail: String(entry?.uploadedByEmail || entry?.email || "").trim().toLowerCase(),
      };
    })
    .filter(Boolean);
  return {
    location,
    status,
    transactionId,
    deliveryDateKey,
    deliveryDateLabel,
    windowStartAtMs,
    windowEndAtMs,
    address,
    phone,
    notes,
    source,
    transactionStatus,
    scheduledAtMs,
    delivered,
    deliveredLotsCount,
    deliveredStatusLabel,
    deliveredAtMs,
    failed,
    failedReason,
    failedAtMs,
    proofPhotos,
    hasSelection: !!(location && deliveryDateKey),
    isScheduled: status === "scheduled" && !!transactionId,
  };
};
const getPickupAppointmentSelectionKey = (appointment = {}) => {
  if (!appointment?.hasSelection) return "not_selected";
  const source = String(appointment?.source || "").trim().toLowerCase();
  if (source === "internal_dashboard") return "jeff";
  if (source === "public_link") return "email";
  return "email";
};
const getPickupAppointmentSelectionMeta = (appointment = {}) => {
  const key = getPickupAppointmentSelectionKey(appointment);
  if (key === "jeff") {
    return {
      key,
      label: "Jeff",
      textColor: "#92400e",
      background: "rgba(245,158,11,0.14)",
      border: "1px solid rgba(245,158,11,0.24)",
    };
  }
  if (key === "email") {
    return {
      key,
      label: "Email",
      textColor: "#1d4ed8",
      background: "rgba(59,130,246,0.12)",
      border: "1px solid rgba(59,130,246,0.22)",
    };
  }
  return {
    key,
    label: "Not selected",
    textColor: "#94a3b8",
    background: "rgba(148,163,184,0.10)",
    border: "1px solid rgba(148,163,184,0.18)",
  };
};
const getRegistrationFulfillmentMode = ({ row = {}, shippingRequested = false, delivery = null } = {}) => {
  const nextDelivery = delivery && typeof delivery === "object" ? delivery : getRegistrationDelivery(row);
  if (
    nextDelivery?.isScheduled ||
    nextDelivery?.hasSelection ||
    nextDelivery?.location ||
    nextDelivery?.deliveryDateKey
  ) {
    return "delivery";
  }
  return shippingRequested ? "shipping" : "pickup";
};
const getRegistrationFulfillmentButtonMeta = (mode = "pickup") => {
  if (mode === "delivery") {
    return {
      label: "Delivery",
      color: "#854d0e",
      background: "linear-gradient(90deg, rgba(250,204,21,0.2), rgba(245,158,11,0.24))",
      border: "1px solid rgba(245,158,11,0.35)",
    };
  }
  if (mode === "shipping") {
    return {
      label: "Shipping",
      color: "#374151",
      background: "linear-gradient(90deg, rgba(203,213,225,0.4), rgba(148,163,184,0.34))",
      border: "1px solid rgba(148,163,184,0.42)",
    };
  }
  return {
    label: "Pickup",
    color: "#1d4ed8",
    background: "linear-gradient(90deg, rgba(96,165,250,0.18), rgba(37,99,235,0.2))",
    border: "1px solid rgba(59,130,246,0.35)",
  };
};
const pickupAppointmentMatchesDateRange = (appointment = {}, startMs = 0, endMs = 0) => {
  const appointmentAtMs = safeToMillis(appointment?.appointmentAtMs) || 0;
  if (appointmentAtMs) {
    const safeStart = startMs > 0 ? startMs : 0;
    const safeEnd = endMs > 0 ? endMs : 0;
    if (!safeStart && !safeEnd) return true;
    if (safeStart && appointmentAtMs < safeStart) return false;
    if (safeEnd && appointmentAtMs > safeEnd) return false;
    return true;
  }
  const rowStart = safeToMillis(appointment?.startAtMs) || 0;
  const rowEnd = safeToMillis(appointment?.endAtMs) || rowStart;
  if (!rowStart) return false;
  const safeStart = startMs > 0 ? startMs : 0;
  const safeEnd = endMs > 0 ? endMs : 0;
  if (!safeStart && !safeEnd) return true;
  if (safeStart && rowEnd < safeStart) return false;
  if (safeEnd && rowStart > safeEnd) return false;
  return true;
};
const matchesFulfillmentProgressFilter = ({ totalCount = 0, doneCount = 0, complete = false } = {}, filter = "all") => {
  if (filter === "all") return true;
  const total = toAbsInt(totalCount);
  const done = Math.min(total, toAbsInt(doneCount));
  if (total <= 0) return false;
  if (filter === "complete") return !!complete;
  if (filter === "none") return done <= 0 && !complete;
  if (filter === "partial") return done > 0 && done < total && !complete;
  return true;
};
const isFloorBidderEmail = (value = "") => normalize(value) === FLOOR_BIDDER_EMAIL;
const formatLotDimensions = (lot = {}) => {
  const length = safeNum(lot?.listernautLength || 0);
  const width = safeNum(lot?.listernautWidth || 0);
  const height = safeNum(lot?.listernautHeight || 0);
  const values = [length, width, height].filter((value) => value > 0);
  if (!values.length) return "—";
  return values.map((value) => formatNumber2(value)).join(" x ");
};
const detectDelimiter = (text) => {
  const firstLine =
    String(text || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .find((l) => l.trim().length > 0) || "";
  const commaCount = (firstLine.match(/,/g) || []).length;
  const tabCount = (firstLine.match(/\t/g) || []).length;
  return tabCount > commaCount ? "\t" : ",";
};
const parseDelimitedTextToRows = (text) => {
  const src = String(text || "");
  if (!src.trim()) return [];
  const delim = detectDelimiter(src);
  const lines = src.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const parseCsvLine = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          cur += ch;
        }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === delim) {
          out.push(cur);
          cur = "";
        } else cur += ch;
      }
    }
    out.push(cur);
    return out.map((x) => x.trim());
  };
  const headers = parseCsvLine(lines[0]).map((h) => String(h || "").trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const obj = {};
    headers.forEach((h, idx) => (obj[h] = cols[idx] ?? ""));
    rows.push(obj);
  }
  return rows;
};
const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Failed to read file"));
    r.onload = () => resolve(String(r.result || ""));
    r.readAsText(file);
  });
const toInputDateTimeLocal = (msOrTs) => {
  const ms = safeToMillis(msOrTs);
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromInputDateTimeLocalToMs = (v) => {
  const s = String(v || "").trim();
  if (!s) return null;
  const d = new Date(s);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};
const uniqSortedOptions = (values) =>
  Array.from(new Set((Array.isArray(values) ? values : []).map((v) => String(v || "").trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
const isDefaultOverallDashboardLocation = (value = "") => {
  const safe = normalize(value).replace(/\s+/g, " ");
  return safe === "indianapolis, in, usa" || safe === "indy";
};
const formatOverallDashboardLocationShort = (value = "") => {
  const safe = normalize(value).replace(/\s+/g, " ");
  if (!safe) return "—";
  if (safe === "indy" || safe.includes("indianapolis")) return "IND";
  if (safe === "lou" || safe.includes("louisville")) return "LOU";
  if (safe === "laf" || safe.includes("lafayette")) return "LAF";
  if (safe === "mer" || safe.includes("merrillville")) return "MER";
  if (safe === "fwa" || safe.includes("fort wayne")) return "FWA";
  return String(value || "").trim() || "—";
};
const deriveAuctionFieldOptionsFromRows = (rows) => ({
  consignors: uniqSortedOptions((Array.isArray(rows) ? rows : []).map((r) => r?.consignor)),
  locations: uniqSortedOptions((Array.isArray(rows) ? rows : []).map((r) => r?.location)),
});
const mergeAuctionFieldOptions = (...optionsSets) => ({
  consignors: uniqSortedOptions(optionsSets.flatMap((o) => o?.consignors || [])),
  locations: uniqSortedOptions(optionsSets.flatMap((o) => o?.locations || [])),
});
const getConsignorsFromAuction = (auction = {}) =>
  uniqSortedOptions([
    ...(Array.isArray(auction?.consignors) ? auction.consignors : []),
    auction?.consignor,
  ]);
const createPickupSlotId = () =>
  `slot_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const sanitizeAuctionAccountManagerIds = (list, managers = []) => {
  const validIds = new Set(
    (Array.isArray(managers) ? managers : [])
      .map((manager) => normalizeAccountManagerId(manager?.id || ""))
      .filter(Boolean)
  );
  const rows = Array.isArray(list) ? list : [list];
  const seen = new Set();
  const out = [];
  rows.forEach((item) => {
    const id = normalizeAccountManagerId(item?.id || item?.managerId || item || "");
    if (!id || seen.has(id)) return;
    if (validIds.size && !validIds.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
};
const getAuctionAccountManagerIds = (auction = {}, managers = []) =>
  sanitizeAuctionAccountManagerIds(
    [
      ...(Array.isArray(auction?.auctionAccountManagerIds) ? auction.auctionAccountManagerIds : []),
      ...(Array.isArray(auction?.accountManagerIds) ? auction.accountManagerIds : []),
      ...((Array.isArray(auction?.auctionAccountManagers) ? auction.auctionAccountManagers : []).map((row) => row?.id || row?.managerId)),
    ],
    managers
  );
const sanitizeAuctionPickupLocations = (values = []) => uniqSortedOptions(values);
const sanitizeAuctionPickupSlots = (slots = [], { fallbackLocations = [] } = {}) => {
  const rows = Array.isArray(slots) ? slots : [];
  const safeFallbackLocations = sanitizeAuctionPickupLocations(fallbackLocations);
  const seen = new Set();
  return rows
    .map((slot) => {
      const slotId = String(slot?.slotId || slot?.id || "").trim() || createPickupSlotId();
      const startAtMs = safeToMillis(slot?.startAtMs ?? slot?.startMs ?? slot?.startAt);
      const endAtMs = safeToMillis(slot?.endAtMs ?? slot?.endMs ?? slot?.endAt);
      const location =
        String(slot?.location || slot?.pickupLocation || "").trim() ||
        (safeFallbackLocations.length === 1 ? safeFallbackLocations[0] : "");
      if (!startAtMs || !endAtMs) return null;
      if (!location) return null;
      const startSafe = Math.floor(startAtMs);
      const endSafe = Math.max(startSafe, Math.floor(endAtMs));
      if (seen.has(slotId)) return null;
      seen.add(slotId);
      return {
        slotId,
        location,
        startAtMs: startSafe,
        endAtMs: endSafe,
        label: String(slot?.label || "").trim(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const startDiff = safeNum(a?.startAtMs || 0) - safeNum(b?.startAtMs || 0);
      if (startDiff !== 0) return startDiff;
      return safeNum(a?.endAtMs || 0) - safeNum(b?.endAtMs || 0);
    });
};
const getAuctionPickupLocationsFromAuction = (auction = {}) =>
  sanitizeAuctionPickupLocations([
    ...(Array.isArray(auction?.pickupLocations) ? auction.pickupLocations : []),
    ...((Array.isArray(auction?.pickupSlots) ? auction.pickupSlots : []).map((slot) => slot?.location || slot?.pickupLocation || "")),
  ]);
const buildAuctionPickupSlotDrafts = (auction = {}) =>
  sanitizeAuctionPickupSlots(auction?.pickupSlots || [], {
    fallbackLocations: getAuctionPickupLocationsFromAuction(auction),
  }).map((slot) => ({
    slotId: String(slot?.slotId || "").trim() || createPickupSlotId(),
    location: String(slot?.location || "").trim(),
    startAtLocal: toInputDateTimeLocal(slot?.startAtMs),
    endAtLocal: toInputDateTimeLocal(slot?.endAtMs),
  }));
const buildAuctionPickupSlotPayload = (drafts = [], pickupLocations = []) =>
  (Array.isArray(drafts) ? drafts : [])
    .map((slot) => {
      const safePickupLocations = sanitizeAuctionPickupLocations(pickupLocations);
      const slotId = String(slot?.slotId || "").trim() || createPickupSlotId();
      const location =
        String(slot?.location || "").trim() ||
        (safePickupLocations.length === 1 ? safePickupLocations[0] : "");
      const startAtMs = fromInputDateTimeLocalToMs(slot?.startAtLocal);
      const endAtMs = fromInputDateTimeLocalToMs(slot?.endAtLocal);
      if (!location || !startAtMs || !endAtMs) return null;
      const startSafe = Math.floor(startAtMs);
      const endSafe = Math.max(startSafe, Math.floor(endAtMs));
      return {
        slotId,
        location,
        startAtMs: startSafe,
        endAtMs: endSafe,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const startDiff = safeNum(a?.startAtMs || 0) - safeNum(b?.startAtMs || 0);
      if (startDiff !== 0) return startDiff;
      return safeNum(a?.endAtMs || 0) - safeNum(b?.endAtMs || 0);
    });
const getAuctionPickupLocationChoices = (auction = {}, currentLocation = "") =>
  sanitizeAuctionPickupLocations([
    ...getAuctionPickupLocationsFromAuction(auction),
    currentLocation,
  ].filter(Boolean));
const formatPickupSlotLabel = (slot = {}) => {
  const startAtMs = safeToMillis(slot?.startAtMs ?? slot?.startAt);
  const endAtMs = safeToMillis(slot?.endAtMs ?? slot?.endAt);
  if (!startAtMs || !endAtMs) return "—";
  const location = String(slot?.location || slot?.pickupLocation || "").trim();
  const start = new Date(startAtMs);
  const end = new Date(endAtMs);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) {
    const rangeLabel = `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${start.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    })} - ${end.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    })}`;
    return location ? `${location} • ${rangeLabel}` : rangeLabel;
  }
  const rangeLabel = `${start.toLocaleString()} - ${end.toLocaleString()}`;
  return location ? `${location} • ${rangeLabel}` : rangeLabel;
};
const localDateKeyFromMs = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "";
  const d = new Date(Number(ms));
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const buildPickupDateOptionsForLocation = (slots = [], location = "") => {
  const targetLocation = normalize(String(location || ""));
  if (!targetLocation) return [];
  const map = new Map();
  sanitizeAuctionPickupSlots(slots || []).forEach((slot) => {
    if (normalize(String(slot?.location || "")) !== targetLocation) return;
    const startAtMs = Number(slot?.startAtMs || 0) || 0;
    if (!startAtMs) return;
    const dateKey = localDateKeyFromMs(startAtMs);
    if (!dateKey) return;
    const existing = map.get(dateKey);
    if (!existing || startAtMs < existing.firstMs) {
      map.set(dateKey, {
        dateKey,
        firstMs: startAtMs,
        label: new Date(startAtMs).toLocaleDateString([], {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => Number(a.firstMs || 0) - Number(b.firstMs || 0));
};
const buildPickupTimeOptionsForLocationAndDate = (slots = [], location = "", dateKey = "") => {
  const targetLocation = normalize(String(location || ""));
  if (!targetLocation || !dateKey) return [];
  const rows = [];
  const seen = new Set();
  sanitizeAuctionPickupSlots(slots || []).forEach((slot) => {
    const slotLocation = normalize(String(slot?.location || ""));
    const slotId = String(slot?.slotId || "").trim();
    const startAtMs = Number(slot?.startAtMs || 0) || 0;
    const endAtMs = Number(slot?.endAtMs || 0) || 0;
    if (slotLocation !== targetLocation || !slotId || !startAtMs || !endAtMs || endAtMs < startAtMs) return;
    if (localDateKeyFromMs(startAtMs) !== dateKey) return;
    for (let atMs = startAtMs; atMs <= endAtMs; atMs += 15 * 60 * 1000) {
      const key = `${slotId}:${atMs}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        value: String(atMs),
        appointmentAtMs: atMs,
        dateKey,
        slotId,
        location: String(slot?.location || "").trim(),
        startAtMs,
        endAtMs,
        slotLabel: String(slot?.label || "").trim(),
        label: new Date(atMs).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        }),
      });
    }
  });
  return rows.sort((a, b) => Number(a.appointmentAtMs || 0) - Number(b.appointmentAtMs || 0));
};
const EMPTY_EMAIL_DESIGN = {
  body: {
    rows: [],
    values: {
      backgroundColor: "#ffffff",
      contentWidth: "680px",
      fontFamily: { label: "Arial", value: "arial,helvetica,sans-serif" },
    },
  },
};
const getFirstLastNameParts = (name) => {
  const full = String(name || "").trim();
  if (!full) return { firstName: "-", lastName: "-" };
  const [firstName = "", ...rest] = full.split(/\s+/).filter(Boolean);
  const lastName = rest.length ? rest[rest.length - 1] : "-";
  return {
    firstName: firstName || "-",
    lastName: lastName || "-",
  };
};
const getDisplayBidderName = (row = {}) => {
  const emailLower = normalize(row?.bidderEmail || row?.email || row?.emailLower || row?.id || "");
  const bidderName = String(row?.bidderName || "").trim();
  const username = String(row?.username || "").trim();
  if (emailLower === FLOOR_BIDDER_EMAIL) return "Passed";
  if (/^floor\b/i.test(bidderName)) return "Passed";
  if (/^floor\b/i.test(username)) return "Passed";
  return bidderName;
};
const getDisplayUsername = (row = {}) => {
  const emailLower = normalize(row?.bidderEmail || row?.email || row?.emailLower || row?.id || "");
  if (emailLower === FLOOR_BIDDER_EMAIL) return "Passed";
  const username = String(row?.username || "").trim();
  if (/^floor\b/i.test(username)) return "Passed";
  return username;
};
const hasValue = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string" && !v.trim()) return false;
  return true;
};

const normalizeSplitTotals = ({ total = 0, bought, floor } = {}) => {
  let t = toAbsInt(total);
  const hasBought = hasValue(bought);
  const hasFloor = hasValue(floor);
  let b = hasBought ? toAbsInt(bought) : null;
  let f = hasFloor ? toAbsInt(floor) : null;

  if (t <= 0) {
    b = b == null ? 0 : b;
    f = f == null ? 0 : f;
    t = b + f;
    return { total: t, bought: b, floor: f };
  }

  if (b == null && f == null) {
    b = t;
    f = 0;
  } else if (b == null) {
    b = Math.max(0, t - (f || 0));
  } else if (f == null) {
    f = Math.max(0, t - (b || 0));
  } else {
    const sum = b + f;
    if (sum !== t) {
      if (b >= t) {
        b = t;
        f = 0;
      } else {
        f = Math.max(0, t - b);
      }
    }
  }

  return { total: t, bought: toAbsInt(b), floor: toAbsInt(f) };
};
const isRegistrationMarkedNewUser = (registration = {}) => {
  if (typeof registration?.isNewUser === "boolean") return registration.isNewUser;
  const explicitNewUser = normalize(registration?.isNewUser);
  if (explicitNewUser === "yes" || explicitNewUser === "true" || explicitNewUser === "1") return true;
  if (explicitNewUser === "no" || explicitNewUser === "false" || explicitNewUser === "0") return false;
  const newUserText = normalize(registration?.newUser);
  if (newUserText === "yes" || newUserText === "true" || newUserText === "1") return true;
  if (newUserText === "no" || newUserText === "false" || newUserText === "0") return false;
  const regCountRaw =
    registration?.myAuctionsRegistrationCountValue ??
    registration?.myAuctionsRegistrationCount ??
    registration?.myAuctionsRegistrationCountRaw ??
    registration?.["My Auctions Registration Count"];
  const regCount = toAbsInt(regCountRaw);
  if (regCount > 0) return regCount === 1;
  return false;
};
const computeAuctionAnalytics = (summary, registrationRows = []) => {
  const s = summary || {};
  const registrations = toAbsInt(s?.registrationsCount ?? s?.registrations ?? 0);
  const uniqueBiddersWon = toAbsInt(s?.lotsUniqueBidders ?? s?.uniqueBidders ?? 0);
  const uniqueBiddersAll = toAbsInt(s?.lotsUniqueBiddersAll ?? uniqueBiddersWon);

  const totalLots = toAbsInt(s?.lotsTotalCount ?? s?.totalLots ?? 0);
  const lotsOnFloor = toAbsInt(s?.lotsFloorCount ?? 0);
  const lotsWon = toAbsInt(s?.lotsWonCount ?? Math.max(0, totalLots - lotsOnFloor));

  const totalHighBid = safeNum(s?.lotsTotalHighBid ?? s?.totalHighBid ?? 0);
  const totalMaxBid = safeNum(s?.lotsTotalMaxBid ?? 0);
  const bidsSplit = normalizeSplitTotals({
    total: s?.lotsTotalBidsPlaced ?? 0,
    bought: s?.lotsBoughtBidsPlaced,
    floor: s?.lotsFloorBidsPlaced,
  });
  const totalBidsPlaced = bidsSplit.total;
  const boughtBidsPlaced = bidsSplit.bought;
  const floorBidsPlaced = bidsSplit.floor;

  const viewsSplit = normalizeSplitTotals({
    total: s?.lotsTotalViews ?? 0,
    bought: s?.lotsBoughtViews,
    floor: s?.lotsFloorViews,
  });
  const totalViews = viewsSplit.total;
  const boughtViews = viewsSplit.bought;
  const floorViews = viewsSplit.floor;

  const watchesSplit = normalizeSplitTotals({
    total: s?.lotsTotalWatches ?? 0,
    bought: s?.lotsBoughtWatches,
    floor: s?.lotsFloorWatches,
  });
  const totalWatches = watchesSplit.total;
  const boughtWatches = watchesSplit.bought;
  const floorWatches = watchesSplit.floor;

  const pct = (num, den) => (den > 0 ? (num / den) * 100 : null);
  const avg = (num, den) => (den > 0 ? num / den : null);
  const rows = Array.isArray(registrationRows) ? registrationRows : [];
  const rowsCount = rows.length;
  const hasRows = rowsCount > 0;
  const derivedNewUsersCount = hasRows ? rows.reduce((sum, row) => sum + (isRegistrationMarkedNewUser(row) ? 1 : 0), 0) : null;
  const newUsersCountFromSummaryRaw =
    s?.newUsersCount ??
    s?.newUserCount ??
    s?.registrationsNewUsersCount ??
    s?.registrationsNewUserCount ??
    null;
  const hasSummaryNewUsersCount = Number.isFinite(Number(newUsersCountFromSummaryRaw));
  const newUsersCount = hasRows
    ? derivedNewUsersCount
    : hasSummaryNewUsersCount
      ? toAbsInt(newUsersCountFromSummaryRaw)
      : null;
  const newUsersTotalCount = hasRows ? rowsCount : registrations > 0 ? registrations : null;

  return {
    registrations,
    uniqueBiddersWon,
    uniqueBiddersAll,
    newUsersCount,
    newUsersTotalCount,
    totalLots,
    lotsWon,
    lotsOnFloor,
    totalHighBid,
    totalMaxBid,
    totalBidsPlaced,
    boughtBidsPlaced,
    floorBidsPlaced,
    totalViews,
    boughtViews,
    floorViews,
    totalWatches,
    boughtWatches,
    floorWatches,

    sellThroughPct: pct(lotsWon, totalLots),
    floorSharePct: pct(lotsOnFloor, totalLots),
    regToWinnerPct: pct(uniqueBiddersWon, registrations),
    newUsersPct: pct(newUsersCount, newUsersTotalCount),

    avgHammerPerWonLot: avg(totalHighBid, lotsWon),
    avgHammerPerTotalLot: avg(totalHighBid, totalLots),
    avgMaxPerWonLot: avg(totalMaxBid, lotsWon),
    avgBidsPerWonLot: avg(totalBidsPlaced, lotsWon),
    avgBidsPerRegistration: avg(totalBidsPlaced, registrations),
    viewsPerTotalHammerPrice: avg(totalViews, totalHighBid),
    bidsPerTotalHammerPrice: avg(totalBidsPlaced, totalHighBid),
    watchesPerTotalHammerPrice: avg(totalWatches, totalHighBid),

    viewsPerLot: avg(totalViews, totalLots),
    watchesPerLot: avg(totalWatches, totalLots),
    viewsPerBid: avg(totalViews, totalBidsPlaced),
    watchToViewPct: pct(totalWatches, totalViews),
    boughtWatchToViewPct: pct(boughtWatches, boughtViews),
    floorWatchToViewPct: pct(floorWatches, floorViews),
  };
};
const normalizeAuctionRegistrationFulfillmentStats = (auction = {}) => {
  const source =
    auction?.registrationFulfillmentStats &&
      typeof auction.registrationFulfillmentStats === "object" &&
      !Array.isArray(auction.registrationFulfillmentStats)
      ? auction.registrationFulfillmentStats
      : {};
  const totalRegistrations = toAbsInt(
    source?.totalRegistrations ?? auction?.registrationsCount ?? auction?.registrations ?? 0
  );
  const shippingRequestedCount = Math.min(
    totalRegistrations,
    toAbsInt(source?.shippingRequestedCount ?? auction?.shippingRequestedCount ?? 0)
  );
  const pickupCount = (() => {
    const raw = source?.pickupCount ?? auction?.pickupCount;
    if (raw === null || raw === undefined || String(raw).trim() === "") {
      return Math.max(0, totalRegistrations - shippingRequestedCount);
    }
    return Math.min(totalRegistrations, toAbsInt(raw));
  })();
  const newUsersCount = Math.min(
    totalRegistrations,
    toAbsInt(
      source?.newUsersCount ??
      auction?.newUsersCount ??
      auction?.newUserCount ??
      auction?.registrationsNewUsersCount ??
      0
    )
  );
  const newUsersShippingRequestedCount = Math.min(
    newUsersCount,
    toAbsInt(source?.newUsersShippingRequestedCount ?? auction?.newUsersShippingRequestedCount ?? 0)
  );
  const toPct = (explicitValue, numerator, denominator) => {
    if (explicitValue !== null && explicitValue !== undefined && String(explicitValue).trim() !== "") {
      return safeNum(explicitValue);
    }
    return denominator > 0 ? (numerator / denominator) * 100 : 0;
  };

  return {
    totalRegistrations,
    shippingRequestedCount,
    pickupCount,
    shippingRequestedPct: toPct(source?.shippingRequestedPct, shippingRequestedCount, totalRegistrations),
    pickupPct: toPct(source?.pickupPct, pickupCount, totalRegistrations),
    newUsersCount,
    newUsersPct: toPct(source?.newUsersPct, newUsersCount, totalRegistrations),
    newUsersShippingRequestedCount,
    newUsersShippingRequestedPct: toPct(
      source?.newUsersShippingRequestedPct,
      newUsersShippingRequestedCount,
      newUsersCount
    ),
    newUsersShippingRequestedOfTotalPct: toPct(
      source?.newUsersShippingRequestedOfTotalPct,
      newUsersShippingRequestedCount,
      totalRegistrations
    ),
    updatedAtMs: safeToMillis(source?.updatedAtMs),
  };
};
const AF_DEFAULT_BASE_HAMMER_PCT = 0.3;
const AF_REVENUE_PER_SOLD_LOT = 1;
const HOUSE_SELLER_AF_BASE_HAMMER_PCT = 1;
const HOUSE_SELLER_CONSIGNOR_ID = "4b78d21b-a";
const HOUSE_SELLER_CONSIGNOR_NAME = "house seller";
const normalizeConsignorNameForMatch = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const getHouseSellerHammerTotal = ({ sellerShareRows = [], consignorRows = [] } = {}) => {
  const targetId = normalizeConsignorProfileId(HOUSE_SELLER_CONSIGNOR_ID);
  const targetNames = new Set([normalizeConsignorNameForMatch(HOUSE_SELLER_CONSIGNOR_NAME)]);

  (Array.isArray(consignorRows) ? consignorRows : []).forEach((entry) => {
    const consignorId = normalizeConsignorProfileId(entry?.consignorId || entry?.id || "");
    if (consignorId !== targetId) return;
    const consignorName = normalizeConsignorNameForMatch(entry?.consignorName || entry?.name || "");
    if (consignorName) targetNames.add(consignorName);
  });

  const sellerRows = Array.isArray(sellerShareRows) ? sellerShareRows : [];
  const directHouseSellerMatch = sellerRows.find((row) => {
    const sellerName = normalizeConsignorNameForMatch(row?.name || row?.sellerName || "");
    return targetNames.has(sellerName);
  });
  if (directHouseSellerMatch) {
    return safeNum(directHouseSellerMatch?.hammerTotal);
  }

  return sellerRows.reduce((sum, row) => {
    const sellerId = normalizeConsignorProfileId(row?.consignorId || row?.id || "");
    if (sellerId && sellerId === targetId) return sum + safeNum(row?.hammerTotal);
    const sellerName = normalizeConsignorNameForMatch(row?.name || row?.sellerName || "");
    return targetNames.has(sellerName) ? sum + safeNum(row?.hammerTotal) : sum;
  }, 0);
};

const computeAuctionAfGain = ({
  totalHighBid = 0,
  lotsWon = 0,
  sellerShareRows = [],
  consignorRows = [],
} = {}) => {
  const hammer = safeNum(totalHighBid);
  const soldLots = toAbsInt(lotsWon);
  const houseSellerHammer = Math.min(
    hammer,
    getHouseSellerHammerTotal({ sellerShareRows, consignorRows })
  );
  const baseRevenue =
    hammer * AF_DEFAULT_BASE_HAMMER_PCT +
    soldLots * AF_REVENUE_PER_SOLD_LOT;
  const houseSellerOverrideRevenue =
    houseSellerHammer * (HOUSE_SELLER_AF_BASE_HAMMER_PCT - AF_DEFAULT_BASE_HAMMER_PCT);
  return baseRevenue + houseSellerOverrideRevenue;
};
const computeSellerRowRevenue = (row, { consignorRows = [] } = {}) => {
  const name = normalizeConsignorNameForMatch(row?.name || row?.sellerName || "");
  const hammer = safeNum(row?.hammerTotal || 0);
  const lots = toAbsInt(row?.lotsCount || 0);
  const isHouse =
    name === normalizeConsignorNameForMatch(HOUSE_SELLER_CONSIGNOR_NAME) ||
    normalizeConsignorProfileId(row?.consignorId || row?.id || "") === normalizeConsignorProfileId(HOUSE_SELLER_CONSIGNOR_ID);
  const basePct = isHouse ? HOUSE_SELLER_AF_BASE_HAMMER_PCT : AF_DEFAULT_BASE_HAMMER_PCT;
  const revenue = hammer * basePct + lots * AF_REVENUE_PER_SOLD_LOT;
  const revenuePerLot = lots > 0 ? revenue / lots : 0;
  return { revenue, revenuePerLot };
};
const computeAuctionShareTotals = (
  rows = [],
  { showRevenue = false, consignorRows = [], showDisposition = false } = {}
) => {
  const items = Array.isArray(rows) ? rows : [];
  const totals = items.reduce(
    (acc, item) => {
      acc.lotsCount += toAbsInt(item?.lotsCount || 0);
      acc.soldLotsCount += toAbsInt(item?.soldLotsCount || 0);
      acc.passedLotsCount += toAbsInt(item?.passedLotsCount || 0);
      acc.hammerTotal += safeNum(item?.hammerTotal || 0);
      if (showRevenue) {
        const rev = computeSellerRowRevenue(item, { consignorRows });
        acc.revenue += safeNum(rev?.revenue || 0);
      }
      return acc;
    },
    {
      lotsCount: 0,
      soldLotsCount: 0,
      passedLotsCount: 0,
      hammerTotal: 0,
      revenue: 0,
    }
  );
  return {
    ...totals,
    lotsSharePct: items.length ? 100 : 0,
    hammerSharePct: items.length ? 100 : 0,
    avgHammerPerSoldLot: totals.soldLotsCount > 0 ? totals.hammerTotal / totals.soldLotsCount : 0,
    revenuePerLot: totals.lotsCount > 0 ? totals.revenue / totals.lotsCount : 0,
    showDisposition,
  };
};

const aggregateAuctionRows = (rows) => {
  const totals = {
    registrationsCount: 0,
    lotsUniqueBidders: 0,
    lotsUniqueBiddersAll: 0,
    lotsTotalCount: 0,
    lotsWonCount: 0,
    lotsFloorCount: 0,
    lotsTotalHighBid: 0,
    lotsTotalMaxBid: 0,
    lotsTotalBidsPlaced: 0,
    lotsBoughtBidsPlaced: 0,
    lotsFloorBidsPlaced: 0,
    lotsTotalViews: 0,
    lotsBoughtViews: 0,
    lotsFloorViews: 0,
    lotsTotalWatches: 0,
    lotsBoughtWatches: 0,
    lotsFloorWatches: 0,
  };

  for (const row of Array.isArray(rows) ? rows : []) {
    const rowTotalLots = toAbsInt(row?.lotsTotalCount ?? row?.totalLots ?? 0);
    const rowFloorLots = toAbsInt(row?.lotsFloorCount ?? 0);
    const rowBidsSplit = normalizeSplitTotals({
      total: row?.lotsTotalBidsPlaced ?? 0,
      bought: row?.lotsBoughtBidsPlaced,
      floor: row?.lotsFloorBidsPlaced,
    });
    const rowViewsSplit = normalizeSplitTotals({
      total: row?.lotsTotalViews ?? 0,
      bought: row?.lotsBoughtViews,
      floor: row?.lotsFloorViews,
    });
    const rowWatchesSplit = normalizeSplitTotals({
      total: row?.lotsTotalWatches ?? 0,
      bought: row?.lotsBoughtWatches,
      floor: row?.lotsFloorWatches,
    });
    totals.registrationsCount += toAbsInt(row?.registrationsCount ?? row?.registrations ?? 0);
    totals.lotsUniqueBidders += toAbsInt(row?.lotsUniqueBidders ?? row?.uniqueBidders ?? 0);
    totals.lotsUniqueBiddersAll += toAbsInt(row?.lotsUniqueBiddersAll ?? row?.lotsUniqueBidders ?? row?.uniqueBidders ?? 0);
    totals.lotsTotalCount += rowTotalLots;
    totals.lotsWonCount += toAbsInt(row?.lotsWonCount ?? Math.max(0, rowTotalLots - rowFloorLots));
    totals.lotsFloorCount += rowFloorLots;
    totals.lotsTotalHighBid += safeNum(row?.lotsTotalHighBid ?? row?.totalHighBid ?? 0);
    totals.lotsTotalMaxBid += safeNum(row?.lotsTotalMaxBid ?? 0);
    totals.lotsTotalBidsPlaced += rowBidsSplit.total;
    totals.lotsBoughtBidsPlaced += rowBidsSplit.bought;
    totals.lotsFloorBidsPlaced += rowBidsSplit.floor;
    totals.lotsTotalViews += rowViewsSplit.total;
    totals.lotsBoughtViews += rowViewsSplit.bought;
    totals.lotsFloorViews += rowViewsSplit.floor;
    totals.lotsTotalWatches += rowWatchesSplit.total;
    totals.lotsBoughtWatches += rowWatchesSplit.bought;
    totals.lotsFloorWatches += rowWatchesSplit.floor;
  }

  if (totals.lotsWonCount <= 0 && totals.lotsTotalCount > 0) {
    totals.lotsWonCount = Math.max(0, totals.lotsTotalCount - totals.lotsFloorCount);
  }

  return totals;
};
function NamedUrlsEditor({
  rows,
  onChange,
  onCommit,
  disabled = false,
  title = "Named URLs",
  helperText = "Add label + URL pairs like Facebook, Instagram, Website, or Google Review.",
}) {
  const draftRows = coerceNamedUrlDraftRows(rows);
  const rowsToRender = draftRows.length ? draftRows : [createNamedUrlEntry()];
  const handleChange = (index, key, value) => {
    const next = rowsToRender.map((row, idx) => (idx === index ? { ...row, [key]: value } : row));
    onChange?.(next);
  };
  const handleCommit = () => {
    onCommit?.(normalizeNamedUrlsInput(rowsToRender));
  };
  const handleAdd = () => {
    onChange?.([...rowsToRender, createNamedUrlEntry()]);
  };
  const handleRemove = (index) => {
    const next = rowsToRender.filter((_, idx) => idx !== index);
    onChange?.(next.length ? next : []);
    onCommit?.(normalizeNamedUrlsInput(next));
  };

  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: 2,
        border: "1px solid rgba(229,231,235,0.95)",
        backgroundColor: "#fff",
        gridColumn: { xs: "1 / -1", md: "1 / -1" },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: "#6b7280" }}>
            {helperText}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={handleAdd}
          sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800 }}
        >
          Add URL
        </Button>
      </Box>
      <Box sx={{ mt: 1, display: "grid", gap: 1 }}>
        {rowsToRender.map((row, idx) => {
          const launchUrl = toLaunchableUrl(row?.url || "");
          return (
            <Box
              key={`named-url-${idx}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(160px, 220px) 1fr auto auto" },
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                label="Name"
                size="small"
                value={row?.name || ""}
                disabled={disabled}
                onChange={(e) => handleChange(idx, "name", e.target.value)}
                onBlur={handleCommit}
                sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}
              />
              <TextField
                label="URL"
                size="small"
                value={row?.url || ""}
                disabled={disabled}
                onChange={(e) => handleChange(idx, "url", e.target.value)}
                onBlur={handleCommit}
                sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}
              />
              <Button
                size="small"
                variant="outlined"
                component="a"
                href={launchUrl || undefined}
                target="_blank"
                rel="noreferrer"
                disabled={!launchUrl}
                sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800, minWidth: 72 }}
              >
                Open
              </Button>
              <Button
                size="small"
                color="error"
                variant="text"
                disabled={disabled}
                onClick={() => handleRemove(idx)}
                sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800, minWidth: 78 }}
              >
                Remove
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function HibidDashboard() {
  const primary = "#333999";
  const location = useLocation();
  const navigate = useNavigate();
  const pendingAuctionRouteIdRef = useRef("");
  const hibidPathParts = useMemo(() => {
    const normalizedPath = String(location.pathname || "").replace(/\/+$/, "");
    const match = normalizedPath.match(/\/hibid(?:\/(.*))?$/i);
    return String(match?.[1] || "")
      .split("/")
      .map((part) => String(part || "").trim())
      .filter(Boolean);
  }, [location.pathname]);

  const currentSection = useMemo(() => {
    const section = String(hibidPathParts[0] || "").trim().toLowerCase();
    if (["users", "auctions", "daily-registration", "product-search", "definitions"].includes(section)) return section;
    return "";
  }, [hibidPathParts]);

  const currentAuctionRouteId = useMemo(() => {
    if (currentSection !== "auctions") return "";
    return String(hibidPathParts[1] || "").trim();
  }, [currentSection, hibidPathParts]);

  const currentAuctionSubview = useMemo(() => {
    if (currentSection !== "auctions" || !currentAuctionRouteId) return "stats";
    return String(hibidPathParts[2] || "").trim().toLowerCase() === "registrations"
      ? "registrations"
      : "stats";
  }, [currentSection, currentAuctionRouteId, hibidPathParts]);

  const tab = useMemo(() => {
    if (currentSection === "users") return 0;
    if (currentSection === "auctions") return 1;
    if (currentSection === "daily-registration") return 2;
    if (currentSection === "product-search") return 3;
    return -1;
  }, [currentSection]);
  const currentPageTitle = useMemo(() => {
    if (currentSection === "users") return "Users";
    if (currentSection === "daily-registration") return "Daily Registration Activity";
    if (currentSection === "product-search") return "Product Search";
    if (currentSection === "definitions") return "Tier & LCV Definitions";
    if (currentSection === "auctions") {
      if (currentAuctionRouteId) {
        return currentAuctionSubview === "registrations" ? "Auction Registrations + Lots" : "Auction Stats";
      }
      return "Auctions";
    }
    return "HiBid Sidebar Options";
  }, [currentSection, currentAuctionRouteId, currentAuctionSubview]);
  const currentPageSubtitle = useMemo(() => {
    if (currentSection === "users") return "Search, filter, and manage bidders and customer details.";
    if (currentSection === "auctions") {
      if (currentAuctionRouteId) return String(currentAuctionRouteId || "").trim();
      return "Open auction stats, registrations, lots, and fulfillment tools.";
    }
    if (currentSection === "daily-registration") return "Review registration trends and drill into daily activity.";
    if (currentSection === "product-search") return "Search lot data across auctions with filters, sorting, and export tools.";
    if (currentSection === "definitions") return "Plain-English rules currently used in HiBid scoring.";
    return "Use the HiBid sidebar to move between sections.";
  }, [currentSection, currentAuctionRouteId, currentAuctionSubview]);

  const openHibidSection = useCallback(
    (section = "") => {
      const safeSection = String(section || "").trim().toLowerCase();
      navigate(safeSection ? `/hibid/${safeSection}` : "/hibid");
    },
    [navigate]
  );

  const openAuctionRoute = useCallback(
    (auctionOrId, { viewMode = "stats" } = {}) => {
      const auctionId = String(
        auctionOrId?.auctionId || auctionOrId?.id || auctionOrId || ""
      ).trim();
      if (!auctionId) return;
      const safeViewMode = viewMode === "registrations" ? "registrations" : "stats";
      navigate(`/hibid/auctions/${encodeURIComponent(auctionId)}/${safeViewMode}`);
    },
    [navigate]
  );
  useEffect(() => {
    if (currentSection !== "auctions" || !currentAuctionRouteId) return;
    const safeViewMode = currentAuctionSubview === "registrations" ? "registrations" : "stats";
    const expectedPath = `/hibid/auctions/${encodeURIComponent(currentAuctionRouteId)}/${safeViewMode}`;
    const normalizedCurrentPath = String(location.pathname || "").replace(/\/+$/, "");
    if (normalizedCurrentPath === expectedPath) return;
    navigate(expectedPath, { replace: true });
  }, [currentAuctionRouteId, currentAuctionSubview, currentSection, location.pathname, navigate]);

  const [userId, setUserId] = useState(() => localStorage.getItem("userId") || "");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [subscriptionName, setSubscriptionName] = useState("");
  const [tokensRemaining, setTokensRemaining] = useState(() => localStorage.getItem("tokens") ?? "0");
  const [orgReady, setOrgReady] = useState(false);
  const [needsSubscriptionLink, setNeedsSubscriptionLink] = useState(false);
  const [subLinkMsg, setSubLinkMsg] = useState("");
  const [syncInfo, setSyncInfo] = useState(null);

  const [toast, setToast] = useState({ open: false, msg: "", severity: "info" });
  const toastOpen = (msg, severity = "info") => setToast({ open: true, msg, severity });
  const toastClose = () => setToast((t) => ({ ...t, open: false }));

  const [accountManagers, setAccountManagers] = useState([]);
  const [accountManagerTierMap, setAccountManagerTierMap] = useState(() =>
    sanitizeAccountManagerTierMap({}, [])
  );
  const [accountManagerModalOpen, setAccountManagerModalOpen] = useState(false);
  const [accountManagerModalMode, setAccountManagerModalMode] = useState("create");
  const [accountManagersLoading, setAccountManagersLoading] = useState(false);
  const [accountManagerCreateSaving, setAccountManagerCreateSaving] = useState(false);
  const [accountManagerTierSaving, setAccountManagerTierSaving] = useState(false);
  const [accountManagerCreateFormOpen, setAccountManagerCreateFormOpen] = useState(false);
  const [accountManagerForm, setAccountManagerForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
  });
  const [consignors, setConsignors] = useState([]);
  const [consignorModalOpen, setConsignorModalOpen] = useState(false);
  const [consignorsLoading, setConsignorsLoading] = useState(false);
  const [consignorCreateSaving, setConsignorCreateSaving] = useState(false);
  const [consignorCreateFormOpen, setConsignorCreateFormOpen] = useState(false);
  const [consignorForm, setConsignorForm] = useState({
    consignorId: "",
    consignorName: "",
    pointOfContact: "",
    email: "",
    phone: "",
  });
  const [menuRow, setMenuRow] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [editForm, setEditForm] = useState({
    bidderName: "",
    username: "",
    company: "",
    phone: "",
    address: "",
    defaultPickupLocationId: "",
    defaultPickupLocationName: "",
    defaultPickupLocationAddress: "",
    namedUrls: [],
    myPrivateNotes: "",
    publicNotes: "",
    bidPermission: "",
    auctionStatus: "",
    lastAuctionName: "",
  });

  const [auctions, setAuctions] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const selectedAuctionId = selectedAuction ? selectedAuction.auctionId || selectedAuction.id : "";
  const selectedAuctionLabel = selectedAuction ? selectedAuction.auctionName || selectedAuctionId : "";
  const setAuctionViewMode = useCallback(
    (nextMode) => {
      if (!currentAuctionRouteId) return;
      const safeMode = String(nextMode || "").trim().toLowerCase() === "registrations"
        ? "registrations"
        : "stats";
      const nextPath = `/hibid/auctions/${encodeURIComponent(currentAuctionRouteId)}/${safeMode}`;
      const normalizedCurrentPath = String(location.pathname || "").replace(/\/+$/, "");
      if (normalizedCurrentPath === nextPath) return;
      navigate(nextPath);
    },
    [currentAuctionRouteId, location.pathname, navigate]
  );

  const [auctionSummary, setAuctionSummary] = useState(null);
  const [auctionSummaryLoading, setAuctionSummaryLoading] = useState(false);

  const [auctionRegs, setAuctionRegs] = useState([]);
  const [aregLoading, setAregLoading] = useState(false);
  const [aregSearch, setAregSearch] = useState("");
  const [auctionHibidSearch, setAuctionHibidSearch] = useState("");
  const [auctionLocationFilter, setAuctionLocationFilter] = useState("all");
  const [auctionTableSortKey, setAuctionTableSortKey] = useState("endMs");
  const [auctionTableSortDir, setAuctionTableSortDir] = useState("desc");
  const [auctionWeeklyStatsOpen, setAuctionWeeklyStatsOpen] = useState(false);
  const [locationsManagerOpen, setLocationsManagerOpen] = useState(false);
  const [auctionRegTrendChartType, setAuctionRegTrendChartType] = useState("bar");
  const [auctionRegTrendViewBy, setAuctionRegTrendViewBy] = useState("day");
  const [auctionRegTrendDateMode, setAuctionRegTrendDateMode] = useState("range");
  const [auctionRegTrendStartDate, setAuctionRegTrendStartDate] = useState("");
  const [auctionRegTrendEndDate, setAuctionRegTrendEndDate] = useState("");
  const [auctionInlineInfoDraftById, setAuctionInlineInfoDraftById] = useState({});
  const [auctionInlineInfoSavingById, setAuctionInlineInfoSavingById] = useState({});
  const [auctionInlineLocationInputById, setAuctionInlineLocationInputById] = useState({});
  const [auctionInlineLocationOptionsById, setAuctionInlineLocationOptionsById] = useState({});
  const [auctionListLayout, setAuctionListLayout] = useState(() => {
    try {
      const raw = String(localStorage.getItem(HIBID_AUCTION_LIST_LAYOUT_LS_KEY) || "").trim().toLowerCase();
      if (raw === "cards") return "cards";
      return "table";
    } catch {
      return "table";
    }
  });
  const [aregShipperFilter, setAregShipperFilter] = useState("all");
  const [aregFulfillmentFilter, setAregFulfillmentFilter] = useState("all");
  const [aregNewUserFilter, setAregNewUserFilter] = useState("all");
  const [aregPickupFilter, setAregPickupFilter] = useState("all");
  const [aregShippedFilter, setAregShippedFilter] = useState("all");
  const [aregDeliveryFilter, setAregDeliveryFilter] = useState("all");
  const [aregConsignorFilter, setAregConsignorFilter] = useState("all");
  const [aregPickupLocationFilter, setAregPickupLocationFilter] = useState("all");
  const [aregDeliveryLocationFilter, setAregDeliveryLocationFilter] = useState("all");
  const [aregPickupSelectionFilter, setAregPickupSelectionFilter] = useState("all");
  const [aregPickupDateStart, setAregPickupDateStart] = useState("");
  const [aregPickupDateEnd, setAregPickupDateEnd] = useState("");
  const [aregPickupDraftByEmail, setAregPickupDraftByEmail] = useState({});
  const [aregPickupSavingByEmail, setAregPickupSavingByEmail] = useState({});
  const [aregPickupEditByEmail, setAregPickupEditByEmail] = useState({});
  const [aregShelfDraftByEmail, setAregShelfDraftByEmail] = useState({});
  const [aregShelfSavingByEmail, setAregShelfSavingByEmail] = useState({});
  const [aregAmountDueDraftByEmail, setAregAmountDueDraftByEmail] = useState({});
  const [auctionUserDetailsOpen, setAuctionUserDetailsOpen] = useState(false);
  const [auctionUserDetailsBusy, setAuctionUserDetailsBusy] = useState(false);
  const [auctionUserDetailsErr, setAuctionUserDetailsErr] = useState("");
  const [auctionUserDetailsData, setAuctionUserDetailsData] = useState(null);
  const [auctionUserDetailsEmail, setAuctionUserDetailsEmail] = useState("");
  const [auctionUserDetailsSourceReg, setAuctionUserDetailsSourceReg] = useState(null);
  const [auctionUserDetailsForm, setAuctionUserDetailsForm] = useState(null);
  const [auctionUserDetailsSavingByField, setAuctionUserDetailsSavingByField] = useState({});
  const [auctionUserDetailsSaveError, setAuctionUserDetailsSaveError] = useState("");
  const auctionUserDetailsSaveTimersRef = useRef({});
  const registrationFiltersKeyRef = useRef("");
  const [selectedRegsExportBusy, setSelectedRegsExportBusy] = useState(false);
  const [fastRevenueReportBusy, setFastRevenueReportBusy] = useState(false);
  const [buyerRetentionReportBusy, setBuyerRetentionReportBusy] = useState(false);
  const [buyerRetentionAuctionCount, setBuyerRetentionAuctionCount] = useState("1");
  const [buyerRetentionMatchMode, setBuyerRetentionMatchMode] = useState("exact");
  const [pickupPicklistExcelBusy, setPickupPicklistExcelBusy] = useState(false);
  const [pickupPicklistPdfBusy, setPickupPicklistPdfBusy] = useState(false);
  const [pickupPicklistLotsPdfBusy, setPickupPicklistLotsPdfBusy] = useState(false);
  const [deliveryManifestExcelBusy, setDeliveryManifestExcelBusy] = useState(false);
  const [deliveryManifestPdfBusy, setDeliveryManifestPdfBusy] = useState(false);
  const [deliveryManifestLotsPdfBusy, setDeliveryManifestLotsPdfBusy] = useState(false);
  const [registrationBoxLabelsBusy, setRegistrationBoxLabelsBusy] = useState(false);
  const [registrationBoxLabelsBusyByKey, setRegistrationBoxLabelsBusyByKey] = useState({});
  const [registrationFulfillmentConfirm, setRegistrationFulfillmentConfirm] = useState({
    open: false,
    type: "",
    nextValue: false,
    row: null,
    userName: "",
  });
  const [registrationFulfillmentDetails, setRegistrationFulfillmentDetails] = useState({
    open: false,
    row: null,
  });
  const [registrationFulfillmentConfirmBusy, setRegistrationFulfillmentConfirmBusy] = useState(false);
  const [aregSortKey, setAregSortKey] = useState("registeredAt");
  const [aregSortDir, setAregSortDir] = useState("desc");
  const [aregSelected, setAregSelected] = useState(() => new Set());
  const [auctionEndSaving, setAuctionEndSaving] = useState(false);
  const auctionSettingsAutosaveTimerRef = useRef(null);
  const auctionSettingsLastAutoSaveSigRef = useRef("");
  const registrationBoxLogoRef = useRef("");
  const registrationBoxReviewQrRef = useRef("");
  const registrationBoxFooterLogoRef = useRef("");
  const [lotsAgg, setLotsAgg] = useState({});
  const [lotsOpen, setLotsOpen] = useState(false);
  const [lotsBusy, setLotsBusy] = useState(false);
  const [lotsErr, setLotsErr] = useState("");
  const [lotsData, setLotsData] = useState(null);
  const [lotsModalSortKey, setLotsModalSortKey] = useState("lot");
  const [lotsModalSortDir, setLotsModalSortDir] = useState("asc");
  const [lotsUser, setLotsUser] = useState(null);
  const [fullDescriptionDialog, setFullDescriptionDialog] = useState({
    open: false,
    title: "Full Description",
    text: "",
  });
  const [expandedMediaUrlsByKey, setExpandedMediaUrlsByKey] = useState({});
  const [chargebackSignOpen, setChargebackSignOpen] = useState(false);
  const [chargebackSignRow, setChargebackSignRow] = useState(null);
  const [chargebackSignSaving, setChargebackSignSaving] = useState(false);
  const [chargebackSignError, setChargebackSignError] = useState("");
  const [chargebackSignSuccess, setChargebackSignSuccess] = useState("");
  const [chargebackSignSignatureName, setChargebackSignSignatureName] = useState("");
  const [chargebackSignSignatureHasInk, setChargebackSignSignatureHasInk] = useState(false);
  const chargebackSignatureCanvasRef = useRef(null);
  const chargebackSignatureDrawingRef = useRef(false);

  const [auctionEndAtLocal, setAuctionEndAtLocal] = useState("");
  const [auctionConsignors, setAuctionConsignors] = useState([]);
  const [auctionAccountManagerIds, setAuctionAccountManagerIds] = useState([]);
  const [auctionPickupLocations, setAuctionPickupLocations] = useState([]);
  const [auctionPickupSlots, setAuctionPickupSlots] = useState([]);
  const [auctionLocation, setAuctionLocation] = useState("");
  const [auctionLocationInput, setAuctionLocationInput] = useState("");
  const [auctionFieldOptions, setAuctionFieldOptions] = useState({ consignors: [], locations: [] });
  const [locationMetadataRows, setLocationMetadataRows] = useState([]);
  const [cityPlacesReady, setCityPlacesReady] = useState(false);
  const [auctionLocationCityOptions, setAuctionLocationCityOptions] = useState([]);
  const [createAuctionLocationCityOptions, setCreateAuctionLocationCityOptions] = useState([]);
  const [cityOptionsLoading, setCityOptionsLoading] = useState(false);
  const auctionViewMode = currentAuctionSubview;
  const [selectedAuctionStatsTab, setSelectedAuctionStatsTab] = useState("overview");
  const [overallDashboardOpen, setOverallDashboardOpen] = useState(false);
  const [compareAuctionsOpen, setCompareAuctionsOpen] = useState(false);
  const [compareAuctionIds, setCompareAuctionIds] = useState([""]);
  const [compareAuctionView, setCompareAuctionView] = useState("data");
  const [compareAuctionRegTrendChartType, setCompareAuctionRegTrendChartType] = useState("bar");
  const [compareAuctionRegTrendViewBy, setCompareAuctionRegTrendViewBy] = useState("day");
  const [compareAuctionRegTrendDateMode, setCompareAuctionRegTrendDateMode] = useState("range");
  const [compareAuctionRegTrendStartDate, setCompareAuctionRegTrendStartDate] = useState("");
  const [compareAuctionRegTrendEndDate, setCompareAuctionRegTrendEndDate] = useState("");
  const [compareAuctionRegsById, setCompareAuctionRegsById] = useState({});
  const [compareAuctionRegsLoadingById, setCompareAuctionRegsLoadingById] = useState({});
  const [compareAuctionRegsErrorById, setCompareAuctionRegsErrorById] = useState({});
  const [overallAuctionSortKey, setOverallAuctionSortKey] = useState("auctionEndDate");
  const [overallAuctionSortDir, setOverallAuctionSortDir] = useState("desc");
  const [overallAuctionSearch, setOverallAuctionSearch] = useState("");
  const [createAuctionOpen, setCreateAuctionOpen] = useState(false);
  const [createAuctionBusy, setCreateAuctionBusy] = useState(false);
  const [createAuctionName, setCreateAuctionName] = useState("");
  const [createAuctionEndAtLocal, setCreateAuctionEndAtLocal] = useState("");
  const [createAuctionConsignor, setCreateAuctionConsignor] = useState("");
  const [createAuctionLocation, setCreateAuctionLocation] = useState("");
  const [createAuctionLocationInput, setCreateAuctionLocationInput] = useState("");
  const [createAuctionRegistrationFile, setCreateAuctionRegistrationFile] = useState(null);
  const [createAuctionLotsFile, setCreateAuctionLotsFile] = useState(null);
  const [fileNameMismatchDialog, setFileNameMismatchDialog] = useState({
    open: false,
    uploadKindLabel: "file",
    auctionName: "",
    fileName: "",
  });

  const customersFileInputRef = useRef(null);
  const lotsFileInputRef = useRef(null);
  const listernautFileInputRef = useRef(null);
  const regsFileInputRef = useRef(null);
  const regsUploadModeRef = useRef("append");
  const createAuctionRegistrationFileInputRef = useRef(null);
  const createAuctionLotsFileInputRef = useRef(null);
  const shippingFileInputRef = useRef(null);
  const compareAuctionRegsRequestsRef = useRef(new Set());
  const fileNameMismatchResolveRef = useRef(null);
  const cityAutocompleteServiceRef = useRef(null);
  const auctionCityOptionsTimerRef = useRef(null);
  const createAuctionCityOptionsTimerRef = useRef(null);
  const inlineAuctionCityOptionsTimerByIdRef = useRef({});
  const inlineAuctionCityOptionsSeqByIdRef = useRef({});
  const auctionInlineAutosaveTimerByIdRef = useRef({});
  const auctionInlineLastAutoSaveSigByIdRef = useRef({});
  const auctionCityOptionsSeqRef = useRef(0);
  const createAuctionCityOptionsSeqRef = useRef(0);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [listernautUploadBusy, setListernautUploadBusy] = useState(false);
  const [shipBusy, setShipBusy] = useState(false);
  const [regUploadBusy, setRegUploadBusy] = useState(false);
  const [uploadDialogText, setUploadDialogText] = useState("");

  const [chargebackEmailFulfillmentFilter, setChargebackEmailFulfillmentFilter] = useState("all");
  const [chargebackEmailAlreadySentFilter, setChargebackEmailAlreadySentFilter] = useState("all");
  const [chargebackEmailWonLotFilter, setChargebackEmailWonLotFilter] = useState("won_lots");
  const skipNextAuctionFilterResetRef = useRef(false);
  const previousSelectedAuctionIdRef = useRef("");
  const resetAuctionRegistrationFilters = useCallback(() => {
    setAregSearch("");
    setAregShipperFilter("all");
    setAregFulfillmentFilter("all");
    setAregNewUserFilter("all");
    setAregPickupFilter("all");
    setAregShippedFilter("all");
    setAregDeliveryFilter("all");
    setAregConsignorFilter("all");
    setAregPickupLocationFilter("all");
    setAregDeliveryLocationFilter("all");
    setAregPickupSelectionFilter("all");
    setAregPickupDateStart("");
    setAregPickupDateEnd("");
    setChargebackEmailFulfillmentFilter("all");
    setChargebackEmailAlreadySentFilter("all");
    setChargebackEmailWonLotFilter("won_lots");
    setAregSelected(() => new Set());
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(HIBID_AUCTION_LIST_LAYOUT_LS_KEY, auctionListLayout === "cards" ? "cards" : "table");
    } catch { }
  }, [auctionListLayout]);
  useEffect(() => {
    return () => {
      const resolver = fileNameMismatchResolveRef.current;
      fileNameMismatchResolveRef.current = null;
      if (typeof resolver === "function") resolver(false);
    };
  }, []);
  useEffect(() => {
    return () => {
      const timers = inlineAuctionCityOptionsTimerByIdRef.current || {};
      Object.keys(timers).forEach((key) => {
        if (timers[key]) clearTimeout(timers[key]);
      });
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const bootPlaces = async () => {
      try {
        if (window.google?.maps?.places) {
          cityAutocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
          if (!cancelled) setCityPlacesReady(true);
          return;
        }
        if (!GOOGLE_MAPS_API_KEY) {
          if (!cancelled) setCityPlacesReady(false);
          return;
        }
        const loader = new Loader({
          apiKey: GOOGLE_MAPS_API_KEY,
          version: "weekly",
          libraries: ["places"],
        });
        await loader.load();
        if (!window.google?.maps?.places) throw new Error("Google Places not available");
        cityAutocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        if (!cancelled) setCityPlacesReady(true);
      } catch {
        if (!cancelled) setCityPlacesReady(false);
      }
    };
    bootPlaces();
    return () => {
      cancelled = true;
      if (auctionCityOptionsTimerRef.current) clearTimeout(auctionCityOptionsTimerRef.current);
      if (createAuctionCityOptionsTimerRef.current) clearTimeout(createAuctionCityOptionsTimerRef.current);
    };
  }, []);
  useEffect(() => {
    setAuctionLocationInput(String(auctionLocation || ""));
  }, [auctionLocation]);
  useEffect(() => {
    setCreateAuctionLocationInput(String(createAuctionLocation || ""));
  }, [createAuctionLocation]);
  useEffect(() => {
    setSelectedAuctionStatsTab("overview");
  }, [selectedAuctionId]);
  useEffect(
    () => () => {
      if (auctionSettingsAutosaveTimerRef.current) clearTimeout(auctionSettingsAutosaveTimerRef.current);
    },
    []
  );

  const fetchCityPredictions = async (inputValue = "") => {
    const query = String(inputValue || "").trim();
    if (!cityAutocompleteServiceRef.current || !query || query.length < 2) return [];

    return new Promise((resolve) => {
      cityAutocompleteServiceRef.current.getPlacePredictions(
        {
          input: query,
          types: ["(cities)"],
          componentRestrictions: { country: "us" },
        },
        (predictions, status) => {
          const ok = status === window.google?.maps?.places?.PlacesServiceStatus?.OK;
          if (!ok || !Array.isArray(predictions)) return resolve([]);
          const options = uniqSortedOptions(
            predictions
              .map((p) => String(p?.description || "").trim())
              .filter(Boolean)
          );
          resolve(options);
        }
      );
    });
  };
  const queueAuctionCityOptions = (inputValue = "") => {
    const query = String(inputValue || "").trim();
    if (!cityPlacesReady || query.length < 2) {
      if (auctionCityOptionsTimerRef.current) clearTimeout(auctionCityOptionsTimerRef.current);
      setAuctionLocationCityOptions([]);
      setCityOptionsLoading(false);
      return;
    }
    const seq = ++auctionCityOptionsSeqRef.current;
    if (auctionCityOptionsTimerRef.current) clearTimeout(auctionCityOptionsTimerRef.current);
    setCityOptionsLoading(true);
    auctionCityOptionsTimerRef.current = setTimeout(async () => {
      const options = await fetchCityPredictions(query);
      if (seq !== auctionCityOptionsSeqRef.current) return;
      setAuctionLocationCityOptions(options);
      setCityOptionsLoading(false);
    }, 220);
  };
  const queueCreateAuctionCityOptions = (inputValue = "") => {
    const query = String(inputValue || "").trim();
    if (!cityPlacesReady || query.length < 2) {
      if (createAuctionCityOptionsTimerRef.current) clearTimeout(createAuctionCityOptionsTimerRef.current);
      setCreateAuctionLocationCityOptions([]);
      setCityOptionsLoading(false);
      return;
    }
    const seq = ++createAuctionCityOptionsSeqRef.current;
    if (createAuctionCityOptionsTimerRef.current) clearTimeout(createAuctionCityOptionsTimerRef.current);
    setCityOptionsLoading(true);
    createAuctionCityOptionsTimerRef.current = setTimeout(async () => {
      const options = await fetchCityPredictions(query);
      if (seq !== createAuctionCityOptionsSeqRef.current) return;
      setCreateAuctionLocationCityOptions(options);
      setCityOptionsLoading(false);
    }, 220);
  };
  const auctionLocationOptions = useMemo(
    () =>
      uniqSortedOptions([
        ...(Array.isArray(auctionFieldOptions?.locations) ? auctionFieldOptions.locations : []),
        ...auctionLocationCityOptions,
        String(auctionLocation || "").trim(),
      ]),
    [auctionFieldOptions?.locations, auctionLocationCityOptions, auctionLocation]
  );
  const createAuctionLocationOptions = useMemo(
    () =>
      uniqSortedOptions([
        ...(Array.isArray(auctionFieldOptions?.locations) ? auctionFieldOptions.locations : []),
        ...createAuctionLocationCityOptions,
        String(createAuctionLocation || "").trim(),
      ]),
    [auctionFieldOptions?.locations, createAuctionLocationCityOptions, createAuctionLocation]
  );
  const registrationConsignorOptions = useMemo(() => {
    const byId = new Map();
    let hasUnassigned = false;
    const addOption = (entry = {}) => {
      const consignorId = normalizeConsignorProfileId(entry?.consignorId || entry?.id || "");
      const consignorName = String(entry?.consignorName || entry?.name || consignorId || "").trim();
      const key = consignorId || normalize(consignorName);
      if (!key || byId.has(key)) return;
      byId.set(key, {
        value: consignorId || key,
        label: consignorName || consignorId || key,
      });
    };
    (Array.isArray(auctionSummary?.listernautConsignorRows) ? auctionSummary.listernautConsignorRows : []).forEach(addOption);
    (Array.isArray(auctionRegs) ? auctionRegs : []).forEach((row) => {
      const regRows = getRegistrationConsignorRows(row);
      if (!regRows.length) hasUnassigned = true;
      regRows.forEach(addOption);
    });
    const sorted = Array.from(byId.values()).sort((a, b) => {
      const labelDiff = String(a?.label || "").localeCompare(String(b?.label || ""));
      if (labelDiff !== 0) return labelDiff;
      return String(a?.value || "").localeCompare(String(b?.value || ""));
    });
    if (hasUnassigned) {
      sorted.push({ value: AUCTION_CONSIGNOR_FILTER_UNASSIGNED, label: "Unassigned" });
    }
    return sorted;
  }, [auctionSummary?.listernautConsignorRows, auctionRegs]);
  const pickupLocationMetadataNames = useMemo(
    () => uniqSortedOptions((Array.isArray(locationMetadataRows) ? locationMetadataRows : []).map((row) => row?.name || "")),
    [locationMetadataRows]
  );
  const getPickupLocationSelectOptions = useCallback(
    (values = []) =>
      uniqSortedOptions([
        ...pickupLocationMetadataNames,
        ...(Array.isArray(values) ? values : []),
      ]),
    [pickupLocationMetadataNames]
  );
  const selectedAuctionPickupLocationChoices = useMemo(
    () =>
      getPickupLocationSelectOptions(
        getAuctionPickupLocationChoices(
          auctionSummary || selectedAuction || {},
          ""
        )
      ),
    [auctionSummary, selectedAuction, getPickupLocationSelectOptions]
  );
  const selectedAuctionPickupSlotsSource = useMemo(
    () =>
      sanitizeAuctionPickupSlots((auctionSummary || selectedAuction || {})?.pickupSlots || [], {
        fallbackLocations: getAuctionPickupLocationChoices(auctionSummary || selectedAuction || {}, ""),
      }),
    [auctionSummary, selectedAuction]
  );
  const registrationPickupLocationOptions = useMemo(() => {
    const byValue = new Map();
    selectedAuctionPickupLocationChoices.forEach((location) => {
      const value = String(location || "").trim();
      const key = normalize(value);
      if (!key || byValue.has(key)) return;
      byValue.set(key, {
        value,
        label: value,
      });
    });
    (Array.isArray(auctionRegs) ? auctionRegs : []).forEach((row) => {
      const appointment = getRegistrationPickupAppointment(row);
      const location = String(appointment?.location || "").trim();
      if (!location) return;
      const key = normalize(location);
      if (!key || byValue.has(key)) return;
      byValue.set(key, {
        value: location,
        label: location,
      });
    });
    return Array.from(byValue.values()).sort((a, b) => String(a?.label || "").localeCompare(String(b?.label || "")));
  }, [auctionRegs, selectedAuctionPickupLocationChoices]);
  const registrationDeliveryLocationOptions = useMemo(() => {
    const byValue = new Map();
    (Array.isArray(auctionRegs) ? auctionRegs : []).forEach((row) => {
      const delivery = getRegistrationDelivery(row);
      const location = String(delivery?.location || "").trim();
      const key = normalize(location);
      if (!key || byValue.has(key)) return;
      byValue.set(key, {
        value: location,
        label: location,
      });
    });
    return Array.from(byValue.values()).sort((a, b) => String(a?.label || "").localeCompare(String(b?.label || "")));
  }, [auctionRegs]);
  const applyLocationMetadataRows = useCallback((rows = []) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    setLocationMetadataRows(safeRows);
    const names = safeRows.map((row) => String(row?.name || "").trim()).filter(Boolean);
    if (names.length) {
      setAuctionFieldOptions((prev) => mergeAuctionFieldOptions(prev, { locations: names }));
    }
  }, []);

  useEffect(() => {
    const uid = localStorage.getItem("userId") || "";
    if (uid && uid !== userId) setUserId(uid);
  }, []);

  useEffect(() => {
    if (!userId || !subscriptionId) {
      setLocationMetadataRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/alllocation`, {
          params: { userId, subscriptionId },
          headers: {
            "x-user-id": userId,
            "x-subscription-id": subscriptionId,
          },
        });
        if (cancelled) return;
        applyLocationMetadataRows(data);
      } catch {
        if (cancelled) return;
        setLocationMetadataRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyLocationMetadataRows, subscriptionId, userId]);

  async function ensureOrg(existingSubId) {
    const sub = String(existingSubId || "").trim();
    if (sub) return sub;
    throw new Error("Missing subscriptionId. Link your subscriptionId first in Settings.");
  }

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    (async () => {
      try {
        setOrgReady(false);
        setNeedsSubscriptionLink(false);
        setSubLinkMsg("");

        const { data } = await axios.get(`${config.API_BASE_URL}/get-user-details?userId=${userId}`);

        const customSubObj =
          data?.customSubscription && typeof data.customSubscription === "object" ? data.customSubscription : null;

        let subFromUser = "";
        let subKeyFromUser = "";
        if (customSubObj) {
          const entries = Object.entries(customSubObj)
            .map(([k, v]) => [String(k || "").trim(), String(v || "").trim()])
            .filter(([k, v]) => Boolean(k) && Boolean(v));
          if (entries.length) {
            const [k, v] = entries[entries.length - 1];
            subKeyFromUser = k;
            subFromUser = v;
          }
        }

        const subName =
          String(data?.subscriptionName || data?.subscriptionLabel || data?.orgName || data?.company || "").trim() ||
          String(subKeyFromUser || "").trim() ||
          "";

        const nextTokens =
          data?.tokens !== undefined && data?.tokens !== null ? String(data.tokens) : localStorage.getItem("tokens") ?? "0";

        let subId = "";
        try {
          subId = await ensureOrg(subFromUser);
        } catch (e) {
          if (cancelled) return;
          setSubscriptionId("");
          setNeedsSubscriptionLink(true);
          setSubLinkMsg(e?.message || "Missing subscriptionId. Link your subscriptionId first in Settings.");
          setOrgReady(false);
          return;
        }

        if (cancelled) return;

        setSubscriptionId(subId);
        setSubscriptionName(subName);
        setTokensRemaining(nextTokens);
        try {
          localStorage.setItem("tokens", String(nextTokens));
        } catch { }
        setOrgReady(true);
      } catch (e) {
        if (cancelled) return;
        setSubscriptionId("");
        setNeedsSubscriptionLink(true);
        setSubLinkMsg("Unable to load account details. Refresh and ensure your subscriptionId is linked in Settings.");
        setOrgReady(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);
  const auctionAnalytics = useMemo(() => computeAuctionAnalytics(auctionSummary, auctionRegs), [auctionSummary, auctionRegs]);
  const auctionNewUsersStats = useMemo(() => {
    const rows = Array.isArray(auctionRegs) ? auctionRegs : [];
    const totals = rows.reduce(
      (acc, row) => {
        const emailLower = normalize(row?.bidderEmail || row?.email);
        const lotsCount = toAbsInt(row?.lotsCount ?? lotsAgg[emailLower]?.lotsCount ?? 0);
        const hammerTotal = safeNum(row?.totalHighBid ?? lotsAgg[emailLower]?.totalHighBid ?? 0);
        const isNew = isRegistrationMarkedNewUser(row);
        acc.totalUsers += 1;
        acc.totalLots += lotsCount;
        acc.totalHammer += hammerTotal;
        if (isNew) {
          acc.newUsers += 1;
          acc.newLots += lotsCount;
          acc.newHammer += hammerTotal;
        }
        return acc;
      },
      { totalUsers: 0, newUsers: 0, totalLots: 0, newLots: 0, totalHammer: 0, newHammer: 0 }
    );
    const pct = (num, den) => (den > 0 ? (num / den) * 100 : null);
    const totalUsersForShare = totals.totalUsers > 0 ? totals.totalUsers : toAbsInt(auctionAnalytics?.registrations ?? 0);
    const totalHammerForShare = safeNum(auctionAnalytics?.totalHighBid ?? 0) > 0 ? safeNum(auctionAnalytics?.totalHighBid ?? 0) : totals.totalHammer;
    const totalLotsForShare = toAbsInt(auctionAnalytics?.lotsWon ?? 0) > 0 ? toAbsInt(auctionAnalytics?.lotsWon ?? 0) : totals.totalLots;
    return {
      newUsersCount: totals.newUsers,
      totalUsersCount: totalUsersForShare,
      newUsersPct: pct(totals.newUsers, totalUsersForShare),
      newUsersHammerTotal: totals.newHammer,
      totalHammer: totalHammerForShare,
      newUsersHammerSharePct: pct(totals.newHammer, totalHammerForShare),
      newUsersLotsTotal: totals.newLots,
      totalLots: totalLotsForShare,
      newUsersLotsSharePct: pct(totals.newLots, totalLotsForShare),
    };
  }, [auctionRegs, lotsAgg, auctionAnalytics]);
  const overallAuctionsSummary = useMemo(() => aggregateAuctionRows(auctions), [auctions]);
  const overallAuctionAnalytics = useMemo(() => computeAuctionAnalytics(overallAuctionsSummary), [overallAuctionsSummary]);
  const auctionsWithLotsCount = useMemo(
    () => (Array.isArray(auctions) ? auctions.filter((a) => toAbsInt(a?.lotsTotalCount ?? a?.totalLots ?? 0) > 0).length : 0),
    [auctions]
  );
  const auctionsWithRegistrationsCount = useMemo(
    () => (Array.isArray(auctions) ? auctions.filter((a) => toAbsInt(a?.registrationsCount ?? a?.registrations ?? 0) > 0).length : 0),
    [auctions]
  );
  const auctionAccountManagerById = useMemo(() => {
    const map = new Map();
    (Array.isArray(accountManagers) ? accountManagers : []).forEach((manager) => {
      const id = normalizeAccountManagerId(manager?.id || "");
      if (!id || map.has(id)) return;
      map.set(id, manager);
    });
    return map;
  }, [accountManagers]);
  const auctionHibidViewRows = useMemo(() => {
    const nowMs = Date.now();
    return (Array.isArray(auctions) ? auctions : [])
      .map((row) => {
        const auctionId = String(row?.auctionId || row?.id || "").trim();
        const auctionName = String(row?.auctionName || auctionId || "Untitled Auction").trim();
        const endMs =
          safeToMillis(
            row?.auctionEndAtMs ??
            row?.auctionEndMs ??
            row?.auctionEndAt ??
            row?.endAtMs ??
            row?.endAt ??
            row?.lastEmailSentAt
          ) ?? null;
        const updatedMs = safeToMillis(row?.updatedAt) ?? 0;
        const registrationsUploadAtMs = safeToMillis(row?.registrationsUploadAtMs ?? row?.registrationsUploadAt) ?? 0;
        const lotsUploadAtMs = safeToMillis(row?.lotsUpdatedAtMs ?? row?.lotsUpdatedAt) ?? 0;
        const shippingUploadAtMs = safeToMillis(row?.shippingUploadAtMs ?? row?.shippingUploadAt) ?? 0;
        const listernautUploadAtMs = safeToMillis(row?.listernautUploadAtMs ?? row?.listernautUploadAt) ?? 0;
        const listernautUploadId = String(row?.listernautActiveLotsUploadId || "").trim();
        const activeLotsUploadId = String(row?.activeLotsUploadId || "").trim();
        const listernautRows = toAbsInt(row?.listernautRows || 0);
        const listernautMatchedLots = toAbsInt(row?.listernautMatchedLots || 0);
        const listernautUpdatedLots = toAbsInt(row?.listernautUpdatedLots || 0);
        const hasRegistrationsUpload = registrationsUploadAtMs > 0 || toAbsInt(row?.registrationsCount) > 0;
        const hasLotsUpload = !!String(row?.activeLotsUploadId || "").trim() || lotsUploadAtMs > 0;
        const hasShippingUpload = shippingUploadAtMs > 0;
        const listernautUploadStatus = getListernautUploadStatusMeta({
          listernautUploadAtMs,
          listernautUploadId,
          activeLotsUploadId,
          listernautRows,
          listernautMatchedLots,
          listernautUpdatedLots,
          lotsUploadAtMs,
          hasLotsUpload,
        });
        const hasListernautUpload = !!listernautUploadStatus.hasUpload;
        const isEnded = !!endMs && nowMs >= endMs;
        const isRunning = !endMs || nowMs < endMs;
        const bothAfterEnd = !!(isEnded && hasLotsUpload && hasShippingUpload && lotsUploadAtMs > endMs && shippingUploadAtMs > endMs);
        const needsLatestAfterEnd = !!(isEnded && !bothAfterEnd);
        const statusTone = isRunning ? (hasLotsUpload && hasShippingUpload ? "warning" : "error") : bothAfterEnd ? "success" : "error";
        const statusLabel =
          statusTone === "success"
            ? "Green: Ready"
            : statusTone === "warning"
              ? "Yellow: Running, both uploaded"
              : isRunning
                ? "Red: Running, pending files"
                : "Red: Ended, upload latest files";
        const consignor = uniqSortedOptions([...(Array.isArray(row?.consignors) ? row.consignors : []), row?.consignor]).join(", ");
        const location = String(row?.location || "").trim();
        const auctionAccountManagerIds = getAuctionAccountManagerIds(row, accountManagers);
        const storedAuctionAccountManagerRows = Array.isArray(row?.auctionAccountManagers) ? row.auctionAccountManagers : [];
        const auctionAccountManagerNames = uniqSortedOptions([
          ...(Array.isArray(row?.accountManagerNames) ? row.accountManagerNames : []),
          ...storedAuctionAccountManagerRows.map((manager) => manager?.fullName || manager?.email || manager?.id || ""),
          ...auctionAccountManagerIds.map((id) => {
            const manager = auctionAccountManagerById.get(id);
            return manager ? accountManagerDisplayName(manager) : id;
          }),
        ]);
        const pickupLocations = getAuctionPickupLocationsFromAuction(row);
        const pickupSlots = sanitizeAuctionPickupSlots(row?.pickupSlots || [], { fallbackLocations: pickupLocations });
        const metrics = computeAuctionAnalytics(row);
        const fulfillmentStats = normalizeAuctionRegistrationFulfillmentStats(row);
        const fulfillmentShippingCount = toAbsInt(fulfillmentStats?.shippingRequestedCount ?? 0);
        const fulfillmentPickupCount = toAbsInt(fulfillmentStats?.pickupCount ?? 0);
        const fulfillmentNewUsersCount = toAbsInt(fulfillmentStats?.newUsersCount ?? 0);
        const fulfillmentNewShippingCount = toAbsInt(fulfillmentStats?.newUsersShippingRequestedCount ?? 0);
        const fulfillmentShippingPct = safeNum(fulfillmentStats?.shippingRequestedPct ?? 0);
        const fulfillmentPickupPct = safeNum(fulfillmentStats?.pickupPct ?? 0);
        const fulfillmentNewUsersPct = safeNum(fulfillmentStats?.newUsersPct ?? 0);
        const fulfillmentNewShippingPctOfNew = safeNum(fulfillmentStats?.newUsersShippingRequestedPct ?? 0);
        const fulfillmentNewShareOfShippersPct =
          fulfillmentShippingCount > 0 ? (fulfillmentNewShippingCount / fulfillmentShippingCount) * 100 : 0;
        const fulfillmentNewShippingPctOfTotalRegs =
          toAbsInt(metrics.registrations) > 0 ? (fulfillmentNewShippingCount / toAbsInt(metrics.registrations)) * 100 : 0;
        const afGain = computeAuctionAfGain({
          totalHighBid: metrics.totalHighBid,
          lotsWon: metrics.lotsWon,
          sellerShareRows: row?.sellerShareRows,
          consignorRows: row?.listernautConsignorRows || row?.consignorRows,
        });
        const totalLots = toAbsInt(metrics.totalLots);
        const sellerCount = toAbsInt(row?.sellerCount || 0);
        const listerCount = toAbsInt(row?.listerCount || 0);
        const topSellerName = String(row?.topSellerName || "").trim();
        const topListerName = String(row?.topListerName || "").trim();
        const topSellerLotsCount = toAbsInt(row?.topSellerLotsCount || 0);
        const topListerLotsCount = toAbsInt(row?.topListerLotsCount || 0);
        const topSellerHammerTotal = safeNum(row?.topSellerHammerTotal || 0);
        const topListerHammerTotal = safeNum(row?.topListerHammerTotal || 0);
        const sellerShareRows = Array.isArray(row?.sellerShareRows) ? row.sellerShareRows : [];
        const listerShareRows = Array.isArray(row?.listerShareRows) ? row.listerShareRows : [];
        return {
          auctionId,
          auctionName,
          endMs,
          updatedMs,
          endLabel: endMs ? new Date(endMs).toLocaleString() : "—",
          consignor: consignor || "—",
          location: location || "—",
          auctionAccountManagerIds,
          auctionAccountManagerNames,
          pickupLocations,
          pickupSlots,
          totalRegistrations: metrics.registrations,
          totalUniqueBidders: metrics.uniqueBiddersWon,
          totalViews: metrics.totalViews,
          totalWatches: metrics.totalWatches,
          totalBids: metrics.totalBidsPlaced,
          avgBidsPerWonLot: safeNum(metrics.avgBidsPerWonLot || 0),
          totalHighValue: metrics.totalHighBid,
          sellerCount,
          listerCount,
          topSellerName,
          topSellerLotsCount,
          topSellerHammerTotal,
          topListerName,
          topListerLotsCount,
          topListerHammerTotal,
          sellerShareRows,
          listerShareRows,
          fulfillmentStats,
          fulfillmentShippingCount,
          fulfillmentShippingPct,
          fulfillmentPickupCount,
          fulfillmentPickupPct,
          fulfillmentNewUsersCount,
          fulfillmentNewUsersPct,
          fulfillmentNewShippingCount,
          fulfillmentNewShippingPctOfNew,
          fulfillmentNewShareOfShippersPct,
          fulfillmentNewShippingPctOfTotalRegs,
          totalLots: metrics.totalLots,
          totalLotsWon: metrics.lotsWon,
          totalLotsPassed: metrics.lotsOnFloor,
          boughtLotsPct: metrics.sellThroughPct,
          passedLotsPct: metrics.floorSharePct,
          afGain,
          afGainPerLot: totalLots > 0 ? afGain / totalLots : 0,
          registrationsUploadAtMs,
          lotsUploadAtMs,
          shippingUploadAtMs,
          listernautUploadAtMs,
          listernautRows,
          listernautMatchedLots,
          listernautUpdatedLots,
          hasRegistrationsUpload,
          hasLotsUpload,
          hasShippingUpload,
          hasListernautUpload,
          listernautUploadNeedsRefresh: !!listernautUploadStatus.needsRefresh,
          listernautUploadStatusLabel: listernautUploadStatus.label,
          listernautUploadStatusColor: listernautUploadStatus.color,
          listernautUploadStatusBg: listernautUploadStatus.background,
          listernautUploadStatusNote: listernautUploadStatus.note,
          isRunning,
          isEnded,
          bothAfterEnd,
          needsLatestAfterEnd,
          statusTone,
          statusLabel,
          row,
        };
      })
      .sort((a, b) => (b.endMs || b.updatedMs || 0) - (a.endMs || a.updatedMs || 0));
  }, [auctions, accountManagers, auctionAccountManagerById]);
  const selectedAuctionViewRow = useMemo(() => {
    const auctionId = String(selectedAuctionId || "").trim();
    if (!auctionId) return null;
    return (
      (Array.isArray(auctionHibidViewRows) ? auctionHibidViewRows : []).find(
        (row) => String(row?.auctionId || "").trim() === auctionId
      ) || null
    );
  }, [auctionHibidViewRows, selectedAuctionId]);
  const auctionHibidViewRowById = useMemo(() => {
    const map = new Map();
    (Array.isArray(auctionHibidViewRows) ? auctionHibidViewRows : []).forEach((row) => {
      const auctionId = String(row?.auctionId || row?.id || "").trim();
      if (!auctionId || map.has(auctionId)) return;
      map.set(auctionId, row);
    });
    return map;
  }, [auctionHibidViewRows]);
  const selectedAuctionSavedSettings = useMemo(() => {
    const source = auctionSummary || selectedAuction || {};
    const endMs = safeToMillis(source?.auctionEndAtMs ?? source?.auctionEndAt);
    const consignors = getConsignorsFromAuction(source);
    const location = String(source?.location || "").trim();
    const accountManagerIds = getAuctionAccountManagerIds(source, accountManagers);
    const pickupLocations = getAuctionPickupLocationsFromAuction(source);
    const pickupSlots = sanitizeAuctionPickupSlots(source?.pickupSlots || [], { fallbackLocations: pickupLocations }).map((slot) => ({
      slotId: String(slot?.slotId || "").trim(),
      location: String(slot?.location || "").trim(),
      startAtMs: safeToMillis(slot?.startAtMs),
      endAtMs: safeToMillis(slot?.endAtMs),
    }));
    return {
      endMs: endMs && Number.isFinite(Number(endMs)) ? Math.floor(Number(endMs)) : null,
      consignors,
      location,
      accountManagerIds,
      pickupLocations,
      pickupSlots,
    };
  }, [selectedAuction, auctionSummary, accountManagers]);
  const selectedAuctionDraftSettings = useMemo(() => {
    const endMs = fromInputDateTimeLocalToMs(auctionEndAtLocal);
    const consignors = uniqSortedOptions(auctionConsignors || []);
    const location = String(auctionLocation || "").trim();
    const accountManagerIds = sanitizeAuctionAccountManagerIds(auctionAccountManagerIds, accountManagers);
    const pickupLocations = sanitizeAuctionPickupLocations(auctionPickupLocations);
    const pickupSlots = buildAuctionPickupSlotPayload(auctionPickupSlots, pickupLocations);
    return {
      endMs: endMs && Number.isFinite(Number(endMs)) ? Math.floor(Number(endMs)) : null,
      consignors,
      location,
      accountManagerIds,
      pickupLocations,
      pickupSlots,
    };
  }, [auctionEndAtLocal, auctionConsignors, auctionLocation, auctionAccountManagerIds, auctionPickupLocations, auctionPickupSlots, accountManagers]);
  const selectedAuctionSavedSettingsSig = useMemo(
    () => JSON.stringify(selectedAuctionSavedSettings),
    [selectedAuctionSavedSettings]
  );
  const selectedAuctionDraftSettingsSig = useMemo(
    () => JSON.stringify(selectedAuctionDraftSettings),
    [selectedAuctionDraftSettings]
  );
  const auctionLocationFilterOptions = useMemo(
    () =>
      uniqSortedOptions(
        (Array.isArray(auctionHibidViewRows) ? auctionHibidViewRows : [])
          .map((row) => String(row?.location || "").trim())
          .filter(Boolean)
      ),
    [auctionHibidViewRows]
  );
  const locationScopedAuctionHibidViewRows = useMemo(() => {
    const rows = Array.isArray(auctionHibidViewRows) ? auctionHibidViewRows : [];
    const locationFilter = String(auctionLocationFilter || "all").trim();
    if (!locationFilter || locationFilter === "all") return rows;
    const target = normalize(locationFilter);
    return rows.filter((row) => normalize(String(row?.location || "").trim()) === target);
  }, [auctionHibidViewRows, auctionLocationFilter]);
  const filteredAuctionHibidViewRows = useMemo(() => {
    const q = normalize(auctionHibidSearch);
    if (!q) return locationScopedAuctionHibidViewRows;
    return locationScopedAuctionHibidViewRows.filter((row) =>
      normalize([row.auctionName, row.auctionId, row.consignor, row.location, row.endLabel].filter(Boolean).join(" | ")).includes(q)
    );
  }, [locationScopedAuctionHibidViewRows, auctionHibidSearch]);
  const auctionHibidTrendByRowKey = useMemo(() => {
    const rows = Array.isArray(locationScopedAuctionHibidViewRows) ? locationScopedAuctionHibidViewRows : [];
    const sums = {};
    AUCTION_TABLE_TREND_METRICS.forEach((metric) => {
      sums[metric.key] = 0;
    });
    rows.forEach((row) => {
      AUCTION_TABLE_TREND_METRICS.forEach((metric) => {
        sums[metric.key] += safeNum(row?.[metric.key] || 0);
      });
    });
    const out = {};
    rows.forEach((row, idx) => {
      const rowKey = String(row?.auctionId || row?.auctionName || idx);
      const previous = rows[idx + 1] || null;
      const peerCount = rows.length - 1;
      const trend = {};
      AUCTION_TABLE_TREND_METRICS.forEach((metric) => {
        const current = safeNum(row?.[metric.key] || 0);
        trend[metric.key] = {
          previous: previous ? safeNum(previous?.[metric.key] || 0) : null,
          avg: peerCount > 0 ? (sums[metric.key] - current) / peerCount : null,
        };
      });
      out[rowKey] = trend;
    });
    return out;
  }, [locationScopedAuctionHibidViewRows]);
  const sortedFilteredAuctionHibidViewRows = useMemo(() => {
    const base = Array.isArray(filteredAuctionHibidViewRows) ? filteredAuctionHibidViewRows : [];
    const dir = auctionTableSortDir === "asc" ? 1 : -1;
    const getVal = (row) => {
      switch (auctionTableSortKey) {
        case "auctionName":
          return normalize(row?.auctionName);
        case "status":
          return normalize(row?.statusLabel);
        case "consignor":
          return normalize(row?.consignor);
        case "location":
          return normalize(row?.location);
        case "totalRegistrations":
          return toAbsInt(row?.totalRegistrations || 0);
        case "totalLots":
          return toAbsInt(row?.totalLots || 0);
        case "totalLotsWon":
          return toAbsInt(row?.totalLotsWon || 0);
        case "totalViews":
          return toAbsInt(row?.totalViews || 0);
        case "totalUniqueBidders":
          return toAbsInt(row?.totalUniqueBidders || 0);
        case "totalBids":
          return toAbsInt(row?.totalBids || 0);
        case "totalHighValue":
          return safeNum(row?.totalHighValue || 0);
        case "fulfillmentShipping":
          return safeNum(row?.fulfillmentStats?.shippingRequestedPct || 0);
        case "files":
          return Math.max(toAbsInt(row?.shippingUploadAtMs || 0), toAbsInt(row?.lotsUploadAtMs || 0));
        case "endMs":
        default:
          return toAbsInt(row?.endMs || row?.updatedMs || 0);
      }
    };
    return [...base].sort((a, b) => compareSortValues(getVal(a), getVal(b)) * dir);
  }, [filteredAuctionHibidViewRows, auctionTableSortKey, auctionTableSortDir]);
  const renderTrendDeltaPair = (currentValue, previousValue, avgValue, opts = {}) => {
    const renderTrendBadge = (label, baseline) => {
      const trend = getTrendDeltaDisplay(currentValue, baseline, opts);
      const bg =
        trend.direction === "up"
          ? "rgba(22,163,74,0.14)"
          : trend.direction === "down"
            ? "rgba(220,38,38,0.14)"
            : "rgba(100,116,139,0.14)";
      const border =
        trend.direction === "up"
          ? "1px solid rgba(22,163,74,0.32)"
          : trend.direction === "down"
            ? "1px solid rgba(220,38,38,0.32)"
            : "1px solid rgba(100,116,139,0.32)";
      return (
        <Box
          sx={{
            px: 0.65,
            py: 0.2,
            borderRadius: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.3,
            color: trend.color,
            background: bg,
            border,
            whiteSpace: "nowrap",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 900, color: trend.color, lineHeight: 1.15 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 900, color: trend.color, lineHeight: 1.15 }}>
            {trend.text}
          </Typography>
        </Box>
      );
    };
    return (
      <Box sx={{ mt: 0.2, display: "inline-flex", alignItems: "center", gap: 0.45, flexWrap: "nowrap", whiteSpace: "nowrap", minHeight: 18 }}>
        {renderTrendBadge("P", previousValue)}
        {renderTrendBadge("A", avgValue)}
      </Box>
    );
  };
  const renderTrendDeltaPairFromTrend = (previousTrend, avgTrend) => {
    const renderTrendBadge = (label, trendObj) => {
      const delta = trendObj?.delta;
      const hasBaseline = delta != null;
      const direction = !hasBaseline ? "flat" : delta > 0 ? "up" : delta < 0 ? "down" : "flat";
      const color = direction === "up" ? "#15803d" : direction === "down" ? "#b91c1c" : "#64748b";
      const bg =
        direction === "up"
          ? "rgba(22,163,74,0.14)"
          : direction === "down"
            ? "rgba(220,38,38,0.14)"
            : "rgba(100,116,139,0.14)";
      const border =
        direction === "up"
          ? "1px solid rgba(22,163,74,0.32)"
          : direction === "down"
            ? "1px solid rgba(220,38,38,0.32)"
            : "1px solid rgba(100,116,139,0.32)";
      return (
        <Box
          sx={{
            px: 0.65,
            py: 0.2,
            borderRadius: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.3,
            color,
            background: bg,
            border,
            whiteSpace: "nowrap",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 900, color, lineHeight: 1.15 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 900, color, lineHeight: 1.15 }}>
            {hasBaseline ? trendObj?.deltaText || "0" : "—"}
          </Typography>
        </Box>
      );
    };
    return (
      <Box sx={{ mt: 0.2, display: "inline-flex", alignItems: "center", gap: 0.45, flexWrap: "nowrap", whiteSpace: "nowrap", minHeight: 18 }}>
        {renderTrendBadge("P", previousTrend)}
        {renderTrendBadge("A", avgTrend)}
      </Box>
    );
  };

  const auctionSummarySections = useMemo(() => {
    const a = auctionAnalytics || {};
    const nu = auctionNewUsersStats || {};
    const fmtInt = (n) => formatNumber2(toAbsInt(n));
    const fmtMoney = (n) => (n == null ? "—" : `$${moneyPlain(n)}`);
    const fmtNum = (n) => (n == null ? "—" : formatNumber2(safeNum(n)));
    const fmtRatio = (n) => (n == null ? "—" : formatNumber2(safeNum(n)));
    const fmtPct = (n) => (n == null ? "—" : formatPercent2(n));

    return [
      {
        key: "participation",
        title: "Participation",
        subtitle: "How many registered and how many converted to winners.",
        tone: "#2563eb",
        items: [
          { label: "Registrations", value: fmtInt(a.registrations) },
          { label: "Number of settlements", value: fmtInt(a.uniqueBiddersWon) },
          { label: "Reg -> Winner", value: fmtPct(a.regToWinnerPct) },
        ],
      },
      {
        key: "newUsers",
        title: "New Users",
        subtitle: "Participation, hammer share, and lot share from users marked new.",
        tone: "#0f766e",
        items: [
          {
            label: "New Users Participation",
            subValue: fmtPct(nu.newUsersPct),
            value:
              nu.newUsersCount == null || nu.totalUsersCount == null
                ? "—"
                : `${fmtInt(nu.newUsersCount)} / ${fmtInt(nu.totalUsersCount)}`,
          },
          {
            label: "new users total revenue",
            subValue: fmtPct(nu.newUsersHammerSharePct),
            value:
              nu.newUsersHammerTotal == null || nu.totalHammer == null
                ? "—"
                : `${fmtMoney(nu.newUsersHammerTotal)} / ${fmtMoney(nu.totalHammer)}`,
          },
          {
            label: "new user lots purhased",
            value: fmtPct(nu.newUsersLotsSharePct),
            subValue:
              nu.newUsersLotsTotal == null || nu.totalLots == null
                ? "—"
                : `${fmtInt(nu.newUsersLotsTotal)} / ${fmtInt(nu.totalLots)}`,
          },
        ],
      },
      {
        key: "lotMix",
        title: "Lot Mix",
        subtitle: "Sell-through and passed composition across all uploaded lots.",
        tone: "#16a34a",
        items: [
          { label: "Total Lots", value: fmtInt(a.totalLots) },
          { label: "Lots Won", value: fmtInt(a.lotsWon) },
          { label: "Passed Lots", value: fmtInt(a.lotsOnFloor) },
          { label: "Sell-through", value: fmtPct(a.sellThroughPct) },
          { label: "Passed Share", value: fmtPct(a.floorSharePct) },
        ],
      },
      {
        key: "pricing",
        title: "Bid & Price",
        subtitle: "Monetary outcomes and bid intensity for won inventory.",
        tone: "#f97316",
        items: [
          { label: "Total High Bid", value: fmtMoney(a.totalHighBid) },
          { label: "Total Max Bid", value: fmtMoney(a.totalMaxBid) },
          { label: "Total Bids Placed", value: fmtInt(a.totalBidsPlaced) },
          { label: "Avg Hammer / Won Lot", value: fmtMoney(a.avgHammerPerWonLot) },
          { label: "Avg Hammer / Total Lot", value: fmtMoney(a.avgHammerPerTotalLot) },
          { label: "Avg Max / Won Lot", value: fmtMoney(a.avgMaxPerWonLot) },
          { label: "Avg Bids / Won Lot", value: fmtNum(a.avgBidsPerWonLot) },
          { label: "Avg Bids / Registration", value: fmtNum(a.avgBidsPerRegistration) },
          // { label: "Views / Total Hammer Price", value: fmtRatio(a.viewsPerTotalHammerPrice) },
          // { label: "Bids / Total Hammer Price", value: fmtRatio(a.bidsPerTotalHammerPrice) },
          // { label: "Watches / Total Hammer Price", value: fmtRatio(a.watchesPerTotalHammerPrice) },
        ],
      },
      {
        key: "engagement",
        title: "Engagement",
        subtitle: "Views/watches split by bought vs passed plus efficiency rates.",
        tone: "#0891b2",
        items: [
          { label: "Total Views", value: fmtInt(a.totalViews) },
          { label: "Bought Views", value: fmtInt(a.boughtViews) },
          { label: "Passed Views", value: fmtInt(a.floorViews) },
          { label: "Total Watches", value: fmtInt(a.totalWatches) },
          { label: "Bought Watches", value: fmtInt(a.boughtWatches) },
          { label: "Passed Watches", value: fmtInt(a.floorWatches) },
          { label: "Watch / View", value: fmtPct(a.watchToViewPct) },
          { label: "Bought Watch / View", value: fmtPct(a.boughtWatchToViewPct) },
          { label: "Passed Watch / View", value: fmtPct(a.floorWatchToViewPct) },
          { label: "Views / Lot", value: fmtNum(a.viewsPerLot) },
          { label: "Watches / Lot", value: fmtNum(a.watchesPerLot) },
          { label: "Views / Bid", value: fmtNum(a.viewsPerBid) },
        ],
      },
    ];
  }, [auctionAnalytics, auctionNewUsersStats]);
  const auctionEntitySharePanels = useMemo(
    () => [
      {
        key: "sellerShare",
        title: "Seller Share",
        subtitle: "Total, sold, and passed lots plus average hammer per sold lot by seller.",
        tone: "#7c3aed",
        rows: Array.isArray(auctionSummary?.sellerShareRows) ? auctionSummary.sellerShareRows : [],
      },
      {
        key: "listerShare",
        title: "Lister Share",
        subtitle: "Total, sold, and passed lots plus average hammer per sold lot by lister.",
        tone: "#0f766e",
        rows: Array.isArray(auctionSummary?.listerShareRows) ? auctionSummary.listerShareRows : [],
      },
    ],
    [auctionSummary]
  );
  const auctionSummarySectionsByKey = useMemo(() => {
    const map = {};
    (Array.isArray(auctionSummarySections) ? auctionSummarySections : []).forEach((section) => {
      if (section?.key) map[section.key] = section;
    });
    return map;
  }, [auctionSummarySections]);
  const auctionParticipationSections = useMemo(
    () =>
      ["participation", "newUsers"]
        .map((key) => auctionSummarySectionsByKey[key])
        .filter(Boolean),
    [auctionSummarySectionsByKey]
  );
  const auctionLotStatsSections = useMemo(
    () =>
      ["lotMix", "pricing", "engagement"]
        .map((key) => auctionSummarySectionsByKey[key])
        .filter(Boolean),
    [auctionSummarySectionsByKey]
  );
  const auctionKpiCards = useMemo(() => {
    const a = auctionAnalytics || {};
    const fmtInt = (n) => formatNumber2(toAbsInt(n));
    const fmtMoney = (n) => (n == null ? "—" : `$${moneyPlain(n)}`);
    const fmtPct = (n) => (n == null ? "—" : formatPercent2(n));
    return [
      {
        key: "hammer",
        label: "Gross High Bid",
        value: fmtMoney(a.totalHighBid),
        helper: `Avg Won Lot ${fmtMoney(a.avgHammerPerWonLot)}`,
        accent: "#0f766e",
        bg: "linear-gradient(135deg,rgba(16,185,129,0.16),rgba(6,182,212,0.10))",
      },
      {
        key: "sellthrough",
        label: "Sell-through",
        value: fmtPct(a.sellThroughPct),
        helper: `${fmtInt(a.lotsWon)} won of ${fmtInt(a.totalLots)} lots`,
        accent: "#2563eb",
        bg: "linear-gradient(135deg,rgba(37,99,235,0.16),rgba(14,165,233,0.10))",
      },
      {
        key: "registrations",
        label: "Registrations",
        value: fmtInt(a.registrations),
        helper: `Winner conversion ${fmtPct(a.regToWinnerPct)}`,
        accent: "#7c3aed",
        bg: "linear-gradient(135deg,rgba(124,58,237,0.16),rgba(168,85,247,0.10))",
      },
      {
        key: "engagement",
        label: "Watch / View",
        value: fmtPct(a.watchToViewPct),
        helper: `${fmtInt(a.totalWatches)} watches from ${fmtInt(a.totalViews)} views`,
        accent: "#b45309",
        bg: "linear-gradient(135deg,rgba(249,115,22,0.18),rgba(251,191,36,0.12))",
      },
      {
        key: "bids",
        label: "Total Bids",
        value: fmtInt(a.totalBidsPlaced),
        helper: `Avg bids per won lot ${safeNum(a.avgBidsPerWonLot || 0).toFixed(2)}`,
        accent: "#be123c",
        bg: "linear-gradient(135deg,rgba(244,63,94,0.18),rgba(251,113,133,0.10))",
      },
      {
        key: "velocity",
        label: "Views / Lot",
        value: safeNum(a.viewsPerLot || 0).toFixed(2),
        helper: `Watches / lot ${safeNum(a.watchesPerLot || 0).toFixed(2)}`,
        accent: "#0369a1",
        bg: "linear-gradient(135deg,rgba(14,165,233,0.16),rgba(2,132,199,0.10))",
      },
    ];
  }, [auctionAnalytics]);
  const overallKpiCards = useMemo(() => {
    const a = overallAuctionAnalytics || {};
    const fmtInt = (n) => formatNumber2(toAbsInt(n));
    const fmtMoney = (n) => (n == null ? "—" : `$${moneyPlain(n)}`);
    const fmtPct = (n) => (n == null ? "—" : formatPercent2(n));
    return [
      {
        key: "auctions",
        label: "Auctions Loaded",
        value: fmtInt(auctions.length),
        helper: `${fmtInt(auctionsWithLotsCount)} with lot stats`,
        accent: "#1d4ed8",
        bg: "linear-gradient(135deg,rgba(37,99,235,0.16),rgba(59,130,246,0.10))",
      },
      {
        key: "regs",
        label: "Total Registrations",
        value: fmtInt(a.registrations),
        helper: `${fmtInt(auctionsWithRegistrationsCount)} auctions with registrations`,
        accent: "#7c3aed",
        bg: "linear-gradient(135deg,rgba(124,58,237,0.16),rgba(147,51,234,0.10))",
      },
      {
        key: "hammer",
        label: "Combined High Bid",
        value: fmtMoney(a.totalHighBid),
        helper: `Combined max bid ${fmtMoney(a.totalMaxBid)}`,
        accent: "#0f766e",
        bg: "linear-gradient(135deg,rgba(16,185,129,0.16),rgba(6,182,212,0.10))",
      },
      {
        key: "avgRevenuePerLot",
        label: "Avg Revenue Per Lot",
        value: fmtMoney(a.avgHammerPerTotalLot),
        helper: `${fmtMoney(a.totalHighBid)} over ${fmtInt(a.totalLots)} lots`,
        accent: "#0369a1",
        bg: "linear-gradient(135deg,rgba(14,165,233,0.16),rgba(37,99,235,0.10))",
      },
      {
        key: "sellthrough",
        label: "Combined Sell-through",
        value: fmtPct(a.sellThroughPct),
        helper: `${fmtInt(a.lotsWon)} won of ${fmtInt(a.totalLots)} lots`,
        accent: "#b45309",
        bg: "linear-gradient(135deg,rgba(249,115,22,0.18),rgba(251,191,36,0.12))",
      },
    ];
  }, [overallAuctionAnalytics, auctions, auctionsWithLotsCount, auctionsWithRegistrationsCount]);
  const auctionChartData = useMemo(() => {
    const a = auctionAnalytics || {};
    return {
      sellThrough: [
        { name: "Won", value: toAbsInt(a.lotsWon), color: "#16a34a" },
        { name: "Passed", value: toAbsInt(a.lotsOnFloor), color: "#f97316" },
      ],
      funnel: [
        { stage: "Registrations", value: toAbsInt(a.registrations) },
        { stage: "Bidders", value: toAbsInt(a.uniqueBiddersAll) },
        { stage: "Winners", value: toAbsInt(a.uniqueBiddersWon) },
      ],
      engagement: [
        {
          metric: "Views",
          bought: toAbsInt(a.boughtViews),
          floor: toAbsInt(a.floorViews),
          total: toAbsInt(a.totalViews),
        },
        {
          metric: "Watches",
          bought: toAbsInt(a.boughtWatches),
          floor: toAbsInt(a.floorWatches),
          total: toAbsInt(a.totalWatches),
        },
        {
          metric: "Bids",
          bought: toAbsInt(a.boughtBidsPlaced),
          floor: toAbsInt(a.floorBidsPlaced),
          total: toAbsInt(a.totalBidsPlaced),
        },
      ],
    };
  }, [auctionAnalytics]);
  const auctionRegTrendViewByOptions = useMemo(
    () => [
      { value: "day", label: "Day" },
      { value: "hour", label: "Hours" },
    ],
    []
  );
  const auctionRegTrendDateModeOptions = useMemo(
    () => [
      { value: "single", label: "Specific Date" },
      { value: "range", label: "Date Range" },
    ],
    []
  );
  const auctionRegTrendAvailableRange = useMemo(() => {
    const rows = Array.isArray(auctionRegs) ? auctionRegs : [];
    const dateKeys = new Set();
    let minDateKey = "";
    let maxDateKey = "";

    rows.forEach((row) => {
      const registeredMs = safeToMillis(row?.registeredAt || row?.emailSentAt || row?.createdAt);
      if (!Number.isFinite(registeredMs)) return;
      const dateKey = utcMsToDateKey(registeredMs);
      if (!isDateKeyUTC(dateKey)) return;
      dateKeys.add(dateKey);
      if (!minDateKey || dateKey < minDateKey) minDateKey = dateKey;
      if (!maxDateKey || dateKey > maxDateKey) maxDateKey = dateKey;
    });

    return {
      hasData: Boolean(minDateKey && maxDateKey),
      minDateKey,
      maxDateKey,
      totalDays: dateKeys.size,
    };
  }, [auctionRegs]);
  useEffect(() => {
    if (!auctionRegTrendAvailableRange.hasData) {
      setAuctionRegTrendStartDate("");
      setAuctionRegTrendEndDate("");
      return;
    }
    const minDateKey = String(auctionRegTrendAvailableRange.minDateKey || "").trim();
    const maxDateKey = String(auctionRegTrendAvailableRange.maxDateKey || "").trim();
    setAuctionRegTrendStartDate((prev) => clampDateKeyToRangeUTC(prev, minDateKey, maxDateKey) || minDateKey);
    setAuctionRegTrendEndDate((prev) => clampDateKeyToRangeUTC(prev, minDateKey, maxDateKey) || maxDateKey);
  }, [
    auctionRegTrendAvailableRange.hasData,
    auctionRegTrendAvailableRange.minDateKey,
    auctionRegTrendAvailableRange.maxDateKey,
  ]);
  const auctionRegTrendResolvedRange = useMemo(() => {
    const minDateKey = String(auctionRegTrendAvailableRange.minDateKey || "").trim();
    const maxDateKey = String(auctionRegTrendAvailableRange.maxDateKey || "").trim();
    if (!auctionRegTrendAvailableRange.hasData || !isDateKeyUTC(minDateKey) || !isDateKeyUTC(maxDateKey)) {
      return {
        hasData: false,
        mode: "range",
        startDate: "",
        endDate: "",
        minDateKey: "",
        maxDateKey: "",
        label: "No Dates",
      };
    }

    const clampedStart = clampDateKeyToRangeUTC(auctionRegTrendStartDate, minDateKey, maxDateKey) || minDateKey;
    const clampedEnd = clampDateKeyToRangeUTC(auctionRegTrendEndDate, minDateKey, maxDateKey) || maxDateKey;

    if (auctionRegTrendDateMode === "single") {
      const singleDate = clampedStart || clampedEnd || minDateKey;
      return {
        hasData: true,
        mode: "single",
        startDate: singleDate,
        endDate: singleDate,
        minDateKey,
        maxDateKey,
        label: formatDateKeyShortUTC(singleDate),
      };
    }

    let startDate = clampedStart;
    let endDate = clampedEnd;
    if (startDate > endDate) {
      const tmp = startDate;
      startDate = endDate;
      endDate = tmp;
    }
    return {
      hasData: true,
      mode: "range",
      startDate,
      endDate,
      minDateKey,
      maxDateKey,
      label: formatDateKeyRangeShortUTC(startDate, endDate) || `${startDate} - ${endDate}`,
    };
  }, [
    auctionRegTrendAvailableRange.hasData,
    auctionRegTrendAvailableRange.minDateKey,
    auctionRegTrendAvailableRange.maxDateKey,
    auctionRegTrendDateMode,
    auctionRegTrendStartDate,
    auctionRegTrendEndDate,
  ]);
  const auctionRegistrationTrendData = useMemo(() => {
    if (!auctionRegTrendResolvedRange.hasData) return [];
    const rows = Array.isArray(auctionRegs) ? auctionRegs : [];
    const byBucket = new Map();
    const isHourView = auctionRegTrendViewBy === "hour";
    const rangeIsSingleDay = auctionRegTrendResolvedRange.startDate === auctionRegTrendResolvedRange.endDate;

    rows.forEach((row) => {
      const registeredMs = safeToMillis(row?.registeredAt || row?.emailSentAt || row?.createdAt);
      if (!Number.isFinite(registeredMs)) return;
      const dateKey = utcMsToDateKey(registeredMs);
      if (!isDateKeyUTC(dateKey)) return;
      if (dateKey < auctionRegTrendResolvedRange.startDate || dateKey > auctionRegTrendResolvedRange.endDate) return;

      const emailLower = normalize(row?.bidderEmail || row?.email);
      const lotsCount = toAbsInt(row?.lotsCount ?? lotsAgg[emailLower]?.lotsCount ?? 0);
      const shippingRequested = isShippingRequested(row);

      if (isHourView) {
        const zonedParts = getDatePartsInTimeZone(registeredMs, REGISTRATION_TREND_TIME_ZONE);
        if (!zonedParts) return;
        const hour = Number(zonedParts.hour);
        const localDateKey = dateKeyInTimeZoneFromMs(registeredMs, REGISTRATION_TREND_TIME_ZONE);
        const hourStartMs = Date.UTC(
          Number(zonedParts.year),
          Math.max(0, Number(zonedParts.month) - 1),
          Number(zonedParts.day),
          hour,
          0,
          0,
          0
        );
        if (!Number.isFinite(hourStartMs)) return;
        const hourLabel = formatHourLabelEastern(hour);
        const bucketKey = `${localDateKey}T${String(hour).padStart(2, "0")}:00`;
        const existing = byBucket.get(bucketKey) || {
          dateKey: localDateKey || dateKey,
          xLabel: rangeIsSingleDay ? hourLabel : `${formatDateKeyShortUTC(localDateKey || dateKey)} ${hourLabel}`,
          bucketLabel: `${formatDateKeyShortUTC(localDateKey || dateKey)} ${hourLabel} ET`,
          sortMs: hourStartMs,
          registrations: 0,
          shippingRequestedCount: 0,
          pickupCount: 0,
          winners: 0,
        };
        existing.registrations += 1;
        if (shippingRequested) existing.shippingRequestedCount += 1;
        else existing.pickupCount += 1;
        if (lotsCount > 0) existing.winners += 1;
        byBucket.set(bucketKey, existing);
        return;
      }

      const sortMs = dateKeyToUtcMs(dateKey);
      if (!Number.isFinite(sortMs)) return;
      const existing = byBucket.get(dateKey) || {
        dateKey,
        xLabel: formatDateKeyShortUTC(dateKey),
        bucketLabel: dateKey,
        sortMs,
        registrations: 0,
        shippingRequestedCount: 0,
        pickupCount: 0,
        winners: 0,
      };
      existing.registrations += 1;
      if (shippingRequested) existing.shippingRequestedCount += 1;
      else existing.pickupCount += 1;
      if (lotsCount > 0) existing.winners += 1;
      byBucket.set(dateKey, existing);
    });

    return Array.from(byBucket.values())
      .sort((a, b) => safeNum(a?.sortMs) - safeNum(b?.sortMs))
      .map((row) => ({
        ...row,
        conversionPct: row.registrations > 0 ? (row.winners / row.registrations) * 100 : 0,
      }));
  }, [
    auctionRegs,
    lotsAgg,
    auctionRegTrendViewBy,
    auctionRegTrendResolvedRange.hasData,
    auctionRegTrendResolvedRange.startDate,
    auctionRegTrendResolvedRange.endDate,
  ]);
  const auctionRegistrationTrendTotalInRange = useMemo(
    () => auctionRegistrationTrendData.reduce((sum, row) => sum + toAbsInt(row?.registrations || 0), 0),
    [auctionRegistrationTrendData]
  );
  const auctionRegistrationTrendHasData = useMemo(
    () => auctionRegistrationTrendData.some((row) => toAbsInt(row?.registrations || 0) > 0),
    [auctionRegistrationTrendData]
  );
  const overallChartData = useMemo(() => {
    const a = overallAuctionAnalytics || {};
    return {
      sellThrough: [
        { name: "Won", value: toAbsInt(a.lotsWon), color: "#16a34a" },
        { name: "Passed", value: toAbsInt(a.lotsOnFloor), color: "#f97316" },
      ],
      funnel: [
        { stage: "Registrations", value: toAbsInt(a.registrations) },
        { stage: "Bidders", value: toAbsInt(a.uniqueBiddersAll) },
        { stage: "Winners", value: toAbsInt(a.uniqueBiddersWon) },
      ],
      engagement: [
        {
          metric: "Views",
          bought: toAbsInt(a.boughtViews),
          floor: toAbsInt(a.floorViews),
          total: toAbsInt(a.totalViews),
        },
        {
          metric: "Watches",
          bought: toAbsInt(a.boughtWatches),
          floor: toAbsInt(a.floorWatches),
          total: toAbsInt(a.totalWatches),
        },
        {
          metric: "Bids",
          bought: toAbsInt(a.boughtBidsPlaced),
          floor: toAbsInt(a.floorBidsPlaced),
          total: toAbsInt(a.totalBidsPlaced),
        },
      ],
    };
  }, [overallAuctionAnalytics]);
  const orgHeaders = useMemo(() => {
    if (!orgReady) return {};
    const h = {};
    if (userId) h["x-user-id"] = userId;
    if (subscriptionId) h["x-subscription-id"] = subscriptionId;
    return h;
  }, [userId, subscriptionId, orgReady]);
  const locationMetadataByName = useMemo(() => {
    const map = new Map();
    (Array.isArray(locationMetadataRows) ? locationMetadataRows : []).forEach((row) => {
      const name = String(row?.name || "").trim();
      const key = normalize(name);
      if (!key || map.has(key)) return;
      map.set(key, {
        id: String(row?.id || "").trim(),
        name,
        address: String(row?.address || "").trim(),
      });
    });
    return map;
  }, [locationMetadataRows]);
  const getLocationAddress = useCallback(
    (name = "") => String(locationMetadataByName.get(normalize(name))?.address || "").trim(),
    [locationMetadataByName]
  );
  const overallAuctionTableRows = useMemo(
    () => {
      const nowMs = Date.now();
      return (Array.isArray(auctionHibidViewRows) ? auctionHibidViewRows : []).map((row) => {
        const pickupLocations = uniqSortedOptions(
          (Array.isArray(row?.pickupLocations) ? row.pickupLocations : []).map((name) => {
            const safeName = String(name || "").trim();
            if (!safeName) return "";
            return String(locationMetadataByName.get(normalize(safeName))?.name || safeName).trim();
          })
        );
        const fallbackLocation = String(
          locationMetadataByName.get(normalize(row?.location || ""))?.name || row?.location || ""
        ).trim();
        const totalLots = toAbsInt(row?.totalLots || 0);
        const soldLots = toAbsInt(row?.totalLotsWon || 0);
        const salesTotalHammer = safeNum(row?.totalHighValue || 0);
        const totalBids = safeNum(row?.totalBids || 0);
        const auctionEndSortMs = safeNum(row?.endMs || row?.updatedMs || 0);
        const isCompleted = auctionEndSortMs > 0 && auctionEndSortMs <= nowMs;
        const displayLocationCandidates = pickupLocations.length ? pickupLocations : fallbackLocation ? [fallbackLocation] : [];
        const nonDefaultDisplayLocations = displayLocationCandidates.filter((name) => !isDefaultOverallDashboardLocation(name));
        const displayLocation =
          displayLocationCandidates.length > 1 && nonDefaultDisplayLocations.length
            ? nonDefaultDisplayLocations[0]
            : displayLocationCandidates[0] || "—";
        return {
          auctionId: String(row?.auctionId || row?.auctionName || "").trim(),
          sourceAuction: row?.row || row,
          location: formatOverallDashboardLocationShort(displayLocation),
          auctionEndDate: row?.endMs ? new Date(row.endMs).toLocaleDateString() : "—",
          auctionEndSortMs,
          auctionName: String(row?.auctionName || row?.auctionId || "Untitled Auction").trim(),
          status: isCompleted ? "Completed" : "Ongoing",
          statusSortValue: isCompleted ? 1 : 0,
          salesTotalHammer,
          newUsers: toAbsInt(row?.fulfillmentNewUsersCount || 0),
          newShippers: toAbsInt(row?.fulfillmentNewShippingCount || 0),
          totalPickups: toAbsInt(row?.fulfillmentPickupCount || 0),
          totalRegistrations: toAbsInt(row?.totalRegistrations || 0),
          totalShippers: toAbsInt(row?.fulfillmentShippingCount || 0),
          bidsPerLot: totalLots > 0 ? totalBids / totalLots : 0,
          avgHammerPrice: soldLots > 0 ? salesTotalHammer / soldLots : 0,
          avgRevenue: safeNum(row?.afGainPerLot || 0),
          soldLots,
          soldLotsPct: safeNum(row?.boughtLotsPct || 0),
          revenuePerLot: safeNum(row?.afGainPerLot || 0),
          totalLots,
          passedLots: toAbsInt(row?.totalLotsPassed || 0),
          passedPct: safeNum(row?.passedLotsPct || 0),
        };
      });
    },
    [auctionHibidViewRows, locationMetadataByName]
  );
  const filteredOverallAuctionTableRows = useMemo(() => {
    const rows = Array.isArray(overallAuctionTableRows) ? overallAuctionTableRows : [];
    const q = normalize(overallAuctionSearch);
    if (!q) return rows;
    return rows.filter((row) =>
      normalize(
        [
          row?.location,
          row?.auctionEndDate,
          row?.auctionName,
          row?.status,
          row?.salesTotalHammer,
          row?.newUsers,
          row?.newShippers,
          row?.totalPickups,
          row?.totalRegistrations,
          row?.totalShippers,
          row?.bidsPerLot,
          row?.revenuePerLot,
          row?.totalLots,
          row?.passedLots,
          row?.passedPct,
        ].join(" | ")
      ).includes(q)
    );
  }, [overallAuctionSearch, overallAuctionTableRows]);
  const sortedOverallAuctionTableRows = useMemo(() => {
    const base = Array.isArray(filteredOverallAuctionTableRows) ? filteredOverallAuctionTableRows : [];
    const dir = overallAuctionSortDir === "asc" ? 1 : -1;
    const getVal = (row) => {
      switch (overallAuctionSortKey) {
        case "location":
          return normalize(row?.location);
        case "auctionEndDate":
          return safeNum(row?.auctionEndSortMs || 0);
        case "auctionName":
          return normalize(row?.auctionName);
        case "status":
          return safeNum(row?.statusSortValue || 0);
        case "newUsers":
          return toAbsInt(row?.newUsers || 0);
        case "newShippers":
          return toAbsInt(row?.newShippers || 0);
        case "totalPickups":
          return toAbsInt(row?.totalPickups || 0);
        case "totalRegistrations":
          return toAbsInt(row?.totalRegistrations || 0);
        case "totalShippers":
          return toAbsInt(row?.totalShippers || 0);
        case "bidsPerLot":
          return safeNum(row?.bidsPerLot || 0);
        case "revenuePerLot":
          return safeNum(row?.revenuePerLot || 0);
        case "totalLots":
          return toAbsInt(row?.totalLots || 0);
        case "passedLots":
          return toAbsInt(row?.passedLots || 0);
        case "passedPct":
          return safeNum(row?.passedPct || 0);
        case "salesTotalHammer":
        default:
          return safeNum(row?.salesTotalHammer || 0);
      }
    };
    return [...base].sort((a, b) => compareSortValues(getVal(a), getVal(b)) * dir);
  }, [filteredOverallAuctionTableRows, overallAuctionSortDir, overallAuctionSortKey]);
  const overallAuctionCompareOptions = useMemo(
    () =>
      [...(Array.isArray(overallAuctionTableRows) ? overallAuctionTableRows : [])]
        .filter((row) => String(row?.auctionId || "").trim())
        .sort((a, b) => {
          const endDiff = safeNum(b?.auctionEndSortMs || 0) - safeNum(a?.auctionEndSortMs || 0);
          if (endDiff) return endDiff;
          const nameDiff = normalize(a?.auctionName).localeCompare(normalize(b?.auctionName));
          if (nameDiff) return nameDiff;
          return normalize(a?.location).localeCompare(normalize(b?.location));
        })
        .map((row) => ({
          id: String(row?.auctionId || "").trim(),
          label: String(row?.auctionName || "Untitled Auction").trim(),
          subLabel: [String(row?.location || "").trim(), String(row?.auctionEndDate || "").trim()].filter(Boolean).join(" • "),
          row,
        })),
    [overallAuctionTableRows]
  );
  const overallAuctionCompareRowById = useMemo(() => {
    const map = new Map();
    overallAuctionCompareOptions.forEach((option) => {
      if (!option?.id) return;
      map.set(option.id, option.row);
    });
    return map;
  }, [overallAuctionCompareOptions]);
  const selectedCompareAuctionRows = useMemo(
    () =>
      (Array.isArray(compareAuctionIds) ? compareAuctionIds : [])
        .map((id) => overallAuctionCompareRowById.get(String(id || "").trim()))
        .filter(Boolean)
        .slice(0, 5),
    [compareAuctionIds, overallAuctionCompareRowById]
  );
  const compareAuctionRegTrendRows = useMemo(
    () =>
      selectedCompareAuctionRows.map((row) => {
        const auctionId = String(row?.auctionId || row?.id || "").trim();
        const auctionName = String(row?.auctionName || row?.label || "Untitled Auction").trim();
        return {
          auctionId,
          auctionName,
          auctionEndDate: String(row?.auctionEndDate || "").trim(),
          rows: Array.isArray(compareAuctionRegsById?.[auctionId]) ? compareAuctionRegsById[auctionId] : [],
          loading: compareAuctionRegsLoadingById?.[auctionId] === true,
          error: String(compareAuctionRegsErrorById?.[auctionId] || "").trim(),
        };
      }),
    [compareAuctionRegsById, compareAuctionRegsErrorById, compareAuctionRegsLoadingById, selectedCompareAuctionRows]
  );
  const compareAuctionRegTrendAvailableRange = useMemo(() => {
    const dateKeys = new Set();
    let minDateKey = "";
    let maxDateKey = "";

    compareAuctionRegTrendRows.forEach((auction) => {
      (Array.isArray(auction?.rows) ? auction.rows : []).forEach((row) => {
        const registeredMs = safeToMillis(row?.registeredAt || row?.emailSentAt || row?.createdAt);
        if (!Number.isFinite(registeredMs)) return;
        const dateKey = utcMsToDateKey(registeredMs);
        if (!isDateKeyUTC(dateKey)) return;
        dateKeys.add(dateKey);
        if (!minDateKey || dateKey < minDateKey) minDateKey = dateKey;
        if (!maxDateKey || dateKey > maxDateKey) maxDateKey = dateKey;
      });
    });

    return {
      hasData: Boolean(minDateKey && maxDateKey),
      minDateKey,
      maxDateKey,
      totalDays: dateKeys.size,
    };
  }, [compareAuctionRegTrendRows]);
  useEffect(() => {
    if (!compareAuctionRegTrendAvailableRange.hasData) {
      setCompareAuctionRegTrendStartDate("");
      setCompareAuctionRegTrendEndDate("");
      return;
    }
    const minDateKey = String(compareAuctionRegTrendAvailableRange.minDateKey || "").trim();
    const maxDateKey = String(compareAuctionRegTrendAvailableRange.maxDateKey || "").trim();
    setCompareAuctionRegTrendStartDate((prev) => clampDateKeyToRangeUTC(prev, minDateKey, maxDateKey) || minDateKey);
    setCompareAuctionRegTrendEndDate((prev) => clampDateKeyToRangeUTC(prev, minDateKey, maxDateKey) || maxDateKey);
  }, [
    compareAuctionRegTrendAvailableRange.hasData,
    compareAuctionRegTrendAvailableRange.minDateKey,
    compareAuctionRegTrendAvailableRange.maxDateKey,
  ]);
  const compareAuctionRegTrendResolvedRange = useMemo(() => {
    const minDateKey = String(compareAuctionRegTrendAvailableRange.minDateKey || "").trim();
    const maxDateKey = String(compareAuctionRegTrendAvailableRange.maxDateKey || "").trim();
    if (!compareAuctionRegTrendAvailableRange.hasData || !isDateKeyUTC(minDateKey) || !isDateKeyUTC(maxDateKey)) {
      return {
        hasData: false,
        mode: "range",
        startDate: "",
        endDate: "",
        minDateKey: "",
        maxDateKey: "",
        label: "No Dates",
      };
    }

    const clampedStart =
      clampDateKeyToRangeUTC(compareAuctionRegTrendStartDate, minDateKey, maxDateKey) || minDateKey;
    const clampedEnd =
      clampDateKeyToRangeUTC(compareAuctionRegTrendEndDate, minDateKey, maxDateKey) || maxDateKey;

    if (compareAuctionRegTrendDateMode === "single") {
      const singleDate = clampedStart || clampedEnd || minDateKey;
      return {
        hasData: true,
        mode: "single",
        startDate: singleDate,
        endDate: singleDate,
        minDateKey,
        maxDateKey,
        label: formatDateKeyShortUTC(singleDate),
      };
    }

    let startDate = clampedStart;
    let endDate = clampedEnd;
    if (startDate > endDate) {
      const tmp = startDate;
      startDate = endDate;
      endDate = tmp;
    }
    return {
      hasData: true,
      mode: "range",
      startDate,
      endDate,
      minDateKey,
      maxDateKey,
      label: formatDateKeyRangeShortUTC(startDate, endDate) || `${startDate} - ${endDate}`,
    };
  }, [
    compareAuctionRegTrendAvailableRange.hasData,
    compareAuctionRegTrendAvailableRange.maxDateKey,
    compareAuctionRegTrendAvailableRange.minDateKey,
    compareAuctionRegTrendDateMode,
    compareAuctionRegTrendEndDate,
    compareAuctionRegTrendStartDate,
  ]);
  const compareAuctionRegTrendSeries = useMemo(
    () =>
      compareAuctionRegTrendRows.map((auction) => {
        const rows = Array.isArray(auction?.rows) ? auction.rows : [];
        const byBucket = new Map();
        const isHourView = compareAuctionRegTrendViewBy === "hour";
        const rangeIsSingleDay =
          compareAuctionRegTrendResolvedRange.startDate === compareAuctionRegTrendResolvedRange.endDate;

        rows.forEach((row) => {
          const registeredMs = safeToMillis(row?.registeredAt || row?.emailSentAt || row?.createdAt);
          if (!Number.isFinite(registeredMs)) return;
          const dateKey = utcMsToDateKey(registeredMs);
          if (!isDateKeyUTC(dateKey)) return;
          if (
            !compareAuctionRegTrendResolvedRange.hasData ||
            dateKey < compareAuctionRegTrendResolvedRange.startDate ||
            dateKey > compareAuctionRegTrendResolvedRange.endDate
          ) {
            return;
          }

          const lotsCount = toAbsInt(row?.lotsCount || 0);
          const shippingRequested = isShippingRequested(row);

          if (isHourView) {
            const zonedParts = getDatePartsInTimeZone(registeredMs, REGISTRATION_TREND_TIME_ZONE);
            if (!zonedParts) return;
            const hour = Number(zonedParts.hour);
            const localDateKey = dateKeyInTimeZoneFromMs(registeredMs, REGISTRATION_TREND_TIME_ZONE);
            const hourStartMs = Date.UTC(
              Number(zonedParts.year),
              Math.max(0, Number(zonedParts.month) - 1),
              Number(zonedParts.day),
              hour,
              0,
              0,
              0
            );
            if (!Number.isFinite(hourStartMs)) return;
            const hourLabel = formatHourLabelEastern(hour);
            const bucketKey = `${localDateKey}T${String(hour).padStart(2, "0")}:00`;
            const existing = byBucket.get(bucketKey) || {
              dateKey: localDateKey || dateKey,
              xLabel: rangeIsSingleDay ? hourLabel : `${formatDateKeyShortUTC(localDateKey || dateKey)} ${hourLabel}`,
              bucketLabel: `${formatDateKeyShortUTC(localDateKey || dateKey)} ${hourLabel} ET`,
              sortMs: hourStartMs,
              registrations: 0,
              shippingRequestedCount: 0,
              pickupCount: 0,
              winners: 0,
            };
            existing.registrations += 1;
            if (shippingRequested) existing.shippingRequestedCount += 1;
            else existing.pickupCount += 1;
            if (lotsCount > 0) existing.winners += 1;
            byBucket.set(bucketKey, existing);
            return;
          }

          const sortMs = dateKeyToUtcMs(dateKey);
          if (!Number.isFinite(sortMs)) return;
          const existing = byBucket.get(dateKey) || {
            dateKey,
            xLabel: formatDateKeyShortUTC(dateKey),
            bucketLabel: dateKey,
            sortMs,
            registrations: 0,
            shippingRequestedCount: 0,
            pickupCount: 0,
            winners: 0,
          };
          existing.registrations += 1;
          if (shippingRequested) existing.shippingRequestedCount += 1;
          else existing.pickupCount += 1;
          if (lotsCount > 0) existing.winners += 1;
          byBucket.set(dateKey, existing);
        });

        const data = Array.from(byBucket.values())
          .sort((a, b) => safeNum(a?.sortMs) - safeNum(b?.sortMs))
          .map((row) => ({
            ...row,
            conversionPct: row.registrations > 0 ? (row.winners / row.registrations) * 100 : 0,
          }));

        return {
          ...auction,
          data,
          hasData: data.some((item) => toAbsInt(item?.registrations || 0) > 0),
          totalInRange: data.reduce((sum, item) => sum + toAbsInt(item?.registrations || 0), 0),
        };
      }),
    [
      compareAuctionRegTrendResolvedRange.endDate,
      compareAuctionRegTrendResolvedRange.hasData,
      compareAuctionRegTrendResolvedRange.startDate,
      compareAuctionRegTrendRows,
      compareAuctionRegTrendViewBy,
      formatDateKeyRangeShortUTC,
      isShippingRequested,
      safeToMillis,
      toAbsInt,
    ]
  );
  const overallAuctionCompareFields = useMemo(
    () => [
      { key: "location", label: "Location", getValue: (row) => row?.location || "—" },
      { key: "auctionEndDate", label: "Auction End Date", getValue: (row) => row?.auctionEndDate || "—" },
      { key: "status", label: "Status", getValue: (row) => row?.status || "—" },
      { key: "salesTotalHammer", label: "Sales (Total Hammer)", getValue: (row) => `$${moneyPlain(row?.salesTotalHammer || 0)}` },
      { key: "newUsers", label: "New Users", getValue: (row) => formatNumber2(row?.newUsers || 0) },
      { key: "newShippers", label: "New Shippers", getValue: (row) => formatNumber2(row?.newShippers || 0) },
      { key: "totalRegistrations", label: "Total Registrations", getValue: (row) => formatNumber2(row?.totalRegistrations || 0) },
      { key: "totalShippers", label: "Total Shippers", getValue: (row) => formatNumber2(row?.totalShippers || 0) },
      { key: "bidsPerLot", label: "Bids / Lot", getValue: (row) => formatNumber2(row?.bidsPerLot || 0) },
      { key: "avgHammerPrice", label: "Avg Hammer Price", getValue: (row) => `$${moneyPlain(row?.avgHammerPrice || 0)}` },
      { key: "avgRevenue", label: "Avg Revenue", getValue: (row) => `$${moneyPlain(row?.avgRevenue || 0)}` },
      { key: "totalLots", label: "Total Lots", getValue: (row) => formatNumber2(row?.totalLots || 0) },
      { key: "soldLots", label: "Sold Lots", getValue: (row) => formatNumber2(row?.soldLots || 0) },
      { key: "soldLotsPct", label: "Sold Lots %", getValue: (row) => formatPercent2(row?.soldLotsPct || 0) },
      { key: "passedLots", label: "Passed Lots", getValue: (row) => formatNumber2(row?.passedLots || 0) },
      { key: "passedPct", label: "Passed %", getValue: (row) => formatPercent2(row?.passedPct || 0) },
    ],
    []
  );
  const openCompareAuctionsDialog = useCallback(() => {
    setCompareAuctionIds((prev) => {
      const next = (Array.isArray(prev) ? prev : [])
        .map((id) => String(id || "").trim())
        .slice(0, 5);
      return next.length ? next : [""];
    });
    setCompareAuctionsOpen(true);
  }, []);
  const addCompareAuctionSlot = useCallback(() => {
    setCompareAuctionIds((prev) => {
      const current = Array.isArray(prev) && prev.length ? [...prev] : [""];
      if (current.length >= 5) return current;
      return [...current, ""];
    });
  }, []);
  const removeCompareAuctionSlot = useCallback((index) => {
    setCompareAuctionIds((prev) => {
      const current = Array.isArray(prev) ? prev : [""];
      const next = current.filter((_, slotIndex) => slotIndex !== index);
      return next.length ? next : [""];
    });
  }, []);
  const updateCompareAuctionSlot = useCallback((index, nextId) => {
    setCompareAuctionIds((prev) => {
      const current = Array.isArray(prev) && prev.length ? [...prev] : [""];
      while (current.length <= index) current.push("");
      current[index] = String(nextId || "").trim();
      return current.slice(0, 5);
    });
  }, []);
  const renderPickupLocationSelectedDetails = useCallback(
    (values = []) => {
      const rows = (Array.isArray(values) ? values : [])
        .map((name) => {
          const safeName = String(name || "").trim();
          if (!safeName) return null;
          return {
            name: safeName,
            address: getLocationAddress(safeName),
          };
        })
        .filter(Boolean);
      if (!rows.length) return null;
      return (
        <Box sx={{ mt: 0.55, display: "grid", gap: 0.45 }}>
          {rows.map((row) => (
            <Box
              key={row.name}
              sx={{
                px: 1,
                py: 0.65,
                borderRadius: 1.5,
                border: "1px solid rgba(226,232,240,0.95)",
                background: "#fff",
              }}
            >
              <Typography variant="caption" sx={{ display: "block", color: "#111827", fontWeight: 900 }}>
                {row.name}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {row.address || "No address saved in location metadata"}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    },
    [getLocationAddress]
  );

  const cacheBaseKey = (() => {
    const sub = String(subscriptionId || "").trim();
    return sub ? `hibid_dashboard_${HIBID_CACHE_VERSION}_${sub}` : "";
  })();
  const cacheKey = (kind, suffix = "") => {
    if (!cacheBaseKey) return "";
    const sf = String(suffix || "").trim();
    return sf ? `${cacheBaseKey}_${kind}_${encodeURIComponent(sf)}` : `${cacheBaseKey}_${kind}`;
  };
  const readCache = (key, { allowStale = false, ttlMs = HIBID_USERS_CACHE_TTL_MS } = {}) => {
    try {
      if (!key) return null;
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const ts = Number(parsed.ts || 0);
      if (!Number.isFinite(ts) || ts <= 0) return null;
      if (!allowStale && Number.isFinite(ttlMs) && ttlMs > 0 && Date.now() - ts > ttlMs) return null;
      return parsed.payload;
    } catch {
      return null;
    }
  };
  const writeCache = (key, payload) => {
    try {
      if (!key) return;
      sessionStorage.setItem(
        key,
        JSON.stringify({
          ts: Date.now(),
          payload,
        })
      );
    } catch { }
  };
  const clearCache = (key) => {
    try {
      if (!key) return;
      sessionStorage.removeItem(key);
    } catch { }
  };
  const usersCachePrefix = cacheBaseKey ? `${cacheBaseKey}_users_` : "";
  const usersCacheKey = (querySig, cursorDocId = "") => {
    const cursor = String(cursorDocId || "").trim() || "first";
    return cacheKey("users_query", `${querySig}::${cursor}`);
  };
  const auctionsCacheKey = cacheKey("auctions");
  const productSearchCacheKey = cacheKey("product_search");
  const auctionSummaryCacheKey = (auctionId) => cacheKey("auction_summary", auctionId);
  const auctionRegsCacheKey = (auctionId) => cacheKey("auction_regs", auctionId);
  const userAuctionsCacheKey = (emailLower) => cacheKey("user_auctions", emailLower);

  const invalidateAuctionsCache = () => clearCache(auctionsCacheKey);
  const invalidateAuctionSummaryCache = (auctionId) => clearCache(auctionSummaryCacheKey(auctionId));
  const invalidateAuctionRegsCache = (auctionId) => clearCache(auctionRegsCacheKey(auctionId));
  const clearRegsSearch = () => {
    setAregSearch("");
  };

  const apiFetchJson = async (url, options = {}) => {
    const headers = { ...(orgHeaders || {}), ...(options.headers || {}) };
    if (options.body && !(options.body instanceof FormData)) {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }
    const res = await fetch(url, { ...options, headers });
    let data = null;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) data = await res.json();
    else data = await res.text();
    return { res, data };
  };
  useEffect(() => {
    if (!orgReady) return;
    let cancelled = false;
    (async () => {
      try {
        const { res, data } = await apiFetchJson(`${API}${ROUTES.orgProfile}`, { method: "GET" });
        if (!res.ok || data?.ok !== true) return;
        const name = String(data?.orgName || "").trim();
        const shippingPolicyText = String(data?.shippingPolicyText || DEFAULT_SHIPPING_POLICY_TEXT).trim() || DEFAULT_SHIPPING_POLICY_TEXT;
        const managers = sanitizeAccountManagers(data?.accountManagers || []);
        const tierMap = sanitizeAccountManagerTierMap(data?.accountManagerTierMap || {}, managers);
        if (cancelled) return;
        if (name) {
          setSubscriptionName(name);
        }
        setShippingTextDraft(shippingPolicyText);
        setShippingTextSaved(shippingPolicyText);
        setConsignors(sanitizeConsignors(data?.consignors || []));
        setAccountManagers(managers);
        setAccountManagerTierMap(tierMap);
      } catch { }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgReady, subscriptionId]);

  const handleNeedsAuth = (res, data) => {
    if (res.status === 401 && data?.needsAuth) {
      toastOpen(data?.message || "Authorization required.", "error");
      return true;
    }
    return false;
  };
  const fetchCompareAuctionRegs = useCallback(async (auctionId) => {
    const safeAuctionId = String(auctionId || "").trim();
    if (!orgReady || !safeAuctionId) return;
    if (compareAuctionRegsRequestsRef.current.has(safeAuctionId)) return;

    compareAuctionRegsRequestsRef.current.add(safeAuctionId);
    setCompareAuctionRegsLoadingById((prev) => ({ ...prev, [safeAuctionId]: true }));
    setCompareAuctionRegsErrorById((prev) => {
      const next = { ...prev };
      delete next[safeAuctionId];
      return next;
    });

    try {
      const allRows = [];
      let cursorDocId = null;
      let page = 0;

      while (true) {
        const qs = new URLSearchParams();
        qs.set("auctionId", safeAuctionId);
        qs.set("limit", "1000");
        qs.set("sync", "false");
        if (cursorDocId) qs.set("cursorDocId", String(cursorDocId));

        const { res, data } = await apiFetchJson(`${API}${ROUTES.auctionRegistrations}?${qs.toString()}`);
        if (handleNeedsAuth(res, data)) throw new Error("Authorization required.");
        if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);

        const rows = Array.isArray(data?.rows) ? data.rows : [];
        allRows.push(...rows);

        const nextCursor = data?.nextCursorDocId ?? data?.nextCursorEmailSentAt ?? null;
        const nextCursorStr = String(nextCursor || "").trim();
        if (!nextCursorStr || rows.length === 0 || nextCursorStr === String(cursorDocId || "")) break;
        cursorDocId = nextCursorStr;
        page += 1;
        if (page > 100) break;
      }

      setCompareAuctionRegsById((prev) => ({ ...prev, [safeAuctionId]: allRows }));
    } catch (e) {
      setCompareAuctionRegsById((prev) => ({ ...prev, [safeAuctionId]: [] }));
      setCompareAuctionRegsErrorById((prev) => ({
        ...prev,
        [safeAuctionId]: e?.message || "Failed to load registration trend.",
      }));
    } finally {
      compareAuctionRegsRequestsRef.current.delete(safeAuctionId);
      setCompareAuctionRegsLoadingById((prev) => ({ ...prev, [safeAuctionId]: false }));
    }
  }, [API, ROUTES.auctionRegistrations, apiFetchJson, handleNeedsAuth, orgReady]);
  useEffect(() => {
    if (!compareAuctionsOpen || compareAuctionView !== "registrationTrend") return;
    selectedCompareAuctionRows.forEach((row) => {
      const auctionId = String(row?.auctionId || row?.id || "").trim();
      if (!auctionId) return;
      if (compareAuctionRegsById[auctionId] !== undefined) return;
      if (compareAuctionRegsLoadingById[auctionId]) return;
      void fetchCompareAuctionRegs(auctionId);
    });
  }, [
    compareAuctionRegsById,
    compareAuctionRegsLoadingById,
    compareAuctionView,
    compareAuctionsOpen,
    fetchCompareAuctionRegs,
    selectedCompareAuctionRows,
  ]);
  const {
    regActivityRangePreset,
    setRegActivityRangePreset,
    regActivityCustomStartDate,
    setRegActivityCustomStartDate,
    regActivityCustomEndDate,
    setRegActivityCustomEndDate,
    regActivityMeasure,
    setRegActivityMeasure,
    regActivityTotals,
    regActivityLoading,
    regActivitySortKey,
    setRegActivitySortKey,
    regActivitySortDir,
    setRegActivitySortDir,
    regActivityUsersOpen,
    regActivityUsersLoading,
    regActivityUsersError,
    regActivityUsersDateKey,
    regActivityUsersType,
    regActivityUsersRows,
    regActivityRangeOptions,
    regActivityResolvedRange,
    regActivityMeasureOptions,
    registrationActivityChartData,
    sortedRegistrationActivityRows,
    registrationActivityHasData,
    fetchRegistrationActivityUsers,
    closeRegActivityUsersDialog,
  } = useDailyRegistrationActivity({
    orgReady,
    tab,
    apiBaseUrl: API,
    routes: ROUTES,
    apiFetchJson,
    handleNeedsAuth,
    toastOpen,
    toAbsInt,
    safeNum,
    compareSortValues,
    utcMsToDateKey,
    addDaysToDateKeyUTC,
    addMonthsToDateKeyUTC,
    isDateKeyUTC,
    formatDateKeyRangeShortUTC,
    dateKeyToUtcMs,
    formatDateKeyShortUTC,
    weekStartDateKeyUTC,
    monthLabelFromKeyUTC,
  });
  const {
    productSearchAllRows,
    productSearchBusy,
    productSearchErr,
    productSearchSearchInput,
    setProductSearchSearchInput,
    productSearchField,
    setProductSearchField,
    productSearchSortKey,
    productSearchSortDir,
    productSearchLoadedOnce,
    productSearchExportBusy,
    productSearchVisibleCount,
    setProductSearchVisibleCount,
    productSearchLoadedCount,
    productSearchTotalCount,
    productSearchRefreshConfirmOpen,
    productSearchContainerRef,
    productSearchDisplayColumns,
    productSearchSorted,
    productSearchRows,
    onProductSearchScroll,
    onProductSearchSort,
    exportFilteredProductSearchCsv,
    openProductSearchRefreshConfirm,
    closeProductSearchRefreshConfirm,
    continueProductSearchRefreshAnyway,
  } = useProductSearch({
    orgReady,
    tab,
    apiBaseUrl: API,
    routes: ROUTES,
    apiFetchJson,
    handleNeedsAuth,
    toastOpen,
    readCache,
    writeCache,
    clearCache,
    productSearchCacheKey,
    productSearchColumnsAllowed: PRODUCT_SEARCH_COLUMNS,
    toAbsInt,
    productSearchCacheTtlMs: HIBID_PRODUCT_SEARCH_CACHE_TTL_MS,
    productSearchBatchSize: HIBID_PRODUCT_SEARCH_BATCH_SIZE,
    productSearchParallelBatches: HIBID_PRODUCT_SEARCH_PARALLEL_BATCHES,
  });
  const {
    search,
    tierFilter,
    setTierFilter,
    newTierSubFilter,
    setNewTierSubFilter,
    regsThresholdFilter,
    setRegsThresholdFilter,
    lastEmailFilter,
    setLastEmailFilter,
    customerTypeFilter,
    setCustomerTypeFilter,
    accountManagerFilter,
    setAccountManagerFilter,
    setUserRows,
    userTableContainerRef,
    userSelected,
    userSortKey,
    setUserSortKey,
    userSortDir,
    setUserSortDir,
    userHasMore,
    userLoadingMore,
    usersInitialLoading,
    usersTableLoading,
    customerTypeSavingByEmail,
    accountManagerSavingByEmail,
    usersSearchInputRef,
    showUsersLoadingModal,
    fetchUsers,
    invalidateUsersCache,
    applyUsersSearch,
    clearUsersSearch,
    reloadUsersFromServer,
    onUsersTableScroll,
    allFilteredUsers,
    visibleUsers,
    userTableTopSpacerHeight,
    userTableBottomSpacerHeight,
    usersTotalHeaderText,
    getUserEmailLower,
    getOverallHammerPrice,
    applyUserPatchLocally,
    toggleSelectUser,
    toggleSelectAllUsers,
    selectedFilteredUsers,
    allFilteredUsersSelected,
    someFilteredUsersSelected,
    updateCustomerTypeForUser,
    updateAccountManagerForUser,
  } = useHibidUsers({
    orgReady,
    tab,
    userId,
    subscriptionId,
    needsSubscriptionLink,
    apiBaseUrl: API,
    routes: ROUTES,
    apiFetchJson,
    handleNeedsAuth,
    toastOpen,
    setSyncInfo,
    readCache,
    writeCache,
    clearCache,
    usersCacheKey,
    usersCachePrefix,
    usersFetchSig: HIBID_USERS_FETCH_SIG,
    usersCacheTtlMs: HIBID_USERS_CACHE_TTL_MS,
    usersPageSize: USERS_PAGE_SIZE,
    userTableRowHeight: USER_TABLE_ROW_HEIGHT,
    userTableOverscanRows: USER_TABLE_OVERSCAN_ROWS,
    normalize,
    normalizeTierKey,
    normalizeCustomerTypeKey,
    normalizeAccountManagerId,
    isEmail,
    safeToMillis,
    safeNum,
    toAbsInt,
    formatNumber2,
    waitForNextPaint,
    accountManagerTierMap,
  });
  const resetAccountManagerForm = () => {
    setAccountManagerForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
    });
  };
  const resetConsignorForm = () => {
    setConsignorForm({
      consignorId: "",
      consignorName: "",
      pointOfContact: "",
      email: "",
      phone: "",
    });
  };
  const fetchAccountManagersConfig = async () => {
    if (!orgReady) return;
    setAccountManagersLoading(true);
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.accountManagers}`, { method: "GET" });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const managers = sanitizeAccountManagers(data?.managers || []);
      const tierMap = sanitizeAccountManagerTierMap(data?.accountManagerTierMap || {}, managers);
      setAccountManagers(managers);
      setAccountManagerTierMap(tierMap);
    } catch (e) {
      toastOpen(e?.message || "Failed to fetch account managers.", "error");
    } finally {
      setAccountManagersLoading(false);
    }
  };
  const fetchConsignorsConfig = async () => {
    if (!orgReady) return;
    setConsignorsLoading(true);
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.consignors}`, { method: "GET" });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      setConsignors(sanitizeConsignors(data?.consignors || []));
    } catch (e) {
      toastOpen(e?.message || "Failed to fetch consignors.", "error");
    } finally {
      setConsignorsLoading(false);
    }
  };
  const openAccountManagerModal = async (mode = "create") => {
    setAccountManagerModalMode(mode === "assign" ? "assign" : "create");
    setAccountManagerModalOpen(true);
    setAccountManagerCreateFormOpen(false);
    resetAccountManagerForm();
    await fetchAccountManagersConfig();
  };
  const openConsignorModal = async () => {
    setConsignorModalOpen(true);
    setConsignorCreateFormOpen(false);
    resetConsignorForm();
    await fetchConsignorsConfig();
  };
  const createAccountManager = async () => {
    if (!orgReady || accountManagerCreateSaving) return;
    const payload = {
      firstName: String(accountManagerForm?.firstName || "").trim(),
      lastName: String(accountManagerForm?.lastName || "").trim(),
      email: String(accountManagerForm?.email || "").trim().toLowerCase(),
      phone: String(accountManagerForm?.phone || "").trim(),
      role: String(accountManagerForm?.role || "").trim(),
    };
    if (!payload.firstName || !payload.lastName || !payload.email || !payload.phone || !payload.role) {
      toastOpen("Please fill first name, last name, email, phone, and role.", "error");
      return;
    }
    if (!isEmail(payload.email)) {
      toastOpen("Please enter a valid manager email.", "error");
      return;
    }

    setAccountManagerCreateSaving(true);
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.accountManagersCreate}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const managers = sanitizeAccountManagers(data?.managers || []);
      const tierMap = sanitizeAccountManagerTierMap(data?.accountManagerTierMap || {}, managers);
      setAccountManagers(managers);
      setAccountManagerTierMap(tierMap);
      setAccountManagerCreateFormOpen(false);
      resetAccountManagerForm();
      toastOpen("Account manager created.", "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to create account manager.", "error");
    } finally {
      setAccountManagerCreateSaving(false);
    }
  };
  const createConsignor = async () => {
    if (!orgReady || consignorCreateSaving) return;
    const payload = {
      consignorId: normalizeConsignorProfileId(consignorForm?.consignorId || ""),
      consignorName: String(consignorForm?.consignorName || "").trim(),
      pointOfContact: String(consignorForm?.pointOfContact || "").trim(),
      email: String(consignorForm?.email || "").trim().toLowerCase(),
      phone: String(consignorForm?.phone || "").trim(),
    };
    if (!payload.consignorId || !payload.consignorName) {
      toastOpen("Please fill consignor ID and consignor name.", "error");
      return;
    }
    if (payload.email && !isEmail(payload.email)) {
      toastOpen("Please enter a valid consignor email.", "error");
      return;
    }

    setConsignorCreateSaving(true);
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.consignorsCreate}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const nextConsignors = sanitizeConsignors(data?.consignors || []);
      setConsignors(nextConsignors);
      setAuctionFieldOptions((prev) =>
        mergeAuctionFieldOptions(prev, { consignors: [payload.consignorName] })
      );
      setConsignorCreateFormOpen(false);
      resetConsignorForm();
      toastOpen("Consignor created.", "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to create consignor.", "error");
    } finally {
      setConsignorCreateSaving(false);
    }
  };
  const saveAccountManagerTierAssignments = async () => {
    if (!orgReady || accountManagerTierSaving) return;
    setAccountManagerTierSaving(true);
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.accountManagersAssignTiers}`, {
        method: "POST",
        body: JSON.stringify({ accountManagerTierMap }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const managers = sanitizeAccountManagers(data?.managers || accountManagers);
      const tierMap = sanitizeAccountManagerTierMap(data?.accountManagerTierMap || accountManagerTierMap, managers);
      setAccountManagers(managers);
      setAccountManagerTierMap(tierMap);
      invalidateUsersCache();
      await fetchUsers({ force: true, sync: false, reset: true });
      toastOpen("Tier assignments saved and users updated.", "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to save tier assignments.", "error");
    } finally {
      setAccountManagerTierSaving(false);
    }
  };

const buildAuctionInlineInfoDraftFromRow = (row = {}) => ({
  endAtLocal: row?.endMs ? toInputDateTimeLocal(row.endMs) : "",
  location: String(row?.location || "").trim() === "—" ? "" : String(row?.location || "").trim(),
  accountManagerIds: getAuctionAccountManagerIds(row, accountManagers),
  pickupLocations: getAuctionPickupLocationsFromAuction(row),
  pickupSlots: buildAuctionPickupSlotDrafts(row),
});
  const getAuctionInlineInfoDraft = (row = {}) => {
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    if (!auctionId) return buildAuctionInlineInfoDraftFromRow(row);
    return auctionInlineInfoDraftById[auctionId] || buildAuctionInlineInfoDraftFromRow(row);
  };
  const setAuctionInlineInfoField = (row, field, value) => {
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    if (!auctionId) return;
    setAuctionInlineInfoDraftById((prev) => {
      const base = prev[auctionId] || buildAuctionInlineInfoDraftFromRow(row);
      return { ...prev, [auctionId]: { ...base, [field]: value } };
    });
  };
  const getAuctionInlineAccountManagerIds = (row = {}) =>
    sanitizeAuctionAccountManagerIds(getAuctionInlineInfoDraft(row)?.accountManagerIds || [], accountManagers);
  const getAuctionInlineAccountManagerRows = (row = {}) =>
    getAuctionInlineAccountManagerIds(row).map((id) => {
      const manager = auctionAccountManagerById.get(id);
      return {
        id,
        label: manager ? accountManagerDisplayName(manager) : id,
      };
    });
  const setAuctionInlineAccountManagerIds = (row, nextIds = []) => {
    setAuctionInlineInfoField(row, "accountManagerIds", sanitizeAuctionAccountManagerIds(nextIds, accountManagers));
  };
  const getAuctionInlinePickupLocations = (row = {}) =>
    sanitizeAuctionPickupLocations(getAuctionInlineInfoDraft(row)?.pickupLocations || []);
  const setAuctionInlinePickupLocations = (row, nextLocations = []) => {
    const pickupLocations = sanitizeAuctionPickupLocations(nextLocations);
    const pickupSlots = getAuctionInlinePickupSlots(row).map((slot) => {
      const slotLocation = String(slot?.location || "").trim();
      return pickupLocations.includes(slotLocation)
        ? slot
        : { ...slot, location: pickupLocations.length === 1 ? pickupLocations[0] : "" };
    });
    setAuctionInlineInfoField(row, "pickupLocations", pickupLocations);
    setAuctionInlineInfoField(row, "pickupSlots", pickupSlots);
  };
  const getAuctionInlinePickupSlots = (row = {}) => {
    const draft = getAuctionInlineInfoDraft(row);
    return Array.isArray(draft?.pickupSlots) ? draft.pickupSlots : [];
  };
  const addAuctionInlinePickupSlot = (row) => {
    const fallbackLocation = String((getAuctionInlineInfoDraft(row)?.pickupLocations || [])[0] || "").trim();
    const next = [...getAuctionInlinePickupSlots(row), { slotId: createPickupSlotId(), location: fallbackLocation, startAtLocal: "", endAtLocal: "" }];
    setAuctionInlineInfoField(row, "pickupSlots", next);
  };
  const updateAuctionInlinePickupSlot = (row, slotId, field, value) => {
    const targetId = String(slotId || "").trim();
    if (!targetId) return;
    const next = getAuctionInlinePickupSlots(row).map((slot) =>
      String(slot?.slotId || "").trim() === targetId ? { ...slot, [field]: String(value || "") } : slot
    );
    setAuctionInlineInfoField(row, "pickupSlots", next);
  };
  const removeAuctionInlinePickupSlot = (row, slotId) => {
    const targetId = String(slotId || "").trim();
    if (!targetId) return;
    const next = getAuctionInlinePickupSlots(row).filter((slot) => String(slot?.slotId || "").trim() !== targetId);
    setAuctionInlineInfoField(row, "pickupSlots", next);
  };
  const getAuctionInlineLocationInput = (row = {}) => {
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    if (!auctionId) return String(getAuctionInlineInfoDraft(row)?.location || "");
    if (Object.prototype.hasOwnProperty.call(auctionInlineLocationInputById, auctionId)) {
      return String(auctionInlineLocationInputById[auctionId] || "");
    }
    return String(getAuctionInlineInfoDraft(row)?.location || "");
  };
  const setAuctionInlineLocationInput = (row, value) => {
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    if (!auctionId) return;
    setAuctionInlineLocationInputById((prev) => ({ ...prev, [auctionId]: String(value || "") }));
  };
  const getAuctionInlineLocationOptions = (row = {}) => {
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    const storedOptions = auctionId ? auctionInlineLocationOptionsById[auctionId] || [] : [];
    return uniqSortedOptions([
      ...(Array.isArray(auctionFieldOptions?.locations) ? auctionFieldOptions.locations : []),
      ...storedOptions,
      String(getAuctionInlineLocationInput(row) || ""),
      String(getAuctionInlineInfoDraft(row)?.location || ""),
    ]);
  };
  const queueAuctionInlineCityOptions = (row, inputValue = "") => {
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    if (!auctionId) return;
    const query = String(inputValue || "").trim();
    if (!cityPlacesReady || query.length < 2) {
      const existingTimer = inlineAuctionCityOptionsTimerByIdRef.current?.[auctionId];
      if (existingTimer) clearTimeout(existingTimer);
      setAuctionInlineLocationOptionsById((prev) => ({ ...prev, [auctionId]: [] }));
      return;
    }
    const seq = (inlineAuctionCityOptionsSeqByIdRef.current[auctionId] || 0) + 1;
    inlineAuctionCityOptionsSeqByIdRef.current[auctionId] = seq;
    const existingTimer = inlineAuctionCityOptionsTimerByIdRef.current?.[auctionId];
    if (existingTimer) clearTimeout(existingTimer);
    inlineAuctionCityOptionsTimerByIdRef.current[auctionId] = setTimeout(async () => {
      const options = await fetchCityPredictions(query);
      if (inlineAuctionCityOptionsSeqByIdRef.current[auctionId] !== seq) return;
      setAuctionInlineLocationOptionsById((prev) => ({ ...prev, [auctionId]: options }));
    }, 220);
  };
  const buildAuctionInlineInfoSignature = useCallback(
    ({ endMs = null, location = "", accountManagerIds = [], pickupLocations = [], pickupSlots = [] } = {}) =>
      JSON.stringify({
        endMs: endMs && Number.isFinite(Number(endMs)) ? Math.floor(Number(endMs)) : null,
        location: String(location || "").trim(),
        accountManagerIds: sanitizeAuctionAccountManagerIds(accountManagerIds, accountManagers),
        pickupLocations: sanitizeAuctionPickupLocations(pickupLocations),
        pickupSlots: buildAuctionPickupSlotPayload(pickupSlots, pickupLocations).map((slot) => ({
          slotId: String(slot?.slotId || "").trim(),
          location: String(slot?.location || "").trim(),
          startAtMs: safeToMillis(slot?.startAtMs),
          endAtMs: safeToMillis(slot?.endAtMs),
        })),
      }),
    [accountManagers]
  );
  const getAuctionInlineSavedSignature = useCallback(
    (row = {}) =>
      buildAuctionInlineInfoSignature({
        endMs: row?.endMs,
        location: String(row?.location || "").trim() === "—" ? "" : String(row?.location || "").trim(),
        accountManagerIds: getAuctionAccountManagerIds(row, accountManagers),
        pickupLocations: getAuctionPickupLocationsFromAuction(row),
        pickupSlots: buildAuctionPickupSlotDrafts(row),
      }),
    [accountManagers, buildAuctionInlineInfoSignature]
  );
  const saveAuctionInlineInfo = async (row, draftOverride = null, { silent = false } = {}) => {
    if (!orgReady) return;
    const auctionId = String(row?.auctionId || row?.id || "").trim();
    if (!auctionId) return;
    const draft = draftOverride || getAuctionInlineInfoDraft(row);
    const endMs = fromInputDateTimeLocalToMs(draft?.endAtLocal);
    const location = String(draft?.location || "").trim();
    const accountManagerIds = sanitizeAuctionAccountManagerIds(draft?.accountManagerIds || [], accountManagers);
    const pickupLocations = sanitizeAuctionPickupLocations(draft?.pickupLocations || []);
    const auctionAccountManagers = accountManagerIds.map((id) => {
      const manager = auctionAccountManagerById.get(id);
      return manager
        ? {
          id,
          firstName: String(manager?.firstName || "").trim(),
          lastName: String(manager?.lastName || "").trim(),
          email: String(manager?.email || "").trim().toLowerCase(),
          phone: String(manager?.phone || "").trim(),
          role: String(manager?.role || "").trim(),
          fullName: accountManagerDisplayName(manager),
        }
        : { id, fullName: id };
    });
    const accountManagerNames = auctionAccountManagers.map((manager) => manager.fullName || manager.id).filter(Boolean);
    const pickupSlots = buildAuctionPickupSlotPayload(draft?.pickupSlots || [], pickupLocations).map((slot) => ({
      ...slot,
      label: formatPickupSlotLabel(slot),
    }));
    if (!endMs && !location && !accountManagerIds.length && !pickupLocations.length && !pickupSlots.length) {
      toastOpen("Set auction end, location, account manager, pickup locations, or pickup slots before saving.", "error");
      return;
    }

    setAuctionInlineInfoSavingById((prev) => ({ ...prev, [auctionId]: true }));
    try {
      const body = { auctionId };
      if (endMs) body.auctionEndAtMs = endMs;
      if (location) body.location = location;
      body.accountManagerIds = accountManagerIds;
      body.pickupLocations = pickupLocations;
      body.pickupSlots = pickupSlots;
      const { res, data } = await apiFetchJson(`${API}${ROUTES.auctionsUpdateEnd}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Save failed (${res.status})`);
      setAuctionFieldOptions((prev) =>
        mergeAuctionFieldOptions(prev, { locations: location ? [location] : [] })
      );
      setAuctions((prev) =>
        (prev || []).map((a) => {
          const aid = String(a?.auctionId || a?.id || "").trim();
          if (aid !== auctionId) return a;
          return {
            ...a,
            ...(endMs ? { auctionEndAtMs: endMs } : {}),
            ...(location ? { location } : {}),
            auctionAccountManagerIds: accountManagerIds,
            auctionAccountManagers,
            accountManagerNames,
            pickupLocations,
            pickupSlots,
            updatedAt: new Date().toISOString(),
          };
        })
      );
      setSelectedAuction((prev) => {
        if (!prev) return prev;
        const aid = String(prev?.auctionId || prev?.id || "").trim();
        if (aid !== auctionId) return prev;
        return {
          ...prev,
          ...(endMs ? { auctionEndAtMs: endMs } : {}),
          ...(location ? { location } : {}),
          auctionAccountManagerIds: accountManagerIds,
          auctionAccountManagers,
          accountManagerNames,
          pickupLocations,
          pickupSlots,
          updatedAt: new Date().toISOString(),
        };
      });
      setAuctionInlineInfoDraftById((prev) => ({
        ...prev,
        [auctionId]: {
          endAtLocal: endMs ? toInputDateTimeLocal(endMs) : String(prev?.[auctionId]?.endAtLocal || draft?.endAtLocal || ""),
          location,
          accountManagerIds,
          pickupLocations,
          pickupSlots: (draft?.pickupSlots || []).map((slot) => ({
            slotId: String(slot?.slotId || "").trim() || createPickupSlotId(),
            location: String(slot?.location || ""),
            startAtLocal: String(slot?.startAtLocal || ""),
            endAtLocal: String(slot?.endAtLocal || ""),
          })),
        },
      }));
      setAuctionInlineLocationInputById((prev) => ({ ...prev, [auctionId]: location }));
      if (String(selectedAuctionId || "") === auctionId) {
        if (endMs) setAuctionEndAtLocal(toInputDateTimeLocal(endMs));
        if (location) setAuctionLocation(location);
        setAuctionAccountManagerIds(accountManagerIds);
        setAuctionPickupLocations(pickupLocations);
        setAuctionPickupSlots(
          (draft?.pickupSlots || []).map((slot) => ({
            slotId: String(slot?.slotId || "").trim() || createPickupSlotId(),
            location: String(slot?.location || ""),
            startAtLocal: String(slot?.startAtLocal || ""),
            endAtLocal: String(slot?.endAtLocal || ""),
          }))
        );
      }
      invalidateAuctionsCache();
      invalidateAuctionSummaryCache(auctionId);
      if (!silent) toastOpen("Auction info saved.", "success");
      if (String(selectedAuctionId || "") === auctionId) {
        await fetchAuctionSummary(auctionId, { force: true });
      }
    } catch (e) {
      toastOpen(e?.message || "Failed to save auction info", "error");
    } finally {
      setAuctionInlineInfoSavingById((prev) => ({ ...prev, [auctionId]: false }));
    }
  };
  const queueAuctionInlineInfoAutosave = useCallback(
    (row, draft, { immediate = false } = {}) => {
      const auctionId = String(row?.auctionId || row?.id || "").trim();
      if (!auctionId) return;
      const savedSig = getAuctionInlineSavedSignature(row);
      const draftSig = buildAuctionInlineInfoSignature({
        endMs: fromInputDateTimeLocalToMs(draft?.endAtLocal),
        location: String(draft?.location || "").trim(),
        accountManagerIds: draft?.accountManagerIds || [],
        pickupLocations: draft?.pickupLocations || [],
        pickupSlots: draft?.pickupSlots || [],
      });
      if (draftSig === savedSig) {
        auctionInlineLastAutoSaveSigByIdRef.current[auctionId] = draftSig;
        const existingTimer = auctionInlineAutosaveTimerByIdRef.current?.[auctionId];
        if (existingTimer) clearTimeout(existingTimer);
        return;
      }
      if (draftSig === auctionInlineLastAutoSaveSigByIdRef.current?.[auctionId] && !immediate) return;
      const runSave = () => {
        auctionInlineLastAutoSaveSigByIdRef.current[auctionId] = draftSig;
        void saveAuctionInlineInfo(row, draft, { silent: true });
      };
      const existingTimer = auctionInlineAutosaveTimerByIdRef.current?.[auctionId];
      if (existingTimer) clearTimeout(existingTimer);
      if (immediate) {
        runSave();
        return;
      }
      auctionInlineAutosaveTimerByIdRef.current[auctionId] = setTimeout(runSave, 650);
    },
    [buildAuctionInlineInfoSignature, getAuctionInlineSavedSignature, saveAuctionInlineInfo]
  );
  const flushAuctionInlineInfoAutosave = useCallback(
    (row) => {
      const auctionId = String(row?.auctionId || row?.id || "").trim();
      if (!auctionId) return;
      const existingTimer = auctionInlineAutosaveTimerByIdRef.current?.[auctionId];
      if (existingTimer) clearTimeout(existingTimer);
      queueAuctionInlineInfoAutosave(row, getAuctionInlineInfoDraft(row), { immediate: true });
    },
    [getAuctionInlineInfoDraft, queueAuctionInlineInfoAutosave]
  );
  const saveAuctionEndAt = useCallback(async () => {
    if (!orgReady || !selectedAuctionId) return;
    const endMs = fromInputDateTimeLocalToMs(auctionEndAtLocal);
    const consignors = uniqSortedOptions(auctionConsignors || []);
    const primaryConsignor = String(consignors[0] || "").trim();
    const location = String(auctionLocation || "").trim();
    const accountManagerIds = sanitizeAuctionAccountManagerIds(auctionAccountManagerIds, accountManagers);
    const pickupLocations = sanitizeAuctionPickupLocations(auctionPickupLocations);
    const auctionAccountManagers = accountManagerIds.map((id) => {
      const manager = auctionAccountManagerById.get(id);
      return manager
        ? {
          id,
          firstName: String(manager?.firstName || "").trim(),
          lastName: String(manager?.lastName || "").trim(),
          email: String(manager?.email || "").trim().toLowerCase(),
          phone: String(manager?.phone || "").trim(),
          role: String(manager?.role || "").trim(),
          fullName: accountManagerDisplayName(manager),
        }
        : { id, fullName: id };
    });
    const accountManagerNames = auctionAccountManagers.map((manager) => manager.fullName || manager.id).filter(Boolean);
    const pickupSlots = buildAuctionPickupSlotPayload(auctionPickupSlots, pickupLocations).map((slot) => ({
      ...slot,
      label: formatPickupSlotLabel(slot),
    }));
    if (!endMs && !consignors.length && !location && !accountManagerIds.length && !pickupLocations.length && !pickupSlots.length) {
      return;
    }

    setAuctionEndSaving(true);
    try {
      const body = { auctionId: selectedAuctionId };
      if (endMs) body.auctionEndAtMs = endMs;
      if (consignors.length) body.consignors = consignors;
      if (primaryConsignor) body.consignor = primaryConsignor;
      if (location) body.location = location;
      body.accountManagerIds = accountManagerIds;
      body.pickupLocations = pickupLocations;
      body.pickupSlots = pickupSlots;
      const { res, data } = await apiFetchJson(`${API}${ROUTES.auctionsUpdateEnd}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Save failed (${res.status})`);
      setAuctionFieldOptions((prev) =>
        mergeAuctionFieldOptions(prev, { consignors, locations: location ? [location] : [] })
      );
      setAuctions((prev) =>
        (prev || []).map((a) => {
          const aid = String(a?.auctionId || a?.id || "").trim();
          if (aid !== selectedAuctionId) return a;
          return {
            ...a,
            ...(endMs ? { auctionEndAtMs: endMs } : {}),
            ...(consignors.length ? { consignors, consignor: primaryConsignor } : {}),
            ...(location ? { location } : {}),
            auctionAccountManagerIds: accountManagerIds,
            auctionAccountManagers,
            accountManagerNames,
            pickupLocations,
            pickupSlots,
            updatedAt: new Date().toISOString(),
          };
        })
      );
      setSelectedAuction((prev) =>
        prev
          ? {
            ...prev,
            ...(endMs ? { auctionEndAtMs: endMs } : {}),
            ...(consignors.length ? { consignors, consignor: primaryConsignor } : {}),
            ...(location ? { location } : {}),
            auctionAccountManagerIds: accountManagerIds,
            auctionAccountManagers,
            accountManagerNames,
            pickupLocations,
            pickupSlots,
            updatedAt: new Date().toISOString(),
          }
          : prev
      );
      invalidateAuctionsCache();
      invalidateAuctionSummaryCache(selectedAuctionId);
      toastOpen("Auction info saved.", "success");
      await fetchAuctionSummary(selectedAuctionId, { force: true });
    } catch (e) {
      toastOpen(e?.message || "Failed to save auction info", "error");
    } finally {
      setAuctionEndSaving(false);
    }
  }, [
    orgReady,
    selectedAuctionId,
    auctionEndAtLocal,
    auctionConsignors,
    auctionLocation,
    auctionAccountManagerIds,
    auctionPickupLocations,
    auctionPickupSlots,
    accountManagers,
    auctionAccountManagerById,
  ]);
  const addAuctionPickupSlot = useCallback(() => {
    const fallbackLocation = String((sanitizeAuctionPickupLocations(auctionPickupLocations) || [])[0] || "").trim();
    setAuctionPickupSlots((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      { slotId: createPickupSlotId(), location: fallbackLocation, startAtLocal: "", endAtLocal: "" },
    ]);
  }, [auctionPickupLocations]);
  const updateAuctionPickupSlot = useCallback((slotId, field, value) => {
    const targetId = String(slotId || "").trim();
    if (!targetId) return;
    setAuctionPickupSlots((prev) =>
      (Array.isArray(prev) ? prev : []).map((slot) =>
        String(slot?.slotId || "").trim() === targetId
          ? { ...slot, [field]: String(value || "") }
          : slot
      )
    );
  }, []);
  const removeAuctionPickupSlot = useCallback((slotId) => {
    const targetId = String(slotId || "").trim();
    if (!targetId) return;
    setAuctionPickupSlots((prev) =>
      (Array.isArray(prev) ? prev : []).filter((slot) => String(slot?.slotId || "").trim() !== targetId)
    );
  }, []);
  const updateAuctionPickupLocations = useCallback((nextLocations = []) => {
    const pickupLocations = sanitizeAuctionPickupLocations(nextLocations);
    setAuctionPickupLocations(pickupLocations);
    setAuctionPickupSlots((prev) =>
      (Array.isArray(prev) ? prev : []).map((slot) => {
        const slotLocation = String(slot?.location || "").trim();
        return pickupLocations.includes(slotLocation)
          ? slot
          : { ...slot, location: pickupLocations.length === 1 ? pickupLocations[0] : "" };
      })
    );
  }, []);
  useEffect(() => {
    Object.entries(auctionInlineInfoDraftById || {}).forEach(([auctionId, draft]) => {
      const row = auctionHibidViewRowById.get(String(auctionId || "").trim());
      if (!row) return;
      queueAuctionInlineInfoAutosave(row, draft);
    });
    return () => {
      Object.values(auctionInlineAutosaveTimerByIdRef.current || {}).forEach((timerId) => {
        if (timerId) clearTimeout(timerId);
      });
    };
  }, [auctionInlineInfoDraftById, auctionHibidViewRowById, queueAuctionInlineInfoAutosave]);
  useEffect(() => {
    if (!orgReady || !selectedAuctionId) return;
    if (auctionSettingsAutosaveTimerRef.current) clearTimeout(auctionSettingsAutosaveTimerRef.current);
    if (selectedAuctionDraftSettingsSig === selectedAuctionSavedSettingsSig) {
      auctionSettingsLastAutoSaveSigRef.current = selectedAuctionDraftSettingsSig;
      return;
    }
    if (selectedAuctionDraftSettingsSig === auctionSettingsLastAutoSaveSigRef.current) return;
    if (
      !selectedAuctionDraftSettings.endMs &&
      !selectedAuctionDraftSettings.consignors.length &&
      !selectedAuctionDraftSettings.location &&
      !selectedAuctionDraftSettings.accountManagerIds.length &&
      !selectedAuctionDraftSettings.pickupSlots.length
    ) {
      return;
    }

    auctionSettingsAutosaveTimerRef.current = setTimeout(() => {
      auctionSettingsLastAutoSaveSigRef.current = selectedAuctionDraftSettingsSig;
      saveAuctionEndAt();
    }, 650);

    return () => {
      if (auctionSettingsAutosaveTimerRef.current) clearTimeout(auctionSettingsAutosaveTimerRef.current);
    };
  }, [
    orgReady,
    saveAuctionEndAt,
    selectedAuctionDraftSettingsSig,
    selectedAuctionId,
    selectedAuctionSavedSettingsSig,
  ]);
  const flushAuctionSettingsAutosave = useCallback(() => {
    if (!orgReady || !selectedAuctionId) return;
    if (auctionSettingsAutosaveTimerRef.current) clearTimeout(auctionSettingsAutosaveTimerRef.current);
    if (selectedAuctionDraftSettingsSig === selectedAuctionSavedSettingsSig) {
      auctionSettingsLastAutoSaveSigRef.current = selectedAuctionDraftSettingsSig;
      return;
    }
    if (
      !selectedAuctionDraftSettings.endMs &&
      !selectedAuctionDraftSettings.consignors.length &&
      !selectedAuctionDraftSettings.location &&
      !selectedAuctionDraftSettings.accountManagerIds.length &&
      !selectedAuctionDraftSettings.pickupSlots.length
    ) {
      return;
    }
    auctionSettingsLastAutoSaveSigRef.current = selectedAuctionDraftSettingsSig;
    saveAuctionEndAt();
  }, [
    orgReady,
    saveAuctionEndAt,
    selectedAuctionDraftSettings,
    selectedAuctionDraftSettingsSig,
    selectedAuctionId,
    selectedAuctionSavedSettingsSig,
  ]);
  const {
    fetchAuctions,
    fetchAuctionSummary,
    fetchAuctionRegsPage,
    onSelectAuction: selectAuctionInternal,
    openAuctionFromHibidView: openAuctionFromHibidViewInternal,
    backToAuctionList: backToAuctionListInternal,
    fetchUserLots,
    openLotsModal,
    updateRegistrationDelivered,
    uploadRegistrationDeliveryProofPhotos,
    closeRegistrationFulfillmentConfirm,
    openRegistrationFulfillmentConfirm,
    confirmRegistrationFulfillmentChange,
    updateLotPickup,
    updateLotShipped,
    updateLotDelivered,
    resolveFileNameMismatchDialog,
    confirmAuctionFileNameMatch,
    uploadShippingXlsx,
    requestShippingUploadFile,
    uploadLotsCsv,
    uploadListernautFile,
    requestListernautUploadFile,
    openCreateAuctionDialog,
    createAuctionNow,
  } = useHibidAuctionsLogic({
    orgReady,
    tab,
    apiBaseUrl: API,
    routes: ROUTES,
    apiFetchJson,
    handleNeedsAuth,
    toastOpen,
    fetchUsers,
    invalidateUsersCache,
    accountManagers,
    selectedAuction,
    selectedAuctionId,
    selectedAuctionLabel,
    auctionRegs,
    lotsAgg,
    lotsUser,
    createAuctionBusy,
    createAuctionName,
    createAuctionEndAtLocal,
    createAuctionConsignor,
    createAuctionLocation,
    createAuctionRegistrationFile,
    createAuctionLotsFile,
    setAuctions,
    setSelectedAuction,
    setAuctionSummary,
    setAuctionSummaryLoading,
    setAuctionRegs,
    setAregLoading,
    setAregSearch,
    setAregShipperFilter,
    setAregNewUserFilter,
    setAregPickupFilter,
    setAregShippedFilter,
    setAregConsignorFilter,
    setAregPickupDraftByEmail,
    setAregPickupSavingByEmail,
    setAregPickupEditByEmail,
    setAregShelfDraftByEmail,
    setAregShelfSavingByEmail,
    setAregAmountDueDraftByEmail,
    setAregSelected,
    setAuctionEndAtLocal,
    setAuctionConsignors,
    setAuctionAccountManagerIds,
    setAuctionPickupLocations,
    setAuctionPickupSlots,
    setAuctionLocation,
    setAuctionFieldOptions,
    setSyncInfo,
    setAuctionViewMode,
    setLotsAgg,
    setLotsOpen,
    setLotsBusy,
    setLotsErr,
    setLotsData,
    setLotsUser,
    setRegistrationFulfillmentConfirm,
    registrationFulfillmentConfirm,
    registrationFulfillmentConfirmBusy,
    setRegistrationFulfillmentConfirmBusy,
    setUploadBusy,
    setListernautUploadBusy,
    setShipBusy,
    setRegUploadBusy,
    setUploadDialogText,
    setCreateAuctionOpen,
    setCreateAuctionBusy,
    setCreateAuctionName,
    setCreateAuctionEndAtLocal,
    setCreateAuctionLocation,
    setCreateAuctionLocationInput,
    setCreateAuctionLocationCityOptions,
    setCreateAuctionRegistrationFile,
    setCreateAuctionLotsFile,
    setFileNameMismatchDialog,
    fileNameMismatchResolveRef,
    shippingFileInputRef,
    listernautFileInputRef,
    toAbsInt,
    safeNum,
    normalize,
    isEmail,
    safeToMillis,
    toInputDateTimeLocal,
    fromInputDateTimeLocalToMs,
    localDateKeyFromMs,
    getConsignorsFromAuction,
    getAuctionAccountManagerIds,
    getAuctionPickupLocationsFromAuction,
    buildAuctionPickupSlotDrafts,
    mergeAuctionFieldOptions,
    deriveAuctionFieldOptionsFromRows,
    getRegistrationPickupAppointment,
    getDisplayBidderName,
    getDisplayUsername,
    getLotPickupCounts,
    getLotShippedCounts,
    isShippingRequested,
    isXlsxFile,
    uniqSortedOptions,
    formatNumber2,
  });
  const onSelectAuction = useCallback(
    async (auctionRow, { viewMode = "stats" } = {}) => {
      pendingAuctionRouteIdRef.current = String(
        auctionRow?.auctionId || auctionRow?.id || ""
      ).trim();
      await selectAuctionInternal(auctionRow, { viewMode });
      openAuctionRoute(auctionRow, { viewMode });
    },
    [openAuctionRoute, selectAuctionInternal]
  );
  const openAuctionFromHibidView = useCallback(
    async (auctionRow) => {
      pendingAuctionRouteIdRef.current = String(
        auctionRow?.auctionId || auctionRow?.id || ""
      ).trim();
      await openAuctionFromHibidViewInternal(auctionRow);
      openAuctionRoute(auctionRow, { viewMode: "stats" });
    },
    [openAuctionFromHibidViewInternal, openAuctionRoute]
  );
  const openAuctionNewUsersFromOverall = useCallback(
    async (auctionRow) => {
      skipNextAuctionFilterResetRef.current = true;
      setAregSearch("");
      setAregShipperFilter("all");
      setAregPickupFilter("all");
      setAregPickupLocationFilter("all");
      setAregDeliveryFilter("all");
      setAregDeliveryLocationFilter("all");
      setAregPickupSelectionFilter("all");
      setAregShippedFilter("all");
      setAregNewUserFilter("new");
      await onSelectAuction(auctionRow, { viewMode: "registrations" });
    },
    [
      onSelectAuction,
      setAregNewUserFilter,
      setAregDeliveryFilter,
      setAregDeliveryLocationFilter,
      setAregPickupFilter,
      setAregPickupLocationFilter,
      setAregPickupSelectionFilter,
      setAregSearch,
      setAregShippedFilter,
      setAregShipperFilter,
    ]
  );
  const backToAuctionList = useCallback(() => {
    backToAuctionListInternal();
    openHibidSection("auctions");
  }, [backToAuctionListInternal, openHibidSection]);
  useEffect(() => {
    const nextAuctionId = String(selectedAuctionId || "").trim();
    const previousAuctionId = previousSelectedAuctionIdRef.current;
    if (!previousAuctionId) {
      previousSelectedAuctionIdRef.current = nextAuctionId;
      return;
    }
    if (previousAuctionId !== nextAuctionId) {
      if (skipNextAuctionFilterResetRef.current) {
        skipNextAuctionFilterResetRef.current = false;
      } else {
        resetAuctionRegistrationFilters();
      }
      setSelectedAuctionStatsTab("overview");
    }
    previousSelectedAuctionIdRef.current = nextAuctionId;
  }, [resetAuctionRegistrationFilters, selectedAuctionId]);
  useEffect(() => {
    if (currentSection === "auctions") return;
    skipNextAuctionFilterResetRef.current = false;
    resetAuctionRegistrationFilters();
    setSelectedAuctionStatsTab("overview");
  }, [currentSection, resetAuctionRegistrationFilters]);
  useEffect(() => {
    if (currentSection !== "auctions") return;
    if (!currentAuctionRouteId) {
      if (
        selectedAuctionId &&
        pendingAuctionRouteIdRef.current &&
        pendingAuctionRouteIdRef.current === selectedAuctionId
      ) {
        return;
      }
      if (selectedAuctionId) backToAuctionListInternal();
      return;
    }
    if (pendingAuctionRouteIdRef.current === currentAuctionRouteId) {
      pendingAuctionRouteIdRef.current = "";
    }
    if (selectedAuctionId === currentAuctionRouteId) return;
    const routeAuction = (Array.isArray(auctions) ? auctions : []).find(
      (row) => String(row?.auctionId || row?.id || "").trim() === currentAuctionRouteId
    );
    void selectAuctionInternal(
      routeAuction || { auctionId: currentAuctionRouteId, auctionName: "" },
      { viewMode: currentAuctionSubview }
    );
  }, [
    auctions,
    backToAuctionListInternal,
    currentAuctionRouteId,
    currentAuctionSubview,
    currentSection,
    selectAuctionInternal,
    selectedAuctionId,
  ]);
  const getLcvEstimate = (row) => {
    const v = row?.lcv?.estimate ?? row?.lcvEstimate ?? row?.derived?.lcvEstimate ?? null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const getUserLastRegistrationMs = (row) =>
    safeToMillis(
      row?.lastRegistrationDate ??
      row?.lastRegistrationAt ??
      row?.lastRegistrationAtMs ??
      row?.lastRegisteredAt ??
      null
    ) ?? null;
  const getUserLastAuctionName = (row) =>
    String(
      row?.lastAuctionName ??
      row?.lastRegisteredAuctionName ??
      row?.derived?.lastAuctionName ??
      ""
    ).trim();
  const formatDateOnlyCell = (value) => {
    const ms = safeToMillis(value);
    return ms ? new Date(ms).toISOString().slice(0, 10) : "";
  };
  const downloadFastRevenueReport = async () => {
    if (fastRevenueReportBusy) return;
    const sourceRows = Array.isArray(allFilteredUsers) ? allFilteredUsers : [];
    if (!sourceRows.length) {
      toastOpen("No users available to export.", "error");
      return;
    }

    setFastRevenueReportBusy(true);
    try {
      const xlsxModule = await import("xlsx-js-style");
      const XLSX = xlsxModule?.default?.utils ? xlsxModule.default : xlsxModule?.utils ? xlsxModule : xlsxModule?.default;
      if (!XLSX?.utils || typeof XLSX.writeFile !== "function") {
        throw new Error("XLSX export library failed to load.");
      }

      const detailHeaders = ["Name", "Email", "User Type", "Overall Hammer Price", "LCV"];
      const detailRows = sourceRows.map((row) => {
        const name =
          String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.email || row?.id || "").trim() || "—";
        const email = String(row?.email || row?.id || "").trim() || "—";
        const userTypeKey = getCustomerType(row);
        const userType = String(customerTypeLabel(userTypeKey) || "").trim() || "Unassigned";
        const overallHammerPrice = Number(safeNum(getOverallHammerPrice(row) ?? 0).toFixed(2));
        const lcv = getLcvEstimate(row);

        return [
          name,
          email,
          userType,
          overallHammerPrice,
          lcv == null ? "" : Number(safeNum(lcv).toFixed(2)),
        ];
      });

      const totalUsers = detailRows.length;
      const totalHammer = detailRows.reduce((sum, row) => sum + safeNum(row[3]), 0);
      const groupedByType = detailRows.reduce((map, row) => {
        const key = String(row[2] || "Unassigned").trim() || "Unassigned";
        const entry = map.get(key) || { userType: key, users: 0, totalHammer: 0 };
        entry.users += 1;
        entry.totalHammer += safeNum(row[3]);
        map.set(key, entry);
        return map;
      }, new Map());

      const summaryDataRows = Array.from(groupedByType.values())
        .sort((a, b) => b.totalHammer - a.totalHammer || a.userType.localeCompare(b.userType))
        .map((entry) => {
          const sharePct = totalHammer > 0 ? (entry.totalHammer / totalHammer) * 100 : 0;
          const avgPerType = entry.users > 0 ? entry.totalHammer / entry.users : 0;
          const avgPerOverall = totalUsers > 0 ? entry.totalHammer / totalUsers : 0;
          return [
            entry.userType,
            Number(entry.totalHammer.toFixed(2)),
            formatPercent2(sharePct),
            Number(avgPerType.toFixed(2)),
            Number(avgPerOverall.toFixed(2)),
            entry.users,
          ];
        });

      const generatedAt = new Date();
      const summaryRows = [
        ["Fast Revenue Report"],
        ["Generated", generatedAt.toLocaleString()],
        ["Users Included", totalUsers],
        ["Total Hammer", Number(totalHammer.toFixed(2))],
        ["Average Revenue Per User", Number((totalUsers > 0 ? totalHammer / totalUsers : 0).toFixed(2))],
        [],
        ["Revenue By User Type"],
        ["User Type", "Total Hammer", "Share of Total", "Avg Revenue Per Person In Type", "Avg Revenue Per Person Overall", "Users"],
        ...summaryDataRows,
      ];

      const headerStyle = buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_HEADER_RGB,
        textRgb: "FFFFFF",
        bold: true,
        horizontal: "center",
      });
      const titleStyle = buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_HEADER_RGB,
        textRgb: "FFFFFF",
        bold: true,
      });
      const detailWs = XLSX.utils.aoa_to_sheet([detailHeaders, ...detailRows]);
      detailWs["!cols"] = [
        { wch: 30 },
        { wch: 30 },
        { wch: 24 },
        { wch: 20 },
        { wch: 16 },
      ];
      detailWs["!autofilter"] = { ref: `A1:E${Math.max(1, detailRows.length + 1)}` };
      styleWorksheetRow(XLSX, detailWs, 1, detailHeaders.length, headerStyle);

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
      summaryWs["!cols"] = [
        { wch: 28 },
        { wch: 18 },
        { wch: 18 },
        { wch: 30 },
        { wch: 30 },
        { wch: 12 },
      ];
      setWorksheetCellStyle(summaryWs, "A1", titleStyle);
      styleWorksheetRow(XLSX, summaryWs, 8, 6, headerStyle);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, detailWs, "details");
      XLSX.utils.book_append_sheet(workbook, summaryWs, "summary");
      const stamp = generatedAt.toISOString().replace(/[:.]/g, "-");
      XLSX.writeFile(workbook, `fast-revenue-report-${stamp}.xlsx`);
      toastOpen(`Downloaded fast revenue report for ${formatNumber2(totalUsers)} users.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to download fast revenue report.", "error");
    } finally {
      setFastRevenueReportBusy(false);
    }
  };
  const downloadBuyerRetentionTable = async () => {
    if (buyerRetentionReportBusy) return;
    const sourceRows = Array.isArray(allFilteredUsers) ? allFilteredUsers : [];
    if (!sourceRows.length) {
      toastOpen("No users available to export.", "error");
      return;
    }

    const auctionCount = Math.max(0, Math.floor(Number(buyerRetentionAuctionCount || 0)));
    if (!Number.isFinite(auctionCount) || auctionCount <= 0) {
      toastOpen("Enter a valid auction count greater than 0.", "error");
      return;
    }

    setBuyerRetentionReportBusy(true);
    try {
      const xlsxModule = await import("xlsx-js-style");
      const XLSX = xlsxModule?.default?.utils ? xlsxModule.default : xlsxModule?.utils ? xlsxModule : xlsxModule?.default;
      if (!XLSX?.utils || typeof XLSX.writeFile !== "function") {
        throw new Error("XLSX export library failed to load.");
      }

      const cutoffMs = Date.now() - 60 * 24 * 60 * 60 * 1000;
      const exactMode = String(buyerRetentionMatchMode || "exact") === "exact";
      const filterLabel = exactMode ? `exact ${auctionCount}` : `<= ${auctionCount}`;
      const matchesAuctionCount = (row) => {
        const regs = getRegsCount(row);
        if (exactMode) return regs === auctionCount;
        return regs > 0 && regs <= auctionCount;
      };

      const matchedRows = sourceRows
        .filter((row) => matchesAuctionCount(row))
        .sort((a, b) => {
          const aMs = getUserLastRegistrationMs(a) ?? Number.MAX_SAFE_INTEGER;
          const bMs = getUserLastRegistrationMs(b) ?? Number.MAX_SAFE_INTEGER;
          if (aMs !== bMs) return aMs - bMs;
          return String(a?.email || a?.id || "").localeCompare(String(b?.email || b?.id || ""));
        });

      if (!matchedRows.length) {
        toastOpen(`No users matched ${filterLabel} auctions.`, "error");
        return;
      }

      const last60Rows = matchedRows.filter((row) => {
        const ms = getUserLastRegistrationMs(row);
        return Number.isFinite(ms) && ms >= cutoffMs;
      });
      const gt60Rows = matchedRows.filter((row) => {
        const ms = getUserLastRegistrationMs(row);
        return Number.isFinite(ms) && ms < cutoffMs;
      });
      const undatedRows = matchedRows.filter((row) => !Number.isFinite(getUserLastRegistrationMs(row)));

      const detailHeaders = ["Name", "Email", "Auction Count", "User Type", "Last Auction Registered", "Last Registration Date", "Overall Hammer Price", "LCV"];
      const toDetailRows = (rows) =>
        rows.map((row) => {
          const name =
            String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.email || row?.id || "").trim() || "—";
          const email = String(row?.email || row?.id || "").trim() || "—";
          const userTypeKey = getCustomerType(row);
          const userType = String(customerTypeLabel(userTypeKey) || "").trim() || "Unassigned";
          const lastAuctionName = getUserLastAuctionName(row) || "—";
          const lastRegistrationMs = getUserLastRegistrationMs(row);
          const overallHammerPrice = Number(safeNum(getOverallHammerPrice(row) ?? 0).toFixed(2));
          const lcv = getLcvEstimate(row);
          return [
            name,
            email,
            getRegsCount(row),
            userType,
            lastAuctionName,
            formatDateOnlyCell(lastRegistrationMs),
            overallHammerPrice,
            lcv == null ? "" : Number(safeNum(lcv).toFixed(2)),
          ];
        });

      const buildSummaryRows = (label, rows, missingLastRegistrationCount = 0) => {
        const detailRows = toDetailRows(rows);
        const totalUsers = detailRows.length;
        const totalHammer = detailRows.reduce((sum, row) => sum + safeNum(row[5]), 0);
        const avgHammer = totalUsers > 0 ? totalHammer / totalUsers : 0;
        const groupedByType = detailRows.reduce((map, row) => {
          const key = String(row[3] || "Unassigned").trim() || "Unassigned";
          const entry = map.get(key) || { userType: key, users: 0, totalHammer: 0, totalLcv: 0 };
          entry.users += 1;
          entry.totalHammer += safeNum(row[5]);
          entry.totalLcv += safeNum(row[6]);
          map.set(key, entry);
          return map;
        }, new Map());

        const breakdownRows = Array.from(groupedByType.values())
          .sort((a, b) => b.users - a.users || b.totalHammer - a.totalHammer || a.userType.localeCompare(b.userType))
          .map((entry) => [
            entry.userType,
            entry.users,
            formatPercent2(totalUsers > 0 ? (entry.users / totalUsers) * 100 : 0),
            Number(entry.totalHammer.toFixed(2)),
            Number((entry.users > 0 ? entry.totalHammer / entry.users : 0).toFixed(2)),
            Number((entry.users > 0 ? entry.totalLcv / entry.users : 0).toFixed(2)),
          ]);

        return [
          ["Buyer Retention Table"],
          ["Scope", label],
          ["Generated", new Date().toLocaleString()],
          ["Auction Count Filter", filterLabel],
          ["Users Included", totalUsers],
          ["Users Missing Last Registration Date", missingLastRegistrationCount],
          ["Total Hammer", Number(totalHammer.toFixed(2))],
          ["Average Hammer Per User", Number(avgHammer.toFixed(2))],
          [],
          ["User Type Breakdown"],
          ["User Type", "Users", "Share of Scope", "Total Hammer", "Avg Hammer Per User", "Avg LCV"],
          ...breakdownRows,
        ];
      };

      const scopes = [
        {
          slug: "overall",
          detailName: "overall_details",
          summaryName: "overall_summary",
          label: "Overall",
          rows: matchedRows,
          missingLastRegistrationCount: undatedRows.length,
        },
        {
          slug: "last-60",
          detailName: "last_60_details",
          summaryName: "last_60_summary",
          label: "Last 60 Days",
          rows: last60Rows,
          missingLastRegistrationCount: 0,
        },
        {
          slug: "gt-60",
          detailName: "gt_60_details",
          summaryName: "gt_60_summary",
          label: "Greater Than 60 Days",
          rows: gt60Rows,
          missingLastRegistrationCount: 0,
        },
      ];

      const headerStyle = buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_HEADER_RGB,
        textRgb: "FFFFFF",
        bold: true,
        horizontal: "center",
      });
      const titleStyle = buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_HEADER_RGB,
        textRgb: "FFFFFF",
        bold: true,
      });

      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      scopes.forEach((scope) => {
        const workbook = XLSX.utils.book_new();
        const detailRows = toDetailRows(scope.rows);
        const detailWs = XLSX.utils.aoa_to_sheet([detailHeaders, ...detailRows]);
        detailWs["!cols"] = [
          { wch: 28 },
          { wch: 30 },
          { wch: 14 },
          { wch: 22 },
          { wch: 34 },
          { wch: 20 },
          { wch: 20 },
          { wch: 14 },
        ];
        detailWs["!autofilter"] = { ref: `A1:H${Math.max(1, detailRows.length + 1)}` };
        styleWorksheetRow(XLSX, detailWs, 1, detailHeaders.length, headerStyle);

        const summaryRows = buildSummaryRows(scope.label, scope.rows, scope.missingLastRegistrationCount);
        const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
        summaryWs["!cols"] = [
          { wch: 28 },
          { wch: 18 },
          { wch: 18 },
          { wch: 18 },
          { wch: 20 },
          { wch: 14 },
        ];
        setWorksheetCellStyle(summaryWs, "A1", titleStyle);
        styleWorksheetRow(XLSX, summaryWs, 11, 6, headerStyle);

        XLSX.utils.book_append_sheet(workbook, detailWs, scope.detailName);
        XLSX.utils.book_append_sheet(workbook, summaryWs, scope.summaryName);
        XLSX.writeFile(workbook, `buyer-retention-table-${scope.slug}-${stamp}.xlsx`);
      });
      toastOpen(
        `Downloaded buyer retention files for ${formatNumber2(matchedRows.length)} users (${filterLabel} auctions).`,
        "success"
      );
    } catch (e) {
      toastOpen(e?.message || "Failed to download buyer retention table.", "error");
    } finally {
      setBuyerRetentionReportBusy(false);
    }
  };
  const getBuyerTier = (row) => row?.buyerTierManual ?? row?.buyerTier ?? row?.tier ?? row?.derived?.tier ?? "";
  const getCustomerType = (row) =>
    normalizeCustomerTypeKey(row?.customerTypeManual ?? row?.customerType ?? row?.derived?.customerType ?? "");
  const getRollingWin = (row, key) => row?.[key] ?? row?.rolling?.[key] ?? null;
  const getLotsWonFromWin = (win) => {
    const n = Number(win?.lotsWon ?? null);
    return Number.isFinite(n) ? n : null;
  };
  const getAvgHammerFromWin = (win) => {
    const n = Number(win?.avgHammer ?? null);
    return Number.isFinite(n) ? n : null;
  };
  const getRegsCount = (row) =>
    toAbsInt(
      row?.myAuctionsRegistrationCountValue ??
      row?.myAuctionsRegistrationCount ??
      row?.regs ??
      row?.derived?.registrationsCount ??
      0
    );
  const getBidsCount = (row) =>
    toAbsInt(
      row?.myAuctionsUniqueBidCountValue ??
      row?.myAuctionsUniqueBidCount ??
      row?.bids ??
      0
    );
  const getHibidBids90Count = (row) =>
    toAbsInt(
      row?.rolling?.win90?.totalBidsPlaced ??
      row?.win90?.totalBidsPlaced ??
      row?.derived?.hibidBids90 ??
      0
    );
  const getBidIntensity90 = (row) =>
    safeNum(
      row?.rolling?.win90?.bidIntensity ??
      row?.win90?.bidIntensity ??
      row?.derived?.bidIntensity90 ??
      0
    );
  const getBpi90 = (row) =>
    safeNum(
      row?.derived?.bpi90 ??
      row?.derived?.resellerPotentialScore90 ??
      row?.bpi?.d90 ??
      0
    );
  const getRegs12mCount = (row) =>
    toAbsInt(
      row?.totalRegistrationsLast12MonthsValue ??
      row?.totalRegistrationsLast12Months ??
      row?.regs12m ??
      0
    );
  const getEffectiveTier = (row) => {
    const manualTier = normalizeTierKey(row?.buyerTierManual || "");
    if (manualTier) return manualTier;
    return getRegsCount(row) === 1 ? "New" : getBuyerTier(row);
  };
  const accountManagerById = useMemo(() => {
    const map = new Map();
    (Array.isArray(accountManagers) ? accountManagers : []).forEach((m) => {
      const id = normalizeAccountManagerId(m?.id || "");
      if (!id || map.has(id)) return;
      map.set(id, m);
    });
    return map;
  }, [accountManagers]);
  const auctionAccountManagerOptions = useMemo(
    () =>
      (Array.isArray(accountManagers) ? accountManagers : [])
        .map((manager) => ({
          id: normalizeAccountManagerId(manager?.id || ""),
          label: accountManagerDisplayName(manager),
        }))
        .filter((row) => row.id),
    [accountManagers]
  );
  const selectedAuctionAccountManagerRows = useMemo(
    () =>
      sanitizeAuctionAccountManagerIds(auctionAccountManagerIds, accountManagers)
        .map((id) => {
          const manager = accountManagerById.get(id);
          return {
            id,
            label: manager ? accountManagerDisplayName(manager) : id,
          };
        }),
    [auctionAccountManagerIds, accountManagers, accountManagerById]
  );
  const getAccountManagerTierKeyForUser = (row) => normalizeTierKey(getEffectiveTier(row));
  const getResolvedAccountManagerIdForUser = (row = {}) => {
    const explicitId = normalizeAccountManagerId(row?.accountManagerId || "");
    if (explicitId) return explicitId;
    const tierKey = getAccountManagerTierKeyForUser(row);
    return normalizeAccountManagerId(accountManagerTierMap?.[tierKey] || "");
  };
  const getResolvedAccountManagerNameForUser = (row = {}) => {
    const managerId = getResolvedAccountManagerIdForUser(row);
    if (!managerId) return "";
    const manager = accountManagerById.get(managerId);
    if (!manager) return managerId;
    return accountManagerDisplayName(manager);
  };
  const getAccountManagerSelectOptionsForUser = (row = {}) => {
    const selectedManagerId = getResolvedAccountManagerIdForUser(row);
    const fromList = (Array.isArray(accountManagers) ? accountManagers : []).map((m) => ({
      id: normalizeAccountManagerId(m?.id || ""),
      label: accountManagerDisplayName(m),
    }));
    const baseOptions = fromList.filter((opt) => opt.id);
    if (!selectedManagerId) return baseOptions;
    if (baseOptions.some((opt) => opt.id === selectedManagerId)) return baseOptions;
    return [
      {
        id: selectedManagerId,
        label: getResolvedAccountManagerNameForUser(row) || selectedManagerId,
      },
      ...baseOptions,
    ];
  };
  const clearAuctionUserDetailsAutoSaveTimers = () => {
    const timers = auctionUserDetailsSaveTimersRef.current || {};
    Object.keys(timers).forEach((k) => {
      const timerId = timers[k];
      if (timerId) window.clearTimeout(timerId);
    });
    auctionUserDetailsSaveTimersRef.current = {};
  };
  const buildAuctionUserDetailsEditableForm = (row = {}, fallbackEmail = "") => {
    const email = normalize(row?.email || row?.id || row?.bidderEmail || row?.emailLower || fallbackEmail || "");
    const addressPrimary = String(row?.address || row?.billToAddress1 || "").trim();
    const addressCity = String(row?.billToCity || "").trim();
    const addressState = String(row?.billToState || "").trim();
    const addressZip = String(row?.zip || row?.billToZip || "").trim();
    const addressComposite = [addressPrimary, addressCity, addressState, addressZip].filter(Boolean).join(", ");
    const resolvedManagerId = getResolvedAccountManagerIdForUser(row);
    const defaultPickupLocationName = String(row?.defaultPickupLocationName || "").trim();
    const defaultPickupLocationMeta = locationMetadataByName.get(normalize(defaultPickupLocationName)) || null;
    return {
      email,
      bidderName: String(row?.bidderName || "").trim(),
      username: String(row?.username || "").trim(),
      company: String(row?.company || row?.companyName || "").trim(),
      phone: String(row?.phone || "").trim(),
      address: addressComposite || addressPrimary || "",
      defaultPickupLocationId: String(row?.defaultPickupLocationId || defaultPickupLocationMeta?.id || "").trim(),
      defaultPickupLocationName: String(defaultPickupLocationMeta?.name || defaultPickupLocationName).trim(),
      defaultPickupLocationAddress: String(
        row?.defaultPickupLocationAddress || defaultPickupLocationMeta?.address || ""
      ).trim(),
      namedUrls: normalizeNamedUrlsInput(row?.namedUrls || row?.profileLinks || row?.socialLinks || []),
      customerType: normalizeCustomerTypeKey(row?.customerTypeManual ?? row?.customerType ?? row?.derived?.customerType ?? ""),
      tier: normalizeTierKey(row?.buyerTierManual ?? row?.buyerTier ?? row?.tier ?? row?.derived?.tier ?? ""),
      accountManagerId: normalizeAccountManagerId(row?.accountManagerId || resolvedManagerId || ""),
    };
  };
  const applyAuctionUserDetailsPatchLocally = (emailLower = "", patch = {}, returned = {}) => {
    const targetEmail = normalize(emailLower);
    if (!isEmail(targetEmail)) return;
    const returnedSafe = returned && typeof returned === "object"
      ? Object.fromEntries(
        Object.entries(returned).filter(([k]) => !["ok", "error", "intel"].includes(String(k || "")))
      )
      : {};
    const mergedPatch = {
      ...patch,
      ...returnedSafe,
      updatedAt: new Date().toISOString(),
    };
    setAuctionUserDetailsData((prev) => {
      const existing = prev && typeof prev === "object" ? prev : {};
      return { ...existing, ...mergedPatch, email: targetEmail, id: targetEmail };
    });
    setAuctionUserDetailsSourceReg((prev) => {
      const existing = prev && typeof prev === "object" ? prev : {};
      const existingEmail = normalize(existing?.bidderEmail || existing?.email || existing?.emailLower);
      if (existingEmail && existingEmail !== targetEmail) return prev;
      return { ...existing, ...mergedPatch, bidderEmail: targetEmail, email: targetEmail, emailLower: targetEmail };
    });
    setUserRows((prev) =>
      (prev || []).map((u) => {
        const em = normalize(u?.email || u?.id);
        if (em !== targetEmail) return u;
        return { ...u, ...mergedPatch, email: targetEmail, id: targetEmail };
      })
    );
    setAuctionRegs((prev) =>
      (prev || []).map((r) => {
        const em = normalize(r?.bidderEmail || r?.email);
        if (em !== targetEmail) return r;
        return { ...r, ...mergedPatch, bidderEmail: targetEmail, email: targetEmail, emailLower: targetEmail };
      })
    );
  };
  const saveAuctionUserDetailsField = async (fieldKey, nextValueRaw) => {
    const emailLower = normalize(auctionUserDetailsEmail || auctionUserDetailsForm?.email || "");
    if (!isEmail(emailLower)) return;

    let updates = {};
    if (fieldKey === "bidderName") {
      updates = { bidderName: String(nextValueRaw || "").trim() };
    } else if (fieldKey === "username") {
      updates = { username: String(nextValueRaw || "").trim() };
    } else if (fieldKey === "company") {
      updates = { company: String(nextValueRaw || "").trim() };
    } else if (fieldKey === "phone") {
      updates = { phone: String(nextValueRaw || "").trim() };
    } else if (fieldKey === "address") {
      updates = { address: String(nextValueRaw || "").trim() };
    } else if (fieldKey === "namedUrls") {
      updates = { namedUrls: normalizeNamedUrlsInput(nextValueRaw) };
    } else if (fieldKey === "customerType") {
      const normalizedType = normalizeCustomerTypeKey(nextValueRaw || "");
      updates = {
        customerType: normalizedType || null,
        customerTypeManual: normalizedType || null,
      };
    } else if (fieldKey === "tier") {
      const normalizedTier = normalizeTierKey(nextValueRaw || "");
      updates = {
        buyerTierManual: normalizedTier || null,
        buyerTier: normalizedTier || null,
        tier: normalizedTier || null,
      };
    } else if (fieldKey === "accountManagerId") {
      const managerId = normalizeAccountManagerId(nextValueRaw || "");
      updates = { accountManagerId: managerId || null };
    } else if (fieldKey === "defaultPickupLocationName") {
      const safeLocationName = String(nextValueRaw || "").trim();
      const locationMeta = locationMetadataByName.get(normalize(safeLocationName)) || null;
      updates = {
        defaultPickupLocationId: String(locationMeta?.id || "").trim(),
        defaultPickupLocationName: String(locationMeta?.name || safeLocationName).trim(),
        defaultPickupLocationAddress: String(locationMeta?.address || "").trim(),
      };
    }

    if (!Object.keys(updates).length) return;
    setAuctionUserDetailsSaveError("");
    setAuctionUserDetailsSavingByField((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.usersUpdate}`, {
        method: "POST",
        body: JSON.stringify({ email: emailLower, updates }),
      });
      if (handleNeedsAuth(res, data)) throw new Error("Authorization required.");
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      applyAuctionUserDetailsPatchLocally(emailLower, updates, data);
      applyUserPatchLocally(emailLower, (user) => ({
        ...user,
        ...updates,
        ...(data && typeof data === "object" ? data : {}),
        updatedAt: new Date().toISOString(),
      }));
      setAuctionUserDetailsForm((prev) => {
        if (!prev || normalize(prev?.email) !== emailLower) return prev;
        return {
          ...prev,
          ...(fieldKey === "customerType" ? { customerType: normalizeCustomerTypeKey(nextValueRaw || "") } : {}),
          ...(fieldKey === "tier" ? { tier: normalizeTierKey(nextValueRaw || "") } : {}),
          ...(fieldKey === "accountManagerId" ? { accountManagerId: normalizeAccountManagerId(nextValueRaw || "") } : {}),
          ...(fieldKey === "defaultPickupLocationName"
            ? (() => {
              const safeLocationName = String(nextValueRaw || "").trim();
              const locationMeta = locationMetadataByName.get(normalize(safeLocationName)) || null;
              return {
                defaultPickupLocationId: String(locationMeta?.id || "").trim(),
                defaultPickupLocationName: String(locationMeta?.name || safeLocationName).trim(),
                defaultPickupLocationAddress: String(locationMeta?.address || "").trim(),
              };
            })()
            : {}),
          ...(fieldKey === "bidderName" ? { bidderName: String(nextValueRaw || "").trim() } : {}),
          ...(fieldKey === "username" ? { username: String(nextValueRaw || "").trim() } : {}),
          ...(fieldKey === "company" ? { company: String(nextValueRaw || "").trim() } : {}),
          ...(fieldKey === "phone" ? { phone: String(nextValueRaw || "").trim() } : {}),
          ...(fieldKey === "address" ? { address: String(nextValueRaw || "").trim() } : {}),
          ...(fieldKey === "namedUrls" ? { namedUrls: normalizeNamedUrlsInput(nextValueRaw) } : {}),
        };
      });
    } catch (e) {
      setAuctionUserDetailsSaveError(e?.message || "Failed to auto-save user field.");
    } finally {
      setAuctionUserDetailsSavingByField((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    }
  };
  const setAuctionUserDetailsField = (fieldKey, nextValueRaw, { saveNow = false } = {}) => {
    const nextValue = fieldKey === "namedUrls"
      ? coerceNamedUrlDraftRows(nextValueRaw)
      : nextValueRaw == null
        ? ""
        : String(nextValueRaw);
    setAuctionUserDetailsForm((prev) => {
      const base = prev && typeof prev === "object" ? prev : buildAuctionUserDetailsEditableForm({}, auctionUserDetailsEmail);
      return { ...base, [fieldKey]: nextValue };
    });
    if (saveNow) {
      saveAuctionUserDetailsField(fieldKey, nextValue);
    }
  };
  const flushAuctionUserDetailsFieldSave = (fieldKey, valueOverride) => {
    const timers = auctionUserDetailsSaveTimersRef.current || {};
    if (timers[fieldKey]) {
      window.clearTimeout(timers[fieldKey]);
      delete timers[fieldKey];
      auctionUserDetailsSaveTimersRef.current = timers;
    }
    const value = valueOverride == null ? auctionUserDetailsForm?.[fieldKey] : valueOverride;
    saveAuctionUserDetailsField(fieldKey, value);
  };
  useEffect(() => () => clearAuctionUserDetailsAutoSaveTimers(), []);

  // Tier chip color style helper
  const tierChipSx = (tierRaw) => {
    const t = String(tierRaw || "").trim().toLowerCase();
    const base = {
      borderRadius: 999,
      fontWeight: 900,
      color: "#fff",
      border: "1px solid rgba(15,23,42,0.10)",
    };
    if (t === "new") {
      return { ...base, background: "linear-gradient(90deg,#0ea5e9,#38bdf8)" };
    }
    if (t === "elite") {
      return { ...base, background: "linear-gradient(90deg,#f59e0b,#fbbf24)" };
    }
    if (t === "power") {
      return { ...base, background: "linear-gradient(90deg,#16a34a,#22c55e)" };
    }
    if (t === "emerging") {
      return { ...base, background: "linear-gradient(90deg,#2563eb,#60a5fa)" };
    }
    if (t === "casual") {
      return { ...base, background: "linear-gradient(90deg,#ef4444,#fca5a5)" };
    }

    return {
      ...base,
      color: "#111827",
      background: "linear-gradient(90deg,rgba(99,102,241,0.12),rgba(99,102,241,0.06))",
      border: "1px solid rgba(51,57,153,0.18)",
    };
  };

  const openEdit = (row) => {
    const r = row || {};
    const defaultPickupLocationName = String(r?.defaultPickupLocationName || "").trim();
    const defaultPickupLocationMeta = locationMetadataByName.get(normalize(defaultPickupLocationName)) || null;
    setEditErr("");
    setEditForm({
      bidderName: r.bidderName || "",
      username: r.username || "",
      company: r.company || "",
      phone: r.phone || "",
      address: r.address || "",
      defaultPickupLocationId: String(r?.defaultPickupLocationId || defaultPickupLocationMeta?.id || "").trim(),
      defaultPickupLocationName: defaultPickupLocationMeta?.name || defaultPickupLocationName,
      defaultPickupLocationAddress: String(
        r?.defaultPickupLocationAddress || defaultPickupLocationMeta?.address || ""
      ).trim(),
      namedUrls: normalizeNamedUrlsInput(r.namedUrls || r.profileLinks || r.socialLinks || []),
      myPrivateNotes: r.myPrivateNotes || "",
      publicNotes: r.publicNotes || "",
      bidPermission: r.bidPermission || "",
      auctionStatus: r.auctionStatus || "",
      lastAuctionName: r.lastAuctionName || "",
    });
    setMenuRow(row);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    setEditErr("");
    try {
      const email = normalize(menuRow?.email || menuRow?.id);
      if (!isEmail(email)) throw new Error("Missing/invalid email.");
      const { res, data } = await apiFetchJson(`${API}${ROUTES.usersUpdate}`, {
        method: "POST",
        body: JSON.stringify({ email, updates: { ...editForm } }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      applyUserPatchLocally(email, (user) => ({
        ...user,
        ...editForm,
        ...(data && typeof data === "object" ? data : {}),
        updatedAt: new Date().toISOString(),
      }));
      setEditOpen(false);
      toastOpen("Saved.", "success");
    } catch (e) {
      setEditErr(e?.message || "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };

  const updateUserPickupDefaultsForUser = useCallback(
    async (row, { defaultPickupLocationName } = {}) => {
      const email = normalize(row?.email || row?.id);
      if (!isEmail(email)) throw new Error("Missing/invalid email.");

      const updates = {};
      if (defaultPickupLocationName !== undefined) {
        const safeLocationName = String(defaultPickupLocationName || "").trim();
        const locationMeta = locationMetadataByName.get(normalize(safeLocationName)) || null;
        updates.defaultPickupLocationId = String(locationMeta?.id || "").trim();
        updates.defaultPickupLocationName = String(locationMeta?.name || safeLocationName).trim();
        updates.defaultPickupLocationAddress = String(locationMeta?.address || "").trim();
      }
      if (!Object.keys(updates).length) return;

      try {
        const { res, data } = await apiFetchJson(`${API}${ROUTES.usersUpdate}`, {
          method: "POST",
          body: JSON.stringify({ email, updates }),
        });
        if (handleNeedsAuth(res, data)) throw new Error("Authentication required.");
        if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);

        applyUserPatchLocally(email, (user) => ({
          ...user,
          ...updates,
          updatedAt: new Date().toISOString(),
        }));
      } catch (error) {
        toastOpen(error?.message || "Failed to update pickup defaults", "error");
        throw error;
      }
    },
    [apiFetchJson, applyUserPatchLocally, handleNeedsAuth, isEmail, locationMetadataByName, toastOpen]
  );

  const sortedLotsModalRows = useMemo(() => {
    const base = Array.isArray(lotsData?.lots) ? lotsData.lots : [];
    const dir = lotsModalSortDir === "asc" ? 1 : -1;
    const getVal = (lot) => {
      const lotNum = Number(lot?.lot);
      switch (lotsModalSortKey) {
        case "title":
          return normalize(lot?.listernautTitle || lot?.title);
        case "highBid":
          return safeNum(lot?.highBid || 0);
        case "maxBid":
          return safeNum(lot?.maxBid || 0);
        case "bids":
          return toAbsInt(lot?.bids || 0);
        case "views":
          return toAbsInt(lot?.views || 0);
        case "watches":
          return toAbsInt(lot?.watches || 0);
        case "consignor":
          return normalize(lot?.listernautConsignorName || lot?.consignor || lot?.listernautSellerCode);
        case "lot":
        default:
          return Number.isFinite(lotNum) ? lotNum : normalize(lot?.lot);
      }
    };
    return [...base].sort((a, b) => compareSortValues(getVal(a), getVal(b)) * dir);
  }, [lotsData, lotsModalSortKey, lotsModalSortDir]);
  const deleteRow = async (row) => {
    const email = normalize(row?.email || row?.id);
    if (!isEmail(email)) return;
    if (!window.confirm(`Delete HiBid record for ${email}?`)) return;
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.usersDelete}`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      if (handleNeedsAuth(res, data)) return;
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      setUserRows((prev) => (prev || []).filter((u) => normalize(u?.email || u?.id) !== email));
      invalidateUsersCache();
      toastOpen("Deleted.", "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to delete", "error");
    }
  };

  const copyText = async (txt) => {
    try {
      await navigator.clipboard.writeText(String(txt || ""));
      toastOpen("Copied.", "success");
    } catch {
      toastOpen("Copy failed.", "error");
    }
  };

  useEffect(() => {
    const filtersKey = JSON.stringify({
      aregSearch,
      aregShipperFilter,
      aregNewUserFilter,
      aregPickupFilter,
      aregShippedFilter,
      aregDeliveryFilter,
      aregConsignorFilter,
      aregPickupLocationFilter,
      aregDeliveryLocationFilter,
      aregPickupDateStart,
      aregPickupDateEnd,
      chargebackEmailFulfillmentFilter,
      chargebackEmailAlreadySentFilter,
      chargebackEmailWonLotFilter,
    });
    if (!registrationFiltersKeyRef.current) {
      registrationFiltersKeyRef.current = filtersKey;
      return;
    }
    if (registrationFiltersKeyRef.current === filtersKey) return;
    registrationFiltersKeyRef.current = filtersKey;
    setAregSelected((prev) => {
      if (!(prev instanceof Set) || prev.size === 0) return prev;
      return new Set();
    });
  }, [
    aregSearch,
    aregShipperFilter,
    aregNewUserFilter,
    aregPickupFilter,
    aregShippedFilter,
    aregDeliveryFilter,
    aregConsignorFilter,
    aregPickupLocationFilter,
    aregDeliveryLocationFilter,
    aregPickupDateStart,
    aregPickupDateEnd,
    chargebackEmailFulfillmentFilter,
    chargebackEmailAlreadySentFilter,
    chargebackEmailWonLotFilter,
  ]);

  const buildRegSearchHaystack = (r) =>
    normalize(
      [
        r?.bidderName,
        r?.username,
        r?.bidderEmail,
        r?.email,
        r?.company,
        isShippingRequested(r) ? "shipping yes" : "shipping no",
        r?.phone,
        r?.status,
        fmtDate(r?.registeredAt || r?.emailSentAt),
      ]
        .filter(Boolean)
        .join(" | ")
    );

  const getRegistrationShippingPolicy = (r = {}) => getRegistrationShippingPolicyObject(r);
  const isChargebackEmailSent = (r = {}) => isChargebackEmailSentValue(r);
  const getChargebackSignDetails = (r = {}) => {
    const policy = getRegistrationShippingPolicy(r);
    const pickupSig = getRegistrationPickupSignatureObject(r);
    const shippingSigned =
      policy?.optIn === true ||
      policy?.optedIn === true ||
      safeToMillis(policy?.signedAtMs) > 0 ||
      safeToMillis(policy?.signedAt) > 0;
    const pickupSigned = pickupSig?.signed === true || safeToMillis(pickupSig?.signedAtMs) > 0;
    const usePickup = !shippingSigned && pickupSigned;
    const signed = shippingSigned || pickupSigned;
    const sourceObj = usePickup ? pickupSig : policy;
    const signedAtMs =
      safeToMillis(sourceObj?.signedAtMs) ??
      safeToMillis(sourceObj?.signedAt) ??
      safeToMillis(sourceObj?.lastActionAtMs) ??
      safeToMillis(sourceObj?.lastActionAt) ??
      null;
    return {
      signed,
      signedAtMs,
      signatureName: String(sourceObj?.signatureName || "").trim(),
      signatureDataUrl: String(sourceObj?.signatureDataUrl || "").trim(),
      lastAction: String(sourceObj?.lastAction || "").trim(),
      source: String(sourceObj?.source || "").trim(),
      optOutReason: String(sourceObj?.optOutReason || "").trim(),
      version: sourceObj?.version ?? null,
      signMode: usePickup ? "pickup" : "shipping",
    };
  };
  const {
    userRegsOpen,
    userRegsBusy,
    userRegsErr,
    setUserRegsRows,
    userRegsUser,
    userRegsSortKey,
    setUserRegsSortKey,
    userRegsSortDir,
    setUserRegsSortDir,
    userRegsModalView,
    setUserRegsModalView,
    userLotsSortKey,
    setUserLotsSortKey,
    userLotsSortDir,
    setUserLotsSortDir,
    userLotsBusy,
    userLotsLoadingMore,
    userLotsErr,
    setUserLotsErr,
    userLotsHasMore,
    userLotsSearchInput,
    setUserLotsSearchInput,
    userLotsExportBusy,
    setUserLotsExportBusy,
    userLotsContainerRef,
    userRegsTotals,
    userLotsAuctionNameMap,
    sortedUserRegsRows,
    sortedUserLotsRows,
    userRegsChargebackSummary,
    openUserRegistrationsModal,
    closeUserRegistrationsModal,
    fetchUserAuctions,
    fetchUserLotsPage,
    runUserLotsSearch,
    onUserLotsScroll,
  } = useUserRegistrationsLotsModal({
    apiBaseUrl: API,
    routes: ROUTES,
    apiFetchJson,
    handleNeedsAuth,
    readCache,
    writeCache,
    userAuctionsCacheKey,
    auctions,
    userLotsPageSize: USER_LOTS_PAGE_SIZE,
    normalize,
    isEmail,
    toAbsInt,
    safeNum,
    safeToMillis,
    compareSortValues,
    getDisplayBidderName,
    getDisplayUsername,
    getRegsCount,
    getBidsCount,
    isChargebackEmailSentValue,
    isRegistrationSignedOptIn,
    getChargebackSignDetails,
  });
  const openChargebackSignModal = (row = null) => {
    setChargebackSignRow(row);
    setChargebackSignOpen(true);
  };
  const getChargebackSignatureCanvasContext = () => {
    const canvas = chargebackSignatureCanvasRef.current;
    if (!canvas) return { canvas: null, ctx: null };
    const ctx = canvas.getContext("2d");
    if (!ctx) return { canvas: null, ctx: null };
    return { canvas, ctx };
  };
  const resetChargebackSignatureCanvas = () => {
    const { canvas, ctx } = getChargebackSignatureCanvasContext();
    if (!canvas || !ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    chargebackSignatureDrawingRef.current = false;
    setChargebackSignSignatureHasInk(false);
  };
  const drawChargebackSignatureImage = (dataUrl) => {
    const src = String(dataUrl || "").trim();
    if (!src) {
      resetChargebackSignatureCanvas();
      return;
    }
    const { canvas, ctx } = getChargebackSignatureCanvasContext();
    if (!canvas || !ctx) return;
    const img = new Image();
    img.onload = () => {
      resetChargebackSignatureCanvas();
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setChargebackSignSignatureHasInk(true);
    };
    img.onerror = () => {
      resetChargebackSignatureCanvas();
    };
    img.src = src;
  };
  const getChargebackSignaturePointerPos = (evt) => {
    const canvas = chargebackSignatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const touch = evt?.touches?.[0] || evt?.changedTouches?.[0] || null;
    const clientX = touch ? touch.clientX : evt.clientX;
    const clientY = touch ? touch.clientY : evt.clientY;
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };
  const startChargebackSignatureDraw = (evt) => {
    const { ctx } = getChargebackSignatureCanvasContext();
    if (!ctx) return;
    const p = getChargebackSignaturePointerPos(evt);
    if (!p) return;
    chargebackSignatureDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    if (evt.cancelable) evt.preventDefault();
  };
  const moveChargebackSignatureDraw = (evt) => {
    if (!chargebackSignatureDrawingRef.current) return;
    const { ctx } = getChargebackSignatureCanvasContext();
    if (!ctx) return;
    const p = getChargebackSignaturePointerPos(evt);
    if (!p) return;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setChargebackSignSignatureHasInk((prev) => prev || true);
    if (evt.cancelable) evt.preventDefault();
  };
  const endChargebackSignatureDraw = () => {
    chargebackSignatureDrawingRef.current = false;
  };
  const captureChargebackSignatureDataUrl = () => {
    const canvas = chargebackSignatureCanvasRef.current;
    if (!canvas || !chargebackSignSignatureHasInk) return "";
    try {
      return canvas.toDataURL("image/png");
    } catch {
      return "";
    }
  };
  useEffect(() => {
    resetChargebackSignatureCanvas();
  }, []);
  useEffect(() => {
    const row = chargebackSignRow || {};
    const details = getChargebackSignDetails(row);
    const seedName = String(details?.signatureName || getDisplayBidderName(row) || row?.username || "").trim();
    setChargebackSignSignatureName(seedName);
    setChargebackSignError("");
    setChargebackSignSuccess("");
    if (!chargebackSignOpen) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      drawChargebackSignatureImage(details?.signatureDataUrl || "");
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [chargebackSignRow, chargebackSignOpen]);

  const isAuctionRegistrationNewUser = (r) => {
    return isRegistrationMarkedNewUser(r);
  };

  const filteredSortedAuctionRegs = useMemo(() => {
    const q = normalize(aregSearch);
    const base = Array.isArray(auctionRegs) ? auctionRegs : [];
    const searchFiltered = !q ? base : base.filter((r) => buildRegSearchHaystack(r).includes(q));
    const fulfillmentFiltered = searchFiltered.filter((r) => {
      const shippingRequested = isShippingRequested(r);
      const delivery = getRegistrationDelivery(r);
      const mode = getRegistrationFulfillmentMode({ row: r, shippingRequested, delivery });
      if (aregFulfillmentFilter === "shipping") return mode === "shipping";
      if (aregFulfillmentFilter === "pickup") return mode === "pickup";
      if (aregFulfillmentFilter === "delivery") return mode === "delivery";
      if (aregShipperFilter === "shipping_requested") return shippingRequested;
      if (aregShipperFilter === "pickup") return !shippingRequested;
      return true;
    });
    const newUserFiltered = fulfillmentFiltered.filter((r) => {
      if (aregNewUserFilter === "new") return isAuctionRegistrationNewUser(r);
      return true;
    });
    const pickupFiltered = newUserFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "pickup") return true;
      if (aregPickupFilter === "all") return true;
      if (isShippingRequested(r)) return false;
      const emailLower = normalize(r?.bidderEmail || r?.email);
      const details = getLotPickupCounts({
        lotsCount: r?.lotsCount ?? lotsAgg[emailLower]?.lotsCount,
        pickedUpLotsCount: r?.pickedUpLotsCount ?? lotsAgg[emailLower]?.pickedUpLotsCount,
        pickedUp: r?.pickedUp ?? lotsAgg[emailLower]?.pickedUp,
      });
      return matchesFulfillmentProgressFilter(
        {
          totalCount: details.totalLotsCount,
          doneCount: details.pickedUpLotsCount,
          complete: details.pickedUp,
        },
        aregPickupFilter
      );
    });
    const shippedFiltered = pickupFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "shipping") return true;
      if (aregShippedFilter === "all") return true;
      if (!isShippingRequested(r)) return false;
      const emailLower = normalize(r?.bidderEmail || r?.email);
      const details = getLotShippedCounts({
        lotsCount: r?.lotsCount ?? lotsAgg[emailLower]?.lotsCount,
        shippedLotsCount: r?.shippedLotsCount ?? lotsAgg[emailLower]?.shippedLotsCount,
        shipped: r?.shipped ?? lotsAgg[emailLower]?.shipped,
      });
      return matchesFulfillmentProgressFilter(
        {
          totalCount: details.totalLotsCount,
          doneCount: details.shippedLotsCount,
          complete: details.shipped,
        },
        aregShippedFilter
      );
    });
    const consignorFiltered = shippedFiltered.filter((r) => {
      if (aregConsignorFilter === "all") return true;
      if (aregConsignorFilter === AUCTION_CONSIGNOR_FILTER_UNASSIGNED) {
        return getRegistrationConsignorIds(r).length === 0;
      }
      const target = normalizeConsignorProfileId(aregConsignorFilter);
      if (!target) return true;
      const rowConsignorIds = getRegistrationConsignorIds(r).map((value) => normalizeConsignorProfileId(value));
      return rowConsignorIds.includes(target);
    });
    const pickupLocationFiltered = consignorFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "pickup") return true;
      if (aregPickupLocationFilter === "all") return true;
      const appointment = getRegistrationPickupAppointment(r);
      const rowLocation = normalize(String(appointment?.location || "").trim());
      if (!rowLocation) return false;
      return rowLocation === normalize(String(aregPickupLocationFilter || "").trim());
    });
    const pickupSelectionFiltered = pickupLocationFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "pickup") return true;
      if (aregPickupSelectionFilter === "all") return true;
      return getPickupAppointmentSelectionKey(getRegistrationPickupAppointment(r)) === aregPickupSelectionFilter;
    });
    const pickupRangeStartMs = fromInputDateTimeLocalToMs(aregPickupDateStart);
    const pickupRangeEndMs = fromInputDateTimeLocalToMs(aregPickupDateEnd);
    const pickupDateFiltered = pickupSelectionFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "pickup") return true;
      if (!pickupRangeStartMs && !pickupRangeEndMs) return true;
      return pickupAppointmentMatchesDateRange(getRegistrationPickupAppointment(r), pickupRangeStartMs, pickupRangeEndMs);
    });
    const deliveryFiltered = pickupDateFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "delivery") return true;
      if (aregDeliveryFilter === "all") return true;
      const delivery = getRegistrationDelivery(r);
      if (aregDeliveryFilter === "delivered") return delivery?.delivered === true;
      if (aregDeliveryFilter === "not_delivered") return delivery?.delivered !== true;
      return true;
    });
    const deliveryLocationFiltered = deliveryFiltered.filter((r) => {
      if (aregFulfillmentFilter !== "delivery") return true;
      if (aregDeliveryLocationFilter === "all") return true;
      const delivery = getRegistrationDelivery(r);
      const rowLocation = normalize(String(delivery?.location || "").trim());
      if (!rowLocation) return false;
      return rowLocation === normalize(String(aregDeliveryLocationFilter || "").trim());
    });
    const dir = aregSortDir === "asc" ? 1 : -1;
    const getRegisteredMs = (r) => safeToMillis(r?.registeredAt || r?.emailSentAt) || 0;
    const getVal = (r) => {
      const emailLower = normalize(r?.bidderEmail || r?.email);
      const { firstName, lastName } = getFirstLastNameParts(getDisplayBidderName(r));
      switch (aregSortKey) {
        case "bidderName":
          return normalize(firstName || r?.bidderName);
        case "bidderLastName":
          return normalize(lastName || r?.bidderName);
        case "email":
          return normalize(r?.bidderEmail || r?.email);
        case "lotsCount":
          return safeNum(r?.lotsCount || lotsAgg[emailLower]?.lotsCount || 0);
        case "totalHighBid":
          return safeNum(r?.totalHighBid || lotsAgg[emailLower]?.totalHighBid || 0);
        case "shipping":
          return isShippingRequested(r) ? 1 : 0;
        case "isNewUser":
          return isAuctionRegistrationNewUser(r) ? 1 : 0;
        case "chargebackEmailSent":
          return isChargebackEmailSentValue(r) ? 1 : 0;
        case "signed":
          return isRegistrationSignedOptIn(r) ? 1 : 0;
        case "registeredAt":
        default:
          return getRegisteredMs(r);
      }
    };
    const sorted = [...deliveryLocationFiltered].sort((a, b) => {
      return compareSortValues(getVal(a), getVal(b)) * dir;
    });
    const floorRows = [];
    const otherRows = [];
    for (const r of sorted) {
      const em = normalize(r?.bidderEmail || r?.email);
      if (em === FLOOR_BIDDER_EMAIL) floorRows.push(r);
      else otherRows.push(r);
    }
    return [...floorRows, ...otherRows];
  }, [
    auctionRegs,
    aregSearch,
    aregFulfillmentFilter,
    aregShipperFilter,
    aregNewUserFilter,
    aregPickupFilter,
    aregShippedFilter,
    aregDeliveryFilter,
    aregConsignorFilter,
    aregPickupLocationFilter,
    aregDeliveryLocationFilter,
    aregPickupSelectionFilter,
    aregPickupDateStart,
    aregPickupDateEnd,
    aregSortKey,
    aregSortDir,
    lotsAgg,
  ]);

  const toggleSelectReg = (regRow) => {
    const em = normalize(regRow?.bidderEmail || regRow?.email);
    if (!isEmail(em)) return;
    setAregSelected((prev) => {
      const next = new Set(prev || []);
      if (next.has(em)) next.delete(em);
      else next.add(em);
      return next;
    });
  };

  const toggleSelectAllRegs = () => {
    const visible = chargebackFilteredAuctionRegs.map((r) => normalize(r?.bidderEmail || r?.email)).filter((em) => isEmail(em));
    if (!visible.length) return;
    setAregSelected((prev) => {
      const next = new Set(prev || []);
      const allSelected = visible.every((em) => next.has(em));
      if (allSelected) visible.forEach((em) => next.delete(em));
      else visible.forEach((em) => next.add(em));
      return next;
    });
  };

  const getSelectedRegRows = () => {
    const set = aregSelected || new Set();
    return (chargebackFilteredAuctionRegs || []).filter((r) => set.has(normalize(r?.bidderEmail || r?.email)));
  };
  const openRegistrationFulfillmentDetails = (row = null) => {
    if (!row) return;
    setRegistrationFulfillmentDetails({ open: true, row });
  };
  const closeRegistrationFulfillmentDetails = () => {
    setRegistrationFulfillmentDetails({ open: false, row: null });
  };

  const updateRegistrationShipping = async ({ auctionId, email, shipping }) => {
    if (!orgReady) return;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    if (!aId || !isEmail(em)) return;
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.shippingUpdate}`, {
        method: "POST",
        body: JSON.stringify({ auctionId: aId, email: em, shipping: !!shipping }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);
      setAuctionRegs((prev) =>
        (prev || []).map((r) => {
          const rem = normalize(r?.bidderEmail || r?.email);
          if (rem !== em) return r;
          return { ...r, shipping: !!shipping };
        })
      );
      setLotsUser((prev) => {
        const pem = normalize(prev?.bidderEmail || prev?.email);
        if (pem !== em) return prev;
        return { ...(prev || {}), shipping: !!shipping };
      });
      invalidateAuctionRegsCache(aId);
    } catch (e) {
      toastOpen(e?.message || "Failed to update shipping", "error");
    }
  };
  const updateRegistrationPickupAppointment = async ({ auctionId, email, location, appointmentLocal, appointmentAtMs, additionalInfo }) => {
    if (!orgReady) return null;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    const nextLocation = String(location || "").trim();
    const nextAppointmentAtMs = Number(appointmentAtMs || 0) || fromInputDateTimeLocalToMs(appointmentLocal);
    const hasAdditionalInfo = additionalInfo !== undefined;
    if (!aId || !isEmail(em)) return null;
    if (!nextLocation) {
      toastOpen("Pickup location is required.", "error");
      return null;
    }
    if (!nextAppointmentAtMs) {
      toastOpen("Pickup date and time are required.", "error");
      return null;
    }
    setAregPickupSavingByEmail((prev) => ({ ...prev, [em]: true }));
    try {
      const body = {
        auctionId: aId,
        email: em,
        location: nextLocation,
        appointmentAtMs: nextAppointmentAtMs,
      };
      if (hasAdditionalInfo) body.additionalInfo = String(additionalInfo || "");
      const { res, data } = await apiFetchJson(`${API}${ROUTES.pickupAppointmentUpdate}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);
      const nextAppointment = data?.pickupAppointment || {};
      setAuctionRegs((prev) =>
        (prev || []).map((r) => {
          const rem = normalize(r?.bidderEmail || r?.email);
          if (rem !== em) return r;
          return {
            ...r,
            pickupAppointment: nextAppointment,
            pickupAppointmentLocation: String(nextAppointment?.location || "").trim(),
            pickupAppointmentLocationKey: normalize(String(nextAppointment?.location || "").trim()),
            pickupAppointmentSlotId: String(nextAppointment?.slotId || "").trim(),
            pickupAppointmentStartAtMs: safeToMillis(nextAppointment?.startAtMs) || 0,
            pickupAppointmentEndAtMs: safeToMillis(nextAppointment?.endAtMs) || 0,
            pickupAppointmentAtMs: safeToMillis(nextAppointment?.appointmentAtMs) || 0,
            pickupAppointmentDateKey: String(nextAppointment?.appointmentDateKey || "").trim(),
            pickupAppointmentLabel: String(nextAppointment?.label || "").trim(),
            pickupAppointmentAdditionalInfo: String(nextAppointment?.additionalInfo || "").trim(),
            pickupAppointmentSelectedAtMs: safeToMillis(nextAppointment?.selectedAtMs) || 0,
          };
        })
      );
      setAregPickupDraftByEmail((prev) => ({
        ...prev,
        [em]: {
          location: String(nextAppointment?.location || nextLocation || "").trim(),
          appointmentDateKey: String(
            nextAppointment?.appointmentDateKey ||
            localDateKeyFromMs(nextAppointment?.appointmentAtMs || nextAppointmentAtMs || 0)
          ).trim(),
          appointmentAtMs: Number(nextAppointment?.appointmentAtMs || nextAppointmentAtMs || 0) || 0,
          additionalInfo: String(
            nextAppointment?.additionalInfo || (hasAdditionalInfo ? additionalInfo : "")
          ).trim(),
        },
      }));
      if (data?.pickupConfirmationEmailSent) {
        toastOpen("Pickup confirmation email sent.", "success");
      } else if (data?.pickupConfirmationEmailWarning) {
        toastOpen(`Pickup saved, but confirmation email was not sent: ${data.pickupConfirmationEmailWarning}`, "info");
      }
      invalidateAuctionRegsCache(aId);
      return data;
    } catch (e) {
      toastOpen(e?.message || "Failed to update pickup appointment", "error");
      return null;
    } finally {
      setAregPickupSavingByEmail((prev) => ({ ...prev, [em]: false }));
    }
  };
  const canSaveRegistrationPickupDraft = (draft = {}) => {
    const nextLocation = String(draft?.location || "").trim();
    const nextAppointmentAtMs = Number(draft?.appointmentAtMs || 0) || 0;
    return !!nextLocation && nextAppointmentAtMs > 0;
  };
  const openRegistrationPickupEditor = (row) => {
    const em = normalize(row?.bidderEmail || row?.email);
    if (!isEmail(em)) return;
    const pickupAppointment = getRegistrationPickupAppointment(row);
    setAregPickupDraftByEmail((prev) => ({
      ...prev,
      [em]: {
        location: String(pickupAppointment?.location || "").trim(),
        appointmentDateKey: String(
          pickupAppointment?.appointmentDateKey ||
          localDateKeyFromMs(pickupAppointment?.appointmentAtMs || pickupAppointment?.startAtMs || 0)
        ).trim(),
        appointmentAtMs: Number(pickupAppointment?.appointmentAtMs || 0) || 0,
        additionalInfo: String(pickupAppointment?.additionalInfo || "").trim(),
      },
    }));
    setAregPickupEditByEmail((prev) => ({ ...prev, [em]: true }));
  };
  const closeRegistrationPickupEditor = (email) => {
    const em = normalize(email);
    if (!em) return;
    setAregPickupEditByEmail((prev) => {
      const next = { ...prev };
      delete next[em];
      return next;
    });
    setAregPickupDraftByEmail((prev) => {
      const next = { ...prev };
      delete next[em];
      return next;
    });
  };
  const saveRegistrationPickupDraft = async (row, { closeEditor = false, draftOverride = null } = {}) => {
    const em = normalize(row?.bidderEmail || row?.email);
    const aId = String(selectedAuctionId || row?.auctionId || "").trim();
    if (!aId || !isEmail(em)) return;
    const draft = draftOverride || aregPickupDraftByEmail[em] || {};
    if (!canSaveRegistrationPickupDraft(draft)) return null;
    const saved = await updateRegistrationPickupAppointment({
      auctionId: aId,
      email: row?.bidderEmail || row?.email,
      location: String(draft.location || "").trim(),
      appointmentAtMs: Number(draft.appointmentAtMs || 0) || 0,
      additionalInfo: String(draft.additionalInfo || "").trim(),
    });
    if (saved?.ok === true && closeEditor) closeRegistrationPickupEditor(em);
    return saved;
  };
  const renderRegistrationPickupEditButton = (row, disabled = false) => (
    <Tooltip title="Edit pickup details">
      <span>
        <IconButton
          size="small"
          onClick={() => openRegistrationPickupEditor(row)}
          disabled={disabled}
          sx={{
            flexShrink: 0,
            border: "1px solid rgba(99,102,241,0.25)",
            borderRadius: 999,
            color: primary,
            background: "#fff",
          }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
  const renderRegistrationPickupSaveButton = ({ row, label, disabled = false, onClick = null }) => (
    <Button
      size="small"
      variant="contained"
      disabled={disabled}
      onClick={onClick || (() => void saveRegistrationPickupDraft(row))}
      sx={{
        alignSelf: "flex-start",
        textTransform: "none",
        borderRadius: 999,
        px: 1.5,
        py: 0.45,
        fontWeight: 900,
        minWidth: 0,
        background: disabled ? "linear-gradient(90deg,#9ca3af,#6b7280)" : "linear-gradient(90deg,#10b981,#059669)",
        "&:hover": {
          background: disabled ? "linear-gradient(90deg,#9ca3af,#6b7280)" : "linear-gradient(90deg,#059669,#047857)",
        },
      }}
    >
      {label}
    </Button>
  );
  const updateRegistrationShelf = async ({ auctionId, email, shelf }) => {
    if (!orgReady) return;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    const nextShelf = String(shelf || "").trim();
    if (!aId || !isEmail(em)) return;
    setAregShelfSavingByEmail((prev) => ({ ...prev, [em]: true }));
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.registrationShelfUpdate}`, {
        method: "POST",
        body: JSON.stringify({
          auctionId: aId,
          email: em,
          shelf: nextShelf,
        }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);
      const savedShelf = String(data?.shelf || nextShelf || "").trim();
      setAuctionRegs((prev) =>
        (prev || []).map((r) => {
          const rem = normalize(r?.bidderEmail || r?.email);
          if (rem !== em) return r;
          return {
            ...r,
            shelf: savedShelf,
            pickupShelf: savedShelf,
          };
        })
      );
      setAregShelfDraftByEmail((prev) => ({ ...prev, [em]: savedShelf }));
      invalidateAuctionRegsCache(aId);
    } catch (e) {
      toastOpen(e?.message || "Failed to update shelf", "error");
    } finally {
      setAregShelfSavingByEmail((prev) => ({ ...prev, [em]: false }));
    }
  };
  const updateRegistrationSignature = async ({ auctionId, email, signatureName, signatureDataUrl }) => {
    if (!orgReady) return;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    if (!aId || !isEmail(em)) return;
    setChargebackSignSaving(true);
    setChargebackSignError("");
    setChargebackSignSuccess("");
    try {
      const { res, data } = await apiFetchJson(`${API}${ROUTES.registrationSignatureUpdate}`, {
        method: "POST",
        body: JSON.stringify({
          auctionId: aId,
          email: em,
          signatureName,
          signatureDataUrl,
        }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);
      const shippingPolicy = data?.shippingPolicy || null;
      const pickupSignature = data?.pickupSignature || null;
      const applyPatch = (row = {}) => {
        const rowEmail = normalize(row?.bidderEmail || row?.emailLower || row?.email);
        const rowAuctionId = String(row?.auctionId || "").trim();
        if (rowEmail !== em || rowAuctionId !== aId) return row;
        return {
          ...row,
          ...(shippingPolicy ? { shippingPolicy } : {}),
          ...(pickupSignature ? { pickupSignature } : {}),
        };
      };
      setAuctionRegs((prev) => (prev || []).map(applyPatch));
      setUserRegsRows((prev) => (prev || []).map(applyPatch));
      setChargebackSignRow((prev) => (prev ? applyPatch(prev) : prev));
      setChargebackSignSuccess("Signature saved successfully.");
    } catch (e) {
      setChargebackSignError(e?.message || "Failed to save signature.");
    } finally {
      setChargebackSignSaving(false);
    }
  };
  const uploadRegistrationsFileForAuction = async ({
    file,
    auctionId,
    auctionName = "",
    replaceExisting = false,
    requireNameMatch = true,
  } = {}) => {
    if (!orgReady) return null;
    const aId = String(auctionId || "").trim();
    if (!aId || !file) return null;

    const fileNameLower = String(file?.name || "").toLowerCase();
    const isSupported =
      fileNameLower.endsWith(".csv") ||
      fileNameLower.endsWith(".tsv") ||
      fileNameLower.endsWith(".xlsx") ||
      fileNameLower.endsWith(".xls");
    if (!isSupported) {
      toastOpen("Upload a CSV, TSV, XLSX, or XLS file.", "error");
      return null;
    }

    if (requireNameMatch && !(await confirmAuctionFileNameMatch(file, "registration file", auctionName))) {
      throw new Error("Registration upload canceled.");
    }

    setUploadDialogText(replaceExisting ? "Replacing registrations. Please wait…" : "Uploading registration file. Please wait…");
    setRegUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const qs = new URLSearchParams();
      qs.set("auctionId", aId);
      if (auctionName) qs.set("auctionName", auctionName);
      if (replaceExisting) qs.set("replaceExisting", "true");

      const { res, data } = await apiFetchJson(`${API}${ROUTES.auctionRegistrationsUpload}?${qs.toString()}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Registration upload failed (${res.status})`);

      invalidateAuctionRegsCache(aId);
      invalidateAuctionSummaryCache(aId);
      invalidateAuctionsCache();
      invalidateUsersCache();

      if (aId === selectedAuctionId) {
        await fetchAuctionRegsPage({ auctionId: aId, sync: false, force: true });
        await fetchAuctionSummary(aId, { force: true });
      }
      await fetchAuctions({ force: true, sync: false });
      await fetchUsers({ force: true, sync: false });
      return data;
    } finally {
      setRegUploadBusy(false);
      setUploadDialogText("");
    }
  };

  const runAuctionRegistrationsUpload = async (file, { replaceExisting = false } = {}) => {
    const auctionId = selectedAuctionId;
    if (!auctionId || !file) return;
    try {
      const data = await uploadRegistrationsFileForAuction({
        file,
        auctionId,
        auctionName: selectedAuctionLabel,
        replaceExisting,
        requireNameMatch: true,
      });
      if (!data) return;

      const verb = replaceExisting ? "replaced" : "uploaded";
      const validEmails = formatNumber2(toAbsInt(data?.validEmails ?? 0));
      const deleted = formatNumber2(toAbsInt(data?.deletedExistingRegistrations ?? 0));
      const detail = replaceExisting ? ` (removed ${deleted} existing)` : "";
      toastOpen(`Registrations ${verb}: ${validEmails}${detail}`, "success");
    } catch (e) {
      if (String(e?.message || "").toLowerCase().includes("canceled")) {
        toastOpen("Registration upload canceled.", "info");
      } else {
        toastOpen(e?.message || "Registration upload failed", "error");
      }
    }
  };

  const onRegistrationsFileSelected = (file) => {
    if (!file) return;
    void runAuctionRegistrationsUpload(file, { replaceExisting: false });
  };

  const requestRegistrationsUploadFile = () => {
    regsFileInputRef.current?.click();
  };

  const onShippingFileSelected = (file) => {
    if (!file) return;
    void uploadShippingXlsx(file, { replaceExisting: false });
  };

  const uploadLotsCsvForAuction = async ({
    file,
    auctionId,
    auctionName = "",
    auctionEndAtMs = null,
    consignors = [],
    location = "",
    requireNameMatch = true,
    refreshSelectedAuction = true,
  } = {}) => {
    if (!orgReady) return null;
    const aId = String(auctionId || "").trim();
    if (!aId || !file) return null;
    if (requireNameMatch && !(await confirmAuctionFileNameMatch(file, "lot stats file", auctionName))) {
      throw new Error("Lot stats upload canceled.");
    }

    const normalizedConsignors = uniqSortedOptions(consignors || []);
    const primaryConsignor = String(normalizedConsignors[0] || "").trim();
    const locationSafe = String(location || "").trim();
    setUploadDialogText("Uploading lot stats file. Please wait…");
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const qs = new URLSearchParams();
      qs.set("auctionId", aId);
      if (Number.isFinite(Number(auctionEndAtMs)) && Number(auctionEndAtMs) > 0) {
        qs.set("auctionEndAtMs", String(Math.floor(Number(auctionEndAtMs))));
      }
      if (primaryConsignor) qs.set("consignor", primaryConsignor);
      if (normalizedConsignors.length) qs.set("consignors", normalizedConsignors.join("|"));
      if (locationSafe) qs.set("location", locationSafe);
      const { res, data } = await apiFetchJson(`${API}${ROUTES.lotsUpload}?${qs.toString()}`, { method: "POST", body: fd });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Upload failed (${res.status})`);
      setAuctionFieldOptions((prev) =>
        mergeAuctionFieldOptions(prev, { consignors: normalizedConsignors, locations: locationSafe ? [locationSafe] : [] })
      );
      invalidateUsersCache();
      invalidateAuctionsCache();
      invalidateAuctionSummaryCache(aId);
      invalidateAuctionRegsCache(aId);
      await fetchAuctions({ force: true, sync: false });
      if (refreshSelectedAuction && aId === selectedAuctionId) {
        await fetchAuctionSummary(aId, { force: true });
        await fetchAuctionRegsPage({ auctionId: aId, sync: false, force: true });
      }
      return data;
    } finally {
      setUploadBusy(false);
      setUploadDialogText("");
    }
  };

  const buildUserWorkbookManifestSummary = ({ emailLower = "", registrationRows = [], lotStatsRows = [] } = {}) => {
    const fallbackEmail = String(emailLower || userRegsUser?.email || userRegsUser?.id || "").trim();
    const bidderName =
      String(
        getDisplayBidderName(userRegsUser) ||
          getDisplayUsername(userRegsUser) ||
          userRegsUser?.bidderName ||
          userRegsUser?.username ||
          fallbackEmail
      ).trim() || "Bidder";
    const bidderEmail = String(userRegsUser?.email || fallbackEmail || "").trim() || "—";
    const registrationsOnRecord = Math.max(toAbsInt(getRegsCount(userRegsUser)), registrationRows.length);
    const bidsOnRecord = toAbsInt(getBidsCount(userRegsUser));
    const exportedTotalLots = registrationRows.reduce((sum, row) => sum + toAbsInt(row?.Lots || 0), 0);
    const exportedTotalHammer = registrationRows.reduce((sum, row) => sum + safeNum(row?.Hammer || 0), 0);
    return {
      bidderName,
      bidderEmail,
      registrationsOnRecord,
      bidsOnRecord,
      exportedRegistrations: registrationRows.length,
      exportedLots: lotStatsRows.length,
      exportedTotalLots,
      exportedTotalHammer,
      generatedAt: new Date().toLocaleString(),
    };
  };
  const buildUserWorkbookManifestSheet = (
    XLSX,
    {
      sheetLabel = "Manifest",
      rows = [],
      fallbackMessage = "No records found.",
      columnWidths = [],
      summary = {},
    } = {}
  ) => {
    const safeRows = Array.isArray(rows) && rows.length ? rows : [{ Message: fallbackMessage }];
    const tableColumnCount = Math.max(1, Object.keys(safeRows[0] || {}).length);
    const bannerColumnCount = Math.max(tableColumnCount, 6);
    const lastBannerColumn = XLSX.utils.encode_col(bannerColumnCount - 1);
    const summaryRows = [
      ["AuctionFuel Invoice"],
      [`${sheetLabel} Manifest`],
      ["Brand", "AuctionFuel"],
      ["Customer Name", summary?.bidderName || "Bidder"],
      ["Customer Email", summary?.bidderEmail || "—"],
      ["Registrations On Record", formatNumber2(summary?.registrationsOnRecord || 0)],
      ["Bids On Record", formatNumber2(summary?.bidsOnRecord || 0)],
      ["Exported Registrations", formatNumber2(summary?.exportedRegistrations || 0)],
      ["Exported Lot Rows", formatNumber2(summary?.exportedLots || 0)],
      ["Exported Total Lots", formatNumber2(summary?.exportedTotalLots || 0)],
      ["Exported Total Hammer", `$${moneyPlain(summary?.exportedTotalHammer || 0)}`],
      ["Generated", summary?.generatedAt || new Date().toLocaleString()],
      [],
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(summaryRows);
    const headerRowNumber = summaryRows.length + 1;
    XLSX.utils.sheet_add_json(worksheet, safeRows, { origin: `A${headerRowNumber}` });

    worksheet["!merges"] = [
      XLSX.utils.decode_range(`A1:${lastBannerColumn}1`),
      XLSX.utils.decode_range(`A2:${lastBannerColumn}2`),
    ];

    setWorksheetCellStyle(
      worksheet,
      "A1",
      buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_HEADER_RGB,
        textRgb: "FFFFFF",
        bold: true,
        fontSize: 18,
      })
    );
    setWorksheetCellStyle(
      worksheet,
      "A2",
      buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_LIGHT_RGB,
        textRgb: XLSX_MANIFEST_HEADER_RGB,
        bold: true,
        fontSize: 12,
      })
    );

    for (let rowNumber = 3; rowNumber <= summaryRows.length - 1; rowNumber += 1) {
      setWorksheetCellStyle(
        worksheet,
        `A${rowNumber}`,
        buildXlsxCellStyle({
          fillRgb: XLSX_MANIFEST_LIGHT_RGB,
          textRgb: XLSX_MANIFEST_HEADER_RGB,
          bold: true,
        })
      );
      setWorksheetCellStyle(
        worksheet,
        `B${rowNumber}`,
        buildXlsxCellStyle({
          fillRgb: "FFFFFF",
          textRgb: summaryRows[rowNumber - 1]?.[0] === "Generated" ? XLSX_MANIFEST_MUTED_RGB : XLSX_MANIFEST_TEXT_RGB,
          bold: summaryRows[rowNumber - 1]?.[0] === "Customer Name",
          fontSize: summaryRows[rowNumber - 1]?.[0] === "Customer Name" ? 13 : 11,
          italic: summaryRows[rowNumber - 1]?.[0] === "Generated",
        })
      );
    }

    styleWorksheetRow(
      XLSX,
      worksheet,
      headerRowNumber,
      tableColumnCount,
      buildXlsxCellStyle({
        fillRgb: XLSX_MANIFEST_HEADER_RGB,
        textRgb: "FFFFFF",
        bold: true,
        horizontal: "center",
      })
    );

    for (let rowIndex = 0; rowIndex < safeRows.length; rowIndex += 1) {
      const rowNumber = headerRowNumber + 1 + rowIndex;
      const fillRgb = rowIndex % 2 === 0 ? "FFFFFF" : XLSX_MANIFEST_ALT_ROW_RGB;
      styleWorksheetRow(
        XLSX,
        worksheet,
        rowNumber,
        tableColumnCount,
        buildXlsxCellStyle({
          fillRgb,
          textRgb: XLSX_MANIFEST_TEXT_RGB,
        })
      );
    }

    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowNumber - 1, c: 0 },
        e: { r: headerRowNumber - 1, c: tableColumnCount - 1 },
      }),
    };
    worksheet["!cols"] = columnWidths.length
      ? columnWidths
      : [{ wch: 22 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
    worksheet["!rows"] = [
      { hpx: 34 },
      { hpx: 24 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 24 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 22 },
      { hpx: 22 },
    ];
    return worksheet;
  };
  const exportUserRegistrationsAndLotsWorkbook = async () => {
    if (userLotsExportBusy) return;
    const emailLower = normalize(userRegsUser?.email || userRegsUser?.id);
    if (!isEmail(emailLower)) {
      toastOpen("Missing or invalid user email.", "error");
      return;
    }

    setUserLotsExportBusy(true);
    setUserLotsErr("");
    try {
      const registrationRowsRaw = await fetchUserAuctions(emailLower, { force: true });
      const registrationRowsSorted = [...(Array.isArray(registrationRowsRaw) ? registrationRowsRaw : [])].sort((a, b) => {
        const aMs = safeToMillis(a?.auctionEndAtMs ?? a?.auctionEndAt) ?? 0;
        const bMs = safeToMillis(b?.auctionEndAtMs ?? b?.auctionEndAt) ?? 0;
        return bMs - aMs;
      });
      const registrationRows = registrationRowsSorted.map((rr) => {
        const auctionId = String(rr?.auctionId || rr?.id || "").trim();
        const auctionName = String(rr?.auctionName || rr?.auctionLabel || auctionId || "").trim();
        const endMs = safeToMillis(rr?.auctionEndAtMs ?? rr?.auctionEndAt);
        const sentMs = safeToMillis(rr?.emailSentAt);
        const lotsCount = toAbsInt(rr?.lotsCount ?? rr?.lotsWon ?? 0);
        const hammer = safeNum(rr?.totalHighBid ?? rr?.hammerTotal ?? 0);
        const chargebackEmailSent = isChargebackEmailSent(rr);
        const signDetails = getChargebackSignDetails(rr);
        return {
          Auction: auctionName || "—",
          End: endMs ? new Date(endMs).toLocaleString() : "—",
          Lots: lotsCount,
          Hammer: Number(hammer.toFixed(2)),
          "Email Sent": sentMs ? new Date(sentMs).toLocaleString() : "—",
          "Chargeback / Pickup Mail Sent": chargebackEmailSent ? "True" : "False",
          Signed: signDetails?.signed ? "True" : "False",
          "Signed At": signDetails?.signedAtMs ? new Date(signDetails.signedAtMs).toLocaleString() : "—",
          "Shipping Requested": isShippingRequested(rr) ? "Yes" : "No",
          "Bidder Email": String(rr?.bidderEmail || rr?.email || emailLower || "").trim(),
          "Bidder Name": String(rr?.bidderName || "").trim(),
          Username: String(rr?.username || "").trim(),
        };
      });

      const allRows = [];
      const seenOffsets = new Set();
      let cursorOffset = 0;
      let hasMore = true;
      let pageCount = 0;
      const MAX_PAGES = 2000;

      while (hasMore) {
        if (pageCount >= MAX_PAGES) {
          throw new Error("Too many lots to export. Please narrow the scope and try again.");
        }
        pageCount += 1;
        if (seenOffsets.has(cursorOffset)) break;
        seenOffsets.add(cursorOffset);

        const data = await fetchUserLotsPage(emailLower, { cursorOffset, titleSearch: "" });
        const pageRows = Array.isArray(data?.lots) ? data.lots : [];
        allRows.push(...pageRows);

        const nextCursorOffset = Math.max(0, toAbsInt(data?.nextCursorOffset ?? 0));
        hasMore = !!data?.hasMore && nextCursorOffset > cursorOffset;
        cursorOffset = nextCursorOffset;
        if (!pageRows.length) break;
      }

      const sortedByTimeDesc = [...allRows].sort((a, b) => {
        const aMs = safeToMillis(a?.createdAt ?? a?.updatedAt) ?? 0;
        const bMs = safeToMillis(b?.createdAt ?? b?.updatedAt) ?? 0;
        return bMs - aMs;
      });
      const lotStatsRows = sortedByTimeDesc.map((lot) => {
        const auctionId = String(lot?.auctionId || "").trim();
        const auctionName = String(lot?.auctionName || userLotsAuctionNameMap.get(auctionId) || auctionId || "—");
        const createdAtMs = safeToMillis(lot?.createdAt ?? lot?.updatedAt);
        const timeText = String(lot?.timeLeft || "").trim() || (createdAtMs ? new Date(createdAtMs).toLocaleString() : "—");
        return {
          Auction: auctionName,
          Lot: lot?.lot || "—",
          Title: lot?.title || "—",
          Time: timeText,
          "High Bid": Number(safeNum(lot?.highBid || 0).toFixed(2)),
          "Max Bid": Number(safeNum(lot?.maxBid || 0).toFixed(2)),
          Bids: toAbsInt(lot?.bids || 0),
          Views: toAbsInt(lot?.views || 0),
          Watches: toAbsInt(lot?.watches || 0),
        };
      });

      const xlsxModule = await import("xlsx-js-style");
      const XLSX = xlsxModule?.default?.utils ? xlsxModule.default : xlsxModule?.utils ? xlsxModule : xlsxModule?.default;
      if (!XLSX?.utils || typeof XLSX.writeFile !== "function") {
        throw new Error("XLSX export library failed to load.");
      }
      const wb = XLSX.utils.book_new();
      const registrationSheetRows = registrationRows.length ? registrationRows : [{ Message: "No registrations found." }];
      const lotStatsSheetRows = lotStatsRows.length ? lotStatsRows : [{ Message: "No lot stats found." }];
      const workbookSummary = buildUserWorkbookManifestSummary({
        emailLower,
        registrationRows,
        lotStatsRows,
      });
      const registrationWs = buildUserWorkbookManifestSheet(XLSX, {
        sheetLabel: "User Registrations",
        rows: registrationSheetRows,
        fallbackMessage: "No registrations found.",
        summary: workbookSummary,
        columnWidths: [
          { wch: 30 },
          { wch: 34 },
          { wch: 10 },
          { wch: 12 },
          { wch: 22 },
          { wch: 18 },
          { wch: 10 },
          { wch: 22 },
          { wch: 18 },
          { wch: 30 },
          { wch: 22 },
          { wch: 18 },
        ],
      });
      const lotStatsWs = buildUserWorkbookManifestSheet(XLSX, {
        sheetLabel: "User Lots",
        rows: lotStatsSheetRows,
        fallbackMessage: "No lot stats found.",
        summary: workbookSummary,
        columnWidths: [
          { wch: 30 },
          { wch: 18 },
          { wch: 42 },
          { wch: 22 },
          { wch: 12 },
          { wch: 12 },
          { wch: 10 },
          { wch: 10 },
          { wch: 10 },
        ],
      });
      XLSX.utils.book_append_sheet(wb, registrationWs, "registration");
      XLSX.utils.book_append_sheet(wb, lotStatsWs, "lot stats");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const emailPart = sanitizeFileNamePart(emailLower) || "user";
      const fileName = `registration-lot-stats-${emailPart}-${stamp}.xlsx`;
      XLSX.writeFile(wb, fileName, { cellStyles: true });
      toastOpen(
        `Downloaded workbook: ${formatNumber2(registrationRows.length)} registrations and ${formatNumber2(lotStatsRows.length)} lot stats rows.`,
        "success"
      );
    } catch (e) {
      const msg = e?.message || "Failed to export registration + lot stats workbook.";
      setUserLotsErr(msg);
      toastOpen(msg, "error");
    } finally {
      setUserLotsExportBusy(false);
    }
  };
  const exportSelectedRegistrationsWorkbook = async () => {
    if (selectedRegsExportBusy) return;
    const selectedRows = getSelectedRegRows();
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    if (!selectedRows.length) {
      toastOpen("Select at least one registration first.", "error");
      return;
    }

    setSelectedRegsExportBusy(true);
    try {
      const lotsByRegistration = await Promise.all(
        selectedRows.map(async (regRow) => {
          const emailLower = normalize(regRow?.bidderEmail || regRow?.email);
          if (!isEmail(emailLower)) return { regRow, emailLower, lots: [] };
          const data = await fetchUserLots(emailLower, selectedAuctionId);
          return {
            regRow,
            emailLower,
            lots: Array.isArray(data?.lots) ? data.lots : [],
          };
        })
      );

      const registrationRows = selectedRows.map((rr) => {
        const emailLower = normalize(rr?.bidderEmail || rr?.email);
        const cached = lotsAgg[emailLower] || {};
        const lotsCount = toAbsInt(rr?.lotsCount ?? cached?.lotsCount ?? 0);
        const totalHighBid = safeNum(rr?.totalHighBid ?? cached?.totalHighBid ?? 0);
        const pickupDetails = getLotPickupCounts({
          lotsCount,
          pickedUpLotsCount: rr?.pickedUpLotsCount ?? cached?.pickedUpLotsCount,
          pickedUp: rr?.pickedUp ?? cached?.pickedUp,
        });
        const shippedDetails = getLotShippedCounts({
          lotsCount,
          shippedLotsCount: rr?.shippedLotsCount ?? cached?.shippedLotsCount,
          shipped: rr?.shipped ?? cached?.shipped,
        });
        const consignorsText = getRegistrationConsignors(rr).join(", ");
        return {
          Auction: selectedAuctionLabel || selectedAuctionId,
          Registered: fmtDate(rr?.registeredAt || rr?.emailSentAt),
          Lots: lotsCount,
          Hammer: Number(totalHighBid.toFixed(2)),
          Consignors: consignorsText || "—",
          "Shipping Requested": isShippingRequested(rr) ? "Yes" : "No",
          "Picked Up": pickupDetails.totalLotsCount > 0 ? pickupDetails.pickupStatusLabel : "—",
          Shipped: shippedDetails.totalLotsCount > 0 ? shippedDetails.shippedStatusLabel : "—",
          "Bidder Email": String(rr?.bidderEmail || rr?.email || "").trim(),
          "Bidder Name": String(getDisplayBidderName(rr) || "").trim(),
          Username: String(getDisplayUsername(rr) || "").trim(),
          "Registration Id": String(rr?.registrationId || rr?.id || "").trim(),
          "Auction Id": selectedAuctionId,
        };
      });

      const lotStatsRows = lotsByRegistration
        .flatMap(({ regRow, emailLower, lots }) =>
          (Array.isArray(lots) ? lots : []).map((lot) => {
            const createdAtMs = safeToMillis(lot?.createdAt ?? lot?.updatedAt);
            const timeText = String(lot?.timeLeft || "").trim() || (createdAtMs ? new Date(createdAtMs).toLocaleString() : "—");
            return {
              Auction: String(lot?.auctionName || selectedAuctionLabel || selectedAuctionId || "—").trim(),
              Lot: lot?.lot || "—",
              Title: lot?.title || "—",
              Time: timeText,
              "High Bid": Number(safeNum(lot?.highBid || 0).toFixed(2)),
              "Max Bid": Number(safeNum(lot?.maxBid || 0).toFixed(2)),
              Bids: toAbsInt(lot?.bids || 0),
              Views: toAbsInt(lot?.views || 0),
              Watches: toAbsInt(lot?.watches || 0),
              Consignor: String(lot?.consignor || "").trim() || "—",
              "Bidder Email": String(lot?.emailLower || regRow?.bidderEmail || regRow?.email || emailLower || "").trim(),
              "Bidder Name": String(lot?.bidderName || getDisplayBidderName(regRow) || "").trim() || "—",
              "Auction Id": String(lot?.auctionId || selectedAuctionId || "").trim(),
            };
          })
        )
        .sort((a, b) => {
          const aText = String(a?.Time || "");
          const bText = String(b?.Time || "");
          return bText.localeCompare(aText);
        });

      const xlsxModule = await import("xlsx");
      const XLSX = xlsxModule?.utils ? xlsxModule : xlsxModule?.default;
      if (!XLSX?.utils || typeof XLSX.writeFile !== "function") {
        throw new Error("XLSX export library failed to load.");
      }

      const wb = XLSX.utils.book_new();
      const registrationSheetRows = registrationRows.length ? registrationRows : [{ Message: "No registrations found." }];
      const lotStatsSheetRows = lotStatsRows.length ? lotStatsRows : [{ Message: "No lot stats found." }];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(registrationSheetRows), "registration");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lotStatsSheetRows), "lot stats");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(selectedAuctionLabel || selectedAuctionId) || "auction";
      XLSX.writeFile(wb, `selected-registration-lot-stats-${auctionPart}-${stamp}.xlsx`);
      toastOpen(
        `Downloaded workbook: ${formatNumber2(registrationRows.length)} registrations and ${formatNumber2(lotStatsRows.length)} lot stats rows.`,
        "success"
      );
    } catch (e) {
      toastOpen(e?.message || "Failed to export selected registrations workbook.", "error");
    } finally {
      setSelectedRegsExportBusy(false);
    }
  };
  const getRegistrationShelfText = (row = {}) => {
    const emailLower = normalize(row?.bidderEmail || row?.email);
    return aregShelfDraftByEmail[emailLower] !== undefined
      ? String(aregShelfDraftByEmail[emailLower] || "").trim()
      : String(row?.shelf || row?.pickupShelf || "").trim();
  };
  const getRegistrationBoxLabelRowKey = (row = {}, fallback = "") =>
    normalize(row?.bidderEmail || row?.email) ||
    String(row?.id || row?.gmailMessageId || fallback || "").trim() ||
    "registration";
  const getSelectedAuctionEndDisplay = () => {
    const explicit = String(selectedAuctionViewRow?.endLabel || "").trim();
    if (explicit && explicit !== "—") return explicit;
    const endMs =
      safeToMillis(
        selectedAuctionViewRow?.endMs ??
        selectedAuction?.auctionEndAtMs ??
        selectedAuction?.auctionEndAt ??
        auctionSummary?.auctionEndAtMs ??
        auctionSummary?.auctionEndAt ??
        selectedAuctionSavedSettings?.endMs
      ) || 0;
    return endMs > 0 ? new Date(endMs).toLocaleString() : "—";
  };
  const buildRegistrationBoxLabelPages = (rows = []) => {
    const auctionName = String(selectedAuctionLabel || selectedAuctionId || "").trim() || "Auction";
    const auctionEndText = getSelectedAuctionEndDisplay();
    return (Array.isArray(rows) ? rows : []).flatMap((row, idx) => {
      const boxLabels = parseShelfBoxLabels(getRegistrationShelfText(row));
      if (!boxLabels.length) return [];
      const bidderName = String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.bidderEmail || row?.email || "").trim() || "Bidder";
      const rowKey = getRegistrationBoxLabelRowKey(row, idx);
      return boxLabels.map((boxLabel) => ({
        rowKey,
        bidderName,
        boxLabel,
        auctionName,
        auctionEndText,
      }));
    });
  };
  const loadRegistrationBoxLogoDataUrl = async () => {
    if (registrationBoxLogoRef.current) return registrationBoxLogoRef.current;
    const dataUrl = await loadPdfThumbnailDataUrl(AUCTIONFUEL_BOX_LOGO_URL, { maxWidth: 220, maxHeight: 220 });
    registrationBoxLogoRef.current = String(dataUrl || "");
    return registrationBoxLogoRef.current;
  };
  const loadRegistrationBoxReviewQrDataUrl = async () => {
    if (registrationBoxReviewQrRef.current) return registrationBoxReviewQrRef.current;
    const dataUrl = await loadPdfThumbnailDataUrl(AUCTIONFUEL_REVIEW_QR_URL, { maxWidth: 136, maxHeight: 136 });
    registrationBoxReviewQrRef.current = String(dataUrl || "");
    return registrationBoxReviewQrRef.current;
  };
  const loadRegistrationBoxFooterLogoDataUrl = async () => {
    if (registrationBoxFooterLogoRef.current) return registrationBoxFooterLogoRef.current;
    const dataUrl = await loadPdfThumbnailDataUrl(AUCTIONFUEL_BOX_FOOTER_LOGO_URL, { maxWidth: 120, maxHeight: 40 });
    registrationBoxFooterLogoRef.current = String(dataUrl || "");
    return registrationBoxFooterLogoRef.current;
  };
  const getPdfImageRenderSize = (doc, dataUrl, { maxWidth = 120, maxHeight = 120 } = {}) => {
    const fallback = {
      width: maxWidth,
      height: maxHeight,
    };
    if (!dataUrl || typeof doc?.getImageProperties !== "function") return fallback;
    try {
      const props = doc.getImageProperties(dataUrl);
      const naturalWidth = Number(props?.width || props?.w || 0) || 0;
      const naturalHeight = Number(props?.height || props?.h || 0) || 0;
      if (!(naturalWidth > 0) || !(naturalHeight > 0)) return fallback;
      const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
      return {
        width: Math.max(1, Math.round(naturalWidth * scale)),
        height: Math.max(1, Math.round(naturalHeight * scale)),
      };
    } catch (e) {
      return fallback;
    }
  };
  const getPdfFittedFontSize = (
    doc,
    text,
    { maxWidth = 320, maxFontSize = 72, minFontSize = 28, maxHeight = 0, lineHeightRatio = 1 } = {}
  ) => {
    const safeLines = (Array.isArray(text) ? text : [text])
      .map((line) => String(line || "").trim())
      .filter(Boolean);
    if (!safeLines.length) safeLines.push("—");
    const upper = Math.max(minFontSize, maxFontSize);
    const lower = Math.max(8, Math.min(minFontSize, upper));
    if (!doc || typeof doc.getTextWidth !== "function" || typeof doc.setFontSize !== "function") {
      return upper;
    }
    for (let fontSize = upper; fontSize >= lower; fontSize -= 1) {
      doc.setFontSize(fontSize);
      const widestLineWidth = safeLines.reduce((maxWidthSoFar, line) => Math.max(maxWidthSoFar, doc.getTextWidth(line)), 0);
      const blockHeight = safeLines.length <= 1 ? fontSize : fontSize + fontSize * lineHeightRatio * (safeLines.length - 1);
      if (widestLineWidth <= maxWidth && (!maxHeight || blockHeight <= maxHeight)) return fontSize;
    }
    return lower;
  };
  const splitBidderNameForBoxLabel = (name = "") => {
    const words = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) return ["Bidder"];
    if (words.length === 1) return [words[0]];
    if (words.length === 2) return words;
    const midpoint = Math.ceil(words.length / 2);
    return [words.slice(0, midpoint).join(" "), words.slice(midpoint).join(" ")];
  };
  const renderRegistrationBoxLabelPage = (doc, page, { logoDataUrl = "", qrDataUrl = "", footerLogoDataUrl = "" } = {}) => {
    const pageWidth = Number(doc?.internal?.pageSize?.getWidth?.() || 612);
    const pageHeight = Number(doc?.internal?.pageSize?.getHeight?.() || 792);
    const contentMaxWidth = Math.round(pageWidth * 0.85);
    const contentTop = Math.round(pageHeight * 0.05);
    const contentBottom = Math.round(pageHeight * 0.95);
    const centerX = pageWidth / 2;
    let headerY = contentTop;
    if (logoDataUrl) {
      const { width: logoWidth, height: logoHeight } = getPdfImageRenderSize(doc, logoDataUrl, {
        maxWidth: 78,
        maxHeight: 130,
      });
      const logoX = (pageWidth - logoWidth) / 2;
      try {
        doc.addImage(logoDataUrl, "JPEG", logoX, headerY, logoWidth, logoHeight);
      } catch (e) {
        // Ignore logo rendering failures and continue with text-only output.
      }
      headerY += logoHeight + 14;
    }
    doc.setFont("helvetica", "bold");
    const auctionNameFontSize = getPdfFittedFontSize(doc, page?.auctionName, {
      maxWidth: contentMaxWidth,
      maxFontSize: 12,
      minFontSize: 9,
    });
    doc.setFontSize(auctionNameFontSize);
    doc.setTextColor(15, 23, 42);
    doc.text(String(page?.auctionName || "Auction"), centerX, headerY, { align: "center" });
    headerY += auctionNameFontSize + 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(`Auction End: ${String(page?.auctionEndText || "—")}`, centerX, headerY, { align: "center" });
    const headerBottom = headerY + 14;

    const qrMetrics = qrDataUrl
      ? getPdfImageRenderSize(doc, qrDataUrl, {
          maxWidth: 96,
          maxHeight: 96,
        })
      : { width: 0, height: 0 };
    const footerLogoMetrics = footerLogoDataUrl
      ? getPdfImageRenderSize(doc, footerLogoDataUrl, {
          maxWidth: 140,
          maxHeight: 28,
        })
      : { width: 0, height: 0 };
    const footerPoweredFontSize = 17;
    const footerReviewFontSize = 12;
    const footerStackHeight =
      footerPoweredFontSize +
      (footerLogoMetrics.height ? footerLogoMetrics.height + 10 : 0) +
      footerReviewFontSize +
      (qrMetrics.height ? qrMetrics.height + 18 : 0) +
      20;
    const footerTop = contentBottom - footerStackHeight;
    const primaryTop = headerBottom + 8;
    const primaryBottom = footerTop - 18;
    const primaryHeight = Math.max(220, primaryBottom - primaryTop);

    doc.setFont("helvetica", "bold");
    const bidderNameLines = splitBidderNameForBoxLabel(page?.bidderName);
    const bidderDisplayFontSize = getPdfFittedFontSize(doc, bidderNameLines, {
      maxWidth: contentMaxWidth,
      maxFontSize: 122,
      minFontSize: 58,
      maxHeight: Math.round(primaryHeight * 0.5),
      lineHeightRatio: 0.92,
    });
    const boxLabelFontSize = getPdfFittedFontSize(doc, page?.boxLabel, {
      maxWidth: contentMaxWidth,
      maxFontSize: Math.round(bidderDisplayFontSize * 1.55),
      minFontSize: Math.max(52, Math.round(bidderDisplayFontSize * 1.18)),
      maxHeight: Math.round(primaryHeight * 0.34),
    });
    const boxCaptionFontSize = 20;
    const bidderLineHeight = Math.round(bidderDisplayFontSize * 0.92);
    const bidderBlockHeight = bidderDisplayFontSize + bidderLineHeight * (bidderNameLines.length - 1);
    const boxGap = 28;
    const numberGap = 16;
    const footerGap = 18;
    const totalPrimaryStackHeight = bidderBlockHeight + boxGap + boxCaptionFontSize + numberGap + boxLabelFontSize + footerGap;
    const centeredPrimaryStartY = primaryTop + Math.max(0, (primaryHeight - totalPrimaryStackHeight) / 2);
    const primaryStartY = Math.max(primaryTop, centeredPrimaryStartY - 118);

    doc.setFontSize(bidderDisplayFontSize);
    doc.setTextColor(17, 24, 39);
    const nameBaselineY = primaryStartY + bidderDisplayFontSize;
    bidderNameLines.forEach((line, index) => {
      doc.text(line, centerX, nameBaselineY + index * bidderLineHeight, { align: "center" });
    });
    const boxCaptionBaselineY = primaryStartY + bidderBlockHeight + boxGap + boxCaptionFontSize;
    doc.setFontSize(boxCaptionFontSize);
    doc.setTextColor(79, 70, 229);
    doc.text("BOX", centerX, boxCaptionBaselineY, { align: "center" });
    const boxNumberBaselineY = boxCaptionBaselineY + numberGap + boxLabelFontSize;
    doc.setFontSize(boxLabelFontSize);
    doc.setTextColor(15, 23, 42);
    doc.text(String(page?.boxLabel || "—"), centerX, boxNumberBaselineY, { align: "center" });

    let footerY = footerTop + footerPoweredFontSize;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(footerPoweredFontSize);
    doc.setTextColor(17, 24, 39);
    doc.text("Auctionfuel powered by Listernaut", centerX, footerY, { align: "center" });
    footerY += 10;
    if (footerLogoDataUrl) {
      const footerLogoX = (pageWidth - footerLogoMetrics.width) / 2;
      try {
        doc.addImage(
          footerLogoDataUrl,
          "JPEG",
          footerLogoX,
          footerY,
          footerLogoMetrics.width,
          footerLogoMetrics.height
        );
      } catch (e) {
        // Ignore footer logo rendering failures and continue with the rest of the label.
      }
      footerY += footerLogoMetrics.height + 14;
    } else {
      footerY += 18;
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(footerReviewFontSize);
    doc.setTextColor(71, 85, 105);
    doc.text("If you like our service leave a review:", centerX, footerY, { align: "center" });
    if (qrDataUrl) {
      const qrX = (pageWidth - qrMetrics.width) / 2;
      const qrY = footerY + 14;
      try {
        doc.addImage(qrDataUrl, "JPEG", qrX, qrY, qrMetrics.width, qrMetrics.height);
      } catch (e) {
        // Ignore QR rendering failures and continue with text-only output.
      }
    }
  };
  const downloadRegistrationBoxLabelsPdf = async (rows = [], { fileNamePrefix = "box-labels" } = {}) => {
    if (!selectedAuctionId) throw new Error("Select an auction first.");
    const pages = buildRegistrationBoxLabelPages(rows);
    if (!pages.length) throw new Error("Add the number of boxes first.");
    const [{ jsPDF }, logoDataUrl, qrDataUrl, footerLogoDataUrl] = await Promise.all([
      import("jspdf"),
      loadRegistrationBoxLogoDataUrl(),
      loadRegistrationBoxReviewQrDataUrl(),
      loadRegistrationBoxFooterLogoDataUrl(),
    ]);
    if (typeof jsPDF !== "function") {
      throw new Error("PDF export library failed to load.");
    }
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "letter",
      compress: true,
    });
    pages.forEach((page, idx) => {
      if (idx > 0) doc.addPage("letter", "portrait");
      renderRegistrationBoxLabelPage(doc, page, { logoDataUrl, qrDataUrl, footerLogoDataUrl });
    });
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const auctionPart = sanitizeFileNamePart(selectedAuctionLabel || selectedAuctionId) || "auction";
    doc.save(`${fileNamePrefix}-${auctionPart}-${stamp}.pdf`);
    return {
      pages,
      uniqueRowsCount: new Set(pages.map((page) => page.rowKey)).size,
    };
  };
  const exportRegistrationRowBoxLabelsPdf = async (row = {}) => {
    const rowKey = getRegistrationBoxLabelRowKey(row);
    if (registrationBoxLabelsBusy || registrationBoxLabelsBusyByKey[rowKey]) return;
    setRegistrationBoxLabelsBusyByKey((prev) => ({ ...prev, [rowKey]: true }));
    try {
      const bidderName = String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.bidderEmail || row?.email || "").trim() || "Bidder";
      const bidderPart = sanitizeFileNamePart(bidderName) || "bidder";
      const result = await downloadRegistrationBoxLabelsPdf([row], { fileNamePrefix: `box-labels-${bidderPart}` });
      toastOpen(
        `Downloaded ${formatNumber2(result.pages.length)} box label${result.pages.length === 1 ? "" : "s"} for ${bidderName}.`,
        "success"
      );
    } catch (e) {
      toastOpen(e?.message || "Failed to export box labels.", "error");
    } finally {
      setRegistrationBoxLabelsBusyByKey((prev) => ({ ...prev, [rowKey]: false }));
    }
  };
  const exportBulkRegistrationBoxLabelsPdf = async ({ rows = [], mode = "all" } = {}) => {
    if (registrationBoxLabelsBusy) return;
    if (!rows.length) {
      toastOpen(
        mode === "selected"
          ? "Add the number of boxes to at least one selected registration first."
          : "Add the number of boxes to at least one visible registration first.",
        "error"
      );
      return;
    }
    setRegistrationBoxLabelsBusy(true);
    try {
      const result = await downloadRegistrationBoxLabelsPdf(rows, {
        fileNamePrefix: mode === "selected" ? "box-labels-selected" : "box-labels-all",
      });
      toastOpen(
        `Downloaded ${formatNumber2(result.pages.length)} box labels for ${formatNumber2(result.uniqueRowsCount)} registrations.`,
        "success"
      );
    } catch (e) {
      toastOpen(e?.message || "Failed to export all box labels.", "error");
    } finally {
      setRegistrationBoxLabelsBusy(false);
    }
  };
  const buildRegistrationPickupPicklistEntry = (row = {}) => {
    const emailLower = normalize(row?.bidderEmail || row?.email);
    const cached = lotsAgg[emailLower] || {};
    const lotsCount = toAbsInt(row?.lotsCount ?? cached?.lotsCount ?? 0);
    const totalHighBid = safeNum(row?.totalHighBid ?? cached?.totalHighBid ?? 0);
    const locationShort = formatOverallDashboardLocationShort(
      auctionLocation || selectedAuction?.location || auctionSummary?.location || ""
    );
    const pickupAppointment = getRegistrationPickupAppointment(row);
    const rowPickupDraft = aregPickupDraftByEmail[emailLower] || {};
    const pickupAtMs =
      rowPickupDraft.appointmentAtMs !== undefined
        ? Number(rowPickupDraft.appointmentAtMs || 0) || 0
        : Number(pickupAppointment?.appointmentAtMs || 0) || 0;
    const pickupLabel =
      pickupAtMs > 0
        ? fmtDate(pickupAtMs)
        : String(pickupAppointment?.label || "").trim() || "-";
    const shelfText = getRegistrationShelfText(row);
    const amountDueText =
      aregAmountDueDraftByEmail[emailLower] !== undefined
        ? String(aregAmountDueDraftByEmail[emailLower] || "").trim()
        : String(row?.amountDue || row?.amount_due || "").trim();
    return {
      Name: String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.bidderEmail || row?.email || "").trim() || "-",
      Location: locationShort,
      Email: String(row?.bidderEmail || row?.email || "").trim() || "-",
      "Phone Number": String(row?.phone || row?.phoneNumber || row?.bidderPhone || "").trim() || "-",
      "Pickup Date Time": pickupLabel,
      "Total Lots": formatNumber2(lotsCount),
      "Total Hammer": `$${moneyPlain(totalHighBid || 0)}`,
      Shelf: shelfText || "-",
      "Amount Due": amountDueText || "-",
      "Picked Up": "",
    };
  };
  const buildSelectedRegistrationsPicklistData = (rows = []) => {
    const auctionName = String(selectedAuctionLabel || selectedAuctionId || "").trim() || "Auction";
    const picklistRows = (Array.isArray(rows) ? rows : [])
      .map((row) => buildRegistrationPickupPicklistEntry(row))
      .sort((a, b) => String(a?.Name || "").localeCompare(String(b?.Name || ""), undefined, { sensitivity: "base" }));
    return {
      auctionName,
      picklistRows,
    };
  };
  const buildGoogleMapsUrl = (address = "") => {
    const safeAddress = String(address || "").trim();
    if (!safeAddress) return "";
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(safeAddress)}`;
  };
  const buildAppleMapsUrl = (address = "") => {
    const safeAddress = String(address || "").trim();
    if (!safeAddress) return "";
    return `https://maps.apple.com/?q=${encodeURIComponent(safeAddress)}`;
  };
  const getSelectedDeliveryRowsForExport = (rows = []) =>
    (Array.isArray(rows) ? rows : []).filter((row) => {
      const delivery = getRegistrationDelivery(row);
      return !!(
        delivery?.hasSelection ||
        delivery?.delivered ||
        delivery?.address ||
        delivery?.transactionId ||
        delivery?.deliveryDateKey
      );
    });
  const buildRegistrationDeliveryManifestEntry = (row = {}) => {
    const emailLower = normalize(row?.bidderEmail || row?.email);
    const cached = lotsAgg[emailLower] || {};
    const lotsCount = toAbsInt(row?.lotsCount ?? cached?.lotsCount ?? 0);
    const totalHighBid = safeNum(row?.totalHighBid ?? cached?.totalHighBid ?? 0);
    const delivery = getRegistrationDelivery(row);
    const amountDueText =
      aregAmountDueDraftByEmail[emailLower] !== undefined
        ? String(aregAmountDueDraftByEmail[emailLower] || "").trim()
        : String(row?.amountDue || row?.amount_due || "").trim();
    const address = String(delivery?.address || "").trim();
    return {
      Name: String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.bidderEmail || row?.email || "").trim() || "-",
      "Delivery Location": String(delivery?.location || "").trim() || "-",
      Email: String(row?.bidderEmail || row?.email || "").trim() || "-",
      "Phone Number": String(delivery?.phone || row?.phone || row?.phoneNumber || row?.bidderPhone || "").trim() || "-",
      "Delivery Date": String(delivery?.deliveryDateLabel || delivery?.deliveryDateKey || "").trim() || "-",
      "Delivery Address": address || "-",
      "Google Maps": address ? "Google Maps" : "-",
      "Apple Maps": address ? "Apple Maps" : "-",
      "Total Lots": formatNumber2(lotsCount),
      "Total Hammer": `$${moneyPlain(totalHighBid || 0)}`,
      "Amount Due": amountDueText || "-",
      Delivered: String(
        delivery?.deliveredStatusLabel || (delivery?.delivered ? "Delivered" : "Not Delivered")
      ).trim() || "-",
    };
  };
  const buildSelectedRegistrationsDeliveryData = (rows = []) => {
    const auctionName = String(selectedAuctionLabel || selectedAuctionId || "").trim() || "Auction";
    const deliveryRows = getSelectedDeliveryRowsForExport(rows)
      .map((row) => buildRegistrationDeliveryManifestEntry(row))
      .sort((a, b) => String(a?.Name || "").localeCompare(String(b?.Name || ""), undefined, { sensitivity: "base" }));
    return {
      auctionName,
      deliveryRows,
    };
  };
  const exportSelectedPickupPicklistExcel = async () => {
    if (pickupPicklistExcelBusy) return;
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    const selectedRows = getSelectedRegRows();
    if (!selectedRows.length) {
      toastOpen("Select at least one filtered registration first.", "error");
      return;
    }
    setPickupPicklistExcelBusy(true);
    try {
      const { auctionName, picklistRows } = buildSelectedRegistrationsPicklistData(selectedRows);
      const xlsxModule = await import("xlsx");
      const XLSX = xlsxModule?.utils ? xlsxModule : xlsxModule?.default;
      if (!XLSX?.utils || typeof XLSX.writeFile !== "function") {
        throw new Error("XLSX export library failed to load.");
      }
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([
        ["Auction Name", auctionName],
        [],
      ]);
      XLSX.utils.sheet_add_json(worksheet, picklistRows, { origin: "A3" });
      picklistRows.forEach((row, idx) => {
        const telHref = getPhoneTelHref(row?.["Phone Number"]);
        const phoneCellRef = XLSX.utils.encode_cell({ r: 3 + idx, c: 3 });
        if (telHref && worksheet[phoneCellRef]) {
          worksheet[phoneCellRef].l = {
            Target: telHref,
            Tooltip: String(row?.["Phone Number"] || "").trim() || "Call phone number",
          };
        }
      });
      worksheet["!cols"] = [
        { wch: 24 },
        { wch: 10 },
        { wch: 30 },
        { wch: 18 },
        { wch: 24 },
        { wch: 12 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 10 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "pickup picklist");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(auctionName || selectedAuctionId) || "auction";
      XLSX.writeFile(workbook, `pickup-picklist-${auctionPart}-${stamp}.xlsx`, { cellStyles: true });
      toastOpen(`Downloaded pickup picklist for ${formatNumber2(picklistRows.length)} registrations.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to export pickup picklist XLSX.", "error");
    } finally {
      setPickupPicklistExcelBusy(false);
    }
  };
  const exportSelectedPickupPicklistPdf = async () => {
    if (pickupPicklistPdfBusy) return;
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    const selectedRows = getSelectedRegRows();
    if (!selectedRows.length) {
      toastOpen("Select at least one filtered registration first.", "error");
      return;
    }
    setPickupPicklistPdfBusy(true);
    try {
      const { auctionName, picklistRows } = buildSelectedRegistrationsPicklistData(selectedRows);
      const [{ jsPDF }, autoTableModule] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
      const autoTable = autoTableModule?.default || autoTableModule?.autoTable;
      if (typeof jsPDF !== "function" || typeof autoTable !== "function") {
        throw new Error("PDF export library failed to load.");
      }
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "letter",
        compress: true,
      });
      doc.setFontSize(19);
      doc.setTextColor(17, 24, 39);
      doc.text("Pickup Picklist", 36, 40);
      doc.setFontSize(13);
      doc.setTextColor(71, 85, 105);
      doc.text(`Auction Name: ${auctionName}`, 36, 62);
      autoTable(doc, {
        startY: 80,
        head: [["Name", "Location", "Email", "Phone Number", "Pickup Date Time", "Total Lots", "Total Hammer", "Shelf", "Amount Due", "Picked Up"]],
        body: picklistRows.map((row) => [
          row.Name,
          row.Location,
          row.Email,
          row["Phone Number"],
          row["Pickup Date Time"],
          row["Total Lots"],
          row["Total Hammer"],
          row.Shelf,
          row["Amount Due"],
          row["Picked Up"],
        ]),
        margin: { left: 36, right: 36, top: 30, bottom: 30 },
        styles: {
          fontSize: 11,
          cellPadding: 6,
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.5,
          valign: "middle",
        },
        headStyles: {
          fillColor: [79, 70, 229],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 11,
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        columnStyles: {
          0: { cellWidth: 92 },
          1: { cellWidth: 44, halign: "center" },
          2: { cellWidth: 132 },
          3: { cellWidth: 78 },
          4: { cellWidth: 110 },
          5: { cellWidth: 48 },
          6: { cellWidth: 68 },
          7: { cellWidth: 46 },
          8: { cellWidth: 62 },
          9: { cellWidth: 40, halign: "center" },
        },
        didParseCell: (data) => {
          if (data.section !== "body" || data.column.index !== 3) return;
          const phoneValue = Array.isArray(data?.row?.raw) ? data.row.raw[3] : data?.cell?.raw;
          if (!getPhoneTelHref(phoneValue)) return;
          data.cell.styles.textColor = [37, 99, 235];
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== 3) return;
          const phoneValue = Array.isArray(data?.row?.raw) ? data.row.raw[3] : data?.cell?.raw;
          const telHref = getPhoneTelHref(phoneValue);
          if (!telHref) return;
          try {
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: telHref });
          } catch (e) {
            // Ignore link annotation failures and keep the PDF export working.
          }
        },
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(auctionName || selectedAuctionId) || "auction";
      doc.save(`pickup-picklist-${auctionPart}-${stamp}.pdf`);
      toastOpen(`Downloaded pickup picklist PDF for ${formatNumber2(picklistRows.length)} registrations.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to export pickup picklist PDF.", "error");
    } finally {
      setPickupPicklistPdfBusy(false);
    }
  };
  const exportSelectedPickupPicklistLotsPdf = async () => {
    if (pickupPicklistLotsPdfBusy) return;
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    const selectedRows = getSelectedRegRows();
    if (!selectedRows.length) {
      toastOpen("Select at least one filtered registration first.", "error");
      return;
    }
    setPickupPicklistLotsPdfBusy(true);
    try {
      const rowsSorted = [...selectedRows].sort((a, b) =>
        String(getDisplayBidderName(a) || getDisplayUsername(a) || a?.bidderEmail || a?.email || "").localeCompare(
          String(getDisplayBidderName(b) || getDisplayUsername(b) || b?.bidderEmail || b?.email || ""),
          undefined,
          { sensitivity: "base" }
        )
      );
      const exportRows = await Promise.all(
        rowsSorted.map(async (regRow) => {
          const emailLower = normalize(regRow?.bidderEmail || regRow?.email);
          const picklistEntry = buildRegistrationPickupPicklistEntry(regRow);
          if (!isEmail(emailLower)) {
            return { regRow, picklistEntry, lots: [] };
          }
          const data = await fetchUserLots(emailLower, selectedAuctionId);
          const lots = Array.isArray(data?.lots) ? data.lots : [];
          const sortedLots = [...lots].sort((a, b) => {
            const aLot = toAbsInt(a?.lot || 0);
            const bLot = toAbsInt(b?.lot || 0);
            if (aLot !== bLot) return aLot - bLot;
            return String(a?.title || "").localeCompare(String(b?.title || ""), undefined, { sensitivity: "base" });
          });
          return { regRow, picklistEntry, lots: sortedLots };
        })
      );

      const [{ jsPDF }, autoTableModule] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
      const autoTable = autoTableModule?.default || autoTableModule?.autoTable;
      if (typeof jsPDF !== "function" || typeof autoTable !== "function") {
        throw new Error("PDF export library failed to load.");
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "letter",
        compress: true,
      });
      const auctionName = String(selectedAuctionLabel || selectedAuctionId || "").trim() || "Auction";

      exportRows.forEach(({ regRow, picklistEntry, lots }, index) => {
        if (index > 0) doc.addPage("letter", "landscape");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(17, 24, 39);
        doc.text("Pickup Picklist + Lots", 36, 38);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.text(`Auction: ${auctionName}`, 36, 58);

        autoTable(doc, {
          startY: 74,
          head: [["Name", "Location", "Email", "Phone", "Pickup Date Time", "Total Lots", "Total Hammer", "Shelf", "Amount Due"]],
          body: [[
            picklistEntry.Name,
            picklistEntry.Location,
            picklistEntry.Email,
            picklistEntry["Phone Number"],
            picklistEntry["Pickup Date Time"],
            picklistEntry["Total Lots"],
            picklistEntry["Total Hammer"],
            picklistEntry.Shelf,
            picklistEntry["Amount Due"],
          ]],
          margin: { left: 36, right: 36 },
          styles: {
            fontSize: 10,
            cellPadding: 5,
            textColor: [15, 23, 42],
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
            valign: "middle",
          },
          headStyles: {
            fillColor: [79, 70, 229],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
        });

        const sectionStartY = Number(doc.lastAutoTable?.finalY || 126) + 22;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(17, 24, 39);
        doc.text(
          `Lots for ${String(getDisplayBidderName(regRow) || getDisplayUsername(regRow) || regRow?.bidderEmail || regRow?.email || "Bidder").trim()}`,
          36,
          sectionStartY
        );

        autoTable(doc, {
          startY: sectionStartY + 10,
          head: [["Lot", "Title", "Winning Bid"]],
          body: lots.length
            ? lots.map((lot) => [
                String(lot?.lot || "—").trim() || "—",
                String(lot?.title || "—").trim() || "—",
                `$${moneyPlain(safeNum(lot?.highBid || 0))}`,
              ])
            : [["No lots found for this selected registration.", "", ""]],
          margin: { left: 36, right: 36, bottom: 30 },
          styles: {
            fontSize: 9,
            cellPadding: 5,
            textColor: [15, 23, 42],
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
            valign: "top",
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [14, 116, 144],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 46 },
            1: { cellWidth: 512 },
            2: { cellWidth: 86, halign: "right" },
          },
        });
      });

      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(auctionName || selectedAuctionId) || "auction";
      doc.save(`pickup-picklist-lots-${auctionPart}-${stamp}.pdf`);
      toastOpen(`Downloaded picklist + lots PDF for ${formatNumber2(exportRows.length)} registrations.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to export picklist + lots PDF.", "error");
    } finally {
      setPickupPicklistLotsPdfBusy(false);
    }
  };
  const exportSelectedDeliveryManifestExcel = async () => {
    if (deliveryManifestExcelBusy) return;
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    const selectedRows = getSelectedDeliveryRowsForExport(getSelectedRegRows());
    if (!selectedRows.length) {
      toastOpen("Select at least one delivery registration first.", "error");
      return;
    }
    setDeliveryManifestExcelBusy(true);
    try {
      const { auctionName, deliveryRows } = buildSelectedRegistrationsDeliveryData(selectedRows);
      const xlsxModule = await import("xlsx");
      const XLSX = xlsxModule?.utils ? xlsxModule : xlsxModule?.default;
      if (!XLSX?.utils || typeof XLSX.writeFile !== "function") {
        throw new Error("XLSX export library failed to load.");
      }
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([
        ["Auction Name", auctionName],
        [],
      ]);
      XLSX.utils.sheet_add_json(worksheet, deliveryRows, { origin: "A3" });
      deliveryRows.forEach((row, idx) => {
        const phoneValue = row?.["Phone Number"];
        const addressValue = row?.["Delivery Address"];
        const googleMapsUrl = buildGoogleMapsUrl(addressValue);
        const appleMapsUrl = buildAppleMapsUrl(addressValue);
        const phoneCellRef = XLSX.utils.encode_cell({ r: 3 + idx, c: 3 });
        const addressCellRef = XLSX.utils.encode_cell({ r: 3 + idx, c: 5 });
        const googleCellRef = XLSX.utils.encode_cell({ r: 3 + idx, c: 6 });
        const appleCellRef = XLSX.utils.encode_cell({ r: 3 + idx, c: 7 });
        const telHref = getPhoneTelHref(phoneValue);
        if (telHref && worksheet[phoneCellRef]) {
          worksheet[phoneCellRef].l = {
            Target: telHref,
            Tooltip: String(phoneValue || "").trim() || "Call phone number",
          };
        }
        if (googleMapsUrl && worksheet[addressCellRef]) {
          worksheet[addressCellRef].l = {
            Target: googleMapsUrl,
            Tooltip: "Open delivery address in Google Maps",
          };
        }
        if (googleMapsUrl && worksheet[googleCellRef]) {
          worksheet[googleCellRef].l = {
            Target: googleMapsUrl,
            Tooltip: "Open in Google Maps",
          };
        }
        if (appleMapsUrl && worksheet[appleCellRef]) {
          worksheet[appleCellRef].l = {
            Target: appleMapsUrl,
            Tooltip: "Open in Apple Maps",
          };
        }
      });
      worksheet["!cols"] = [
        { wch: 24 },
        { wch: 18 },
        { wch: 30 },
        { wch: 18 },
        { wch: 18 },
        { wch: 44 },
        { wch: 14 },
        { wch: 14 },
        { wch: 11 },
        { wch: 14 },
        { wch: 14 },
        { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "delivery manifest");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(auctionName || selectedAuctionId) || "auction";
      XLSX.writeFile(workbook, `delivery-manifest-${auctionPart}-${stamp}.xlsx`, { cellStyles: true });
      toastOpen(`Downloaded delivery manifest for ${formatNumber2(deliveryRows.length)} registrations.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to export delivery manifest XLSX.", "error");
    } finally {
      setDeliveryManifestExcelBusy(false);
    }
  };
  const exportSelectedDeliveryManifestPdf = async () => {
    if (deliveryManifestPdfBusy) return;
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    const selectedRows = getSelectedDeliveryRowsForExport(getSelectedRegRows());
    if (!selectedRows.length) {
      toastOpen("Select at least one delivery registration first.", "error");
      return;
    }
    setDeliveryManifestPdfBusy(true);
    try {
      const { auctionName, deliveryRows } = buildSelectedRegistrationsDeliveryData(selectedRows);
      const [{ jsPDF }, autoTableModule] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
      const autoTable = autoTableModule?.default || autoTableModule?.autoTable;
      if (typeof jsPDF !== "function" || typeof autoTable !== "function") {
        throw new Error("PDF export library failed to load.");
      }
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "legal",
        compress: true,
      });
      doc.setFontSize(19);
      doc.setTextColor(17, 24, 39);
      doc.text("Delivery Manifest", 36, 40);
      doc.setFontSize(13);
      doc.setTextColor(71, 85, 105);
      doc.text(`Auction Name: ${auctionName}`, 36, 62);
      autoTable(doc, {
        startY: 80,
        head: [[
          "Name",
          "Delivery Location",
          "Email",
          "Phone Number",
          "Delivery Date",
          "Delivery Address",
          "Google Maps",
          "Apple Maps",
          "Total Lots",
          "Total Hammer",
          "Amount Due",
          "Delivered",
        ]],
        body: deliveryRows.map((row) => [
          row.Name,
          row["Delivery Location"],
          row.Email,
          row["Phone Number"],
          row["Delivery Date"],
          row["Delivery Address"],
          row["Google Maps"],
          row["Apple Maps"],
          row["Total Lots"],
          row["Total Hammer"],
          row["Amount Due"],
          row.Delivered,
        ]),
        margin: { left: 36, right: 36, top: 30, bottom: 30 },
        styles: {
          fontSize: 10,
          cellPadding: 5,
          textColor: [15, 23, 42],
          lineColor: [226, 232, 240],
          lineWidth: 0.5,
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [217, 119, 6],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 10,
        },
        alternateRowStyles: {
          fillColor: [255, 251, 235],
        },
        columnStyles: {
          0: { cellWidth: 92 },
          1: { cellWidth: 70 },
          2: { cellWidth: 134 },
          3: { cellWidth: 78 },
          4: { cellWidth: 88 },
          5: { cellWidth: 170 },
          6: { cellWidth: 54, halign: "center" },
          7: { cellWidth: 50, halign: "center" },
          8: { cellWidth: 38, halign: "center" },
          9: { cellWidth: 60, halign: "right" },
          10: { cellWidth: 58, halign: "right" },
          11: { cellWidth: 44, halign: "center" },
        },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          const row = deliveryRows[data.row.index] || null;
          if (!row) return;
          if (data.column.index === 3 && getPhoneTelHref(row["Phone Number"])) {
            data.cell.styles.textColor = [37, 99, 235];
          }
          if (data.column.index === 5 && buildGoogleMapsUrl(row["Delivery Address"])) {
            data.cell.styles.textColor = [37, 99, 235];
          }
          if (data.column.index === 6 && buildGoogleMapsUrl(row["Delivery Address"])) {
            data.cell.styles.textColor = [22, 163, 74];
          }
          if (data.column.index === 7 && buildAppleMapsUrl(row["Delivery Address"])) {
            data.cell.styles.textColor = [79, 70, 229];
          }
        },
        didDrawCell: (data) => {
          if (data.section !== "body") return;
          const row = deliveryRows[data.row.index] || null;
          if (!row) return;
          const phoneHref = getPhoneTelHref(row["Phone Number"]);
          const googleMapsUrl = buildGoogleMapsUrl(row["Delivery Address"]);
          const appleMapsUrl = buildAppleMapsUrl(row["Delivery Address"]);
          try {
            if (data.column.index === 3 && phoneHref) {
              doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: phoneHref });
            }
            if (data.column.index === 5 && googleMapsUrl) {
              doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: googleMapsUrl });
            }
            if (data.column.index === 6 && googleMapsUrl) {
              doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: googleMapsUrl });
            }
            if (data.column.index === 7 && appleMapsUrl) {
              doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: appleMapsUrl });
            }
          } catch (e) {
            // Ignore link annotation failures and keep the PDF export working.
          }
        },
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(auctionName || selectedAuctionId) || "auction";
      doc.save(`delivery-manifest-${auctionPart}-${stamp}.pdf`);
      toastOpen(`Downloaded delivery manifest PDF for ${formatNumber2(deliveryRows.length)} registrations.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to export delivery manifest PDF.", "error");
    } finally {
      setDeliveryManifestPdfBusy(false);
    }
  };
  const exportSelectedDeliveryManifestLotsPdf = async () => {
    if (deliveryManifestLotsPdfBusy) return;
    if (!selectedAuctionId) {
      toastOpen("Select an auction first.", "error");
      return;
    }
    const selectedRows = getSelectedDeliveryRowsForExport(getSelectedRegRows());
    if (!selectedRows.length) {
      toastOpen("Select at least one delivery registration first.", "error");
      return;
    }
    setDeliveryManifestLotsPdfBusy(true);
    try {
      const rowsSorted = [...selectedRows].sort((a, b) =>
        String(getDisplayBidderName(a) || getDisplayUsername(a) || a?.bidderEmail || a?.email || "").localeCompare(
          String(getDisplayBidderName(b) || getDisplayUsername(b) || b?.bidderEmail || b?.email || ""),
          undefined,
          { sensitivity: "base" }
        )
      );
      const exportRows = await Promise.all(
        rowsSorted.map(async (regRow) => {
          const emailLower = normalize(regRow?.bidderEmail || regRow?.email);
          const manifestEntry = buildRegistrationDeliveryManifestEntry(regRow);
          if (!isEmail(emailLower)) {
            return { regRow, manifestEntry, lots: [] };
          }
          const data = await fetchUserLots(emailLower, selectedAuctionId);
          const lots = Array.isArray(data?.lots) ? data.lots : [];
          const sortedLots = [...lots].sort((a, b) => {
            const aLot = toAbsInt(a?.lot || 0);
            const bLot = toAbsInt(b?.lot || 0);
            if (aLot !== bLot) return aLot - bLot;
            return String(a?.title || "").localeCompare(String(b?.title || ""), undefined, { sensitivity: "base" });
          });
          return { regRow, manifestEntry, lots: sortedLots };
        })
      );

      const [{ jsPDF }, autoTableModule] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
      const autoTable = autoTableModule?.default || autoTableModule?.autoTable;
      if (typeof jsPDF !== "function" || typeof autoTable !== "function") {
        throw new Error("PDF export library failed to load.");
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "legal",
        compress: true,
      });
      const auctionName = String(selectedAuctionLabel || selectedAuctionId || "").trim() || "Auction";

      exportRows.forEach(({ regRow, manifestEntry, lots }, index) => {
        if (index > 0) doc.addPage("legal", "landscape");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(17, 24, 39);
        doc.text("Delivery Manifest + Lots", 36, 38);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.text(`Auction: ${auctionName}`, 36, 58);

        autoTable(doc, {
          startY: 74,
          head: [[
            "Name",
            "Delivery Location",
            "Email",
            "Phone",
            "Delivery Date",
            "Delivery Address",
            "Google",
            "Apple",
            "Total Lots",
            "Total Hammer",
            "Amount Due",
            "Delivered",
          ]],
          body: [[
            manifestEntry.Name,
            manifestEntry["Delivery Location"],
            manifestEntry.Email,
            manifestEntry["Phone Number"],
            manifestEntry["Delivery Date"],
            manifestEntry["Delivery Address"],
            manifestEntry["Google Maps"],
            manifestEntry["Apple Maps"],
            manifestEntry["Total Lots"],
            manifestEntry["Total Hammer"],
            manifestEntry["Amount Due"],
            manifestEntry.Delivered,
          ]],
          margin: { left: 36, right: 36 },
          styles: {
            fontSize: 8.5,
            cellPadding: 4,
            textColor: [15, 23, 42],
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
            valign: "middle",
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [217, 119, 6],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [255, 251, 235],
          },
          columnStyles: {
            0: { cellWidth: 92 },
            1: { cellWidth: 70 },
            2: { cellWidth: 134 },
            3: { cellWidth: 76 },
            4: { cellWidth: 88 },
            5: { cellWidth: 170 },
            6: { cellWidth: 44, halign: "center" },
            7: { cellWidth: 40, halign: "center" },
            8: { cellWidth: 38, halign: "center" },
            9: { cellWidth: 60, halign: "right" },
            10: { cellWidth: 58, halign: "right" },
            11: { cellWidth: 44, halign: "center" },
          },
          didParseCell: (data) => {
            if (data.section !== "body") return;
            if (data.column.index === 3 && getPhoneTelHref(manifestEntry["Phone Number"])) {
              data.cell.styles.textColor = [37, 99, 235];
            }
            if (data.column.index === 5 && buildGoogleMapsUrl(manifestEntry["Delivery Address"])) {
              data.cell.styles.textColor = [37, 99, 235];
            }
            if (data.column.index === 6 && buildGoogleMapsUrl(manifestEntry["Delivery Address"])) {
              data.cell.styles.textColor = [22, 163, 74];
            }
            if (data.column.index === 7 && buildAppleMapsUrl(manifestEntry["Delivery Address"])) {
              data.cell.styles.textColor = [79, 70, 229];
            }
          },
          didDrawCell: (data) => {
            if (data.section !== "body") return;
            const phoneHref = getPhoneTelHref(manifestEntry["Phone Number"]);
            const googleMapsUrl = buildGoogleMapsUrl(manifestEntry["Delivery Address"]);
            const appleMapsUrl = buildAppleMapsUrl(manifestEntry["Delivery Address"]);
            try {
              if (data.column.index === 3 && phoneHref) {
                doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: phoneHref });
              }
              if (data.column.index === 5 && googleMapsUrl) {
                doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: googleMapsUrl });
              }
              if (data.column.index === 6 && googleMapsUrl) {
                doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: googleMapsUrl });
              }
              if (data.column.index === 7 && appleMapsUrl) {
                doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: appleMapsUrl });
              }
            } catch (e) {
              // Ignore link annotation failures and keep the PDF export working.
            }
          },
        });

        const sectionStartY = Number(doc.lastAutoTable?.finalY || 126) + 22;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(17, 24, 39);
        doc.text(
          `Lots for ${String(getDisplayBidderName(regRow) || getDisplayUsername(regRow) || regRow?.bidderEmail || regRow?.email || "Bidder").trim()}`,
          36,
          sectionStartY
        );

        autoTable(doc, {
          startY: sectionStartY + 10,
          head: [["Lot", "Title", "Winning Bid"]],
          body: lots.length
            ? lots.map((lot) => [
                String(lot?.lot || "—").trim() || "—",
                String(lot?.title || "—").trim() || "—",
                `$${moneyPlain(safeNum(lot?.highBid || 0))}`,
              ])
            : [["No lots found for this selected registration.", "", ""]],
          margin: { left: 36, right: 36, bottom: 30 },
          styles: {
            fontSize: 9,
            cellPadding: 5,
            textColor: [15, 23, 42],
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
            valign: "top",
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [14, 116, 144],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 46 },
            1: { cellWidth: 720 },
            2: { cellWidth: 86, halign: "right" },
          },
        });
      });

      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const auctionPart = sanitizeFileNamePart(auctionName || selectedAuctionId) || "auction";
      doc.save(`delivery-manifest-lots-${auctionPart}-${stamp}.pdf`);
      toastOpen(`Downloaded delivery + lots PDF for ${formatNumber2(exportRows.length)} registrations.`, "success");
    } catch (e) {
      toastOpen(e?.message || "Failed to export delivery + lots PDF.", "error");
    } finally {
      setDeliveryManifestLotsPdfBusy(false);
    }
  };
  const openAuctionUserDetailsModal = async (regRow) => {
    const emailLower = normalize(regRow?.bidderEmail || regRow?.email);
    if (!isEmail(emailLower)) return;
    const matchedUserRow =
      (Array.isArray(allFilteredUsers) ? allFilteredUsers : []).find(
        (user) => normalize(user?.email || user?.id) === emailLower
      ) || null;
    const seedRow = { ...(matchedUserRow || {}), ...(regRow || {}) };
    clearAuctionUserDetailsAutoSaveTimers();
    setAuctionUserDetailsOpen(true);
    setAuctionUserDetailsBusy(true);
    setAuctionUserDetailsErr("");
    setAuctionUserDetailsSaveError("");
    setAuctionUserDetailsSavingByField({});
    setAuctionUserDetailsData(null);
    setAuctionUserDetailsEmail(emailLower);
    setAuctionUserDetailsSourceReg(seedRow || null);
    setAuctionUserDetailsForm(buildAuctionUserDetailsEditableForm(seedRow || {}, emailLower));
    try {
      const qs = new URLSearchParams();
      qs.set("email", emailLower);
      qs.set("includeRegistrations", "false");
      const { res, data } = await apiFetchJson(`${API}${ROUTES.userByEmail}?${qs.toString()}`);
      if (handleNeedsAuth(res, data)) {
        setAuctionUserDetailsErr("Authorization required.");
        return;
      }
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      setAuctionUserDetailsData(data || null);
    } catch (e) {
      setAuctionUserDetailsErr(e?.message || "Failed to load user details.");
    } finally {
      setAuctionUserDetailsBusy(false);
    }
  };
  useEffect(() => {
    if (!auctionUserDetailsOpen) return;
    const row = {
      ...(auctionUserDetailsSourceReg || {}),
      ...(auctionUserDetailsData || {}),
    };
    const emailLower = normalize(row?.email || row?.id || row?.bidderEmail || row?.emailLower || auctionUserDetailsEmail || "");
    if (!isEmail(emailLower)) return;
    setAuctionUserDetailsForm(buildAuctionUserDetailsEditableForm(row, emailLower));
  }, [auctionUserDetailsOpen, auctionUserDetailsData, auctionUserDetailsSourceReg, auctionUserDetailsEmail]);

  const sanitizeUrl = (u) => {
    const s = String(u || "").trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    return `https://${s}`;
  };

  const chargebackFilteredAuctionRegs = useMemo(() => {
    const base = Array.isArray(filteredSortedAuctionRegs) ? filteredSortedAuctionRegs : [];

    return base.filter((r) => {
      if (chargebackEmailFulfillmentFilter === "shipping_requested" && !isShippingRequested(r)) return false;
      if (chargebackEmailFulfillmentFilter === "pickup" && isShippingRequested(r)) return false;

      const sent = isChargebackEmailSent(r);
      if (chargebackEmailAlreadySentFilter === "sent" && !sent) return false;
      if (chargebackEmailAlreadySentFilter === "not_sent" && sent) return false;

      const emailLower = normalize(r?.bidderEmail || r?.email);
      const lotsCount = toAbsInt(r?.lotsCount ?? lotsAgg[emailLower]?.lotsCount ?? 0);
      const hasDisplayName =
        String(getDisplayBidderName(r) || "").trim().length > 0 ||
        String(getDisplayUsername(r) || "").trim().length > 0;

      if (chargebackEmailWonLotFilter === "won_lots" && lotsCount <= 0) return false;
      if (chargebackEmailWonLotFilter === "did_not_win" && lotsCount > 0) return false;
      if (chargebackEmailWonLotFilter === "won_lots_no_name" && !(lotsCount > 0 && !hasDisplayName)) return false;

      return true;
    });
  }, [
    filteredSortedAuctionRegs,
    chargebackEmailFulfillmentFilter,
    chargebackEmailAlreadySentFilter,
    chargebackEmailWonLotFilter,
    lotsAgg,
  ]);
  const {
    createEmailTemplateBaseId,
    createEmailTemplateName,
    createEmailTemplateOpen,
    defaultEmailFromName,
    deleteSelectedEmailTemplate,
    emailAutoSaveStatus,
    emailBusy,
    emailEditorRef,
    emailFromName,
    emailMode,
    emailOpen,
    emailPreviewBusy,
    emailPreviewHtml,
    emailRecipientTotalForDisplay,
    emailSingleUserTarget,
    emailSubject,
    emailTab,
    emailTemplates,
    emailTemplatesBusy,
    getComposedEmailHtml,
    handleEmailEditorReady,
    handleSelectEmailTemplate,
    openEmailForAuctionSelection,
    openEmailForLoadedUsers,
    openEmailForUser,
    requestEmailPreview,
    saveShippingText,
    selectedEmailTemplateId,
    selectedEmailTemplateName,
    sendEmailNow,
    setCreateEmailTemplateBaseId,
    setCreateEmailTemplateName,
    setCreateEmailTemplateOpen,
    setEmailFromName,
    setEmailOpen,
    setEmailSubject,
    setEmailTab,
    setSelectedEmailTemplateName,
    setShippingTextDraft,
    setShippingTextOpen,
    setShippingTextSaved,
    shippingTextDirty,
    shippingTextDraft,
    shippingTextOpen,
    shippingTextSaved,
    shippingTextSaving,
    createEmailTemplateFromCurrent,
  } = useHibidEmailTools({
    apiBaseUrl: API,
    routes: ROUTES,
    apiFetchJson,
    orgReady,
    primary,
    subscriptionId,
    subscriptionName,
    selectedAuctionId,
    selectedAuctionLabel,
    allFilteredUsers,
    selectedFilteredUsers,
    chargebackFilteredAuctionRegs,
    normalize,
    isEmail,
    toAbsInt,
    toastOpen,
    sanitizeUrl,
    resolveEmailFromName,
    emptyEmailDesign: EMPTY_EMAIL_DESIGN,
    defaultShippingPolicyText: DEFAULT_SHIPPING_POLICY_TEXT,
    setUserRows,
    setAuctionRegs,
    invalidateUsersCache,
    invalidateAuctionRegsCache,
    setChargebackEmailFulfillmentFilter,
    setChargebackEmailAlreadySentFilter,
    setChargebackEmailWonLotFilter,
  });
  const visibleRegistrationBoxLabelRows = useMemo(
    () =>
      (Array.isArray(chargebackFilteredAuctionRegs) ? chargebackFilteredAuctionRegs : []).filter(
        (row) => parseShelfBoxLabels(getRegistrationShelfText(row)).length > 0
      ),
    [chargebackFilteredAuctionRegs, aregShelfDraftByEmail]
  );
  const selectedRegistrationRows = useMemo(
    () => getSelectedRegRows(),
    [chargebackFilteredAuctionRegs, aregSelected]
  );
  const selectedRegistrationBoxLabelRows = useMemo(
    () =>
      (Array.isArray(selectedRegistrationRows) ? selectedRegistrationRows : []).filter(
        (row) => parseShelfBoxLabels(getRegistrationShelfText(row)).length > 0
      ),
    [selectedRegistrationRows, aregShelfDraftByEmail]
  );
  const bulkBoxLabelMode = selectedRegistrationRows.length ? "selected" : "all";
  const bulkBoxLabelRows = bulkBoxLabelMode === "selected" ? selectedRegistrationBoxLabelRows : visibleRegistrationBoxLabelRows;

  const modalPaperSx = {
    borderRadius: "1.25rem",
    overflow: "hidden",
    border: "1px solid rgba(148,163,184,0.4)",
    boxShadow: "0 18px 45px rgba(15,23,42,0.18)",
  };
  const filterRowSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(90px, 20%) minmax(0, 80%)",
      md: "minmax(130px, 20%) minmax(0, 80%)",
    },
    alignItems: "start",
    columnGap: 0.75,
    rowGap: 0.5,
    justifyItems: "start",
    width: "100%",
  };
  const filterLabelSx = {
    fontSize: 12,
    fontWeight: 900,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    width: "100%",
    minWidth: 0,
    pt: 0.6,
  };
  const filterValuesSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    flexWrap: "wrap",
    justifyContent: "flex-start",
    width: "100%",
    minWidth: 0,
  };
  const filterPillSx = (active) => ({
    textTransform: "none",
    borderRadius: 999,
    px: 1.5,
    py: 0.5,
    fontWeight: 900,
    fontSize: 12,
    lineHeight: 1,
    minWidth: 0,
    color: active ? "#fff" : primary,
    border: active ? "1px solid transparent" : "1px solid rgba(51,57,153,0.20)",
    background: active ? "linear-gradient(90deg,#333999,#5555bb)" : "#fff",
    "&:hover": {
      background: active ? "linear-gradient(90deg,#2d2f86,#494cb0)" : "rgba(99,102,241,0.08)",
      borderColor: active ? "transparent" : "rgba(51,57,153,0.28)",
    },
  });
  const tierFilterOptions = [
    { value: "all", label: "All" },
    { value: "new", label: "New" },
    { value: "casual", label: "Casual" },
    { value: "emerging", label: "Emerging" },
    { value: "power", label: "Power" },
    { value: "elite", label: "Elite" },
  ];
  const customerTypeFilterOptions = [
    { value: "all", label: "Any" },
    ...CUSTOMER_TYPE_OPTIONS.map((key) => ({ value: key, label: customerTypeLabel(key) })),
    { value: "unclassified", label: "Unclassified" },
  ];
  const regsFilterOptions = [
    { value: "all", label: "Any" },
    ...Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      return { value: String(n), label: `<= ${n}` };
    }),
  ];
  const lastEmailFilterOptions = [
    { value: "all", label: "Any" },
    { value: "days:1", label: "1d" },
    { value: "days:7", label: "7d" },
    { value: "days:30", label: "30d" },
    { value: "days:80", label: "80d" },
  ];
  const newSubFilterOptions = [
    { value: "new_to_af", label: "New to AF" },
    { value: "new_to_hibid_and_af", label: "New to HiBid + AF" },
  ];
  const accountManagerFilterOptions = (Array.isArray(accountManagers) ? accountManagers : [])
    .map((manager) => ({
      value: normalizeAccountManagerId(manager?.id || ""),
      label: accountManagerDisplayName(manager),
    }))
    .filter((opt) => Boolean(opt.value) && Boolean(String(opt.label || "").trim()))
    .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  const accountManagerFilterPillOptions = [{ value: "all", label: "Any" }, ...accountManagerFilterOptions];
  const uploadDialogOpen = uploadBusy || shipBusy || listernautUploadBusy;
  const uploadDialogMessage = shipBusy
    ? "Uploading shipping file. Please wait…"
    : uploadDialogText || "Uploading file. Please wait…";
  const lotsUserEmailLower = normalize(lotsUser?.bidderEmail || lotsUser?.email);
  const lotsUserIsFloor = !!lotsUser?.isFloorBidder || isFloorBidderEmail(lotsUserEmailLower);
  const lotsUserShippingRequested = isShippingRequested(lotsUser);
  const lotsUserDelivery = getRegistrationDelivery(lotsUser || {});
  const lotsUserFulfillmentMode = lotsUserIsFloor
    ? "none"
    : getRegistrationFulfillmentMode({
      row: lotsUser || {},
      shippingRequested: lotsUserShippingRequested,
      delivery: lotsUserDelivery,
    }) === "delivery"
      ? "delivered"
      : lotsUserShippingRequested
        ? "shipped"
        : "pickedUp";
  const lotsUserFulfillmentLabel =
    lotsUserFulfillmentMode === "shipped"
      ? "Shipped"
      : lotsUserFulfillmentMode === "delivered"
        ? "Delivered"
        : "Picked Up";
  const lotsPickupDetails = getLotPickupCounts(lotsData || {});
  const lotsShippedDetails = getLotShippedCounts(lotsData || {});
  const lotsDeliveredStatusLabel = String(
    lotsData?.deliveredStatusLabel || lotsUserDelivery?.deliveredStatusLabel || ""
  ).trim();
  const lotsUserFulfillmentStatusLabel =
    lotsUserFulfillmentMode === "pickedUp"
      ? lotsPickupDetails.totalLotsCount > 0
        ? lotsPickupDetails.pickupStatusLabel
        : "—"
      : lotsUserFulfillmentMode === "shipped"
        ? lotsShippedDetails.totalLotsCount > 0
          ? lotsShippedDetails.shippedStatusLabel
          : "—"
        : lotsUserFulfillmentMode === "delivered"
          ? lotsDeliveredStatusLabel || "—"
        : "—";
  const registrationFulfillmentDetailsRow = registrationFulfillmentDetails?.row || null;
  const registrationFulfillmentDetailsEmail = normalize(
    registrationFulfillmentDetailsRow?.bidderEmail || registrationFulfillmentDetailsRow?.email
  );
  const registrationFulfillmentDetailsCached = registrationFulfillmentDetailsEmail
    ? lotsAgg[registrationFulfillmentDetailsEmail]
    : null;
  const registrationFulfillmentDetailsLotsCount = safeNum(
    registrationFulfillmentDetailsRow?.lotsCount || registrationFulfillmentDetailsCached?.lotsCount || 0
  );
  const registrationFulfillmentDetailsShippingRequested = isShippingRequested(registrationFulfillmentDetailsRow);
  const registrationFulfillmentDetailsPickup = getLotPickupCounts({
    lotsCount: registrationFulfillmentDetailsLotsCount,
    pickedUpLotsCount:
      registrationFulfillmentDetailsRow?.pickedUpLotsCount ?? registrationFulfillmentDetailsCached?.pickedUpLotsCount,
    pickedUp: registrationFulfillmentDetailsRow?.pickedUp ?? registrationFulfillmentDetailsCached?.pickedUp,
  });
  const registrationFulfillmentDetailsShipped = getLotShippedCounts({
    lotsCount: registrationFulfillmentDetailsLotsCount,
    shippedLotsCount:
      registrationFulfillmentDetailsRow?.shippedLotsCount ?? registrationFulfillmentDetailsCached?.shippedLotsCount,
    shipped: registrationFulfillmentDetailsRow?.shipped ?? registrationFulfillmentDetailsCached?.shipped,
  });
  const registrationFulfillmentDetailsPickupAppointment = getRegistrationPickupAppointment(
    registrationFulfillmentDetailsRow
  );
  const registrationFulfillmentDetailsPickupSelectionMeta = getPickupAppointmentSelectionMeta(
    registrationFulfillmentDetailsPickupAppointment
  );
  const registrationFulfillmentDetailsDelivery = getRegistrationDelivery(registrationFulfillmentDetailsRow);
  const registrationFulfillmentDetailsMode = getRegistrationFulfillmentMode({
    row: registrationFulfillmentDetailsRow,
    shippingRequested: registrationFulfillmentDetailsShippingRequested,
    delivery: registrationFulfillmentDetailsDelivery,
  });
  const registrationFulfillmentDetailsUserName =
    String(
      getDisplayBidderName(registrationFulfillmentDetailsRow) ||
      getDisplayUsername(registrationFulfillmentDetailsRow) ||
      registrationFulfillmentDetailsRow?.bidderEmail ||
      registrationFulfillmentDetailsRow?.email ||
      ""
    ).trim() || "This registration";
  const registrationFulfillmentDetailsButtonMeta = getRegistrationFulfillmentButtonMeta(
    registrationFulfillmentDetailsMode
  );
  const registrationFulfillmentDetailsPickupEditing = !!aregPickupEditByEmail[registrationFulfillmentDetailsEmail];
  const registrationFulfillmentDetailsPickupDraft =
    aregPickupDraftByEmail[registrationFulfillmentDetailsEmail] || {};
  const registrationFulfillmentDetailsPickupLocationValue =
    registrationFulfillmentDetailsPickupEditing &&
    registrationFulfillmentDetailsPickupDraft.location !== undefined
      ? String(registrationFulfillmentDetailsPickupDraft.location || "")
      : String(registrationFulfillmentDetailsPickupAppointment?.location || "");
  const registrationFulfillmentDetailsPickupAppointmentDateKeyValue =
    registrationFulfillmentDetailsPickupEditing &&
    registrationFulfillmentDetailsPickupDraft.appointmentDateKey !== undefined
      ? String(registrationFulfillmentDetailsPickupDraft.appointmentDateKey || "")
      : String(
        registrationFulfillmentDetailsPickupAppointment?.appointmentDateKey ||
        localDateKeyFromMs(
          registrationFulfillmentDetailsPickupAppointment?.appointmentAtMs ||
          registrationFulfillmentDetailsPickupAppointment?.startAtMs ||
          0
        )
      );
  const registrationFulfillmentDetailsPickupAppointmentAtMsValue =
    registrationFulfillmentDetailsPickupEditing &&
    registrationFulfillmentDetailsPickupDraft.appointmentAtMs !== undefined
      ? Number(registrationFulfillmentDetailsPickupDraft.appointmentAtMs || 0) || 0
      : Number(registrationFulfillmentDetailsPickupAppointment?.appointmentAtMs || 0) || 0;
  const registrationFulfillmentDetailsPickupAdditionalInfo =
    registrationFulfillmentDetailsPickupEditing &&
    registrationFulfillmentDetailsPickupDraft.additionalInfo !== undefined
      ? String(registrationFulfillmentDetailsPickupDraft.additionalInfo || "").trim()
      : String(registrationFulfillmentDetailsPickupAppointment?.additionalInfo || "").trim();
  const registrationFulfillmentDetailsPickupLocationOptions = getPickupLocationSelectOptions([
    ...selectedAuctionPickupLocationChoices,
    registrationFulfillmentDetailsPickupLocationValue,
  ]);
  const registrationFulfillmentDetailsPickupDateOptions = buildPickupDateOptionsForLocation(
    selectedAuctionPickupSlotsSource,
    registrationFulfillmentDetailsPickupLocationValue
  );
  const registrationFulfillmentDetailsPickupTimeOptions = buildPickupTimeOptionsForLocationAndDate(
    selectedAuctionPickupSlotsSource,
    registrationFulfillmentDetailsPickupLocationValue,
    registrationFulfillmentDetailsPickupAppointmentDateKeyValue
  );
  const registrationFulfillmentDetailsPickupSelectedTimeLabel =
    registrationFulfillmentDetailsPickupTimeOptions.find(
      (option) => option.value === String(registrationFulfillmentDetailsPickupAppointmentAtMsValue)
    )?.label || String(registrationFulfillmentDetailsPickupAppointment?.label || "").trim();
  const registrationFulfillmentDetailsPickupSaving =
    !!aregPickupSavingByEmail[registrationFulfillmentDetailsEmail];
  const registrationFulfillmentDetailsCanSavePickupDraft = canSaveRegistrationPickupDraft({
    location: registrationFulfillmentDetailsPickupLocationValue,
    appointmentDateKey: registrationFulfillmentDetailsPickupAppointmentDateKeyValue,
    appointmentAtMs: registrationFulfillmentDetailsPickupAppointmentAtMsValue,
    additionalInfo: registrationFulfillmentDetailsPickupAdditionalInfo,
  });
  const openFullDescriptionDialog = (title, text) => {
    const nextText = String(text ?? "");
    if (!nextText.trim()) return;
    setFullDescriptionDialog({
      open: true,
      title: String(title || "Full Description").trim() || "Full Description",
      text: nextText,
    });
  };
  const renderExpandableTextPreview = (value, title = "Full Text", maxLength = DESCRIPTION_PREVIEW_LENGTH) => {
    const text = String(value || "").trim();
    if (!text) return "—";
    const truncated = truncateDescriptionText(text, maxLength);
    const shouldShowFull = truncated !== text;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ color: "#374151", wordBreak: "break-word" }}>
          {truncated}
        </Typography>
        {shouldShowFull ? (
          <Button
            size="small"
            onClick={() => openFullDescriptionDialog(title, text)}
            sx={{
              textTransform: "none",
              minWidth: 0,
              px: 0,
              py: 0,
              fontWeight: 900,
              color: primary,
              "&:hover": { background: "transparent", textDecoration: "underline" },
            }}
          >
            View Full
          </Button>
        ) : null}
      </Box>
    );
  };
  const renderDescriptionPreview = (value, title = "Full Description") =>
    renderExpandableTextPreview(value, title, DESCRIPTION_PREVIEW_LENGTH);
  const toggleMediaUrlsExpanded = useCallback((key) => {
    const safeKey = String(key || "").trim();
    if (!safeKey) return;
    setExpandedMediaUrlsByKey((prev) => ({ ...prev, [safeKey]: !prev?.[safeKey] }));
  }, []);
  const renderAuctionManagerSummary = (managerNames = []) => {
    const rows = uniqSortedOptions(managerNames || []);
    if (!rows.length) {
      return (
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          —
        </Typography>
      );
    }
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {rows.map((name) => (
          <Chip
            key={`auction-manager-${name}`}
            size="small"
            label={name}
            sx={{ borderRadius: 999, fontWeight: 800, background: "#eff6ff", color: "#1d4ed8" }}
          />
        ))}
      </Box>
    );
  };
  const renderAuctionPickupSlotSummary = (slots = [], { limit = 3 } = {}) => {
    const rows = sanitizeAuctionPickupSlots(slots || []);
    if (!rows.length) {
      return (
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          —
        </Typography>
      );
    }
    const visible = rows.slice(0, limit);
    const hiddenCount = Math.max(0, rows.length - visible.length);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
        {visible.map((slot) => (
          <Typography key={String(slot?.slotId || `${slot?.startAtMs}-${slot?.endAtMs}`)} variant="caption" sx={{ color: "#0f172a", fontWeight: 700 }}>
            {formatPickupSlotLabel(slot)}
          </Typography>
        ))}
        {hiddenCount > 0 ? (
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
            +{formatNumber2(hiddenCount)} more
          </Typography>
        ) : null}
      </Box>
    );
  };
  const renderAuctionShareMiniTable = (
    rows = [],
    tone = "#475569",
    { showRevenue = false, consignorRows = [], showDisposition = false, showHammer = false } = {}
  ) => {
    const items = Array.isArray(rows) ? rows : [];
    const totals = computeAuctionShareTotals(items, { showRevenue, consignorRows, showDisposition });
    if (!items.length) {
      return (
        <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 800 }}>
          —
        </Typography>
      );
    }
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 1.2,
          border: "1px solid rgba(226,232,240,0.95)",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Table
          size="small"
          sx={{
            "& .MuiTableCell-root": {
              px: 0.7,
              py: 0.45,
              verticalAlign: "top",
              borderBottom: "1px solid #e2e8f0",
            },
          }}
        >
            <TableHead>
              <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
                <TableCell sx={{ color: tone, fontWeight: 900 }}>Name</TableCell>
                {showDisposition ? (
                  <>
                    <TableCell sx={{ color: tone, fontWeight: 900 }}>Total</TableCell>
                    <TableCell sx={{ color: tone, fontWeight: 900 }}>Sold</TableCell>
                    <TableCell sx={{ color: tone, fontWeight: 900 }}>Passed</TableCell>
                    {showHammer && <TableCell sx={{ color: tone, fontWeight: 900 }}>Total Hammer Price</TableCell>}
                    <TableCell sx={{ color: tone, fontWeight: 900 }}>Avg/Sold</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ color: tone, fontWeight: 900 }}>Lots</TableCell>
                    <TableCell sx={{ color: tone, fontWeight: 900 }}>Total Hammer Price</TableCell>
                  </>
                )}
                {showRevenue && <TableCell sx={{ color: tone, fontWeight: 900 }}>Revenue</TableCell>}
                {showRevenue && <TableCell sx={{ color: tone, fontWeight: 900 }}>Rev/Lot</TableCell>}
              </TableRow>
            </TableHead>
          <TableBody>
            {items.map((item, idx) => {
              const rev = showRevenue ? computeSellerRowRevenue(item, { consignorRows }) : null;
              return (
                <TableRow key={`${String(item?.name || "item").trim() || "item"}-${idx}`}>
                  <TableCell sx={{ color: "#111827", fontWeight: 900 }}>
                    {String(item?.name || "Unassigned").trim() || "Unassigned"}
                  </TableCell>
                  {showDisposition ? (
                    <>
                      <TableCell sx={{ color: "#475569" }}>
                        {formatNumber2(toAbsInt(item?.lotsCount || 0))}
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>
                        {formatNumber2(toAbsInt(item?.soldLotsCount || 0))}
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>
                        {formatNumber2(toAbsInt(item?.passedLotsCount || 0))}
                      </TableCell>
                      {showHammer && (
                        <TableCell sx={{ color: "#475569", fontWeight: 700 }}>
                          ${moneyPlain(item?.hammerTotal || 0)}
                        </TableCell>
                      )}
                      <TableCell sx={{ color: "#475569", fontWeight: 700 }}>
                        {toAbsInt(item?.soldLotsCount || 0) > 0 ? `$${moneyPlain(item?.avgHammerPerSoldLot || 0)}` : "—"}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ color: "#475569" }}>
                        {formatNumber2(toAbsInt(item?.lotsCount || 0))} ({formatPercent2(item?.lotsSharePct || 0)})
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>
                        ${moneyPlain(item?.hammerTotal || 0)} ({formatPercent2(item?.hammerSharePct || 0)})
                      </TableCell>
                    </>
                  )}
                  {showRevenue && (
                    <TableCell sx={{ color: "#047857", fontWeight: 900 }}>
                      ${moneyPlain(rev?.revenue || 0)}
                    </TableCell>
                  )}
                  {showRevenue && (
                    <TableCell sx={{ color: "#047857", fontWeight: 700 }}>
                      ${moneyPlain(rev?.revenuePerLot || 0)}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  backgroundColor: "#e5e7eb",
                  borderBottom: "1px solid #cbd5e1",
                },
              }}
            >
              <TableCell sx={{ color: "#111827", fontWeight: 900 }}>Total</TableCell>
              {showDisposition ? (
                <>
                  <TableCell sx={{ color: "#374151", fontWeight: 900 }}>
                    {formatNumber2(totals.lotsCount)}
                  </TableCell>
                  <TableCell sx={{ color: "#374151", fontWeight: 900 }}>
                    {formatNumber2(totals.soldLotsCount)}
                  </TableCell>
                  <TableCell sx={{ color: "#374151", fontWeight: 900 }}>
                    {formatNumber2(totals.passedLotsCount)}
                  </TableCell>
                  {showHammer && (
                    <TableCell sx={{ color: "#475569", fontWeight: 900 }}>
                      ${moneyPlain(totals.hammerTotal || 0)}
                    </TableCell>
                  )}
                  <TableCell sx={{ color: "#475569", fontWeight: 900 }}>
                    {totals.soldLotsCount > 0 ? `$${moneyPlain(totals.avgHammerPerSoldLot || 0)}` : "—"}
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={{ color: "#374151", fontWeight: 900 }}>
                    {formatNumber2(totals.lotsCount)} ({formatPercent2(totals.lotsSharePct || 0)})
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 900 }}>
                    ${moneyPlain(totals.hammerTotal || 0)} ({formatPercent2(totals.hammerSharePct || 0)})
                  </TableCell>
                </>
              )}
              {showRevenue && (
                <TableCell sx={{ color: "#047857", fontWeight: 900 }}>
                  ${moneyPlain(totals.revenue || 0)}
                </TableCell>
              )}
              {showRevenue && (
                <TableCell sx={{ color: "#047857", fontWeight: 900 }}>
                  ${moneyPlain(totals.revenuePerLot || 0)}
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  const renderRegistrationMetricCard = ({ label, value, subtext = "", valueColor = "#111827" }) => (
    <Box
      sx={{
        p: 1,
        borderRadius: 2,
        border: "1px solid rgba(226,232,240,0.95)",
        background: "#fff",
        minWidth: 0,
      }}
    >
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, display: "block" }}>
        {label}
      </Typography>
      <Typography sx={{ color: valueColor, fontWeight: 900, fontSize: 16, lineHeight: 1.2, mt: 0.25 }}>
        {value}
      </Typography>
      {subtext ? (
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mt: 0.3 }}>
          {subtext}
        </Typography>
      ) : null}
    </Box>
  );
  const renderRegistrationInfoCard = ({ label, action = null, children }) => (
    <Box
      sx={{
        p: 1,
        borderRadius: 2,
        border: "1px solid rgba(226,232,240,0.95)",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 0.65,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
          {label}
        </Typography>
        {action}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
  const renderAuctionRegistrationMobileCard = (r, idx) => {
    const emailLower = normalize(r?.bidderEmail || r?.email);
    const { firstName, lastName } = getFirstLastNameParts(getDisplayBidderName(r));
    const displayUsername = getDisplayUsername(r);
    const cached = lotsAgg[emailLower];
    const lotsCount = safeNum(r?.lotsCount || cached?.lotsCount || 0);
    const totalHighBid = safeNum(r?.totalHighBid || cached?.totalHighBid || 0);
    const shippingRequested = isShippingRequested(r);
    const delivery = getRegistrationDelivery(r);
    const fulfillmentMode = getRegistrationFulfillmentMode({
      row: r,
      shippingRequested,
      delivery,
    });
    const fulfillmentButtonMeta = getRegistrationFulfillmentButtonMeta(fulfillmentMode);
    const isFloorRegistration = !!r?.isFloorBidder || isFloorBidderEmail(emailLower);
    const pickupDetails = getLotPickupCounts({
      lotsCount,
      pickedUpLotsCount: r?.pickedUpLotsCount ?? cached?.pickedUpLotsCount,
      pickedUp: r?.pickedUp ?? cached?.pickedUp,
    });
    const shippedDetails = getLotShippedCounts({
      lotsCount,
      shippedLotsCount: r?.shippedLotsCount ?? cached?.shippedLotsCount,
      shipped: r?.shipped ?? cached?.shipped,
    });
    const checked = aregSelected?.has?.(emailLower);
    const chargebackEmailSent = isChargebackEmailSent(r);
    const signDetails = getChargebackSignDetails(r);
    const isNewUser = isAuctionRegistrationNewUser(r);
    const shelfValue =
      aregShelfDraftByEmail[emailLower] !== undefined
        ? String(aregShelfDraftByEmail[emailLower] || "")
        : String(r?.shelf || r?.pickupShelf || "").trim();
    const shelfSaving = !!aregShelfSavingByEmail[emailLower];
    const amountDueValue =
      aregAmountDueDraftByEmail[emailLower] !== undefined
        ? String(aregAmountDueDraftByEmail[emailLower] || "")
        : String(r?.amountDue || r?.amount_due || "").trim();
    const shelfBoxLabels = parseShelfBoxLabels(shelfValue);
    const boxLabelRowKey = getRegistrationBoxLabelRowKey(r, idx);
    const boxLabelRowBusy = registrationBoxLabelsBusy || !!registrationBoxLabelsBusyByKey[boxLabelRowKey];
    const fulfillmentChecked =
      fulfillmentMode === "shipping"
        ? shippedDetails.shipped
        : fulfillmentMode === "delivery"
          ? delivery.delivered
          : pickupDetails.pickedUp;
    const fulfillmentStatusLabel =
      fulfillmentMode === "shipping"
        ? shippedDetails.shippedStatusLabel
        : fulfillmentMode === "delivery"
          ? delivery.deliveredStatusLabel || `${delivery.delivered ? lotsCount : 0}/${lotsCount || 0}`
          : pickupDetails.pickupStatusLabel;
    const fulfillmentCheckboxDisabled =
      !isEmail(emailLower) ||
      lotsCount <= 0 ||
      (fulfillmentMode === "shipping" && shippedDetails.totalLotsCount === 0) ||
      (fulfillmentMode === "pickup" && pickupDetails.totalLotsCount === 0);

    return (
      <Paper
        key={r.id || r.gmailMessageId || `${emailLower}-${idx}`}
        sx={{
          p: 1.2,
          borderRadius: 3,
          border: "1px solid rgba(51,57,153,0.12)",
          background: "linear-gradient(180deg,#ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.4, minWidth: 0, flex: 1 }}>
            <Checkbox
              size="small"
              checked={!!checked}
              disabled={!isEmail(emailLower)}
              onChange={() => toggleSelectReg(r)}
              sx={{ mt: -0.25 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }}>
                {isEmail(emailLower) ? (
                  <Button
                    size="small"
                    onClick={() => openAuctionUserDetailsModal(r)}
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      px: 0,
                      py: 0,
                      fontWeight: 900,
                      fontSize: 18,
                      color: primary,
                      "&:hover": { background: "transparent", textDecoration: "underline" },
                    }}
                  >
                    {`${firstName || "—"}${lastName ? ` ${lastName}` : ""}`}
                  </Button>
                ) : (
                  <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 18 }}>
                    {`${firstName || "—"}${lastName ? ` ${lastName}` : ""}`}
                  </Typography>
                )}
                {isNewUser ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1,
                      py: 0.3,
                      borderRadius: 999,
                      background: "linear-gradient(90deg,#3b82f6,#2563eb)",
                      color: "#fff",
                      fontWeight: 900,
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    New
                  </Box>
                ) : null}
              </Box>
              <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700, mt: 0.15, wordBreak: "break-word" }}>
                {r?.bidderEmail || r?.email || "—"}
              </Typography>
              {displayUsername ? (
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.2 }}>
                  {displayUsername}
                </Typography>
              ) : null}
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25, fontWeight: 700 }}>
                Registered {fmtDate(r.registeredAt || r.emailSentAt)}
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            onClick={() => openRegistrationFulfillmentDetails(r)}
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              textTransform: "none",
              px: 1.5,
              color: fulfillmentButtonMeta.color,
              background: fulfillmentButtonMeta.background,
              border: fulfillmentButtonMeta.border,
              "&:hover": { background: fulfillmentButtonMeta.background },
            }}
          >
            {fulfillmentButtonMeta.label}
          </Button>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 0.8, mt: 1.1 }}>
          {renderRegistrationMetricCard({
            label: "Lots",
            value: lotsCount > 0 ? formatNumber2(lotsCount) : "0",
            subtext: lotsCount > 0 ? "" : "Not won anything",
          })}
          {renderRegistrationMetricCard({
            label: "HighBid",
            value: totalHighBid > 0 ? `$${moneyPlain(totalHighBid)}` : "—",
          })}
          {renderRegistrationMetricCard({
            label: "Fulfilled",
            value:
              isFloorRegistration || lotsCount <= 0
                ? "—"
                : fulfillmentStatusLabel || "—",
            valueColor: "#0f172a",
          })}
          {renderRegistrationMetricCard({
            label: "Method",
            value: fulfillmentButtonMeta.label,
            valueColor: "#0f172a",
          })}
        </Box>

        <Box sx={{ display: "grid", gap: 0.85, mt: 1 }}>
          {renderRegistrationInfoCard({
            label: "Fulfillment",
            children: (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700 }}>
                  Open the {fulfillmentButtonMeta.label.toLowerCase()} details modal.
                </Typography>
                <Button
                  size="small"
                  onClick={() => openRegistrationFulfillmentDetails(r)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    px: 1.5,
                    fontWeight: 900,
                    color: fulfillmentButtonMeta.color,
                    background: fulfillmentButtonMeta.background,
                    border: fulfillmentButtonMeta.border,
                    "&:hover": { background: fulfillmentButtonMeta.background },
                  }}
                >
                  {fulfillmentButtonMeta.label}
                </Button>
              </Box>
            ),
          })}

          {renderRegistrationInfoCard({
            label: "Shelf",
            children: (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
                <TextField
                  size="small"
                  value={shelfValue}
                  disabled={!isEmail(emailLower) || shelfSaving}
                  onChange={(e) => {
                    const nextShelf = String(e.target.value || "");
                    setAregShelfDraftByEmail((prev) => ({
                      ...prev,
                      [emailLower]: nextShelf,
                    }));
                  }}
                  onBlur={() => {
                    void updateRegistrationShelf({
                      auctionId: selectedAuctionId,
                      email: r?.bidderEmail || r?.email,
                      shelf: shelfValue,
                    });
                  }}
                  placeholder="Number of Boxes (e.g. 3)"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                {shelfSaving ? (
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    Saving...
                  </Typography>
                ) : shelfBoxLabels.length > 1 ? (
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    {formatNumber2(shelfBoxLabels.length)} boxes
                  </Typography>
                ) : null}
              </Box>
            ),
          })}

          {renderRegistrationInfoCard({
            label: "Amount Due",
            children: (
              <TextField
                size="small"
                value={amountDueValue}
                disabled={!isEmail(emailLower)}
                onChange={(e) => {
                  const nextAmountDue = String(e.target.value || "");
                  setAregAmountDueDraftByEmail((prev) => ({
                    ...prev,
                    [emailLower]: nextAmountDue,
                  }));
                }}
                placeholder="Amount Due"
                sx={{ background: "#fff", borderRadius: 2 }}
              />
            ),
          })}

          {renderRegistrationInfoCard({
            label: "Statuses",
            children: (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="caption" sx={{ color: "#334155", fontWeight: 800 }}>
                    Fulfilled
                  </Typography>
                  {isFloorRegistration || lotsCount <= 0 ? (
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 800 }}>—</Typography>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
                      <Checkbox
                        size="small"
                        checked={fulfillmentChecked}
                        disabled={fulfillmentCheckboxDisabled}
                        onChange={(e) =>
                          openRegistrationFulfillmentConfirm({
                            type: fulfillmentMode === "shipping" ? "shipped" : fulfillmentMode === "delivery" ? "delivery" : "pickup",
                            row: r,
                            nextValue: e.target.checked,
                          })
                        }
                      />
                      <Typography variant="caption" sx={{ color: "#475569", fontWeight: 800 }}>
                        {fulfillmentStatusLabel || "—"}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="caption" sx={{ color: "#334155", fontWeight: 800 }}>
                    Shipping
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={String(shippingRequested)}
                      onChange={(e) =>
                        updateRegistrationShipping({
                          auctionId: selectedAuctionId,
                          email: r?.bidderEmail || r?.email,
                          shipping: String(e.target.value) === "true",
                        })
                      }
                      sx={{ borderRadius: 2, background: "#fff" }}
                    >
                      <MenuItem value="false">No</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={`Mail Sent: ${chargebackEmailSent ? "True" : "False"}`}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 900,
                      color: chargebackEmailSent ? "#166534" : "#991b1b",
                      background: chargebackEmailSent ? "rgba(22,163,74,0.14)" : "rgba(248,113,113,0.14)",
                      border: chargebackEmailSent ? "1px solid rgba(22,163,74,0.25)" : "1px solid rgba(220,38,38,0.25)",
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Signed: ${signDetails.signed ? "True" : "False"}`}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 900,
                      color: signDetails.signed ? "#166534" : "#991b1b",
                      background: signDetails.signed ? "rgba(22,163,74,0.14)" : "rgba(248,113,113,0.14)",
                      border: signDetails.signed ? "1px solid rgba(22,163,74,0.25)" : "1px solid rgba(220,38,38,0.25)",
                    }}
                  />
                </Box>
              </Box>
            ),
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            flexWrap: "wrap",
            alignItems: "center",
            mt: 1.15,
            pt: 1,
            borderTop: "1px solid rgba(226,232,240,0.95)",
          }}
        >
          <Button
            onClick={() => openChargebackSignModal(r)}
            size="small"
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }}
          >
            View Sign
          </Button>
          <Button
            onClick={() => openLotsModal(r)}
            disabled={!isEmail(emailLower)}
            startIcon={<ListAltIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2,
              fontWeight: 900,
              color: "#fff",
              background: isEmail(emailLower)
                ? "linear-gradient(90deg,#6366f1,#4f46e5)"
                : "linear-gradient(90deg,#9ca3af,#6b7280)",
              "&:hover": {
                background: isEmail(emailLower)
                  ? "linear-gradient(90deg,#4f46e5,#4338ca)"
                  : "linear-gradient(90deg,#9ca3af,#6b7280)",
              },
            }}
          >
            Lots
          </Button>
          <Button
            onClick={() => openEmailForAuctionSelection([r])}
            disabled={!isEmail(emailLower)}
            startIcon={<MailOutlineIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2,
              fontWeight: 900,
              color: "#fff",
              background: isEmail(emailLower)
                ? "linear-gradient(90deg,#0ea5e9,#0284c7)"
                : "linear-gradient(90deg,#9ca3af,#6b7280)",
              "&:hover": {
                background: isEmail(emailLower)
                  ? "linear-gradient(90deg,#0284c7,#0369a1)"
                  : "linear-gradient(90deg,#9ca3af,#6b7280)",
              },
            }}
          >
            Email
          </Button>
          <Button
            onClick={() => exportRegistrationRowBoxLabelsPdf(r)}
            disabled={!shelfBoxLabels.length || boxLabelRowBusy}
            startIcon={boxLabelRowBusy ? <CircularProgress size={14} /> : <LocalPrintshopOutlinedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2,
              fontWeight: 900,
              color: "#fff",
              background: shelfBoxLabels.length
                ? "linear-gradient(90deg,#f97316,#ea580c)"
                : "linear-gradient(90deg,#9ca3af,#6b7280)",
              "&:hover": {
                background: shelfBoxLabels.length
                  ? "linear-gradient(90deg,#ea580c,#c2410c)"
                  : "linear-gradient(90deg,#9ca3af,#6b7280)",
              },
            }}
          >
            {boxLabelRowBusy ? "Printing..." : "Print Boxes"}
          </Button>
        </Box>
      </Paper>
    );
  };

  const auctionTabProps = {
    AUCTION_CONSIGNOR_FILTER_UNASSIGNED,
    accountManagers,
    addAuctionInlinePickupSlot,
    addAuctionPickupSlot,
    addCompareAuctionSlot,
    applyLocationMetadataRows,
    applySortToggle,
    aregAmountDueDraftByEmail,
    aregConsignorFilter,
    aregFulfillmentFilter,
    aregLoading,
    aregNewUserFilter,
    aregDeliveryFilter,
    aregDeliveryLocationFilter,
    aregPickupDateEnd,
    aregPickupDateStart,
    aregPickupFilter,
    aregPickupLocationFilter,
    aregPickupSelectionFilter,
    aregSearch,
    aregSelected,
    aregShelfDraftByEmail,
    aregShelfSavingByEmail,
    aregShippedFilter,
    aregSortDir,
    aregSortKey,
    auctionAccountManagerOptions,
    auctionChartData,
    auctionEndAtLocal,
    auctionEndSaving,
    auctionEntitySharePanels,
    auctionHibidSearch,
    auctionHibidTrendByRowKey,
    auctionHibidViewRows,
    auctionInlineInfoSavingById,
    auctionKpiCards,
    auctionListLayout,
    auctionLocationFilter,
    auctionLocationFilterOptions,
    auctionLotStatsSections,
    auctionParticipationSections,
    auctionPickupLocations,
    auctionPickupSlots,
    auctionRegs,
    auctionRegTrendAvailableRange,
    auctionRegTrendChartType,
    auctionRegTrendDateMode,
    auctionRegTrendDateModeOptions,
    auctionRegTrendEndDate,
    auctionRegTrendResolvedRange,
    auctionRegTrendStartDate,
    auctionRegTrendViewBy,
    auctionRegTrendViewByOptions,
    auctionRegistrationTrendData,
    auctionRegistrationTrendHasData,
    auctionRegistrationTrendTotalInRange,
    auctionSummary,
    auctionSummaryLoading,
    auctionTableSortDir,
    auctionTableSortKey,
    auctionViewMode,
    auctionWeeklyStatsOpen,
    auctions,
    backToAuctionList,
    bulkBoxLabelMode,
    bulkBoxLabelRows,
    captureChargebackSignatureDataUrl,
    chargebackEmailAlreadySentFilter,
    chargebackEmailFulfillmentFilter,
    chargebackEmailWonLotFilter,
    chargebackFilteredAuctionRegs,
    chargebackSignError,
    chargebackSignOpen,
    chargebackSignRow,
    chargebackSignSaving,
    chargebackSignSignatureHasInk,
    chargebackSignSignatureName,
    chargebackSignSuccess,
    chargebackSignatureCanvasRef,
    cityOptionsLoading,
    cityPlacesReady,
    clampDateKeyToRangeUTC,
    clearRegsSearch,
    compareAuctionIds,
    compareAuctionRegTrendAvailableRange,
    compareAuctionRegTrendChartType,
    compareAuctionRegTrendDateMode,
    compareAuctionRegTrendEndDate,
    compareAuctionRegTrendResolvedRange,
    compareAuctionRegTrendSeries,
    compareAuctionRegTrendStartDate,
    compareAuctionRegTrendViewBy,
    compareAuctionRegsById,
    compareAuctionView,
    compareAuctionsOpen,
    computeAuctionShareTotals,
    computeSellerRowRevenue,
    copyText,
    createAuctionBusy,
    createAuctionEndAtLocal,
    createAuctionLocation,
    createAuctionLocationInput,
    createAuctionLocationOptions,
    createAuctionLotsFile,
    createAuctionLotsFileInputRef,
    createAuctionName,
    createAuctionNow,
    createAuctionOpen,
    createAuctionRegistrationFile,
    createAuctionRegistrationFileInputRef,
    endChargebackSignatureDraw,
    expandedMediaUrlsByKey,
    exportBulkRegistrationBoxLabelsPdf,
    exportSelectedDeliveryManifestExcel,
    exportSelectedDeliveryManifestLotsPdf,
    exportSelectedDeliveryManifestPdf,
    exportRegistrationRowBoxLabelsPdf,
    exportSelectedPickupPicklistExcel,
    exportSelectedPickupPicklistLotsPdf,
    exportSelectedPickupPicklistPdf,
    exportSelectedRegistrationsWorkbook,
    fetchAuctionRegsPage,
    fetchAuctionSummary,
    fileNameMismatchDialog,
    filterLabelSx,
    filteredAuctionHibidViewRows,
    flushAuctionInlineInfoAutosave,
    flushAuctionSettingsAutosave,
    fmtDate,
    formatDateKeyRangeShortUTC,
    formatEngagementSeriesName,
    formatLotDimensions,
    formatNumber2,
    formatPercent2,
    getAuctionInlineAccountManagerRows,
    getAuctionInlineInfoDraft,
    getAuctionInlineLocationInput,
    getAuctionInlineLocationOptions,
    getAuctionInlinePickupLocations,
    getAuctionInlinePickupSlots,
    getChargebackSignDetails,
    getDisplayBidderName,
    getDisplayUsername,
    getFirstLastNameParts,
    getListernautMediaUrls,
    getListernautThumbnailUrl,
    getLocationAddress,
    getLotDescriptionText,
    getLotPickupCounts,
    getLotShippedCounts,
    getPickupLocationSelectOptions,
    getRegistrationBoxLabelRowKey,
    getRegistrationDelivery,
    getSelectedRegRows,
    isAuctionRegistrationNewUser,
    isChargebackEmailSent,
    isEmail,
    isFloorBidderEmail,
    isShippingRequested,
    listernautFileInputRef,
    listernautUploadBusy,
    locationMetadataRows,
    locationScopedAuctionHibidViewRows,
    locationsManagerOpen,
    lotsAgg,
    lotsBusy,
    lotsData,
    lotsErr,
    lotsFileInputRef,
    lotsModalSortDir,
    lotsModalSortKey,
    lotsOpen,
    lotsUser,
    lotsUserFulfillmentLabel,
    lotsUserFulfillmentMode,
    lotsUserFulfillmentStatusLabel,
    modalPaperSx,
    moneyPlain,
    moveChargebackSignatureDraw,
    normalize,
    onShippingFileSelected,
    openAuctionFromHibidView,
    openAuctionNewUsersFromOverall,
    openAuctionUserDetailsModal,
    openChargebackSignModal,
    openCompareAuctionsDialog,
    openCreateAuctionDialog,
    openEmailForAuctionSelection,
    openLotsModal,
    openRegistrationFulfillmentDetails,
    openRegistrationFulfillmentConfirm,
    orgReady,
    overallAuctionCompareFields,
    overallAuctionCompareOptions,
    overallAuctionSearch,
    overallAuctionSortDir,
    overallAuctionSortKey,
    overallChartData,
    overallDashboardOpen,
    overallKpiCards,
    parseShelfBoxLabels,
    deliveryManifestExcelBusy,
    deliveryManifestLotsPdfBusy,
    deliveryManifestPdfBusy,
    pickupPicklistExcelBusy,
    pickupPicklistLotsPdfBusy,
    pickupPicklistPdfBusy,
    primary,
    queueCreateAuctionCityOptions,
    registrationBoxLabelsBusy,
    registrationBoxLabelsBusyByKey,
    registrationConsignorOptions,
    registrationDeliveryLocationOptions,
    registrationPickupLocationOptions,
    removeAuctionInlinePickupSlot,
    removeAuctionPickupSlot,
    removeCompareAuctionSlot,
    renderAuctionManagerSummary,
    renderAuctionPickupSlotSummary,
    renderAuctionRegistrationMobileCard,
    renderAuctionShareMiniTable,
    renderDescriptionPreview,
    renderPickupLocationSelectedDetails,
    renderTrendDeltaPair,
    renderTrendDeltaPairFromTrend,
    requestListernautUploadFile,
    requestShippingUploadFile,
    resetChargebackSignatureCanvas,
    resolveFileNameMismatchDialog,
    safeNum,
    sanitizeAuctionAccountManagerIds,
    sanitizeAuctionPickupLocations,
    selectedAuctionAccountManagerRows,
    selectedAuctionId,
    selectedAuctionLabel,
    selectedAuctionStatsTab,
    selectedAuctionViewRow,
    selectedCompareAuctionRows,
    selectedRegsExportBusy,
    setAregAmountDueDraftByEmail,
    setAregConsignorFilter,
    setAregFulfillmentFilter,
    setAregDeliveryFilter,
    setAregDeliveryLocationFilter,
    setAregNewUserFilter,
    setAregPickupDateEnd,
    setAregPickupDateStart,
    setAregPickupFilter,
    setAregPickupLocationFilter,
    setAregPickupSelectionFilter,
    setAregSearch,
    setAregShelfDraftByEmail,
    setAregShippedFilter,
    setAregShipperFilter,
    setAregSortDir,
    setAregSortKey,
    setAuctionAccountManagerIds,
    setAuctionEndAtLocal,
    setAuctionHibidSearch,
    setAuctionInlineAccountManagerIds,
    setAuctionInlineInfoField,
    setAuctionInlinePickupLocations,
    setAuctionListLayout,
    setAuctionLocationFilter,
    setAuctionRegTrendChartType,
    setAuctionRegTrendDateMode,
    setAuctionRegTrendEndDate,
    setAuctionRegTrendStartDate,
    setAuctionRegTrendViewBy,
    setAuctionTableSortDir,
    setAuctionTableSortKey,
    setAuctionViewMode,
    setAuctionWeeklyStatsOpen,
    setChargebackEmailAlreadySentFilter,
    setChargebackEmailFulfillmentFilter,
    setChargebackEmailWonLotFilter,
    setChargebackSignError,
    setChargebackSignOpen,
    setChargebackSignRow,
    setChargebackSignSignatureName,
    setCompareAuctionIds,
    setCompareAuctionRegTrendChartType,
    setCompareAuctionRegTrendDateMode,
    setCompareAuctionRegTrendEndDate,
    setCompareAuctionRegTrendStartDate,
    setCompareAuctionRegTrendViewBy,
    setCompareAuctionView,
    setCompareAuctionsOpen,
    setCreateAuctionEndAtLocal,
    setCreateAuctionLocation,
    setCreateAuctionLocationInput,
    setCreateAuctionLotsFile,
    setCreateAuctionName,
    setCreateAuctionOpen,
    setCreateAuctionRegistrationFile,
    setLocationsManagerOpen,
    setLotsModalSortDir,
    setLotsModalSortKey,
    setLotsOpen,
    setOverallAuctionSearch,
    setOverallAuctionSortDir,
    setOverallAuctionSortKey,
    setOverallDashboardOpen,
    setSelectedAuctionStatsTab,
    shipBusy,
    shippingFileInputRef,
    sortedFilteredAuctionHibidViewRows,
    sortedLotsModalRows,
    sortedOverallAuctionTableRows,
    startChargebackSignatureDraw,
    subscriptionId,
    toAbsInt,
    toggleMediaUrlsExpanded,
    toggleSelectAllRegs,
    toggleSelectReg,
    updateAuctionInlinePickupSlot,
    updateAuctionPickupLocations,
    updateAuctionPickupSlot,
    updateCompareAuctionSlot,
    updateLotPickup,
    updateLotShipped,
    updateLotDelivered,
    updateRegistrationDelivered,
    uploadRegistrationDeliveryProofPhotos,
    updateRegistrationShelf,
    updateRegistrationShipping,
    updateRegistrationSignature,
    uploadBusy,
    uploadDialogMessage,
    uploadDialogOpen,
    uploadListernautFile,
    uploadLotsCsv,
    userId,
  };

  const usersTabProps = {
    CUSTOMER_TYPE_OPTIONS,
    accountManagerFilter,
    accountManagerFilterPillOptions,
    accountManagerSavingByEmail,
    allFilteredUsers,
    allFilteredUsersSelected,
    applyUsersSearch,
    buyerRetentionAuctionCount,
    buyerRetentionMatchMode,
    buyerRetentionReportBusy,
    clearUsersSearch,
    customerTypeFilter,
    customerTypeFilterOptions,
    customerTypeLabel,
    customerTypeSavingByEmail,
    filterLabelSx,
    filterPillSx,
    filterRowSx,
    filterValuesSx,
    formatNumber2,
    getAccountManagerSelectOptionsForUser,
    getAvgHammerFromWin,
    getBidIntensity90,
    getBidsCount,
    getBpi90,
    getCustomerType,
    getDisplayBidderName,
    getDisplayUsername,
    getEffectiveTier,
    getFirstLastNameParts,
    getHibidBids90Count,
    getOverallHammerPrice,
    getLcvEstimate,
    getLotsWonFromWin,
    getPickupLocationSelectOptions,
    getRegs12mCount,
    getRegsCount,
    getResolvedAccountManagerIdForUser,
    getRollingWin,
    getUserEmailLower,
    isEmail,
    lastEmailFilter,
    lastEmailFilterOptions,
    moneyPlain,
    newSubFilterOptions,
    newTierSubFilter,
    onUsersTableScroll,
    openAccountManagerModal,
    openConsignorModal,
    openEmailForLoadedUsers,
    openEmailForUser,
    openEdit,
    openUserRegistrationsModal,
    orgReady,
    primary,
    deleteRow,
    downloadBuyerRetentionTable,
    regsFilterOptions,
    regsThresholdFilter,
    reloadUsersFromServer,
    downloadFastRevenueReport,
    fastRevenueReportBusy,
    pickupLocationMetadataNames,
    safeNum,
    search,
    selectedFilteredUsers,
    setAccountManagerFilter,
    setBuyerRetentionAuctionCount,
    setBuyerRetentionMatchMode,
    setCustomerTypeFilter,
    setLastEmailFilter,
    setNewTierSubFilter,
    setRegsThresholdFilter,
    setTierFilter,
    setUserSortDir,
    setUserSortKey,
    showUsersLoadingModal,
    someFilteredUsersSelected,
    tierChipSx,
    tierFilter,
    tierFilterOptions,
    toAbsInt,
    toggleSelectAllUsers,
    toggleSelectUser,
    updateAccountManagerForUser,
    updateCustomerTypeForUser,
    updateUserPickupDefaultsForUser,
    userHasMore,
    userLoadingMore,
    userSelected,
    userSortDir,
    userSortKey,
    userTableBottomSpacerHeight,
    userTableContainerRef,
    userTableTopSpacerHeight,
    usersInitialLoading,
    usersSearchInputRef,
    usersTableLoading,
    usersTotalHeaderText,
    visibleUsers,
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg,#f8fafc,#eef2ff)", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: { xs: "100%", md: "90%" }, maxWidth: 1800, mx: "auto" }}>
        {needsSubscriptionLink ? (
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>Subscription not linked</Typography>
                <Typography variant="body2">{subLinkMsg || "Link your subscriptionId in Settings to use HiBid Dashboard."}</Typography>
              </Box>
              <Button
                onClick={() => window.location.assign("/settings")}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#6366f1,#4f46e5)",
                  "&:hover": { background: "linear-gradient(90deg,#4f46e5,#4338ca)" },
                }}
              >
                Go to Settings
              </Button>
            </Box>
          </Alert>
        ) : null}

        <Paper sx={{ borderRadius: 3, p: 2.25, border: "1px solid rgba(51,57,153,0.12)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <GavelIcon sx={{ color: primary }} />
              <Box>
                <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 900, color: primary, lineHeight: 1.15 }}>
                  {currentPageTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, lineHeight: 1.7 }}>
                  {currentPageSubtitle}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {/* <Button
                onClick={() => refreshActiveTab({ force: true, sync: true })}
                disabled={!orgReady || loading}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                title="Click this for realtime data"
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  color: "#fff",
                  border: "1px solid rgba(13,148,136,0.38)",
                  boxShadow: "0 8px 24px rgba(13,148,136,0.28)",
                  background: "linear-gradient(90deg,#0d9488,#0f766e)",
                  "&:hover": { background: "linear-gradient(90deg,#0f766e,#115e59)" },
                }}
              >
                Refresh Data
              </Button> */}

              <Chip
                size="small"
                label={`Tokens: ${tokensRemaining}`}
                sx={{
                  borderRadius: 999,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#333999,#5555bb)",
                }}
              />
            </Box>
          </Box>

          {/* <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#0f766e", fontWeight: 800 }}>
            Click "Refresh Data" for realtime data.
          </Typography> */}

          {syncInfo ? (
            <Box sx={{ mt: 1.75 }}>
              <Alert severity={syncInfo?.ok === false ? "warning" : "info"} sx={{ borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>
                      Gmail Sync: {syncInfo?.ok === false ? "Issue (cached Firestore data)" : "OK"}
                    </Typography>
                    <Typography variant="body2">
                      Scanned: <b>{syncInfo?.scanned ?? "-"}</b> • Wrote: <b>{syncInfo?.wrote ?? "-"}</b> • Window:{" "}
                      <b>
                        {syncInfo?.lastCalledMs ? fmtDate(syncInfo.lastCalledMs) : "first run"} → {syncInfo?.nowMs ? fmtDate(syncInfo.nowMs) : "-"}
                      </b>
                    </Typography>
                    {syncInfo?.error ? <Typography variant="body2" sx={{ color: "#b91c1c", mt: 0.5 }}>{syncInfo.error}</Typography> : null}
                  </Box>
                </Box>
              </Alert>
            </Box>
          ) : null}

          <Divider sx={{ my: 2 }} />

          {!currentSection ? (
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                border: "1px solid rgba(51,57,153,0.12)",
                background: "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: 18, md: 22 } }}>
                HiBid Sidebar Options
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.75, lineHeight: 1.7 }}>
                Use the HiBid sidebar to move between sections.
              </Typography>
              <Box sx={{ mt: 1.75, display: "grid", gap: 1.25 }}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: primary }}>Users</Typography>
                  <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                    Search, filter, and manage bidders, customer details, registrations, and related account data.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: primary }}>Auctions</Typography>
                  <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                    Open auction stats, registrations, lots, fulfillment tools, picklists, box labels, and auction setup workflows.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: primary }}>Daily Registration Activity</Typography>
                  <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                    Review registration trends over time and drill into the users behind each day of activity.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: primary }}>Product Search</Typography>
                  <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                    Search lot data across auctions with table filters, sorting, previews, and CSV export tools.
                  </Typography>
                </Box>
              </Box>
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  background: "rgba(238,242,255,0.9)",
                  border: "1px solid rgba(51,57,153,0.10)",
                }}
              >
                Users and Product Search are cached for 24 hours, but for live values you can refresh them anytime using the refresh button.
              </Alert>
            </Paper>
          ) : currentSection === "definitions" ? (
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                border: "1px solid rgba(51,57,153,0.12)",
                background: "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
              }}
            >
              <Typography variant="caption" sx={{ display: "block", color: "#475569", mb: 1 }}>
                Plain-English rules currently used in HiBid scoring.
              </Typography>
              <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr 1fr" } }}>
                <Box sx={{ p: 1.25, borderRadius: 2, border: "1px solid #e2e8f0", background: "#fff" }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", mb: 0.5 }}>Tier Definitions</Typography>
                  {HIBID_TIER_DEFINITION_LINES.map((line, idx) => (
                    <Box key={`tier-def-page-${idx}`} sx={{ mb: 0.75, p: 1, borderRadius: 1.5, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                      <Typography sx={{ color: "#0f172a", fontWeight: 900, lineHeight: 1.2 }}>
                        {`${idx + 1}. ${line.name}`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.45, mt: 0.35 }}>
                        {line.definition}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ p: 1.25, borderRadius: 2, border: "1px solid #e2e8f0", background: "#fff" }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", mb: 0.5 }}>User Type Definitions</Typography>
                  {HIBID_USER_TYPE_DEFINITION_LINES.map((line, idx) => (
                    <Box key={`user-type-def-page-${idx}`} sx={{ mb: 0.75, p: 1, borderRadius: 1.5, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                      <Typography sx={{ color: "#0f172a", fontWeight: 900, lineHeight: 1.2 }}>
                        {`${idx + 1}. ${line.name}`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.45, mt: 0.35 }}>
                        {line.definition}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ p: 1.25, borderRadius: 2, border: "1px solid #e2e8f0", background: "#fff" }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", mb: 0.5 }}>LCV Calculation (Top Metric)</Typography>
                  {HIBID_LCV_DEFINITION_LINES.map((line, idx) => (
                    <Box key={`lcv-def-page-${idx}`} sx={{ mb: 0.75, p: 1, borderRadius: 1.5, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                      <Typography sx={{ color: "#0f172a", fontWeight: 900, lineHeight: 1.2 }}>
                        {`${idx + 1}. ${line.name}`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.45, mt: 0.35 }}>
                        {line.definition}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ p: 1.25, borderRadius: 2, border: "1px solid #e2e8f0", background: "#fff" }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", mb: 0.5 }}>Registration Activity Definitions</Typography>
                  {HIBID_REGISTRATION_ACTIVITY_DEFINITION_LINES.map((line, idx) => (
                    <Box key={`reg-activity-def-page-${idx}`} sx={{ mb: 0.75, p: 1, borderRadius: 1.5, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                      <Typography sx={{ color: "#0f172a", fontWeight: 900, lineHeight: 1.2 }}>
                        {`${idx + 1}. ${line.name}`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.45, mt: 0.35 }}>
                        {line.definition}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          ) : currentSection === "users" ? (
            <HibidUsersTab {...usersTabProps} />
          ) : currentSection === "auctions" ? (
            <HibidAuctionsTab {...auctionTabProps} />
          ) : currentSection === "daily-registration" ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                alignItems: "start",
                width: "100%",
                minWidth: 0,
              }}
            >
              <DailyRegistrationActivityTab
                primary={primary}
                regActivityRangePreset={regActivityRangePreset}
                setRegActivityRangePreset={setRegActivityRangePreset}
                regActivityRangeOptions={regActivityRangeOptions}
                regActivityCustomStartDate={regActivityCustomStartDate}
                setRegActivityCustomStartDate={setRegActivityCustomStartDate}
                regActivityCustomEndDate={regActivityCustomEndDate}
                setRegActivityCustomEndDate={setRegActivityCustomEndDate}
                regActivityMeasure={regActivityMeasure}
                setRegActivityMeasure={setRegActivityMeasure}
                regActivityMeasureOptions={regActivityMeasureOptions}
                regActivityResolvedRangeLabel={regActivityResolvedRange.label}
                regActivityTotals={regActivityTotals}
                regActivityLoading={regActivityLoading}
                registrationActivityHasData={registrationActivityHasData}
                registrationActivityChartData={registrationActivityChartData}
                formatNumber2={formatNumber2}
                toAbsInt={toAbsInt}
                regActivitySortKey={regActivitySortKey}
                regActivitySortDir={regActivitySortDir}
                setRegActivitySortKey={setRegActivitySortKey}
                setRegActivitySortDir={setRegActivitySortDir}
                applySortToggle={applySortToggle}
                sortedRegistrationActivityRows={sortedRegistrationActivityRows}
                fetchRegistrationActivityUsers={fetchRegistrationActivityUsers}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                alignItems: "start",
                width: "100%",
                minWidth: 0,
              }}
            >
              <ProductSearchTab
                primary={primary}
                productSearchBusy={productSearchBusy}
                productSearchLoadedCount={productSearchLoadedCount}
                productSearchTotalCount={productSearchTotalCount}
                productSearchAllRowsCount={productSearchAllRows.length}
                productSearchExportBusy={productSearchExportBusy}
                onOpenRefreshConfirm={openProductSearchRefreshConfirm}
                onExportFilteredCsv={exportFilteredProductSearchCsv}
                productSearchField={productSearchField}
                setProductSearchField={setProductSearchField}
                productSearchSearchInput={productSearchSearchInput}
                setProductSearchSearchInput={setProductSearchSearchInput}
                setProductSearchVisibleCount={setProductSearchVisibleCount}
                productSearchErr={productSearchErr}
                productSearchLoadedOnce={productSearchLoadedOnce}
                productSearchRows={productSearchRows}
                productSearchSortedLength={productSearchSorted.length}
                productSearchDisplayColumns={productSearchDisplayColumns}
                productSearchSortKey={productSearchSortKey}
                productSearchSortDir={productSearchSortDir}
                onProductSearchSort={onProductSearchSort}
                productSearchContainerRef={productSearchContainerRef}
                onProductSearchScroll={onProductSearchScroll}
                productSearchVisibleCount={productSearchVisibleCount}
                formatNumber2={formatNumber2}
                toAbsInt={toAbsInt}
                safeNum={safeNum}
                moneyPlain={moneyPlain}
                getProductSearchThumbnailUrl={getProductSearchThumbnailUrl}
                getLotDescriptionText={getLotDescriptionText}
                formatLotDimensions={formatLotDimensions}
                getListernautMediaUrls={getListernautMediaUrls}
              />
            </Box>
          )}

          <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: modalPaperSx }}>
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
              Edit Bidder
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                {menuRow?.email || menuRow?.id || ""}
              </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
              {editErr ? (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
                  {editErr}
                </Alert>
              ) : null}

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.25 }}>
                <TextField
                  label="Email"
                  value={menuRow?.email || menuRow?.id || ""}
                  fullWidth
                  size="small"
                  disabled
                  sx={{ background: "#fff", borderRadius: 2, gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                />
                <TextField
                  label="Bidder Name"
                  value={editForm.bidderName}
                  onChange={(e) => setEditForm((p) => ({ ...p, bidderName: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                <TextField
                  label="Username"
                  value={editForm.username}
                  onChange={(e) => setEditForm((p) => ({ ...p, username: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                <TextField
                  label="Company"
                  value={editForm.company}
                  onChange={(e) => setEditForm((p) => ({ ...p, company: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                <TextField
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                <TextField
                  label="Address"
                  value={editForm.address}
                  onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2, gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                />
                <TextField
                  select
                  label="Default Pickup Location"
                  value={editForm.defaultPickupLocationName || ""}
                  onChange={(e) => {
                    const nextLocationName = String(e.target.value || "").trim();
                    const nextLocationMeta = locationMetadataByName.get(normalize(nextLocationName)) || null;
                    setEditForm((p) => ({
                      ...p,
                      defaultPickupLocationId: String(nextLocationMeta?.id || "").trim(),
                      defaultPickupLocationName: String(nextLocationMeta?.name || nextLocationName).trim(),
                      defaultPickupLocationAddress: String(nextLocationMeta?.address || "").trim(),
                    }));
                  }}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>No default location</em>
                  </MenuItem>
                  {getPickupLocationSelectOptions([editForm.defaultPickupLocationName || ""]).map((option) => (
                    <MenuItem key={`edit-pickup-location-${option}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <NamedUrlsEditor
                  rows={editForm.namedUrls}
                  onChange={(next) => setEditForm((p) => ({ ...p, namedUrls: coerceNamedUrlDraftRows(next) }))}
                  title="Named URLs"
                />

                <TextField
                  label="My Private Notes"
                  value={editForm.myPrivateNotes}
                  onChange={(e) => setEditForm((p) => ({ ...p, myPrivateNotes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2, gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                />
                <TextField
                  label="Public Notes"
                  value={editForm.publicNotes}
                  onChange={(e) => setEditForm((p) => ({ ...p, publicNotes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2, gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                />

                <TextField
                  label="Bid Permission"
                  value={editForm.bidPermission}
                  onChange={(e) => setEditForm((p) => ({ ...p, bidPermission: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                <TextField
                  label="Auction Status"
                  value={editForm.auctionStatus}
                  onChange={(e) => setEditForm((p) => ({ ...p, auctionStatus: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
                <TextField
                  label="Last Auction Name"
                  value={editForm.lastAuctionName}
                  onChange={(e) => setEditForm((p) => ({ ...p, lastAuctionName: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={{ background: "#fff", borderRadius: 2, gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
              <Button
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.18)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={saveEdit}
                disabled={editSaving}
                startIcon={editSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#6366f1,#4f46e5)",
                  "&:hover": { background: "linear-gradient(90deg,#4f46e5,#4338ca)" },
                }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
          <UserRegistrationsLotsDialog
            open={userRegsOpen}
            onClose={() => {
              closeUserRegistrationsModal();
              setChargebackSignOpen(false);
              setChargebackSignRow(null);
            }}
            modalPaperSx={modalPaperSx}
            primary={primary}
            userRegsUser={userRegsUser}
            getDisplayBidderName={getDisplayBidderName}
            getDisplayUsername={getDisplayUsername}
            onExportWorkbook={exportUserRegistrationsAndLotsWorkbook}
            userLotsExportBusy={userLotsExportBusy}
            userRegsModalView={userRegsModalView}
            setUserRegsModalView={setUserRegsModalView}
            formatNumber2={formatNumber2}
            toAbsInt={toAbsInt}
            moneyPlain={moneyPlain}
            userRegsTotals={userRegsTotals}
            userRegsChargebackSummary={userRegsChargebackSummary}
            userRegsErr={userRegsErr}
            userRegsBusy={userRegsBusy}
            userRegsSortKey={userRegsSortKey}
            setUserRegsSortKey={setUserRegsSortKey}
            userRegsSortDir={userRegsSortDir}
            setUserRegsSortDir={setUserRegsSortDir}
            sortedUserRegsRows={sortedUserRegsRows}
            safeToMillis={safeToMillis}
            isChargebackEmailSent={isChargebackEmailSent}
            getChargebackSignDetails={getChargebackSignDetails}
            openChargebackSignModal={openChargebackSignModal}
            onOpenAuction={({ auctionId, auctionName }) => {
              if (!auctionId) return;
              void onSelectAuction({ auctionId, auctionName }, { viewMode: "stats" });
              closeUserRegistrationsModal();
            }}
            userLotsErr={userLotsErr}
            userLotsSearchInput={userLotsSearchInput}
            setUserLotsSearchInput={setUserLotsSearchInput}
            runUserLotsSearch={runUserLotsSearch}
            userLotsBusy={userLotsBusy}
            userLotsContainerRef={userLotsContainerRef}
            onUserLotsScroll={onUserLotsScroll}
            userLotsSortKey={userLotsSortKey}
            setUserLotsSortKey={setUserLotsSortKey}
            userLotsSortDir={userLotsSortDir}
            setUserLotsSortDir={setUserLotsSortDir}
            applySortToggle={applySortToggle}
            sortedUserLotsRows={sortedUserLotsRows}
            userLotsAuctionNameMap={userLotsAuctionNameMap}
            userLotsLoadingMore={userLotsLoadingMore}
            userLotsHasMore={userLotsHasMore}
          />
          <Dialog
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
            maxWidth={false}
            PaperProps={{
              sx: {
                ...modalPaperSx,
                width: "90vw",
                maxWidth: "90vw",
                height: "95vh",
                maxHeight: "95vh",
              },
            }}
          >
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
              Send Email
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                Mode: <b>{emailMode}</b>
              </Typography>
            </DialogTitle>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Tabs
                value={emailTab === "compose" ? 0 : 1}
                onChange={async (_, v) => {
                  const next = v === 0 ? "compose" : "preview";
                  setEmailTab(next);
                  if (next === "preview") await requestEmailPreview();
                }}
                sx={{
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 900 },
                  "& .MuiTabs-indicator": { backgroundColor: primary, height: 3, borderRadius: 99 },
                }}
              >
                <Tab label="Editor" />
                <Tab label="Preview" />
              </Tabs>
            </Box>

            <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
              {emailTab === "compose" ? (
                <Box sx={{ display: "grid", gap: 2 }}>
                  <Paper sx={{ borderRadius: 3, p: 2, border: "1px solid rgba(51,57,153,0.10)" }}>
                    <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", alignItems: "center" }}>
                      {emailMode === "users" && emailSingleUserTarget ? (
                        <Typography variant="body2" sx={{ color: "#334155", fontWeight: 800 }}>
                          Sending to: <b>{emailSingleUserTarget}</b>
                        </Typography>
                      ) : null}
                      {emailMode === "auction" ? (
                        <Chip
                          size="small"
                          label={`Total recipients: ${formatNumber2(emailRecipientTotalForDisplay)}`}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 900,
                            backgroundColor: "rgba(99,102,241,0.10)",
                            color: primary,
                            border: "1px solid rgba(51,57,153,0.15)",
                          }}
                        />
                      ) : null}
                      {emailMode === "users" && !emailSingleUserTarget ? (
                        <Chip
                          size="small"
                          label={`Recipients: ${formatNumber2(emailRecipientTotalForDisplay)}`}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 900,
                            backgroundColor: "rgba(99,102,241,0.10)",
                            color: primary,
                            border: "1px solid rgba(51,57,153,0.15)",
                          }}
                        />
                      ) : null}
                    </Box>
                              {emailMode === "auction" ? (
                      <Alert severity="info" sx={{ mt: 1.25, borderRadius: 2 }}>
                        For shipping emails, set your button/link URL to <b>www.shippingplaceholder.com</b>. For pickup scheduling emails, use <b>www.pickupplaceholder.com</b>. The backend will auto-replace each with the auction-level public link for this auction.
                      </Alert>
                    ) : null}
                    <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", alignItems: "center", mt: 1.25 }}>
                      <Button
                        onClick={() => {
                          setCreateEmailTemplateName("");
                          setCreateEmailTemplateBaseId("");
                          setCreateEmailTemplateOpen(true);
                        }}
                        disabled={emailTemplatesBusy}
                        sx={{
                          textTransform: "none",
                          borderRadius: 999,
                          px: 2,
                          fontWeight: 900,
                          border: "1px solid rgba(51,57,153,0.18)",
                          color: primary,
                          background: "#fff",
                          "&:hover": { background: "rgba(99,102,241,0.06)" },
                        }}
                      >
                        + Create New Template
                      </Button>
                      {emailMode === "auction" ? (
                        <Button
                          onClick={() => {
                            setShippingTextDraft(String(shippingTextSaved || DEFAULT_SHIPPING_POLICY_TEXT));
                            setShippingTextOpen(true);
                          }}
                          sx={{
                            textTransform: "none",
                            borderRadius: 999,
                            px: 2,
                            fontWeight: 900,
                            border: "1px solid rgba(2,132,199,0.24)",
                            color: "#075985",
                            background: "#fff",
                            "&:hover": { background: "rgba(14,165,233,0.10)" },
                          }}
                        >
                          Shipping Text
                        </Button>
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        mt: 1.25,
                        display: "grid",
                        gap: 1.25,
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        alignItems: "start",
                      }}
                    >
                      <Box>
                        <FormControl size="small" sx={{ width: "100%" }}>
                          <InputLabel>Existing Templates</InputLabel>
                          <Select
                            label="Existing Templates"
                            value={selectedEmailTemplateId}
                            onChange={(e) => handleSelectEmailTemplate(e.target.value)}
                            sx={{ borderRadius: 2, backgroundColor: "white" }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {(emailTemplates || []).map((tpl) => (
                              <MenuItem key={tpl.id} value={tpl.id}>
                                {tpl.name || tpl.id}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" sx={{ display: "block", color: "#6b7280", mt: 0.75 }}>
                          Selecting a template loads it into the editor.
                        </Typography>
                      </Box>

                      <Box>
                        <TextField
                          size="small"
                          fullWidth
                          label="Selected Template Name"
                          value={selectedEmailTemplateName}
                          onChange={(e) => setSelectedEmailTemplateName(e.target.value)}
                          disabled={!selectedEmailTemplateId || emailTemplatesBusy}
                          sx={{ background: "#fff", borderRadius: 2 }}
                        />
                        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                          {emailAutoSaveStatus === "saving" ? (
                            <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600 }}>Auto-saving…</Typography>
                          ) : emailAutoSaveStatus === "saved" ? (
                            <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>Auto-saved</Typography>
                          ) : emailAutoSaveStatus === "error" ? (
                            <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600 }}>Auto-save failed</Typography>
                          ) : null}
                          <Button
                            onClick={deleteSelectedEmailTemplate}
                            disabled={!selectedEmailTemplateId || emailTemplatesBusy}
                            sx={{
                              textTransform: "none",
                              borderRadius: 999,
                              px: 2,
                              fontWeight: 900,
                              border: "1px solid rgba(185,28,28,0.25)",
                              color: "#991b1b",
                              background: "#fff",
                              "&:hover": { background: "rgba(239,68,68,0.08)" },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper sx={{ borderRadius: 3, p: 2, border: "1px solid rgba(51,57,153,0.10)" }}>
                    <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1 }}>Email</Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.25,
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0,2fr) minmax(220px,1fr)" },
                        mb: 2,
                      }}
                    >
                      <TextField
                        label="Subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                      <TextField
                        label="From Name"
                        value={emailFromName}
                        onChange={(e) => setEmailFromName(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder={defaultEmailFromName}
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 700 }}>
                        Email Designer
                      </Typography>
                      {emailAutoSaveStatus === "saving" ? (
                        <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600 }}>Auto-saving…</Typography>
                      ) : emailAutoSaveStatus === "saved" ? (
                        <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>Auto-saved</Typography>
                      ) : emailAutoSaveStatus === "error" ? (
                        <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600 }}>Auto-save failed</Typography>
                      ) : null}
                    </Box>

                    <Box sx={{ borderRadius: 2, border: "1px solid #e5e7eb", overflow: "hidden", background: "#fff", minHeight: 520 }}>
                      <EmailEditor
                        ref={emailEditorRef}
                        onReady={handleEmailEditorReady}
                        minHeight={520}
                        options={{
                          features: { stockImages: true },
                        }}
                      />
                    </Box>
                  </Paper>
                </Box>
              ) : (
                <>
                  {emailPreviewBusy ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Generating preview…</Typography>
                    </Box>
                  ) : null}

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: "1px solid rgba(51,57,153,0.18)",
                      background: "#fff",
                      overflow: "hidden",
                      boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
                    }}
                  >
                    <iframe
                      title="Email Preview"
                      style={{ width: "100%", height: "70vh", border: 0, display: "block" }}
                      srcDoc={emailPreviewHtml || getComposedEmailHtml()}
                    />
                  </Box>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
              <Button
                onClick={() => setEmailOpen(false)}
                disabled={emailBusy}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.18)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                Close
              </Button>

              <Button
                onClick={sendEmailNow}
                disabled={emailBusy}
                startIcon={emailBusy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#6366f1,#4f46e5)",
                  "&:hover": { background: "linear-gradient(90deg,#4f46e5,#4338ca)" },
                }}
              >
                Send now
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={createEmailTemplateOpen}
            onClose={() => {
              if (emailTemplatesBusy) return;
              setCreateEmailTemplateOpen(false);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: modalPaperSx }}
          >
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>Create New Template</DialogTitle>
            <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Template name"
                placeholder="e.g. Spring Promo v1"
                value={createEmailTemplateName}
                onChange={(e) => setCreateEmailTemplateName(e.target.value)}
                disabled={emailTemplatesBusy}
                sx={{ background: "#fff", borderRadius: 2 }}
              />
              <FormControl size="small" sx={{ mt: 1.5, width: "100%" }}>
                <InputLabel>Start From (Optional)</InputLabel>
                <Select
                  label="Start From (Optional)"
                  value={createEmailTemplateBaseId}
                  onChange={(e) => setCreateEmailTemplateBaseId(e.target.value)}
                  disabled={emailTemplatesBusy}
                  sx={{ borderRadius: 2, backgroundColor: "white" }}
                >
                  <MenuItem value="">
                    <em>Current draft</em>
                  </MenuItem>
                  {(emailTemplates || []).map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>
                      {tpl.name || tpl.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ display: "block", color: "#64748b", mt: 0.75 }}>
                If you select a template above, this will create a new copy from it.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
              <Button
                onClick={() => setCreateEmailTemplateOpen(false)}
                disabled={emailTemplatesBusy}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.18)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  createEmailTemplateFromCurrent({
                    name: createEmailTemplateName,
                    baseTemplateId: createEmailTemplateBaseId,
                  })
                }
                disabled={emailTemplatesBusy || !String(createEmailTemplateName || "").trim()}
                startIcon={emailTemplatesBusy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#6366f1,#4f46e5)",
                  "&:hover": { background: "linear-gradient(90deg,#4f46e5,#4338ca)" },
                }}
              >
                {emailTemplatesBusy ? "Creating..." : "Create Template"}
              </Button>
            </DialogActions>
          </Dialog>

          <AccountManagerAdminDialog
            open={accountManagerModalOpen}
            onClose={() => {
              if (accountManagerCreateSaving || accountManagerTierSaving) return;
              setAccountManagerModalOpen(false);
            }}
            modalPaperSx={modalPaperSx}
            primary={primary}
            accountManagerModalMode={accountManagerModalMode}
            setAccountManagerModalMode={setAccountManagerModalMode}
            accountManagersLoading={accountManagersLoading}
            formatNumber2={formatNumber2}
            accountManagers={accountManagers}
            accountManagerDisplayName={accountManagerDisplayName}
            accountManagerCreateFormOpen={accountManagerCreateFormOpen}
            setAccountManagerCreateFormOpen={setAccountManagerCreateFormOpen}
            accountManagerForm={accountManagerForm}
            setAccountManagerForm={setAccountManagerForm}
            accountManagerCreateSaving={accountManagerCreateSaving}
            createAccountManager={createAccountManager}
            ACCOUNT_MANAGER_TIER_OPTIONS={ACCOUNT_MANAGER_TIER_OPTIONS}
            accountManagerTierMap={accountManagerTierMap}
            normalizeAccountManagerId={normalizeAccountManagerId}
            setAccountManagerTierMap={setAccountManagerTierMap}
            accountManagerTierSaving={accountManagerTierSaving}
            saveAccountManagerTierAssignments={saveAccountManagerTierAssignments}
          />

          <ConsignorAdminDialog
            open={consignorModalOpen}
            onClose={() => {
              if (consignorCreateSaving) return;
              setConsignorModalOpen(false);
            }}
            modalPaperSx={modalPaperSx}
            primary={primary}
            consignorsLoading={consignorsLoading}
            formatNumber2={formatNumber2}
            consignors={consignors}
            consignorCreateFormOpen={consignorCreateFormOpen}
            setConsignorCreateFormOpen={setConsignorCreateFormOpen}
            consignorForm={consignorForm}
            setConsignorForm={setConsignorForm}
            normalizeConsignorProfileId={normalizeConsignorProfileId}
            consignorCreateSaving={consignorCreateSaving}
            createConsignor={createConsignor}
          />

          <ShippingTextDialog
            open={shippingTextOpen}
            onClose={() => {
              if (shippingTextSaving) return;
              setShippingTextOpen(false);
            }}
            modalPaperSx={modalPaperSx}
            primary={primary}
            shippingTextSaving={shippingTextSaving}
            shippingTextDraft={shippingTextDraft}
            setShippingTextDraft={setShippingTextDraft}
            saveShippingText={saveShippingText}
            shippingTextDirty={shippingTextDirty}
          />

          <AuctionUserDetailsDialog
            open={auctionUserDetailsOpen}
            onClose={() => {
              if (auctionUserDetailsBusy) return;
              clearAuctionUserDetailsAutoSaveTimers();
              setAuctionUserDetailsOpen(false);
            }}
            modalPaperSx={modalPaperSx}
            primary={primary}
            auctionUserDetailsBusy={auctionUserDetailsBusy}
            auctionUserDetailsErr={auctionUserDetailsErr}
            auctionUserDetailsData={auctionUserDetailsData}
            auctionUserDetailsSourceReg={auctionUserDetailsSourceReg}
            auctionUserDetailsEmail={auctionUserDetailsEmail}
            auctionUserDetailsForm={auctionUserDetailsForm}
            auctionUserDetailsSaveError={auctionUserDetailsSaveError}
            auctionUserDetailsSavingByField={auctionUserDetailsSavingByField}
            setAuctionUserDetailsField={setAuctionUserDetailsField}
            flushAuctionUserDetailsFieldSave={flushAuctionUserDetailsFieldSave}
            NamedUrlsEditorComponent={NamedUrlsEditor}
            normalize={normalize}
            buildAuctionUserDetailsEditableForm={buildAuctionUserDetailsEditableForm}
            getFirstLastNameParts={getFirstLastNameParts}
            normalizeTierKey={normalizeTierKey}
            getEffectiveTier={getEffectiveTier}
            normalizeCustomerTypeKey={normalizeCustomerTypeKey}
            getCustomerType={getCustomerType}
            normalizeAccountManagerId={normalizeAccountManagerId}
            getResolvedAccountManagerIdForUser={getResolvedAccountManagerIdForUser}
            getAccountManagerSelectOptionsForUser={getAccountManagerSelectOptionsForUser}
            getResolvedAccountManagerNameForUser={getResolvedAccountManagerNameForUser}
            getPickupLocationSelectOptions={getPickupLocationSelectOptions}
            CUSTOMER_TYPE_OPTIONS={CUSTOMER_TYPE_OPTIONS}
            HIBID_TIER_OPTIONS={HIBID_TIER_OPTIONS}
            isEmail={isEmail}
            getLcvEstimate={getLcvEstimate}
            getRegsCount={getRegsCount}
            getBidsCount={getBidsCount}
            getHibidBids90Count={getHibidBids90Count}
            getRegs12mCount={getRegs12mCount}
            toAbsInt={toAbsInt}
            getRollingWin={getRollingWin}
            getLotsWonFromWin={getLotsWonFromWin}
            getAvgHammerFromWin={getAvgHammerFromWin}
            getBidIntensity90={getBidIntensity90}
            getBpi90={getBpi90}
            moneyPlain={moneyPlain}
            formatNumber2={formatNumber2}
            safeNum={safeNum}
            fmtDate={fmtDate}
            customerTypeLabel={customerTypeLabel}
          />

          <RegistrationFulfillmentConfirmDialog
            open={registrationFulfillmentConfirm.open}
            onClose={closeRegistrationFulfillmentConfirm}
            onConfirm={() => {
              void confirmRegistrationFulfillmentChange();
            }}
            modalPaperSx={modalPaperSx}
            primary={primary}
            registrationFulfillmentConfirm={registrationFulfillmentConfirm}
            registrationFulfillmentConfirmBusy={registrationFulfillmentConfirmBusy}
          />

          <Dialog
            open={registrationFulfillmentDetails.open}
            onClose={closeRegistrationFulfillmentDetails}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: modalPaperSx }}
          >
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
              {registrationFulfillmentDetailsButtonMeta.label} Details
            </DialogTitle>
            <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Box>
                  <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                    {registrationFulfillmentDetailsUserName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.35 }}>
                    {registrationFulfillmentDetailsRow?.bidderEmail || registrationFulfillmentDetailsRow?.email || "—"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 900,
                    color: registrationFulfillmentDetailsButtonMeta.color,
                    background: registrationFulfillmentDetailsButtonMeta.background,
                    border: registrationFulfillmentDetailsButtonMeta.border,
                  }}
                >
                  {registrationFulfillmentDetailsButtonMeta.label}
                </Box>
              </Box>

              <Box sx={{ display: "grid", gap: 1.1 }}>
                <Box
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                    Fulfilled
                  </Typography>
                  <Typography sx={{ color: "#111827", fontWeight: 900, mt: 0.35 }}>
                    {registrationFulfillmentDetailsMode === "shipping"
                      ? registrationFulfillmentDetailsShipped.shippedStatusLabel || "—"
                      : registrationFulfillmentDetailsMode === "delivery"
                        ? registrationFulfillmentDetailsDelivery.deliveredStatusLabel ||
                          `${registrationFulfillmentDetailsDelivery.delivered ? registrationFulfillmentDetailsLotsCount : 0}/${registrationFulfillmentDetailsLotsCount || 0}`
                        : registrationFulfillmentDetailsPickup.pickupStatusLabel || "—"}
                  </Typography>
                </Box>

                {registrationFulfillmentDetailsMode === "pickup" ? (
                  <>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                          Pickup Location
                        </Typography>
                        {!registrationFulfillmentDetailsPickupEditing
                          ? renderRegistrationPickupEditButton(
                            registrationFulfillmentDetailsRow,
                            !isEmail(registrationFulfillmentDetailsEmail) || registrationFulfillmentDetailsPickupSaving
                          )
                          : null}
                      </Box>
                      {!registrationFulfillmentDetailsPickupEditing ? (
                        registrationFulfillmentDetailsPickupLocationValue ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                              {registrationFulfillmentDetailsPickupLocationValue}
                            </Typography>
                            {getLocationAddress(registrationFulfillmentDetailsPickupLocationValue) ? (
                              <Typography variant="caption" sx={{ color: "#64748b" }}>
                                {getLocationAddress(registrationFulfillmentDetailsPickupLocationValue)}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 800 }}>—</Typography>
                        )
                      ) : (
                        <FormControl size="small" fullWidth>
                          <Select
                            value={registrationFulfillmentDetailsPickupLocationValue}
                            displayEmpty
                            disabled={!isEmail(registrationFulfillmentDetailsEmail) || registrationFulfillmentDetailsPickupSaving}
                            onChange={(e) => {
                              const nextLocation = String(e.target.value || "");
                              setAregPickupDraftByEmail((prev) => ({
                                ...prev,
                                [registrationFulfillmentDetailsEmail]: {
                                  ...(prev?.[registrationFulfillmentDetailsEmail] || {}),
                                  location: nextLocation,
                                  appointmentDateKey: "",
                                  appointmentAtMs: 0,
                                },
                              }));
                            }}
                            sx={{ borderRadius: 2, background: "#fff" }}
                          >
                            <MenuItem value="">
                              <em>Select location</em>
                            </MenuItem>
                            {registrationFulfillmentDetailsPickupLocationOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2">{option}</Typography>
                                  {getLocationAddress(option) ? (
                                    <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                                      {getLocationAddress(option)}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>

                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Pickup Date Time
                      </Typography>
                      {!registrationFulfillmentDetailsPickupEditing ? (
                        registrationFulfillmentDetailsPickupAppointment?.appointmentAtMs ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.6 }}>
                            <Typography sx={{ color: "#111827", fontWeight: 900 }}>
                              {fmtDate(registrationFulfillmentDetailsPickupAppointment?.appointmentAtMs)}
                            </Typography>
                            {registrationFulfillmentDetailsPickupAppointment?.label ? (
                              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                                {registrationFulfillmentDetailsPickupAppointment.label}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 800, mt: 0.6 }}>—</Typography>
                        )
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, mt: 0.8 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Pickup Date</InputLabel>
                            <Select
                              value={registrationFulfillmentDetailsPickupAppointmentDateKeyValue}
                              label="Pickup Date"
                              disabled={
                                !isEmail(registrationFulfillmentDetailsEmail) ||
                                registrationFulfillmentDetailsPickupSaving ||
                                !registrationFulfillmentDetailsPickupLocationValue
                              }
                              onChange={(e) => {
                                const nextDateKey = String(e.target.value || "");
                                setAregPickupDraftByEmail((prev) => ({
                                  ...prev,
                                  [registrationFulfillmentDetailsEmail]: {
                                    ...(prev?.[registrationFulfillmentDetailsEmail] || {}),
                                    appointmentDateKey: nextDateKey,
                                    appointmentAtMs: 0,
                                  },
                                }));
                              }}
                              sx={{ background: "#fff", borderRadius: 2 }}
                            >
                              {!registrationFulfillmentDetailsPickupLocationValue ? (
                                <MenuItem value="" disabled>
                                  Select a location first
                                </MenuItem>
                              ) : registrationFulfillmentDetailsPickupDateOptions.length === 0 ? (
                                <MenuItem value="" disabled>
                                  No pickup dates available
                                </MenuItem>
                              ) : (
                                registrationFulfillmentDetailsPickupDateOptions.map((option) => (
                                  <MenuItem key={option.dateKey} value={option.dateKey}>
                                    {option.label}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Pickup Time</InputLabel>
                            <Select
                              value={
                                registrationFulfillmentDetailsPickupAppointmentAtMsValue
                                  ? String(registrationFulfillmentDetailsPickupAppointmentAtMsValue)
                                  : ""
                              }
                              label="Pickup Time"
                              disabled={
                                !isEmail(registrationFulfillmentDetailsEmail) ||
                                registrationFulfillmentDetailsPickupSaving ||
                                !registrationFulfillmentDetailsPickupLocationValue ||
                                !registrationFulfillmentDetailsPickupAppointmentDateKeyValue
                              }
                              onChange={(e) => {
                                const nextAppointmentValue = String(e.target.value || "");
                                const nextTime =
                                  registrationFulfillmentDetailsPickupTimeOptions.find(
                                    (option) => option.value === nextAppointmentValue
                                  ) || null;
                                const nextDraft = {
                                  ...(aregPickupDraftByEmail[registrationFulfillmentDetailsEmail] || {}),
                                  location: registrationFulfillmentDetailsPickupLocationValue,
                                  appointmentDateKey: registrationFulfillmentDetailsPickupAppointmentDateKeyValue,
                                  appointmentAtMs: Number(nextTime?.appointmentAtMs || 0) || 0,
                                  additionalInfo: registrationFulfillmentDetailsPickupAdditionalInfo,
                                };
                                setAregPickupDraftByEmail((prev) => ({
                                  ...prev,
                                  [registrationFulfillmentDetailsEmail]: nextDraft,
                                }));
                                if (canSaveRegistrationPickupDraft(nextDraft)) {
                                  void saveRegistrationPickupDraft(registrationFulfillmentDetailsRow, { draftOverride: nextDraft });
                                }
                              }}
                              sx={{ background: "#fff", borderRadius: 2 }}
                            >
                              {!registrationFulfillmentDetailsPickupLocationValue ? (
                                <MenuItem value="" disabled>
                                  Select a location first
                                </MenuItem>
                              ) : !registrationFulfillmentDetailsPickupAppointmentDateKeyValue ? (
                                <MenuItem value="" disabled>
                                  Select a date first
                                </MenuItem>
                              ) : registrationFulfillmentDetailsPickupTimeOptions.length === 0 ? (
                                <MenuItem value="" disabled>
                                  No pickup times available
                                </MenuItem>
                              ) : (
                                registrationFulfillmentDetailsPickupTimeOptions.map((option) => (
                                  <MenuItem key={`${option.slotId}-${option.value}`} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                          {registrationFulfillmentDetailsPickupSelectedTimeLabel ? (
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                              {registrationFulfillmentDetailsPickupSelectedTimeLabel}
                            </Typography>
                          ) : null}
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Additional Information
                      </Typography>
                      {registrationFulfillmentDetailsPickupEditing ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7, mt: 0.8 }}>
                          <TextField
                            size="small"
                            multiline
                            minRows={2}
                            fullWidth
                            value={registrationFulfillmentDetailsPickupAdditionalInfo}
                            disabled={!isEmail(registrationFulfillmentDetailsEmail) || registrationFulfillmentDetailsPickupSaving}
                            onChange={(e) => {
                              const nextAdditionalInfo = String(e.target.value || "");
                              setAregPickupDraftByEmail((prev) => ({
                                ...prev,
                                [registrationFulfillmentDetailsEmail]: {
                                  ...(prev?.[registrationFulfillmentDetailsEmail] || {}),
                                  additionalInfo: nextAdditionalInfo,
                                },
                              }));
                            }}
                            placeholder="Additional info"
                            sx={{ background: "#fff", borderRadius: 2 }}
                          />
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {renderRegistrationPickupSaveButton({
                              row: registrationFulfillmentDetailsRow,
                              label: "Save Pickup",
                              disabled:
                                !isEmail(registrationFulfillmentDetailsEmail) ||
                                registrationFulfillmentDetailsPickupSaving ||
                                !registrationFulfillmentDetailsCanSavePickupDraft,
                            })}
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => closeRegistrationPickupEditor(registrationFulfillmentDetailsEmail)}
                              disabled={registrationFulfillmentDetailsPickupSaving}
                              sx={{
                                textTransform: "none",
                                borderRadius: 999,
                                px: 1.5,
                                fontWeight: 900,
                                color: "#475569",
                              }}
                            >
                              Close
                            </Button>
                          </Box>
                        </Box>
                      ) : registrationFulfillmentDetailsPickupAdditionalInfo ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#334155",
                            fontWeight: 700,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            display: "block",
                            mt: 0.6,
                          }}
                        >
                          {registrationFulfillmentDetailsPickupAdditionalInfo}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 800, mt: 0.6 }}>—</Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Pickup Selection
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1.1,
                          py: 0.45,
                          mt: 0.8,
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 900,
                          color: registrationFulfillmentDetailsPickupSelectionMeta.textColor,
                          background: registrationFulfillmentDetailsPickupSelectionMeta.background,
                          border: registrationFulfillmentDetailsPickupSelectionMeta.border,
                        }}
                      >
                        {registrationFulfillmentDetailsPickupSelectionMeta.label}
                      </Box>
                    </Box>
                  </>
                ) : registrationFulfillmentDetailsMode === "delivery" ? (
                  <>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Delivery Date
                      </Typography>
                      <Typography sx={{ color: "#111827", fontWeight: 900, mt: 0.6 }}>
                        {registrationFulfillmentDetailsDelivery.windowStartAtMs
                          ? fmtDate(registrationFulfillmentDetailsDelivery.windowStartAtMs)
                          : registrationFulfillmentDetailsDelivery.deliveryDateLabel || registrationFulfillmentDetailsDelivery.deliveryDateKey || "—"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Delivery Location
                      </Typography>
                      <Typography sx={{ color: "#111827", fontWeight: 900, mt: 0.6 }}>
                        {registrationFulfillmentDetailsDelivery.location || "—"}
                      </Typography>
                      {registrationFulfillmentDetailsDelivery.address ? (
                        <Typography variant="body2" sx={{ color: "#475569", mt: 0.5 }}>
                          {registrationFulfillmentDetailsDelivery.address}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Delivery Status
                      </Typography>
                        <Typography sx={{ color: "#111827", fontWeight: 900, mt: 0.6 }}>
                        {registrationFulfillmentDetailsDelivery.delivered
                          ? "Delivered"
                          : registrationFulfillmentDetailsDelivery.isScheduled
                            ? "Scheduled"
                            : registrationFulfillmentDetailsDelivery.status || "Pending"}
                        </Typography>
                      {registrationFulfillmentDetailsDelivery.transactionId ? (
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.45 }}>
                          Transaction ID: {registrationFulfillmentDetailsDelivery.transactionId}
                        </Typography>
                      ) : null}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Shipping Status
                      </Typography>
                      <Typography sx={{ color: "#111827", fontWeight: 900, mt: 0.6 }}>
                        {registrationFulfillmentDetailsShipped.shippedStatusLabel || "—"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Method
                      </Typography>
                      <Typography sx={{ color: "#111827", fontWeight: 900, mt: 0.6 }}>
                        Shipping selected for this registration.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
              <Button
                onClick={closeRegistrationFulfillmentDetails}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.18)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <DailyRegistrationActivityUsersDialog
            open={regActivityUsersOpen}
            loading={regActivityUsersLoading}
            onClose={closeRegActivityUsersDialog}
            modalPaperSx={modalPaperSx}
            primary={primary}
            regActivityUsersType={regActivityUsersType}
            regActivityUsersDateKey={regActivityUsersDateKey}
            regActivityUsersError={regActivityUsersError}
            regActivityUsersRows={regActivityUsersRows}
          />

          <ProductSearchRefreshDialog
            open={productSearchRefreshConfirmOpen}
            onClose={closeProductSearchRefreshConfirm}
            onConfirm={continueProductSearchRefreshAnyway}
            productSearchBusy={productSearchBusy}
            modalPaperSx={modalPaperSx}
          />

          <Dialog
            open={fullDescriptionDialog.open}
            onClose={() => setFullDescriptionDialog({ open: false, title: "Full Description", text: "" })}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: modalPaperSx }}
          >
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
              {fullDescriptionDialog.title || "Full Description"}
            </DialogTitle>
            <DialogContent dividers sx={{ backgroundColor: "#f9fafb", maxHeight: "70vh" }}>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  color: "#374151",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  fontFamily: "inherit",
                  fontSize: "0.95rem",
                  lineHeight: 1.65,
                }}
              >
                {fullDescriptionDialog.text || "—"}
              </Box>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
              <Button
                onClick={() => setFullDescriptionDialog({ open: false, title: "Full Description", text: "" })}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#6b7280,#4b5563)",
                  "&:hover": { background: "linear-gradient(90deg,#4b5563,#374151)" },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={toastClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={toastClose} severity={toast.severity} sx={{ borderRadius: 2, fontWeight: 700 }}>
              {toast.msg}
            </Alert>
          </Snackbar>
        </Paper>
      </Box>
    </Box>
  );
}
